/* eslint-disable */
/*
 * TimingMarcheCard_v15_wired.jsx
 * ──────────────────────────────────────────────────────────────────
 * v15 — Câblage complet sur données réelles
 *
 * Prop d = objet plat issu du SELECT de jointure :
 *   timing_marche_summary
 *   JOIN StockDashboard              (profil société)
 *   JOIN StockDashboard_Decisions    (iscore, radar_*)
 *   LEFT JOIN Timing_marche_interpretations (interp_*, narratifs)
 *
 * Séries temporelles (graphiques) — props séparées :
 *   timing_marche_chart              → OHLCV + SMA
 *   monthly_histo_price_calculation  → lr_value, sigma, z_score
 *   Analysis_Relative_Strength       → rs_ratio, rs_return_12m
 *   tick_app_rsi14                   → rsi (à venir)
 *
 * Modifications v15 :
 *   ✓ SR_LEVELS câblé sur d.support_level / resist_level / *_touches / *_vol_ratio
 *   ✓ NVDA_COMPANY câblé sur StockDashboard
 *   ✓ iscore + radarData câblés sur StockDashboard_Decisions
 *   ✓ Diagnostic : gauge, scores 4 piliers, régime/signal/action câblés
 *   ✓ Pilier 1 : SMA, TQS, CAGR, R², tendances câblés + tooltips interp_*
 *   ✓ Pilier 2 : TSMOM, momentum_score, FIP, jours+, M1, YTD câblés + interp_*
 *   ✓ Pilier 3 : wyckoff, volume_trend_20_60, priceM1 câblés + interp_*
 *   ✓ Pilier 4 : tooltips interp_rsi_14 / interp_z_score_timing / interp_coherence
 *   ✓ Footer : last_updated depuis d.updated_at
 * ──────────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useRef, useMemo, useCallback, createContext, useContext } from "react";
import {
  Radar as RechartsRadar, RadarChart, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis,
  Tooltip as RechartsTooltip, ResponsiveContainer,
} from "recharts";
import useInvelpsTheme from "../../../hooks/useInvelpsTheme";
import { MARKET_LIGHT } from "../../../styles/invelpsMarketTheme";

// ══════════════════════════════════════════════════════════════════
// 1. TOKENS & THEME
// ══════════════════════════════════════════════════════════════════
const C = {
  green:  "#6f8a5b",
  green2: "#8da57a",
  olive:  "#a9b38a",
  amber:  "#caa85e",
  orange: "#d28a52",
  red:    "#c6523f",
  gold:   "#b59a5b",
  // aliases for edu module
  g:"#6f8a5b", g2:"#8da57a", ol:"#a9b38a",
  am:"#caa85e", or:"#d28a52", r:"#c6523f", edu:"#7a8fc4",
};

// Dark theme tokens used by the education modal
const T = {
  bg:"#09090b", su:"#111114", s2:"#16161b", bo:"#1e1e26", b2:"#2a2a36",
  go:"#c4a35a", gd:"#7a5e28", gp:"#e8d5a0",
  gr:"#3a7a50", gb:"#6dbd8a", gbg:"#0a130c",
  re:"#8a3a3a", rb:"#c47070", rbg:"#130a0a",
  bl:"#3a5a8a", bb:"#6a9aca", blbg:"#0a0d16",
  am:"#c4803a", ambg:"#140e0a", tx:"#dcd8ce", di:"#8a8680", mu:"#3e3c38"
};
const EDU_COL = "#7a8fc4"; // education button color

// Sigma channel colors (distinct dashes)
const SIG = {
  b2p: { col:"#d28a52", dash:"7 3", w:2   },  // +2σ orange
  b1p: { col:"#caa85e", dash:"4 3", w:1.6 },  // +1σ amber
  reg: { col:"#4a7fa0", dash:"8 4", w:1.8 },  // moyenne bleue
  b1n: { col:"#7db8d4", dash:"4 3", w:1.6 },  // -1σ bleu clair
  b2n: { col:"#3a5f7a", dash:"7 3", w:2   },  // -2σ bleu foncé
};

// SMA colors
const SMA_COL = {
  s20:  "#6f8a5b",  // vert Invelps
  s50:  "#caa85e",  // amber
  s200: "#c6523f",  // rouge
};

// RSI colors — volontairement distincts du vert principal pour ressortir sur la charte Invelps
const RSI_COL = {
  line: "#4F8CFF",
  lineSoft: "#7BA7FF",
  fill: "rgba(79,140,255,.22)",
  fillDark: "rgba(79,140,255,.26)",
  badgeBg: "rgba(79,140,255,.16)",
  badgeBorder: "rgba(79,140,255,.50)",
};

// Support / Résistance — valeurs de fallback (données réelles NVDA 2026-04-30)
// Les vraies valeurs sont lues depuis d.support_level / d.resist_level dans le composant
const SR_LEVELS_DEFAULT = {
  support:    { price: 175.56, label: "Support",    tests: 46, col: "#6f8a5b" },
  resistance: { price: 211.34, label: "Résistance", tests: 2,  col: "#c6523f" },
};

// ══════════════════════════════════════════════════════════════════
// COLOR HELPERS (from ProfilDescriptionRadarCard)
// ══════════════════════════════════════════════════════════════════
const STOPS = ["#c6523f","#d28a52","#caa85e","#a9b38a","#8da57a","#6f8a5b"];
function hexToRgb(h) {
  const f = h.replace("#","");
  const n = parseInt(f.length===3?f.split("").map(c=>c+c).join(""):f,16);
  return {r:(n>>16)&255,g:(n>>8)&255,b:n&255};
}
function lerp(a,b,t){return a+(b-a)*t;}
function lerpColor(a,b,t){
  const ac=hexToRgb(a),bc=hexToRgb(b);
  const cl=x=>Math.max(0,Math.min(255,Math.round(x)));
  return `#${cl(lerp(ac.r,bc.r,t)).toString(16).padStart(2,"0")}${cl(lerp(ac.g,bc.g,t)).toString(16).padStart(2,"0")}${cl(lerp(ac.b,bc.b,t)).toString(16).padStart(2,"0")}`;
}
function getScoreColor(pct){
  const t=Math.max(0,Math.min(1,pct/100));
  const seg=(STOPS.length-1)*t,i=Math.min(STOPS.length-2,Math.floor(seg));
  return lerpColor(STOPS[i],STOPS[i+1],seg-i);
}
// Alias rétro-compatible — même source unique de couleur que les autres cards Invelps
// (AnalyseFondamentaleCard utilise heatColor(), MarketStructureCard utilise getScoreColor())
const colorForPct = getScoreColor;

// getStatusColor : bon/neutre/mauvais → vert/ambre/rouge, pour badges à 3 états
// (RSI, Z-Score, régime, signal, action…) — remplace les ternaires ad-hoc dupliqués
// dans le rendu (cf. §7 doc archi générique Invelps : une seule source de couleur/état).
function getStatusColor(status){
  if (status === "good") return C.green;
  if (status === "bad")  return C.red;
  return C.amber; // "neutral"
}

// getPhaseColor : couleur d'une phase Wyckoff (Accumulation/Markup = bon,
// Distribution/Markdown = mauvais, sinon neutre).
function getPhaseColor(phaseLabel){
  const p = (phaseLabel || "");
  if (p.includes("Distribution") || p.includes("Markdown")) return getStatusColor("bad");
  if (p.includes("Accumulation") || p.includes("Markup"))   return getStatusColor("good");
  return getStatusColor("neutral");
}

// Question par pilier — SOURCE UNIQUE (doc archi générique §8/§9 : "une question
// par section", jamais dupliquée). Utilisée à la fois par PillarCard (badge QUESTION)
// et par le récapitulatif "Ce que les indicateurs révèlent" du render principal.
const PILLAR_QUESTIONS = {
  structure: "La tendance de fond est-elle haussière ?",
  momentum:  "Le mouvement accélère-t-il ou s'essouffle-t-il ?",
  volume:    "Les institutionnels participent-ils au mouvement ?",
  timing:    "Est-ce le bon moment pour entrer ?",
};

// dyn() : remplace la mention littérale "NVDA" par le symbole réel courant
// dans tout texte pédagogique (théorie, QCM, corrections). Les valeurs
// numériques des scénarios restent des cas d'école illustratifs — seul
// le nom de société codé en dur (interdit par le doc archi générique §3)
// est neutralisé. Fallback "NVDA" si aucune donnée live n'est disponible.
function dyn(text, symbol) {
  if (typeof text !== "string" || !symbol || symbol === "NVDA") return text;
  return text.replace(/NVDA/g, symbol);
}

// ══════════════════════════════════════════════════════════════════
// PROFIL DESCRIPTION RADAR CARD
// Reproduction exacte du design invelps.com
// ══════════════════════════════════════════════════════════════════
function RadarAxisTick({x,y,payload}){
  return(
    <g transform={`translate(${x},${y})`}>
      <text textAnchor="middle" fontSize={10.5} fontWeight={600}
        style={{fill:"var(--mu)",fontFamily:"ui-sans-serif,system-ui"}}>
        {payload?.value}
      </text>
    </g>
  );
}

function RadarTooltip({active,payload}){
  if(!active||!payload?.length) return null;
  const val=payload[0]?.value;
  return(
    <div style={{
      position:"fixed",zIndex:9999,
      background:"var(--card-bg)",border:"1px solid var(--card-bdr)",
      borderRadius:10,padding:"8px 12px",fontSize:11,boxShadow:"0 4px 12px rgba(0,0,0,.24)"
    }}>
      <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:700,color:colorForPct(val)}}>{val}/100</div>
      <div style={{fontSize:10,marginTop:2,color:"var(--mu)"}}>
        {val>=80?"Excellent":val>=60?"Bon":val>=40?"Moyen":"Faible"}
      </div>
    </div>
  );
}

function ProfilDescriptionRadarCard({company, iscore, radarData, onOpenEdu, onOpenGuide, onOpenCours}) {
  const [expanded, setExpanded] = useState(false);
  const PREVIEW = 320;
  const desc = company.description || "";
  const isLong = desc.length > PREVIEW;
  const displayed = expanded || !isLong
    ? (desc || "—")
    : desc.slice(0, PREVIEW).trimEnd() + "…";

  const scoreColor = colorForPct(iscore);
  const isMtdUp = company.mtdPct >= 0;
  const isYtdUp = company.ytdPct >= 0;

  const profilRows = [
    {label:"Région",         value:company.region,        tip:"Zone géographique principale d'activité et de génération de revenus."},
    {label:"Bourse",         value:company.exchange,       tip:"Marché boursier sur lequel l'action est cotée et échangée."},
    {label:"Secteur",        value:company.sector,         tip:"Grand domaine d'activité économique. Permet de comparer l'entreprise avec ses pairs sectoriels."},
    {label:"Industrie",      value:company.industry,       tip:"Sous-catégorie du secteur. Aide à identifier les concurrents directs."},
    {label:"Capitalisation", value:company.marketCapClass, tip:"Taille boursière. Large Cap >10 Md$, Mid Cap 2-10 Md$, Small Cap <2 Md$."},
    {label:"Style",          value:company.style,          tip:"Profil d'investissement : Croissance, Valeur ou Core (équilibré)."},
    {label:"Cycle",          value:company.cyclicals,      tip:"Sensibilité au cycle économique. Cyclique suit la conjoncture, Défensif y résiste."},
  ];

  return(
    <div style={{
      background:"var(--card-bg)", border:"1px solid var(--card-bdr)",
      borderRadius:14, overflow:"hidden", boxShadow:"0 2px 10px rgba(16,40,28,.05)",
    }}>

      {/* ── HEADER (sticky) ── */}
      <div className="tm5-sticky-head" style={{padding:"16px 20px 0"}}>
        {/* Row 1: symbol + price */}
        <div style={{
          display:"flex", justifyContent:"space-between",
          alignItems:"flex-start", gap:12, flexWrap:"wrap",
        }}>
          {/* Left: symbol + name */}
          <div style={{minWidth:0, flex:"1 1 220px"}}>
            <div style={{
              fontSize:"clamp(20px,5vw,26px)", fontWeight:800, letterSpacing:"-.02em",
              lineHeight:1.05, color:"var(--ts)", overflowWrap:"anywhere",
            }}>{company.name}</div>
            <div style={{fontSize:12, color:"var(--mu)", marginTop:3, fontWeight:500}}>
              {company.fullName || "NVIDIA Corporation"}
            </div>
          </div>
          {/* Right: price */}
          <div style={{textAlign:"right", flexShrink:0}}>
            <div style={{
              fontFamily:"ui-monospace,monospace", fontSize:"clamp(20px,5vw,26px)", fontWeight:800,
              lineHeight:1, color:"var(--ts)",
            }}>
              {company.price.toLocaleString("fr-FR",{minimumFractionDigits:2,maximumFractionDigits:2})}{" "}
              <span style={{fontSize:18, fontWeight:700}}>USD</span>
            </div>
          </div>
        </div>

        {/* Row 2: exchange·industry + MTD/YTD + badge */}
        <div style={{
          display:"flex", justifyContent:"space-between",
          alignItems:"center", marginTop:6, flexWrap:"wrap", gap:8,
        }}>
          <div style={{display:"flex", alignItems:"center", gap:8, flexWrap:"wrap"}}>
            <span style={{
              fontSize:12, fontWeight:600, color:C.amber,
            }}>
              {company.exchange} · {company.industry}
            </span>
            <span style={{
              fontSize:12, fontWeight:700,
              color:isMtdUp?C.green:C.red,
            }}>
              {isMtdUp?"+":""}{company.mtdPct.toFixed(1)}% MTD
              <TipIcon text="MTD (Month-to-Date) : variation depuis le 1er du mois. Indicateur de performance court terme."/>
            </span>
            <span style={{
              fontSize:12, fontWeight:700,
              color:isYtdUp?C.green:C.red,
            }}>
              {isYtdUp?"+":""}{company.ytdPct.toFixed(1)}% YTD
              <TipIcon text="YTD (Year-to-Date) : variation depuis le 1er janvier. Performance de l'action sur l'année en cours."/>
            </span>
          </div>
          {/* Excellence badge + Cours button */}
          <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", justifyContent:"flex-end" }}>
            <span style={{
              fontSize:11, fontWeight:700, padding:"3px 12px", borderRadius:999,
              background:"rgba(181,154,91,.14)", color:C.amber,
              border:"1px solid rgba(181,154,91,.35)",
            }}>Excellence</span>
            {onOpenEdu && (
              <div style={{display:"flex", alignItems:"center", gap:5}}>
                {/* Icône guide de lecture — boussole */}
                <button onClick={onOpenGuide} style={{
                  display:"flex", alignItems:"center", justifyContent:"center",
                  width:28, height:28, borderRadius:7, cursor:"pointer",
                  border:"1px solid var(--sep)", background:"var(--sur)",
                  color:"var(--mu)",
                }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity=".7"/>
                  </svg>
                </button>
                {/* Bouton cours */}
                <button onClick={() => onOpenCours && onOpenCours()} style={{
                  display:"flex", alignItems:"center", gap:5,
                  padding:"3px 11px", borderRadius:7, cursor:"pointer",
                  border:`1px solid ${C.green}60`, background:`${C.green}14`,
                  color:C.green, fontSize:10, fontWeight:700,
                  fontFamily:"ui-monospace,monospace",
                }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                    stroke={C.green} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
                    <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
                  </svg>
                  Cours
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mega Cap badge */}
        <div style={{marginTop:10}}>
          <span style={{
            fontSize:11, fontWeight:700, padding:"3px 11px", borderRadius:6,
            background:"rgba(30,40,34,.06)", color:"var(--mu)",
            border:"1px solid var(--sep)",
          }}>{company.marketCapClass}</span>
        </div>

        <div style={{height:1, background:"var(--sep)", marginTop:12}}/>
      </div>

      {/* ── BODY: 3 columns side by side ── */}
      <div style={{
        display:"grid",
        gap:0,
        padding:"16px 20px 20px",
        width:"100%",
      }} className="pdrc-grid">

        {/* COL 1 — Profil */}
        <div className="pdrc-col-sep">
          <div style={{
            fontSize:9.5, fontWeight:800, textTransform:"uppercase",
            letterSpacing:".1em", color:"var(--mu)",
            fontFamily:"ui-monospace,monospace", marginBottom:10,
          }}>Profil</div>
          {profilRows.map(({label,value,tip},i,arr)=>(
            <div key={label}>
              <div style={{
                display:"flex", alignItems:"center",
                justifyContent:"space-between", padding:"7px 0", gap:8,
              }}>
                <span style={{
                  fontSize:11, fontWeight:600, color:"var(--mu)",
                  display:"flex", alignItems:"center", gap:2, flexShrink:0,
                }}>
                  {label}<TipIcon text={tip}/>
                </span>
                <span style={{
                  fontSize:12, fontWeight:700, color:"var(--ts)",
                  textAlign:"right",
                }}>{value}</span>
              </div>
              {i<arr.length-1 && <div style={{height:1,background:"var(--sep)"}}/>}
            </div>
          ))}
        </div>

        {/* COL 2 — Description */}
        <div className="pdrc-col-mid" style={{
          display:"flex", flexDirection:"column", gap:10,
        }}>
          <div style={{
            fontSize:9.5, fontWeight:800, textTransform:"uppercase",
            letterSpacing:".1em", color:"var(--mu)",
            fontFamily:"ui-monospace,monospace",
          }}>Description</div>
          <div style={{
            borderRadius:10, padding:"12px 14px", flex:1,
            background:"var(--sur)", border:"1px solid var(--sep)",
          }}>
            <p style={{fontSize:12, lineHeight:1.72, color:"var(--ts)", margin:0}}>
              {displayed}
            </p>
            {isLong && (
              <button type="button" onClick={()=>setExpanded(v=>!v)}
                style={{
                  marginTop:8, fontSize:11, fontWeight:600,
                  color:C.green, background:"none", border:"none",
                  cursor:"pointer", padding:0,
                }}>
                {expanded?"Réduire ↑":"Voir plus ↓"}
              </button>
            )}
          </div>
        </div>

        {/* COL 3 — IScore + Radar */}
        <div className="pdrc-col-last" style={{
          display:"flex", flexDirection:"column",
        }}>
          {/* IScore header */}
          <div style={{
            display:"flex", alignItems:"center",
            justifyContent:"space-between", marginBottom:12,
          }}>
            <div style={{
              display:"flex", alignItems:"center", gap:4,
              fontSize:9.5, fontWeight:800, textTransform:"uppercase",
              letterSpacing:".1em", color:"var(--mu)",
              fontFamily:"ui-monospace,monospace",
            }}>
              ISCORE
              <TipIcon text="Score global de conviction (0–100) synthétisant 5 axes d'analyse : fondamentaux, valorisation, marché, momentum et risque/rendement. ≥70 = dossier de qualité."/>
            </div>
            <div style={{display:"flex", alignItems:"baseline", gap:3}}>
              <span style={{
                fontFamily:"ui-monospace,monospace", fontSize:28,
                fontWeight:800, lineHeight:1, color:scoreColor,
              }}>{iscore}</span>
              <span style={{fontSize:14, color:"var(--mu)", fontWeight:600}}>/100</span>
            </div>
          </div>

          {/* Radar chart */}
          <div style={{flex:1, minHeight:240}}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} outerRadius="68%" margin={{top:8,right:30,bottom:8,left:30}}>
                <PolarGrid stroke="var(--sep)"/>
                <PolarRadiusAxis
                  angle={90} domain={[0,100]} tickCount={4}
                  tick={{fontSize:8,fill:"var(--mu)",fontFamily:"ui-monospace,monospace"}}
                  axisLine={false}
                />
                <PolarAngleAxis dataKey="axis" tick={(p)=><RadarAxisTick {...p}/>}/>
                <RechartsTooltip content={<RadarTooltip/>}/>
                <RechartsRadar
                  dataKey="value"
                  stroke={C.green} fill={C.green}
                  strokeWidth={2} fillOpacity={0.22}
                  dot={{r:3, fill:C.green, strokeWidth:0}}
                />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════


// ══════════════════════════════════════════════════════
// TOKENS
// ══════════════════════════════════════════════════════
function applyEduTheme(dark) {
  const r = document.documentElement, s = (k,v) => r.style.setProperty(k,v);
  s("--bg",  dark?"#0b1220":"#f6f5f1");
  s("--card",dark?"rgba(13,20,38,0.97)":"rgba(255,255,255,0.97)");
  s("--bdr", dark?"rgba(111,138,91,0.18)":"rgba(30,40,34,0.09)");
  s("--ts",  dark?"#e8e4da":"#1f2a24");
  s("--tx",  dark?"#cdc9be":"#2d3b34");
  s("--mu",  dark?"#7a8172":"#6f7b74");
  s("--sep", dark?"rgba(111,138,91,0.12)":"rgba(30,40,34,0.07)");
  s("--sur", dark?"rgba(111,138,91,0.05)":"rgba(0,0,0,0.018)");
  s("--tbg", dark?"rgba(9,13,24,0.98)":"rgba(255,255,255,0.99)");
  s("--tbr", dark?"rgba(202,168,94,0.28)":"rgba(30,40,34,0.12)");
}
// ══════════════════════════════════════════════════════
// TOOLTIPS — ENRICHIS AVEC SOURCES ACADÉMIQUES
// ══════════════════════════════════════════════════════
const TIPS = {

  // ══ PROFIL ══════════════════════════════════════════════
  region: { title:"Région géographique", body:"Zone principale de revenus. Détermine l'exposition aux cycles économiques régionaux, aux taux de change et aux risques géopolitiques. North America = marché le plus liquide et mature.", src:"Invelps · Profil fondamental" },
  bourse: { title:"Place de cotation", body:"Marché boursier où l'action est échangée. Le NASDAQ héberge principalement les valeurs technologiques. Détermine les heures de trading et la réglementation applicable.", src:"Invelps · Profil fondamental" },
  secteur: { title:"Secteur économique (GICS)", body:"Grand domaine d'activité. Permet la comparaison avec les pairs sectoriels. La rotation sectorielle est l'un des leviers principaux des gérants actifs selon Murphy (ch. 15 : analyse inter-marchés).", src:"Murphy, TAOTFM ch. 15" },
  industrie: { title:"Industrie — sous-secteur", body:"Sous-catégorie du secteur. 'Semiconductors' vs 'Software' ont des cycles de valorisation très différents. Les concurrents directs appartiennent à la même industrie.", src:"Invelps · Profil fondamental" },
  capitalisation: { title:"Capitalisation boursière", body:"Cours × Actions en circulation. Mega Cap (>200 Md$) : liquidité maximale. Large Cap (10-200 Md$). Mid Cap (2-10 Md$). Small Cap (<2 Md$) : potentiel supérieur, risque plus élevé. La capitalisation détermine la volatilité structurelle.", src:"Invelps · Classification standard" },
  style: { title:"Style d'investissement", body:"High Quality = rentabilité élevée (ROE, marges fortes) + bilan solide. Growth = forte croissance. Value = décote vs pairs. Blend = équilibré. Le style influence fortement la performance selon la phase du cycle de taux d'intérêt.", src:"Invelps · Facteurs de style (Fama-French, QMJ)" },
  cycle: { title:"Sensibilité cyclique", body:"Cyclique : revenus corrélés à la conjoncture économique (semis, auto, énergie). Défensif : revenus stables quelle que soit la conjoncture (santé, alimentation). Hybride : entre les deux. Crucial pour le positionnement en fin de cycle.", src:"Invelps · Analyse macroéconomique" },
  iscore: { title:"IScore — Score de conviction Invelps", body:"Score composite 0-100 sur 5 dimensions : Fondamentaux (rentabilité, bilan), Valorisation (vs pairs et historique), Marché (structure technique), Momentum (force relative), Risque/Rendement (ratio Sharpe estimé). ≥70 = dossier de qualité. ≥85 = conviction élevée.", src:"Invelps composite · Murphy · Gray & Vogel · Damodaran" },

  // ══ DIAGNOSTIC GLOBAL ═══════════════════════════════════
  score_global: { title:"Score Technique Global", body:"Score composite 100 pts pondéré : Structure de tendance 40% + Momentum 25% + Volumes 20% + Timing 15%. Pondérations calibrées sur l'importance empirique de chaque facteur. 82/100 = signal technique fort — titre dans le top 15% de l'univers.", src:"Murphy TAOTFM · Wyckoff Villahermosa · Gray & Vogel QM · Invelps" },
  regime: { title:"Régime technique", body:"État structurel actuel. 'Haussière mature' = prix au-dessus des 3 SMAs (20/50/200), canal de régression orienté positivement, R² solide. Correspond à la Phase E (markup) de Wyckoff. Régime le plus favorable pour détenir le titre.", src:"Wyckoff, Villahermosa ch. 12 & 20 · Murphy, TAOTFM ch. 1" },
  signal: { title:"Signal principal", body:"'Surveiller' = tendance haussière confirmée mais timing d'entrée sous-optimal actuellement. Le momentum CT diverge du LT. Murphy : 'ne pas agir contre la tendance, mais attendre le meilleur point d'entrée dans la direction haussière'.", src:"Murphy, TAOTFM ch. 4 · Invelps composite" },
  action: { title:"Action suggérée", body:"Recommandation tactique basée sur 3 signaux convergents : Z-Score +1.82σ (sur-extension), RSI 67.4 (proche surachat), TSMOM 3M négatif (divergence CT). Attendre un repli vers SMA20 améliore significativement le ratio risque/rendement.", src:"Murphy ch. 4 & 10 · Gray & Vogel ch. 6 · Invelps" },

  // ══ STRUCTURE DE TENDANCE ════════════════════════════════
  tendance_prim: { title:"Tendance primaire (Dow Theory)", body:"Tendance de fond sur plusieurs mois à années. Haussière = succession de hauts ET bas croissants. C'est le filtre numéro 1 de toute décision. Murphy : 'ne jamais trader contre la tendance primaire.' Elle détermine le biais directionnel — toutes les autres analyses sont secondaires.", src:"Edwards & Magee, TAST ch. 2 · Murphy, TAOTFM ch. 1 · Dow Theory" },
  tendance_5ans: { title:"Tendance long terme — 5 ans", body:"Tendance structurelle multi-annuelle. Haussier sur 5Y = titre dans un cycle de croissance durable. Murphy : 'la tendance LT est la plus importante de toutes.' Les corrections de 20-30% dans une tendance 5Y haussière sont des opportunités d'accumulation, pas des signaux de vente.", src:"Edwards & Magee, TAST ch. 3 · Murphy, TAOTFM ch. 1" },
  tendance_1an: { title:"Tendance secondaire — 1 an", body:"Tendance sur 12 mois. Doit être alignée avec la primaire. Une divergence tendance 1Y / tendance primaire = zone de prudence accrue. Wyckoff : la tendance annuelle correspond souvent à la Phase E (markup) ou au début de la distribution.", src:"Murphy, TAOTFM ch. 4 · Wyckoff, Villahermosa ch. 20" },
  trend_quality: { title:"Trend Quality Score — R² OLS", body:"Régularité géométrique de la progression mesurée par le R² de la régression OLS. Score 92 = R² ≈ 0.92 → 92% de la variance du prix expliquée par la droite de tendance (8% de bruit seulement). Murphy : seuil R² ≥ 0.50 pour signal actionnable, ≥ 0.75 pour tendance solide.", src:"Murphy, TAOTFM ch. 4 · Analyse quantitative de tendance" },
  trend_strength: { title:"Trend Strength Score — Force directionnelle", body:"Intensité combinant la pente β de la régression + sa consistance temporelle. 88/100 = tendance à forte inclinaison maintenue durablement dans le temps. Gann : 'la force d'une tendance se mesure à sa pente et à sa durée.' Un titre avec β fort et régulier est le plus difficile à contrer.", src:"Gann, Reddy ch. 2 · Edwards & Magee, TAST ch. 3 · Invelps" },
  compounder: { title:"Compounder Score — Régularité interannuelle", body:"Mesure si le titre génère une croissance stable d'année en année (type 'compounder'). 81/100 = progression régulière. Covel : 'la régularité du trend importe plus que son amplitude ponctuelle.' Les meilleurs compounders surperforment sur 10+ ans avec une volatilité moindre.", src:"Covel, Trend Following ch. 2 · Gray & Vogel, QM ch. 5 · Invelps" },
  r2: { title:"R² — Coefficient de détermination OLS", body:"R² = 1 − (SS_résiduelle / SS_totale). Mesure quelle part du mouvement du prix est 'tendanciel' vs aléatoire. R² = 0.86 → 86% tendanciel, 14% bruit. <0.30 = signal peu fiable même si la pente β est positive. Murphy : 'le R² valide le β — sans lui, β ne vaut rien'.", src:"Murphy, TAOTFM ch. 4 · Statistiques appliquées" },
  cagr_5y: { title:"CAGR 5 ans — Taux de croissance annuel composé", body:"(Valeur_fin / Valeur_début)^(1/5) − 1. +39.2%/an = 10 000€ investis il y a 5 ans valent ~46 000€ aujourd'hui. Surperforme très largement le S&P500 (~13%/an). Gray & Vogel : le CAGR est la mesure la plus honnête de la performance réelle à long terme.", src:"Gray & Vogel, QM ch. 3 · Finance de marché" },
  sma20: { title:"SMA 20 jours — Support dynamique CT", body:"Moyenne des 20 derniers cours de clôture. Premier support dynamique en tendance haussière. Les replis jusqu'à SMA20 = zones d'achat, pas de vente. Murphy ch. 9 : 'la MA la plus utile pour les entrées tactiques en tendance.' Un recul sous SMA20 en clôture = premier signal d'alerte.", src:"Murphy, TAOTFM ch. 9" },
  sma50: { title:"SMA 50 jours — Support intermédiaire", body:"Moyenne des 50 derniers cours. Deuxième support dynamique, zone de rebond classique. Golden Cross = SMA50 dépasse SMA200 = signal haussier structurel majeur. Death Cross = SMA50 sous SMA200 = signal baissier. Edwards & Magee : 'SMA50 sépare la correction temporaire du retournement'.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  sma200: { title:"SMA 200 jours — Ligne bull/bear fondamentale", body:"La ligne de démarcation institutionnelle entre marché haussier (prix > SMA200) et baissier (prix < SMA200). 97% des gérants long-only n'achètent pas si le titre est sous sa SMA200. Edwards & Magee : 'le test ultime de la tendance primaire.' C'est LA référence mondiale.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  alignement: { title:"Alignement des moyennes mobiles", body:"SMA20 > SMA50 > SMA200 = configuration haussière parfaite — toutes les tendances (CT/MT/LT) pointent dans la même direction. Murphy : 'le test le plus simple et le plus puissant de la santé d'une tendance.' L'inverse signale un bear market structurel.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  regression: { title:"Régression OLS — Droite de tendance théorique", body:"P_t = α + β·t. Ajuste la meilleure droite possible aux prix historiques. β = vitesse en €/période. Mais β sans R² ≥ 0.50 n'est pas exploitable. Murphy ch. 4 : 'une pente positive avec R² faible = tendance théorique qu'on ne peut pas trader en pratique car le chemin est trop erratique'.", src:"Murphy, TAOTFM ch. 4" },
  canal: { title:"Canal statistique ±1σ et ±2σ", body:"Bandes à ±1 et ±2 écarts-types autour de la droite de régression. Par définition : 68% des prix dans ±1σ, 95% dans ±2σ. Prix à +2σ = sur-extension rare (~5% du temps). Pesavento : les niveaux géométriques créent des zones de retournement naturelles exploitables.", src:"Murphy, TAOTFM ch. 4 · Pesavento & Jouflas ch. 2" },
  zscore: { title:"Z-Score — Écart statistique à la moyenne", body:"Z = (Prix − Moyenne) / Écart-type. +1.82σ = 1.82 fois l'écart-type au-dessus de la moyenne annuelle. Zone >+2σ = sur-extension rare (5% des observations). Signal de prudence — pas de vente mécanique. Murphy : 'les extrêmes se corrigent, mais les délais sont imprévisibles'.", src:"Murphy, TAOTFM ch. 4 · Statistiques financières" },
  zscore_chart: { title:"Z-Score glissant (60j) — Panneau graphique", body:"Z-Score calculé sur une fenêtre glissante de 60 jours affiché en sous-graphe. Lignes de référence ±1 et ±2. Un titre durablement au-dessus de +1σ est en tendance forte. Retour de +2σ vers 0 = correction attendue de ~10-15% selon la volatilité historique du titre.", src:"Murphy, TAOTFM ch. 4 · Analyse quantitative" },

  // ══ MOMENTUM ════════════════════════════════════════════
  mom_direction: { title:"Direction du momentum — Lecture multi-horizons", body:"Synthèse directionnelle croisée TSMOM 3M / 6M / 12M. Haussier = les 3 horizons positifs. Retournement baissier = 12M positif mais 6M et 3M négatifs : tendance LT intacte mais moteur CT grippé. Moskowitz, Ooi & Pedersen (JFE 2012) : la divergence 12M+/3M- est la configuration la plus commune avant consolidation. Elle signale l'essoufflement, pas le retournement.", src:"Moskowitz, Ooi & Pedersen, JFE 2012 · Gray & Vogel, QM ch. 3 & 6" },
  mom_pattern: { title:"Pattern du momentum — Dynamique interne", body:"Accélération : 3M > 6M > 12M (le momentum s'intensifie). Essoufflement : 12M > 6M > 3M (la force décroît dans le temps). Transition / Mixte : ordre non monotone, phase de bascule. Gray & Vogel (ch. 6) : l'Essoufflement précède statistiquement la consolidation dans 60% des cas. L'Accélération est le signal le plus favorable pour un nouveau long.", src:"Gray & Vogel, QM ch. 6 · Da, Gurun & Warachka (2014, RFS)" },
  mom_strength: { title:"Intensité du momentum — Force relative dans l'univers", body:"Classement normalisé 0-100 dans l'univers Invelps. Fort (>70) : momentum parmi les meilleurs de l'univers. Faible (<30) : sous-performance relative même si les chiffres absolus sont positifs. Gray & Vogel : la force relative (cross-sectional) est aussi prédictive que la force absolue. Un titre Faible mais en 'Accélération' est un signal à surveiller.", src:"Gray & Vogel, QM ch. 3 · Jegadeesh & Titman (1993, JF)" },
  mom_score: { title:"Momentum Score composite", body:"Score 0-100 pondérant TSMOM 3M (20%) + TSMOM 6M (30%) + TSMOM 12M (50%). Pondérations issues de Gray & Vogel (QM ch. 6). Le TSMOM 12M domine car il capture la persistance documentée sur 200 ans. 74/100 = momentum sain malgré la divergence CT.", src:"Gray & Vogel, QM ch. 3-6 · Moskowitz, Ooi & Pedersen, JFE 2012" },
  leadership: { title:"Leadership sectoriel — Momentum relatif", body:"Mesure si le titre surperforme son secteur sur 6-12 mois. 89/100 = parmi les leaders sectoriels. Gray & Vogel : le momentum RELATIF (cross-sectional) est aussi prédictif que le momentum absolu. Les leaders sectoriels tendent à continuer de surperformer sur 3-12 mois. C'est le signal de 'winner takes all'.", src:"Gray & Vogel, QM ch. 3 · Jegadeesh & Titman (1993, JF)" },
  fip: { title:"FIP — Frog in the Pan (Qualité du momentum)", body:"FIP = SIGN(TSMOM_12M) × (% jours_baissiers − % jours_haussiers). FIP > 0 avec TSMOM > 0 = performance annuelle positive mais construite sur quelques pics haussiers isolés dans une majorité de jours baissiers. Gray & Vogel (QM ch. 6) : ces 'frogs' sous-performent les 'smooth momentum' de 3-5%/an.", src:"Gray & Vogel, QM ch. 6 · Da, Gurun & Warachka (2014, RFS)" },
  tsmom12: { title:"TSMOM 12 mois — Momentum absolu long terme", body:"TSMOM_12M = (P_t / P_{t-252j}) − 1. +62.1% = performance brute sur 12 mois glissants. Moskowitz, Ooi & Pedersen (JFE 2012) documentent la persistance de ce signal sur 200 ans et 40+ marchés. Signal le plus robuste de la finance empirique. La règle : les gagnants récents continuent de gagner.", src:"Gray & Vogel, QM ch. 3 · Moskowitz, Ooi & Pedersen, JFE 2012" },
  tsmom6: { title:"TSMOM 6 mois — Momentum intermédiaire", body:"TSMOM_6M = (P_t / P_{t-126j}) − 1. +11.3% = momentum intermédiaire positif. Signal de validation du TSMOM 12M. Gray & Vogel : un titre avec TSMOM positif sur 6M ET 12M simultanément présente la persistance de momentum la plus forte et la plus fiable statistiquement.", src:"Gray & Vogel, QM ch. 3" },
  tsmom3: { title:"TSMOM 3 mois — Momentum court terme", body:"TSMOM_3M = (P_t / P_{t-63j}) − 1. -2.1% = légère faiblesse récente. Divergence avec TSMOM 12M positif = essoufflement CT dans tendance LT haussière = configuration classique. Gray & Vogel : surveiller le FIP pour distinguer une correction saine d'un vrai retournement de momentum.", src:"Gray & Vogel, QM ch. 6" },
  jours_pos: { title:"% jours de clôture positive", body:"Proportion de séances avec clôture en hausse. 58% = légère dominance des acheteurs. Composante du score FIP : >55% = 'smooth momentum' (de qualité). <50% = 'frog momentum' (risqué malgré bilan positif). Wyckoff : 'la régularité journalière révèle la pression des mains fortes'.", src:"Wyckoff, Studies in Tape Reading · Gray & Vogel, QM ch. 6" },
  m1_return: { title:"Performance mensuelle (M1)", body:"Rendement du dernier mois calendaire complet. +3.2% = accélération récente positive — signal que le momentum CT commence à se redresser après la divergence. Covel : 'les meilleures entrées sont quand le momentum CT et LT convergent à nouveau vers le haut — la tendance reprend de la vigueur'.", src:"Covel, Trend Following ch. 3 · Gray & Vogel, QM ch. 5" },
  ytd: { title:"YTD — Performance depuis le 1er janvier", body:"Year-to-Date = variation totale depuis le 1er janvier. +85.4% = performance exceptionnelle. Gray & Vogel : comparer au percentile dans l'univers pour qualifier le leadership. >80th percentile YTD = signal fort de momentum cross-sectionnel. Ce titre est dans le top 5% de son univers.", src:"Gray & Vogel, QM ch. 3 · Invelps composite" },
  divergence: { title:"Divergence momentum — Signal d'essoufflement", body:"Prix en nouveau haut mais indicateur momentum (RSI, TSMOM 3M) ne confirme pas. Murphy (TAOTFM ch. 10) : 'l'une des configurations les plus précieuses de l'AT — le mouvement perd de sa force interne.' Signal d'essoufflement, pas de retournement certain. Renforce la prudence sans déclencher de vente.", src:"Murphy, TAOTFM ch. 10 · Wilder, New Concepts (1978)" },

  // ══ VOLUME / WYCKOFF ════════════════════════════════════
  vol_score: { title:"Score volumétrique — Participation institutionnelle", body:"Score composite : ratio volume / moyenne + persistance temporelle + qualité institutionnelle. 68/100 = participation correcte mais en dessous des meilleures configurations. Wyckoff Loi 3 : 'le volume est l'effort, le prix est le résultat. L'empreinte des mains fortes ne ment jamais'.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  liq_score: { title:"Score de liquidité du titre", body:"Facilité à acheter/vendre sans impacter significativement le cours (spread faible, profondeur de carnet). 72/100 = liquidité satisfaisante. Murphy : 'un titre illiquide peut produire des faux signaux techniques par manque de volume.' La liquidité conditionne la fiabilité de toute l'analyse.", src:"Murphy, TAOTFM ch. 7 · Invelps composite" },
  vol_5j: { title:"Ratio volume — 5 jours vs moyenne 60J", body:"Volume moyen des 5 dernières séances / moyenne des 60 dernières. 1.40× = activité 40% supérieure à la normale. Wyckoff : un regain de volume sur un support = signal d'absorption institutionnelle (achat discret). Murphy : 'le volume confirme, jamais il n'initie — mais un regain soudain mérite attention'.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  vol_20j: { title:"Ratio volume — 20 jours vs moyenne 60J", body:"Volume moyen des 20 dernières séances vs les 60 précédentes. 1.20× = participation correcte sur le mois. Signal de confirmation de la tendance récente. Wyckoff : un trend haussier soutenu par des volumes constants est plus solide qu'un trend sur volumes déclinants.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, Villahermosa ch. 9" },
  vol_60j: { title:"Ratio volume — 60 jours vs moyenne 120J", body:"Tendance structurelle des volumes sur 2 mois vs les 4 mois précédents. 0.90× = légère baisse de la participation institutionnelle sur le moyen terme. Signal d'alerte sur la durabilité de la tendance. Wyckoff : hausse des prix + baisse des volumes sur 60J = 'No Demand' structurel = fragilité.", src:"Wyckoff, Villahermosa ch. 9 · Murphy, TAOTFM ch. 7" },
  supp_vol: { title:"Support Volume Ratio — Absorption sur les supports", body:"Rapport du volume lors des rebonds sur les supports vs le volume général. 1.05 = acheteurs légèrement plus actifs sur les supports. Wyckoff (Studies in Tape Reading, 1910) : 'No Supply' = rebonds sur supports avec faibles volumes vendeurs = les mains fortes n'allègent pas = signal haussier.", src:"Wyckoff, Studies in Tape Reading (1910) · Wyckoff, Villahermosa ch. 9" },
  res_vol: { title:"Résistance Volume Ratio — Pression sur les résistances", body:"Volume lors des tests de résistance. 0.92 = pression vendeuse modérée. Edwards & Magee (ch. 13) : 'la solidité d'une résistance est proportionnelle au volume échangé lors de sa formation initiale.' Un test de résistance sur faible volume est un signal positif — les vendeurs se font rares.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  absorption: { title:"Signal d'absorption — Phase précurseur", body:"Les acheteurs institutionnels absorbent-ils les ventes sur les creux ? 'Moyen' = absorption partielle. Wyckoff (Studies in Tape Reading, 1910) : l'absorption progressive sur les supports est le signal précurseur systématique des phases de markup. C'est la 'cause' qui précède l'effet (Phase D → E).", src:"Wyckoff, Studies in Tape Reading (1910) · Wyckoff, Villahermosa ch. 15 & 19" },
  wyckoff: { title:"Phase du cycle de Wyckoff", body:"'Distribution' = les grandes institutions allègent progressivement après un markup. Phase précédant le markdown. Le prix peut encore progresser temporairement via un UTAD (Upthrust After Distribution — fausse cassure haute). Wyckoff : 'la distribution se fait dans l'euphorie — la foule achète ce que les mains fortes vendent'.", src:"Wyckoff, Villahermosa ch. 17-18 · Wyckoff, Studies in Tape Reading" },
  prix_vol: { title:"Prix / Volume — relation effort/résultat", body:"Murphy (TAOTFM ch. 7) : 'le volume confirme la tendance — son absence la fragilise.' Wyckoff Loi 3 : effort (volume) doit être proportionnel au résultat (prix). Hausse sur volumes déclinants = 'No Demand'. Fort volume sur hausse = confirmation institutionnelle.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, Villahermosa ch. 9 (Loi de l'Effort et du Résultat)" },

  // ══ TIMING / OPPORTUNITÉ ════════════════════════════════
  rsi: { title:"RSI 14 — Relative Strength Index (Wilder, 1978)", body:"RSI = 100 − [100/(1+RS)]. RS = moy. gains 14j / moy. pertes 14j. 67.4 = proche du surachat (>70). Murphy (ch. 10) : en tendance forte, le RSI peut rester 50-80 de façon prolongée — la zone d'achat se déplace à 40-55 (pas 30). La DIVERGENCE RSI/prix est plus fiable que les niveaux absolus.", src:"Murphy, TAOTFM ch. 10 · Wilder, New Concepts in Technical Trading Systems (1978)" },
  opp_score: { title:"Opportunity Score — Qualité du point d'entrée", body:"Score 0-100 évaluant si c'est le bon moment pour initier ou renforcer. Intègre Z-Score, RSI, divergences momentum et rapport prix/niveaux clés (SMA, régression). 58/100 = opportunité passable. <50 = attendre. >70 = bon point d'entrée. Pesavento : 'l'entrée dans la bonne PRZ améliore le R/R de 2×'.", src:"Pesavento & Jouflas, Trade What You See ch. 4 · Invelps composite" },
  timing_score: { title:"Timing Score — Qualité tactique de l'entrée", body:"Score synthétisant la qualité du timing sur 3 horizons (CT/MT/LT). 61/100 = timing passable — tendance de fond favorable mais point d'entrée immédiat sous-optimal. Murphy : 'même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.' Attendre améliore mécaniquement le R/R.", src:"Murphy, TAOTFM ch. 1 & 4 · Invelps composite" },
  tscore: { title:"TScore — Score Technique Global Invelps", body:"Score composite propriétaire intégrant structure (40%), momentum (25%), volumes (20%), timing (15%). 71/100 = signal technique above average. Calibré pour combiner les approches Murphy (trend), Wyckoff (volume) et Gray & Vogel (momentum quantitatif). Top 25% de l'univers analysé.", src:"Murphy TAOTFM · Wyckoff Villahermosa · Gray & Vogel QM · Invelps" },
  coherence: { title:"Coherence Score — Alignement inter-timeframes", body:"Mesure si les signaux CT, MT et LT convergent dans la même direction. 66/100 = légère divergence. Murphy (TAOTFM ch. 15) : 'quand tous les timeframes pointent dans la même direction, le signal est extrêmement fiable et le ratio R/R optimal.' >70 = forte convergence = setup idéal.", src:"Murphy, TAOTFM ch. 15 — analyse multi-temporelle" },
  cross_sma: { title:"Dernier croisement de moyennes mobiles", body:"Golden Cross = SMA50 dépasse SMA200 par le haut = signal haussier structurel de long terme. Death Cross = signal baissier. 'Aucun' = pas de croisement récent. Murphy : 'les croisements de MA confirment la tendance, ne la prédisent pas — souvent le mouvement a déjà commencé de 10-15%'.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  val_status: { title:"Statut de valorisation fondamentale", body:"Positionnement du cours vs juste valeur estimée (DCF + multiples sectoriels). 'Neutre' = prix cohérent avec les fondamentaux. Edwards & Magee : 'la valorisation fixe les bornes du raisonnable, la technique dit où le prix va maintenant.' Fondamental + technique = analyse complète.", src:"Damodaran — Valuation · Murphy, TAOTFM ch. 1 · Invelps" },
  jauge: { title:"Jauge d'opportunité — Synthèse visuelle", body:"Synthèse de l'attractivité du point d'entrée, de 'Faible' (rouge) à 'Excellent' (vert). Construit sur 4 signaux : Z-Score (éloignement statistique), RSI (momentum CT), divergence TSMOM (qualité), rapport prix/SMA (position dans la tendance). Pesavento : 'la confluence de plusieurs signaux dans une zone PRZ maximise le R/R'.", src:"Pesavento & Jouflas ch. 4 · Murphy ch. 4 & 10 · Invelps" },

  // ══ SUPPORT / RÉSISTANCE ════════════════════════════════
  support: { title:"Niveau de support clé", body:"Zone historique où la demande a systématiquement surpassé l'offre. 3 tests réussis = niveau très significatif. Edwards & Magee (TAST ch. 13) : 'un support représente une concentration de mémoire de marché.' Cassure + clôture en dessous + fort volume = support invalidé = nouvelle résistance (rôle inversé).", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  resistance: { title:"Niveau de résistance clé", body:"Zone historique où l'offre a systématiquement surpassé la demande. 2 tests réussis = niveau significatif. Principe du rôle inversé (Edwards & Magee ch. 13) : résistance cassée avec fort volume = nouveau support. Pesavento : les résistances majeures correspondent souvent à des extensions de Fibonacci.", src:"Edwards & Magee, TAST ch. 13 · Pesavento & Jouflas ch. 3" },

  // ══ GRAPHIQUE — PÉRIODES ════════════════════════════════
  period_3m:  { title:"Vue 3 mois — Tactique court terme", body:"Affiche les 60-65 dernières séances journalières avec bougies OHLCV. Utile pour identifier les points d'entrée tactiques, les cassures de S/R récents et l'état du momentum court terme. Murphy (ch. 3) : 'le court terme précise le timing, le long terme confirme la direction. Ne jamais trader le CT contre le LT.' Combiner 3M + 1Y pour un timing optimal.", src:"Murphy, TAOTFM ch. 3 & 4" },
  period_6m:  { title:"Vue 6 mois — Momentum intermédiaire", body:"Couvre environ 130 séances. Fenêtre idéale pour observer le TSMOM 6M en action sur le graphique et identifier les phases d'accélération / décélération. Moskowitz (JFE 2012) : le momentum 6M est le signal intermédiaire le plus robuste — corrélé positivement avec le momentum 12M et prédictif de la persistance à 3M.", src:"Moskowitz, Ooi & Pedersen, JFE 2012 · Murphy ch. 3" },
  period_1y:  { title:"Vue 1 an — Tendance annuelle et structure", body:"Vue de référence pour l'analyse technique classique. Révèle la structure de tendance annuelle, les Golden/Death Crosses, et la position du prix vis-à-vis de la SMA200. Murphy (ch. 9) : 'La SMA200 est la frontière entre bull et bear market. Un titre au-dessus est dans son marché haussier, en dessous dans son marché baissier.' C'est la vue pivot de l'analyse.", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  period_3y:  { title:"Vue 3 ans — Structure de tendance moyen terme", body:"Passe aux données mensuelles. Affiche le canal de régression linéaire (droite + ±1σ / ±2σ) à la place des SMAs journalières. Permet de visualiser les phases Wyckoff sur l'horizon intermédiaire et d'évaluer si le prix est statistiquement surévalué ou sous-évalué par rapport à sa tendance historique. Le Z-Score est directement lisible sur le graphique.", src:"Wyckoff, SOT · Damodaran — Regression channels" },
  period_5y:  { title:"Vue 5 ans — Tendance structurelle long terme", body:"Vue sur laquelle se lisent les phases de compounding. Le canal ±2σ encadre ~95% des observations historiques. Un prix au-dessus de +2σ est statistiquement rare (<2.5%). Gray & Vogel (QM ch. 6) : 'c'est l'horizon qui discrimine les vrais compounders des cycliques.' R² mesure la cohérence de la progression sur cette fenêtre.", src:"Gray & Vogel, Quantitative Momentum ch. 6 · Murphy ch. 3" },
  period_max: { title:"Vue MAX — Historique complet depuis 2005", body:"Permet de replacer le prix actuel dans la totalité de l'historique disponible. Donne le vrai CAGR réalisé, le multiple de prix (price multiple), et la régression longue. À cette échelle, les corrections même sévères apparaissent comme des détails dans une tendance compoundée. Ilmanen (Expected Returns, ch. 7) : 'le long terme révèle la prime de risque réelle.'", src:"Ilmanen, Expected Returns ch. 7 · Gray & Vogel, QM ch. 6" },

  // ══ GRAPHIQUE — TOGGLES ═════════════════════════════════
  toggle_sma20:   { title:"SMA 20 — Moyenne mobile 20 séances", body:"La SMA20 (20 séances) est la moyenne mobile court terme de référence. Elle représente le comportement moyen des prix sur le dernier mois de trading. Un prix au-dessus de la SMA20 indique un momentum CT positif ; en dessous, une faiblesse CT. En tendance haussière forte, la SMA20 sert de premier support dynamique (rebond attendu). Murphy (ch. 9) : 'la SMA20 est le support CT le plus utilisé par les traders professionnels.'", src:"Murphy, TAOTFM ch. 9 · Covel, TF ch. 4" },
  toggle_sma50:   { title:"SMA 50 — Moyenne mobile 50 séances", body:"La SMA50 représente ~2.5 mois de trading. C'est la ligne de démarcation entre correction saine et retournement intermédiaire. Dans une tendance haussière, chaque retour sur SMA50 est une opportunité d'entrée ou de renforcement. Le Golden Cross (SMA50 > SMA200) est le signal de structure haussière moyen terme le plus reconnu. Murphy (ch. 9) : 'le croisement SMA50/SMA200 est l'un des signaux les plus suivis par les institutionnels.'", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee, TAST ch. 28" },
  toggle_sma200:  { title:"SMA 200 — Moyenne mobile 200 séances", body:"La SMA200 (~10 mois) est la ligne de partage entre bull et bear market. Au-dessus = bull market (biais haussier systématique). En dessous = bear market (biais baissier). De nombreux fonds institutionnels utilisent la SMA200 comme filtre d'exposition (100% long si prix > SMA200, 0% sinon). Murphy (ch. 9) : 'la SMA200 est la frontière universelle entre tendance haussière et baissière.' CAGR moyen des actions au-dessus de leur SMA200 : +12%/an vs -2%/an en dessous.", src:"Murphy, TAOTFM ch. 9 · Faber, GTAA (2007) · Edwards & Magee ch. 28" },
  toggle_reg:     { title:"Droite de régression linéaire", body:"La régression linéaire modélise la trajectoire tendancielle du prix dans le temps. La droite représente la 'juste valeur' statistique à chaque date selon la tendance historique. R² mesure la qualité de l'ajustement : R²=0.84 signifie que 84% de la variance du prix est expliquée par la tendance linéaire. Plus le R² est élevé, plus la tendance est régulière et prévisible. Damodaran : 'la régression historique fixe les bornes du raisonnable pour un titre en croissance.'", src:"Damodaran — Valuation · Murphy ch. 3 · Invelps (log-régression)" },
  toggle_canal:   { title:"Canal de régression ±1σ / ±2σ", body:"Le canal statistique enveloppe la droite de régression avec ±1 et ±2 écarts-types. ±1σ encadre ~68% des observations historiques (zone de valorisation normale). ±2σ encadre ~95% des observations (zone statistiquement rare). Prix proche de +2σ = surévaluation extrême statistique, retour à la moyenne attendu. Prix proche de -1σ ou -2σ = zone de valeur historique, risque plus favorable. Wilder : 'les bandes statistiques révèlent le régime de valorisation relatif à la tendance.'", src:"Murphy, TAOTFM ch. 21 (Bollinger) · Damodaran · Invelps" },
  toggle_zscore:  { title:"Z-Score — Écart à la régression", body:"Le Z-Score mesure l'éloignement du prix actuel par rapport à la droite de régression en unités d'écart-type. Z=0 = prix sur la droite de régression (valorisation neutre). Z=+1 = +1σ au-dessus (légère survalorisation). Z=+2 = +2σ (survalorisation significative, rare). Z=-1 = -1σ (légère sous-valorisation). Le Z-Score est un outil de mean-reversion : plus il est extrême, plus le retour à la moyenne est statistiquement probable. Damodaran : 'le Z-Score de régression est un indicateur de valorisation relative robuste sur longue période.'", src:"Damodaran — Valuation · Murphy ch. 21 · Invelps" },
  toggle_volume:  { title:"Volume — Participation et conviction", body:"Le volume représente le nombre de titres échangés sur la période. Barre verte = séance haussière (clôture > ouverture). Barre rouge = séance baissière. La ligne pointillée = volume moyen 20 séances. Règle fondamentale de Murphy : 'le volume doit confirmer la tendance.' Hausse de prix + volume supérieur à la moyenne = signal fort (participation institutionnelle). Hausse de prix + volume faible = signal fragile (pas de conviction). Phase Wyckoff UTAD : volume anormalement élevé sur une fausse cassure haussière = signal de distribution.", src:"Murphy, TAOTFM ch. 7 · Wyckoff, SOT · Villahermosa, Wyckoff" },
  toggle_rsi:     { title:"RSI 14 — Relative Strength Index", body:"Le RSI (J. Welles Wilder, 1978) mesure la vitesse et l'amplitude des mouvements de prix sur 14 séances. RSI > 70 = surachat (prix monte trop vite, correction possible). RSI < 30 = survente (prix baisse trop vite, rebond possible). RSI entre 40-60 = zone neutre. En tendance haussière forte, le RSI reste souvent entre 40-80 sans atteindre 30 — c'est un signe de force. La divergence (prix fait un plus haut mais RSI baisse) est l'un des signaux de retournement les plus fiables. Murphy (ch. 10) : 'le RSI est l'oscillateur de référence pour la détection des divergences.'", src:"Wilder, New Concepts (1978) · Murphy, TAOTFM ch. 10" },
  toggle_sr:      { title:"Niveaux Support / Résistance clés", body:"Affiche les zones de support et résistance calculées sur 365 jours glissants. Support = zone historique d'accumulation (la demande dépasse systématiquement l'offre). Résistance = zone de distribution. La significativité d'un niveau est proportionnelle au nombre de tests réussis et au volume associé (Vol. Ratio > 1.2 = zone institutionnelle). Edwards & Magee (TAST ch. 13) : 'un support représente une concentration de mémoire de marché — plus il est testé, plus il est significatif.' Cassure + clôture + volume fort = confirmation + rôle inversé.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4 · Pesavento ch. 3" },
  toggle_candle:  { title:"Graphique en bougies japonaises", body:"Chaque bougie représente 4 informations : ouverture, plus haut, plus bas, clôture. Corps vert = clôture > ouverture (séance haussière). Corps rouge = clôture < ouverture (séance baissière). Mèche haute = plus haut de séance. Mèche basse = plus bas. Les patterns de bougies (doji, marteau, étoile filante) sont des signaux de retournement ou de continuation. Nison (Japanese Candlestick Charting Techniques, 1991) : 'les bougies révèlent la psychologie du marché à chaque séance — qui contrôle, acheteurs ou vendeurs.'", src:"Nison, JCCT (1991) · Murphy, TAOTFM ch. 3" },
  toggle_line:    { title:"Graphique en ligne — Clôtures", body:"Le graphique en ligne relie uniquement les prix de clôture. Plus simple visuellement que les bougies, il élimine le 'bruit' intra-séance pour révéler la tendance pure. Préféré pour les vues longues (3Y, 5Y, MAX) où le niveau de clôture est plus pertinent que les extrêmes intra-séance. Murphy (ch. 3) : 'les clôtures sont les prix les plus importants de la séance — elles reflètent le consensus final du marché après toutes les nouvelles de la journée.'", src:"Murphy, TAOTFM ch. 3 · Edwards & Magee, TAST ch. 2" },

  // ══ GRAPHIQUE — LÉGENDE ═════════════════════════════════
  legend_sma20:   { title:"SMA 20 — Support court terme", body:"Ligne bleue fine. Représente la moyenne des 20 dernières clôtures. En tendance haussière, un retour sur SMA20 accompagné d'un rebond de volume est souvent le meilleur point d'entrée tactique. Distance actuelle entre le prix et la SMA20 indique le degré d'extension CT.", src:"Murphy, TAOTFM ch. 9" },
  legend_sma50:   { title:"SMA 50 — Ligne de tendance intermédiaire", body:"Ligne verte. La SMA50 est le pivot de la tendance intermédiaire. Un prix qui défend la SMA50 après une correction confirme la solidité de la tendance. Une cassure de la SMA50 est un avertissement intermédiaire. Murphy : 'la SMA50 est le premier niveau de soutien intermédiaire à surveiller.'", src:"Murphy, TAOTFM ch. 9 · Edwards & Magee ch. 28" },
  legend_sma200:  { title:"SMA 200 — Frontière bull/bear market", body:"Ligne orange épaisse. La SMA200 est la ligne de séparation universelle entre bull et bear market. Au-dessus : biais haussier systématique. En dessous : biais baissier. Sa pente (montante, plate, descendante) est aussi importante que la position du prix vis-à-vis d'elle.", src:"Murphy, TAOTFM ch. 9 · Faber, GTAA (2007)" },
  legend_reg:     { title:"Droite de régression — Tendance historique", body:"Ligne bleue en pointillés. Trajectoire mathématique optimale du prix depuis 2005 (régression log-linéaire). Représente la 'juste valeur tendancielle' à chaque date. Un prix proche de la droite = valorisation neutre. S'éloigner de la droite vers le haut = surévaluation statistique croissante.", src:"Damodaran · Invelps (log-régression sur clôtures mensuelles)" },
  legend_b1:      { title:"Canal ±1σ — Zone de valorisation normale", body:"Lignes pointillées fines. Le canal ±1σ encadre environ 68% des observations historiques (loi normale). C'est la zone de 'valorisation normale' de ce titre au regard de sa tendance. Un prix dans ce canal est statistiquement banal — ni surévalué ni sous-évalué par rapport à son histoire.", src:"Murphy, TAOTFM ch. 21 · Damodaran · Invelps" },
  legend_b2:      { title:"Canal ±2σ — Zone statistiquement rare", body:"Lignes pointillées épaisses. Le canal ±2σ encadre ~95% des observations. Un prix dépassant +2σ est dans les 2.5% les plus chers de son histoire (signal de prudence extrême). Un prix sous -2σ est dans les 2.5% les moins chers — opportunité historique rare. Ces niveaux sont les cibles naturelles du mean-reversion.", src:"Murphy, TAOTFM ch. 21 · Damodaran · Invelps" },
  legend_support: { title:"Support clé — Zone mémoire du marché", body:"Ligne pointillée verte. Niveau historique où la demande a systématiquement surpassé l'offre. Plus le nombre de tests est élevé, plus le niveau est significatif. La distance entre le prix actuel et le support est le 'risque naturel' de la position si le support cède.", src:"Edwards & Magee, TAST ch. 13 · Murphy, TAOTFM ch. 4" },
  legend_resist:  { title:"Résistance clé — Prochain obstacle", body:"Ligne pointillée rouge. Zone historique où l'offre a pris le dessus. La cassure d'une résistance avec fort volume est un signal d'achat majeur (principe du rôle inversé). La distance entre le prix actuel et la résistance est le 'potentiel de gain' vers le prochain niveau.", src:"Edwards & Magee, TAST ch. 13 · Pesavento ch. 3" },

};


// ══════════════════════════════════════════════════════
// TOOLTIP COMPONENT
// ══════════════════════════════════════════════════════
function Tip({ title, body, src, children }) {
  const [vis, setVis] = useState(false);
  const [pos, setPos] = useState({ x:0, y:0 });
  const ref = useRef(null);
  const track = e => {
    const r = ref.current?.getBoundingClientRect();
    if (r) setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
  };
  return (
    <span ref={ref} style={{ position:"relative", display:"inline-flex", alignItems:"center" }}
      onMouseEnter={e=>{ setVis(true); track(e); }}
      onMouseMove={track}
      onMouseLeave={()=>setVis(false)}>
      {children}
      {vis && (
        <span style={{
          position:"fixed",
          left: Math.min(pos.x + (ref.current?.getBoundingClientRect().left||0) + 14, window.innerWidth - 300),
          top:  Math.max((ref.current?.getBoundingClientRect().top||0) + pos.y - 8, 8),
          zIndex:9999,
          minWidth:220, maxWidth:300,
          background:"var(--card-bg)",
          border:"1px solid var(--card-bdr)",
          borderRadius:10, padding:"10px 14px",
          boxShadow:"0 8px 32px rgba(0,0,0,.28)",
          pointerEvents:"none",
          whiteSpace:"normal", lineHeight:1,
        }}>
          {title && <span style={{
            display:"block", fontFamily:"ui-monospace,monospace", fontSize:9,
            textTransform:"uppercase", letterSpacing:".11em",
            color:C.amber, marginBottom:6, paddingBottom:5,
            borderBottom:"1px solid var(--sep)"
          }}>{title}</span>}
          <span style={{ display:"block", fontSize:11.5, color:"var(--tx)", lineHeight:1.65 }}>{body}</span>
          {src && <span style={{
            display:"block", fontFamily:"ui-monospace,monospace", fontSize:8.5,
            color:"var(--mu)", marginTop:7, paddingTop:6,
            borderTop:"1px solid var(--sep)"
          }}>📖 {src}</span>}
        </span>
      )}
    </span>
  );
}

function I() {
  return (
    <svg width="9" height="9" viewBox="0 0 12 12" fill="none" style={{ opacity:.35, marginLeft:3, flexShrink:0 }}>
      <circle cx="6" cy="6" r="5.4" stroke="currentColor" strokeWidth="1.1"/>
      <rect x="5.3" y="5" width="1.4" height="4.2" rx=".7" fill="currentColor"/>
      <rect x="5.3" y="2.8" width="1.4" height="1.4" rx=".7" fill="currentColor"/>
    </svg>
  );
}

function TipI({ tk }) {
  const t = TIPS[tk];
  if (!t) return null;
  return (
    <Tip title={t.title} body={t.body} src={t.src}>
      <span style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:14, height:14, borderRadius:"50%",
        border:`1px solid ${C.am}55`, fontSize:8, color:`${C.am}99`,
        marginLeft:3, flexShrink:0, lineHeight:1, cursor:"help"
      }}>?</span>
    </Tip>
  );
}

// ══════════════════════════════════════════════════════
// COURSE CONTENT — 3 LEVELS, 7 MODULES
// Sources : Murphy TAOTFM · Edwards & Magee TAST
//           Wyckoff (Villahermosa + Tape Reading)
//           Gray & Vogel QM · Covel TF
//           Pesavento & Jouflas · Gann (Reddy)
// ══════════════════════════════════════════════════════
const LEVELS = {
  novice: {
    id:"novice", label:"Novice", icon:"🌱", color:"#6f8a5b",
    desc:"Les fondamentaux visuels. Comprendre sans formules.",
    modules: [
      // ── MODULE INTRO ─────────────────────────────────────────────
      {
        id:"n0", anchor:"n-intro", icon:"🗺",
        sub:"§ Intro — Pourquoi l'analyse technique",
        title:"À quoi sert l'analyse technique ?",
        sources:["Murphy, TAOTFM — Introduction","Edwards & Magee, TAST — Preface","Covel, Trend Following ch. 1","Wyckoff, Studies in Tape Reading (1910)"],
        quote:"« L'analyse technique est l'étude du mouvement des marchés, principalement à travers l'utilisation des graphiques, dans le but de prévoir les futures tendances des prix. » — John J. Murphy, Technical Analysis of the Financial Markets",
        content:[
          {type:"p", text:"L'analyse technique (AT) est l'art de lire ce que le marché dit, ici et maintenant, à travers le prix et les volumes. Contrairement à l'analyse fondamentale qui évalue ce qu'une entreprise VAUT, l'AT évalue dans quelle DIRECTION le marché la pousse. Ce n'est pas un oracle — c'est un cadre de probabilités."},
          {type:"p", text:"Son postulat fondateur, posé par Charles Dow en 1884 et repris par tous ses successeurs : le cours d'une action INTÈGRE DÉJÀ toutes les informations disponibles. Les fondamentaux, les espoirs, les craintes, les rumeurs — tout est déjà dans le prix. Murphy : « Le marché sait tout avant que vous le sachiez. »"},
          {type:"concepts", items:[
            {term:"Les 3 postulats de Dow-Murphy", def:"1) Le marché intègre tout. 2) Les prix évoluent en tendances persistantes. 3) L'histoire se répète — les patterns reviennent car la psychologie humaine est constante.", src:"Murphy, TAOTFM — Introduction"},
            {term:"AT vs Analyse fondamentale", def:"L'AF dit 'ce titre vaut 180€'. L'AT dit 'ce titre va vers 220€ dans les 3 prochains mois'. L'une évalue la valeur, l'autre la direction et le timing. Les deux sont complémentaires, pas opposées.", src:"Murphy, TAOTFM ch. 1"},
            {term:"Le marché comme système de votes", def:"Chaque transaction est un vote : acheteur pense que le prix va monter, vendeur qu'il va baisser. Le graphique reflète le résultat agrégé de millions de ces votes — c'est le consensus en temps réel.", src:"Wyckoff, Studies in Tape Reading"},
            {term:"Probabilités, pas certitudes", def:"L'AT ne prédit pas l'avenir — elle identifie des configurations où la probabilité d'un mouvement donné est statistiquement supérieure à 50%. C'est suffisant pour construire un edge (avantage) systématique.", src:"Covel, Trend Following ch. 1"},
          ]},
          {type:"ibox", color:"#6f8a5b", label:"Pourquoi l'AT fonctionne (réponse de Wyckoff, 1910)",
           text:"Richard Wyckoff observait directement les grands opérateurs de Wall Street au début du 20e siècle. Sa conclusion : les institutions (banques, fonds) doivent acheter ou vendre de telles quantités qu'elles laissent inévitablement des traces dans le prix et le volume. L'AT est l'art de lire ces traces — de déduire ce que les 'mains fortes' font, avant que la foule ne le voie."},
          {type:"ibox", color:"#caa85e", label:"La limite fondamentale : l'AT ne prédit pas, elle réagit",
           text:"Covel (Trend Following, ch. 1) résume parfaitement : « Trend following is reactive, not predictive. » Personne ne sait où ira le marché demain. L'AT ne sert pas à prédire — elle sert à identifier l'état présent du marché et à réagir intelligemment. La différence entre spéculation et investissement structuré, c'est précisément ce cadre de réaction disciplinée."},
          {type:"p", text:"Maintenant la vraie question : face à la multitude d'outils (RSI, MACD, Fibonacci, Wyckoff, Gann, SMAs, régressions…), comment ne pas se perdre ? La réponse tient en une phrase — Murphy l'énonce dès le chapitre 1 : commencez toujours par identifier la tendance primaire, puis descendez vers le court terme."},
          {type:"concepts", items:[
            {term:"La séquence d'analyse en 4 étapes", def:"1. TENDANCE : quelle est la direction primaire ? (SMAs, structure hauts/bas). 2. NIVEAU : où sont les supports/résistances clés ? 3. MOMENTUM : la tendance a-t-elle de la force ? (RSI, volumes, TSMOM). 4. TIMING : est-ce le bon moment pour agir ? (Z-Score, convergence des signaux).", src:"Murphy, TAOTFM ch. 1 & 15"},
            {term:"Le principe de convergence", def:"Un signal est fiable quand plusieurs outils indépendants pointent dans la même direction. Une SMA haussière + RSI positif + volume croissant + support tenu = convergence forte = probabilité élevée.", src:"Murphy, TAOTFM ch. 15 (analyse multi-timeframes)"},
            {term:"Moins d'outils, plus de clarté", def:"Murphy et Covel s'accordent : trop d'indicateurs = paralysie par analyse. Les 3 meilleurs outils simples : 1) La structure de prix (hauts/bas). 2) Les moyennes mobiles (SMA 20/50/200). 3) Le volume. Tout le reste est confirmation.", src:"Murphy, TAOTFM ch. 9 · Covel, Trend Following ch. 3"},
            {term:"Le timeframe détermine la stratégie", def:"Long terme (mensuel/hebdo) = biais directionnel, décision d'allocation. Moyen terme (journalier) = entrées/sorties, gestion de position. Court terme (horaire) = timing précis. On commence TOUJOURS par le LT.", src:"Murphy, TAOTFM ch. 15"},
          ]},
          {type:"ibox", color:"#c6523f", label:"La séquence Invelps : comment lire ce dashboard",
           text:"Ce dashboard implémente exactement la séquence Murphy en 4 niveaux. 1) PROFIL & IScore = contexte fondamental + conviction. 2) DIAGNOSTIC = synthèse de la tendance primaire et du régime. 3) CHART = lecture visuelle de la structure, des SMAs, du canal σ. 4) PILIERS = analyse granulaire de chaque dimension (structure, momentum, volume, timing). Lisez toujours dans cet ordre — du général au particulier."},
        ],
        qcm:[
          {
            q:"Murphy dit que 'le marché intègre tout'. Un analyste fondamental vient de découvrir qu'une entreprise a des problèmes cachés non encore publiés. Pourtant le cours de l'action a baissé de 15% en 3 semaines sans nouvelle officielle. L'AT peut-elle expliquer cela ?",
            choices:[
              {l:"Non — sans information officielle, le cours baisse par hasard. L'AT n'explique pas les mouvements sans cause fondamentale.", c:false,
               f:"Faux. C'est précisément ce que Murphy et Wyckoff expliquent : les insiders (qui connaissent les problèmes) vendent depuis 3 semaines. Leurs ventes laissent des traces dans le graphique. L'AT lit ces traces avant que l'information ne soit publique — c'est l'un de ses avantages fondamentaux."},
              {l:"Oui — les initiés qui connaissent les problèmes vendent depuis 3 semaines. L'AT lit les traces de ces ventes (volume + structure de prix) avant la publication officielle.", c:true,
               f:"Exact. Murphy (Introduction) : 'Le marché sait tout avant que vous le sachiez.' Wyckoff (1910) observait déjà ce phénomène : les grandes ventes institutionnelles laissent des empreintes volumétriques et de prix. L'AT lit ces empreintes en temps réel."},
              {l:"Peut-être — mais l'AT ne peut fonctionner que si on connaît la cause fondamentale.", c:false,
               f:"C'est l'erreur classique. L'AT n'a pas besoin de connaître la cause — elle lit le résultat (le prix et le volume). La cause importe peu pour l'action tactique. Covel : 'I don't need to know why — I need to know what.'"},
            ]
          },
          {
            q:"Face à un graphique NVDA, dans quel ordre Murphy recommande-t-il d'analyser les éléments ?",
            choices:[
              {l:"1. RSI → 2. MACD → 3. Bandes de Bollinger → 4. Tendance", c:false,
               f:"Murphy s'opposerait à cette approche. Commencer par les oscillateurs est une erreur classique de débutant : on cherche des signaux avant de savoir dans quelle direction le marché va. L'oscillateur donne un signal d'achat dans une tendance baissière = catastrophe."},
              {l:"1. Tendance primaire (SMAs, structure) → 2. Supports/résistances clés → 3. Momentum (RSI, volumes) → 4. Timing (Z-Score, convergence)", c:true,
               f:"Parfait. C'est exactement la séquence Murphy (TAOTFM ch. 1 & 15) et la logique de ce dashboard. Du général au particulier. La tendance primaire est le filtre — elle détermine le biais de toutes les décisions suivantes. Sans elle, les signaux CT sont du bruit."},
              {l:"1. Fibonacci → 2. Patterns → 3. Volume → 4. Tendance", c:false,
               f:"Fibonacci et les patterns sont des outils avancés de timing — ils n'ont de sens qu'une fois la tendance et les niveaux clés identifiés. Murphy : 'les outils avancés confirment, ils n'initient pas l'analyse.'"},
            ]
          },
        ]
      },
      // ── MODULES N1-N3 ─────────────────────────────────────────────
      {
        id:"n1", anchor:"n-tendance", icon:"📈",
        sub:"§ N1 — Structure de marché",
        title:"Lire une tendance",
        sources:["Edwards & Magee, TAST ch. 2-3","Wyckoff, Villahermosa ch. 3","Covel, Trend Following ch. 1"],
        quote:"« Le prix suit le chemin de la moindre résistance. La tendance haussière est une série de sommets et de creux croissants. » — Wyckoff / Villahermosa",
        content:[
          {type:"p", text:"Une tendance est la direction persistante des prix dans le temps. Edwards et Magee (1948) définissent précisément : une tendance HAUSSIÈRE est une succession de « hauts de plus en plus hauts ET de bas de plus en plus hauts ». Les deux conditions sont nécessaires. Un seul nouveau haut ne suffit pas."},
          {type:"p", text:"Wyckoff précise que le prix suit toujours « le chemin de la moindre résistance » — il progresse là où la résistance est la plus faible. Michael Covel (Trend Following) va plus loin : « le trend following est réactif, jamais prédictif. » On ne prédit pas la tendance — on la suit."},
          {type:"concepts", items:[
            {term:"Tendance haussière", def:"Hauts et bas TOUS DEUX croissants sur plusieurs cycles. La rupture d'un seul bas précédent est un signal d'alerte structurel.", src:"Edwards & Magee ch. 2"},
            {term:"Tendance baissière", def:"Hauts et bas TOUS DEUX décroissants. Chaque rebond échoue à dépasser le sommet précédent.", src:"Edwards & Magee ch. 2"},
            {term:"Range (consolidation)", def:"Oscillation entre support et résistance sans direction claire. Wyckoff appelle ça la 'construction de la cause' — énergie potentielle accumulée.", src:"Wyckoff, Villahermosa ch. 7"},
            {term:"Retournement vs rebond", def:"Un rebond technique n'est PAS un retournement. Il faut une rupture confirmée de la structure de hauts/bas ET du volume pour parler de retournement.", src:"Murphy, TAOTFM ch. 4"},
          ]},
          {type:"ibox", color:T.am, label:"La théorie de Dow (1884)",
           text:"Charles Dow a posé les bases : une tendance primaire se compose de 3 phases. 1) Accumulation (mains fortes achètent en secret). 2) Participation publique (la tendance est visible). 3) Distribution (mains fortes vendent à la foule enthousiaste). Edwards & Magee ont formalisé cela en 1948. Cette structure est toujours d'actualité."},
          {type:"ibox", color:T.re, label:"Piège classique du débutant",
           text:"Confondre un rebond technique avec un retournement. Après une baisse, chaque rebond semble être 'la reprise'. Vérifiez toujours : est-ce que le nouveau haut DÉPASSE le précédent ? Si non, c'est un rebond dans une tendance baissière, pas un retournement. Covel : 'les marchés testent votre conviction avant de vous récompenser.'"},
        ],
        qcm:[
          {q:"NVDA : H1=180€, B1=165€, H2=205€, B2=170€, H3=195€. Quelle est la structure ?",
           choices:[
            {l:"Tendance haussière confirmée — H2>H1 et B2>B1", c:false,
             f:"Presque. H2(205)>H1(180) ✓ et B2(170)>B1(165) ✓ = haussier jusque-là. Mais H3(195)<H2(205) : la règle des hauts croissants est brisée. C'est un essoufflement (Edwards & Magee : 'non-confirmation') — signal d'alerte, pas encore retournement."},
            {l:"Essoufflement haussier — H3(195) < H2(205) brise la règle des hauts croissants", c:true,
             f:"Exact ! Edwards & Magee (ch. 2) : tendance haussière = CHAQUE haut > haut précédent. H3<H2 signale un essoufflement. Murphy parlerait de 'divergence de structure'. Ce n'est pas encore baissier (les bas restent croissants) mais c'est une alerte majeure."},
            {l:"Tendance baissière — le dernier mouvement est une baisse de 205 à 195", c:false,
             f:"Non. Une baisse de 10 points ne définit pas une tendance baissière. Wyckoff : il faut une succession de hauts ET bas décroissants. Les bas restent croissants (165→170) — structure partiellement intacte."},
           ]
          },
          {q:"Covel (Trend Following) dit : 'Je ne sais pas où ira le marché demain.' Quelle implication pratique ?",
           choices:[
            {l:"Il faut donc ignorer l'AT et faire de l'analyse fondamentale", c:false,
             f:"Non. Covel est un trader pur prix — il n'utilise pas l'analyse fondamentale. Son message : AUCUNE méthode ne prédit l'avenir. L'AT lit l'état présent, elle ne prédit pas."},
            {l:"On réagit au prix réel, on ne prédit pas. On suit la tendance actuelle et on accepte d'être en retard au début.", c:true,
             f:"Parfait. Covel (ch. 1) : 'trend following is reactive, not predictive.' On attend la confirmation de la tendance (parfois 5-10% après le départ) puis on suit. La 'perte' initiale est le coût de la confirmation — c'est normal."},
            {l:"Il faut attendre la certitude absolue avant d'agir", c:false,
             f:"La 'certitude absolue' n'existe pas en bourse. Covel : 'waiting for certainty means never trading.' On accepte l'incertitude, on gère le risque. C'est l'essence du trend following."},
           ]
          },
        ]
      },
      {
        id:"n2", anchor:"n-supports", icon:"🎯",
        sub:"§ N2 — Niveaux clés",
        title:"Supports, Résistances et Rôle inversé",
        sources:["Edwards & Magee, TAST ch. 13","Murphy, TAOTFM ch. 4"],
        quote:"« Ces niveaux critiques changent constamment de rôle — de support à résistance et de résistance à support. C'est l'un des principes les plus importants et les moins compris de l'analyse technique. » — Edwards & Magee, TAST ch. 13",
        content:[
          {type:"p", text:"Edwards et Magee ont consacré tout un chapitre (ch. 13) aux supports et résistances — c'est l'une des sections les plus citées de leur ouvrage fondateur de 1948. Un SUPPORT est une zone de prix où, historiquement, les acheteurs ont surpassé les vendeurs. Une RÉSISTANCE est l'inverse."},
          {type:"p", text:"Le principe du RÔLE INVERSÉ est la découverte clé : quand un support est cassé avec conviction, il devient résistance. Quand une résistance est cassée avec volume, elle devient support. Edwards & Magee l'illustrent avec des dizaines de graphiques dans leur chapitre 13."},
          {type:"concepts", items:[
            {term:"Support", def:"Zone historique où la demande a stoppé la baisse. Chaque test réussi renforce le niveau. Edwards & Magee : le volume lors des touches mesure la 'force' du support.", src:"Edwards & Magee ch. 13"},
            {term:"Résistance", def:"Zone historique où l'offre a stoppé la hausse. Anciens sommets, congestions de prix à volume élevé.", src:"Edwards & Magee ch. 13"},
            {term:"Rôle inversé", def:"Support cassé = nouvelle résistance. Résistance cassée = nouveau support. Validé sur des centaines de graphiques depuis 1948. Murphy le confirme dans TAOTFM ch. 4.", src:"Edwards & Magee ch. 13 · Murphy ch. 4"},
            {term:"Significance (force du niveau)", def:"Dépend de : nombre de touches, volume aux touches, ancienneté. Plus un niveau est testé et tient, plus il est significatif.", src:"Edwards & Magee ch. 13"},
          ]},
          {type:"ibox", color:T.gr, label:"Le volume valide la cassure",
           text:"Murphy (TAOTFM ch. 4) : une cassure de support ou résistance n'a de valeur que si le volume est supérieur à la moyenne des 20 dernières séances. Une cassure sur volume faible = 'fake break' probable. Edwards & Magee parlent de la règle des 3% (confirmer 3% au-delà du niveau). Sur NVDA à 216€ (résistance), une clôture à 222€+ sur fort volume = breakout valide."},
          {type:"ibox", color:T.bl, label:"Zones, pas lignes précises",
           text:"Edwards & Magee insistent : les supports et résistances sont des ZONES, pas des lignes au centime près. Un support à 165€ signifie que tout rebond entre 163 et 167€ est dans la norme. Tracer une ligne exacte à 165.17€ et panicker à 164.80€ est une erreur de novice — les marchés ne sont pas si précis."},
        ],
        qcm:[
          {q:"NVDA a résisté 3 fois à 216€ (résistance). Il clôture à 218€ sur volume 2.5× la moyenne. La semaine suivante il revient tester 216€. Selon Edwards & Magee, comment interpréter ce test ?",
           choices:[
            {l:"Signal de vente — le titre retourne vers sa résistance, risque de rechute", c:false,
             f:"Non. La cassure sur fort volume (2.5×) valide le breakout. Edwards & Magee (ch. 13) : la résistance cassée devient support = rôle inversé. Le retour sur 216€ est un pullback classique sur l'ancien niveau — l'entrée idéale selon la méthode."},
            {l:"Achat sur le test de 216€ — l'ancienne résistance est maintenant support (rôle inversé)", c:true,
             f:"Parfait. C'est le principe du rôle inversé formalisé par Edwards & Magee ch. 13. Le pullback sur 216€ après le breakout sur fort volume est le setup d'entrée classique. Stop sous 214€ (zone). Murphy confirme ce setup dans TAOTFM ch. 4."},
            {l:"Attendre une deuxième clôture au-dessus de 218€ pour confirmer", c:false,
             f:"La cassure initiale (clôture 218€, volume 2.5×) EST la confirmation. Attendre une deuxième cassure revient à entrer avec un retard de 5-10% et un ratio risque/récompense dégradé. Edwards & Magee : la confirmation vient du volume, pas d'un deuxième signal prix."},
           ]
          },
        ]
      },
      {
        id:"n3", anchor:"n-volume", icon:"📊",
        sub:"§ N3 — Volume & Conviction",
        title:"Le volume : l'effort derrière le mouvement",
        sources:["Wyckoff, Villahermosa ch. 9","Murphy, TAOTFM ch. 7","Wyckoff, Studies in Tape Reading"],
        quote:"« En analyse financière, l'effort est représenté par le volume et le résultat par le prix. Si l'effort et le résultat ne correspondent pas, quelque chose ne va pas. » — Wyckoff / Villahermosa, ch. 9",
        content:[
          {type:"p", text:"Richard Wyckoff (1873-1934) a fondé son analyse sur une loi simple : le VOLUME représente l'effort des participants, le PRIX représente le résultat. Un effort doit produire un résultat proportionnel. Si le prix monte vigoureusement sur volume faible — attention. L'effort est disproportionné au résultat."},
          {type:"p", text:"Villahermosa modernise ces concepts : le volume révèle les actions des 'mains fortes' (institutions, smart money) qui ne peuvent pas dissimuler leurs mouvements dans les données volumétriques. Murphy complète : 'le volume doit confirmer la tendance. Une tendance dont le volume est décroissant est suspecte.'"},
          {type:"concepts", items:[
            {term:"Volume conforme", def:"Hausse sur volumes croissants + baisse sur volumes décroissants = tendance saine. Murphy : 'la règle de base.' Tout le reste est une déviation.", src:"Murphy, TAOTFM ch. 7"},
            {term:"Divergence prix/volume", def:"Prix monte mais volume baisse : signal d'essoufflement. Les mains fortes ne participent pas à la hausse. Wyckoff : 'No Demand' — absence de demande institutionnelle.", src:"Wyckoff, Villahermosa ch. 9"},
            {term:"Selling Climax", def:"Pic de volume extrême sur un bas, souvent accompagné d'une actualité négative. Wyckoff : signal de Phase A — l'épuisement de la vente institutionnelle. Potentiellement le bas d'un cycle d'accumulation.", src:"Wyckoff, Villahermosa ch. 15"},
            {term:"No Supply / No Demand", def:"Wyckoff : petits volumes sur les reculs (No Supply = haussier) ou petits volumes sur les rebonds (No Demand = baissier). Les mains fortes s'abstiennent.", src:"Wyckoff, Studies in Tape Reading"},
          ]},
          {type:"ibox", color:T.am, label:"Loi de l'effort et du résultat",
           text:"Wyckoff (Villahermosa ch. 9) : si un titre baisse sur volumes faibles, les gros vendeurs NE vendent pas. La baisse est due à l'absence d'acheteurs, non à des ventes agressives. C'est un signal moins négatif qu'il n'y paraît — potentiellement une zone d'accumulation. À l'inverse, une hausse sur volumes très forts peut être un Buying Climax — signal de distribution imminente."},
          {type:"ibox", color:T.bl, label:"Volumes relatifs, jamais absolus",
           text:"Un volume de 100M titres NVDA est-il fort ou faible ? Cela dépend de la MOYENNE. Sur les 60 dernières séances, NVDA échange ~150M titres/jour. 100M = faible (0.67×). Mais si la moyenne était 40M, alors 100M = très fort (2.5×). Toujours comparer le volume actuel à la moyenne des 20-60 dernières séances."},
        ],
        qcm:[
          {q:"NVDA chute de 3% cette semaine sur volume 40% inférieur à la moyenne 20 jours. Interprétation Wyckoff ?",
           choices:[
            {l:"Signal baissier fort — la baisse sur 3 jours confirme la tendance baissière", c:false,
             f:"Non. L'élément clé est le volume FAIBLE. Wyckoff : une baisse sur volume faible = les gros vendeurs ne sont pas actifs. C'est une baisse par manque d'acheteurs, pas par pression vendeuse institutionnelle. Signal ambigu, pas baissier fort."},
            {l:"No Supply probable — baisse sur volume faible = pas de pression vendeuse institutionnelle. Potentiellement un test sain en tendance haussière.", c:true,
             f:"Exact. Wyckoff (Villahermosa ch. 9) : 'No Supply' = baisse sur volume faible. Les institutions ne vendent pas. La faiblesse est superficielle. En contexte de tendance haussière, c'est souvent un test de support avant la reprise. Murphy confirme : 'une correction sur volume décroissant reste dans la norme.'"},
            {l:"Il faut attendre un rebond avec fort volume pour confirmer quoi que ce soit", c:false,
             f:"Partiellement vrai, mais la conclusion de 'No Supply' est déjà informative. Wyckoff n'attend pas toujours la confirmation — il lit l'état présent. Un rebond sur fort volume serait la confirmation de la Phase D, pas la première lecture."},
           ]
          },
        ]
      },
    ]
  },

  intermediaire: {
    id:"intermediaire", label:"Intermédiaire", icon:"📐", color:"#caa85e",
    desc:"Outils quantitatifs : OLS, RSI, TSMOM. Formules sur données NVDA.",
    modules: [
      {
        id:"i1", anchor:"i-regression", icon:"📐",
        sub:"§ I1 — Quantification de la tendance",
        title:"Régression OLS et Moyennes Mobiles",
        sources:["Murphy, TAOTFM ch. 4 & 9","Edwards & Magee, TAST ch. 28"],
        formula:"P_t = α + β·t       R² = 1 − (SS_res / SS_tot)",
        quote:"« La moyenne mobile ne prédit pas la tendance — elle la confirme. Sa valeur est dans la simplification du signal, pas dans l'anticipation. » — Murphy, TAOTFM ch. 9",
        content:[
          {type:"p", text:"La régression OLS (Ordinary Least Squares) ajuste une droite P_t = α + β·t aux prix historiques. β représente la vitesse théorique de progression (€/semaine ou %/an). Mais Murphy insiste fortement dans TAOTFM ch. 4 : β sans R² n'a aucune valeur. Le R² mesure le 'fit' (0-1). Seuil actionnable : R² ≥ 0.50."},
          {type:"p", text:"Les moyennes mobiles simples (SMA) sont des lissages qui filtrent le bruit à court terme. L'alignement SMA20 > SMA50 > SMA200 est la configuration haussière idéale selon Murphy. Le 'Golden Cross' (SMA50 > SMA200) est l'un des signaux les plus suivis institutionnellement. Edwards & Magee l'appelaient 'la confirmation de la tendance primaire.'"},
          {type:"concepts", items:[
            {term:"Pente OLS (β)", def:"Vitesse théorique de progression. DOIT être validée par R² ≥ 0.50. β positif + R² faible = 'tendance existe mais chemin trop erratique pour agir dessus' (Murphy ch. 4).", src:"Murphy, TAOTFM ch. 4"},
            {term:"R² (coefficient de détermination)", def:"% de la variance du prix expliqué par la tendance linéaire. <0.30 = signal peu fiable. 0.50-0.75 = signal modéré. >0.75 = tendance très ordonnée.", src:"Murphy, TAOTFM ch. 4"},
            {term:"Golden Cross", def:"SMA50 passe au-dessus de SMA200. Signal haussier de long terme. Edwards & Magee : 'la confirmation la plus puissante de la tendance primaire.' Murphy : signal de confirmation, jamais d'anticipation.", src:"Murphy ch. 9 · Edwards & Magee ch. 28"},
            {term:"Alignement des SMA", def:"SMA20>SMA50>SMA200 = ordre haussier parfait. Prix < SMA200 = marché techniquement baissier selon la majorité des institutionnels. La ligne de démarcation bull/bear.", src:"Murphy, TAOTFM ch. 9"},
          ]},
          {type:"ibox", color:T.re, label:"Le piège de β sans R²",
           text:"Exemple : β = +0.097€/sem (positif, encourageant) avec R² = 0.17 (catastrophique). Seulement 17% de la variance est expliquée par la tendance — 83% est du bruit. Murphy (ch. 4) : 'ce titre monte sur le papier mais descend souvent dans la réalité. Le chemin est trop erratique pour être exploitable.' Seuil minimum : R² = 0.50 pour envisager un signal basé sur la régression."},
          {type:"ibox", color:T.gr, label:"Zone de rebond en tendance haussière",
           text:"Murphy (ch. 9) : en tendance haussière forte (SMA20>SMA50>SMA200), les corrections jusqu'à la SMA50 sont des OPPORTUNITÉS D'ACHAT, pas des signaux de vente. La SMA20 est le premier support dynamique. La SMA200 est le dernier rempart bull/bear. Prix qui rebondit sur SMA50 après un test réussi = entrée avec excellent ratio risque/récompense."},
        ],
        qcm:[
          {q:"NVDA : β = +1.2%/sem, R² = 0.72. SMA20=200€, SMA50=187€, SMA200=184€. Prix actuel = 198€. Que conclure ?",
           choices:[
            {l:"Tendance haussière solide et fiable (R²=0.72 > seuil 0.50). Test de SMA20 = entrée potentielle selon Murphy.", c:true,
             f:"Parfait. R²=0.72 > seuil 0.50 → β fiable. Alignement SMA20>SMA50>SMA200 = haussier parfait (Murphy ch. 9). Prix à 198€ teste la SMA20 (200€) = rebond dans la tendance. C'est le setup d'entrée décrit par Murphy ch. 9 : 'pullback sur la première MA en tendance forte.'"},
            {l:"Attention — prix sous SMA20 (200€) → signal baissier de court terme", c:false,
             f:"Murphy distingue 'test' (prix ~SMA20, normal) de 'rupture confirmée' (prix BIEN sous SMA20 sur volume). 198€ vs 200€ = test, pas rupture. L'alignement global reste haussier, R² est solide."},
            {l:"R²=0.72 insuffisant — attendre R²≥0.90 pour agir", c:false,
             f:"Murphy (ch. 4) cite 0.50 comme seuil minimal et 0.65+ comme niveau confortable. 0.72 est un très bon R² — la tendance est fiable statistiquement. 0.90 serait excessivement restrictif et exclurait la plupart des tendances réelles."},
           ]
          },
        ]
      },
      {
        id:"i2", anchor:"i-momentum", icon:"⚡",
        sub:"§ I2 — Mesure du momentum",
        title:"RSI et Time-Series Momentum (TSMOM)",
        sources:["Murphy, TAOTFM ch. 10","Gray & Vogel, QM ch. 3 & 6","Moskowitz, Ooi & Pedersen, JFE 2012"],
        formula:"RSI = 100 − [100 / (1 + RS)]     RS = moy. gains 14j / moy. pertes 14j\nTSMOM_h = (P_t / P_{t-h}) − 1\nFIP = SIGN(TSMOM_12M) × (% jours_négatifs − % jours_positifs)",
        quote:"« Le momentum est la tendance des gagnants à continuer de gagner. Cette anomalie est documentée sur plus de 200 ans et dans 40+ marchés. C'est le plus robuste des facteurs. » — Gray & Vogel, Quantitative Momentum 2016",
        content:[
          {type:"p", text:"Le RSI (Relative Strength Index) a été créé par Welles Wilder en 1978. Murphy insiste : le RSI NE prédit PAS la direction — il qualifie l'état. La vraie puissance du RSI réside dans les DIVERGENCES : si le prix fait de nouveaux hauts mais que le RSI ne confirme pas → signal d'essoufflement."},
          {type:"p", text:"Gray & Vogel (Quantitative Momentum, 2016) introduisent deux métriques clés. Le TSMOM (Time-Series Momentum) mesure le rendement absolu sur 3, 6 ou 12 mois. Moskowitz, Ooi & Pedersen (JFE 2012) ont prouvé sa persistance sur 200 ans. Le FIP (Frog in the Pan) mesure la QUALITÉ du momentum : un momentum positif construit sur des pics haussiers isolés dans une mer de jours baissiers est statistiquement inférieur."},
          {type:"concepts", items:[
            {term:"RSI zones de tendance", def:"En tendance forte, le RSI peut rester >70 pendant des mois. Murphy : 'les zones 30/70 sont pour les marchés en range, pas en tendance. En tendance haussière, acheter autour de 40-50, pas à 30.'", src:"Murphy, TAOTFM ch. 10"},
            {term:"Divergence RSI baissière", def:"Prix fait un nouveau haut mais RSI fait un haut inférieur. Murphy : 'l'un des signaux les plus fiables de l'AT.' Signal d'essoufflement, pas de vente mécanique.", src:"Murphy, TAOTFM ch. 10"},
            {term:"TSMOM (Time-Series Momentum)", def:"Rendement absolu sur h mois. Persistance sur 200+ ans (Moskowitz 2012). TSMOM 12M > 0 = momentum positif. Mais la qualité compte autant que la direction.", src:"Gray & Vogel, QM ch. 3"},
            {term:"FIP (Frog in the Pan)", def:"Score de régularité. FIP > 0 avec TSMOM > 0 = gains concentrés sur quelques pics dans une majorité de jours baissiers. Gray & Vogel : ces 'frogs' sous-performent les 'smooth momentum' de manière statistiquement significative.", src:"Gray & Vogel, QM ch. 6"},
          ]},
          {type:"ibox", color:T.am, label:"RSI en tendance forte : la règle méconnue",
           text:"Murphy (TAOTFM ch. 10) : en tendance haussière très forte, les niveaux d'achat se décalent. Au lieu de 30 (survente classique), la zone d'achat passe à 40-50. Le RSI peut rester entre 50 et 80 pendant des mois en tendance haussière — c'est NORMAL. Sur NVDA en phase de markup, RSI = 67 n'est pas du surachat mais de la vigueur."},
          {type:"ibox", color:T.re, label:"FIP : la grenouille et l'eau qui chauffe",
           text:"Gray & Vogel (QM ch. 6, p.118) : une grenouille dans une eau qui chauffe progressivement ne réagit pas. Identique pour les titres à 'discrete momentum' : TSMOM 12M = +62% mais construit sur quelques séances exceptionnelles et une majorité de jours baissiers. Les participants ne 'voient' pas le danger. Ces titres à FIP élevé sous-performent statistiquement les titres à 'continuous momentum' (progression régulière) sur les 3-12 mois suivants."},
        ],
        qcm:[
          {q:"NVDA : RSI = 68, TSMOM 12M = +62%, TSMOM 3M = -2%. FIP = +0.05 (majorité de jours baissiers malgré bilan annuel positif). Murphy et Gray & Vogel convergent-ils ?",
           choices:[
            {l:"Murphy haussier (RSI<70), Gray & Vogel prudents (FIP élevé = qualité de momentum médiocre). Lecture nuancée : LT haussier mais qualité de CT fragile.", c:true,
             f:"Exact. Murphy lirait RSI=68 comme 'fort mais pas surachat'. Gray & Vogel regarderaient FIP=+0.05 (faible mais positif = 'frog') : momentum positif mais de mauvaise qualité. Conclusion conjointe : tendance LT haussière confirmée, mais prudence sur les entrées à CT. Le momentum de court terme est fragile."},
            {l:"Tout est haussier — TSMOM 12M +62% et RSI < 70 confirment", c:false,
             f:"Gray & Vogel iraient au-delà du TSMOM 12M. Le FIP qualifie la RÉGULARITÉ du momentum. +62% construit sur 4-5 séances exceptionnelles dans une mer de jours baissiers est statistiquement moins robuste qu'un +35% régulier. La qualité compte."},
            {l:"TSMOM 3M négatif (-2%) invalide tout — c'est un signal de vente", c:false,
             f:"Murphy et Gray & Vogel s'accorderaient : TSMOM 3M négatif est une divergence de court terme dans une tendance de fond positive, pas un signal de vente. Gray & Vogel (ch. 3) utilisent TSMOM 12M comme signal principal, 3M comme filtre de qualité."},
           ]
          },
        ]
      },
    ]
  },

  avance: {
    id:"avance", label:"Avancé", icon:"🔬", color:"#c6523f",
    desc:"Wyckoff complet, patterns harmoniques Fibonacci, temps de Gann.",
    modules: [
      {
        id:"a1", anchor:"a-wyckoff", icon:"🔄",
        sub:"§ A1 — Méthode Wyckoff",
        title:"Cycle de marché : 5 phases et événements clés",
        sources:["Wyckoff, Villahermosa ch. 14-20","Wyckoff, Studies in Tape Reading (1910)"],
        formula:"Loi 1 : D > O → Prix ↑  |  O > D → Prix ↓\nLoi 2 : Accumulation → Markup proportionnel (cause → effet)\nLoi 3 : Volume (effort) ∝ Prix (résultat)",
        quote:"« Les grands opérateurs accumulent en secret dans la dépression, distribuent en secret dans l'euphorie. Le volume est leur empreinte digitale. » — Richard D. Wyckoff, Studies in Tape Reading (1910)",
        content:[
          {type:"p", text:"Richard Wyckoff (1873-1934) a développé sa méthode en observant les transactions des 'grands opérateurs' (composite operator) dans les données de prix et volume. Il introduit le concept de 'Composite Operator' : imaginer que toutes les institutions agissent comme un seul opérateur avec une stratégie cohérente. Pas une théorie du complot — une façon de lire les données."},
          {type:"p", text:"Villahermosa (The Wyckoff Methodology in Depth, 2019) modernise ces 5 phases avec des exemples contemporains. La Phase B (construction de la cause) est souvent la plus longue et la plus frustrante pour les traders — le range semble interminable. C'est précisément là que les institutions accumulent ou distribuent discrètement."},
          {type:"concepts", items:[
            {term:"Phase A — Arrêt de tendance", def:"Selling Climax (fort volume + rejet = bas potentiel). Automatic Rally. Secondary Test. La tendance précédente s'épuise. Villahermosa ch. 14-16.", src:"Wyckoff, Villahermosa ch. 14"},
            {term:"Phase B — Construction de la cause", def:"Range latéral. Institutions accumulent discrètement. Volume global décroissant. Tests multiples des bornes du range. La plus longue et la plus trompeuse.", src:"Wyckoff, Villahermosa ch. 17"},
            {term:"Phase C — Spring (test décisif)", def:"Fausse cassure sous le support du range. Volume FAIBLE lors de la cassure = les gros vendeurs ne vendent pas. Retour rapide = Spring confirmé = signal haussier majeur.", src:"Wyckoff, Villahermosa ch. 18"},
            {term:"Phase D — Confirmation (LPS)", def:"Last Point of Support : premier 'higher low' après le Spring. Breakout de la résistance du range sur fort volume. L'entrée optimale selon Wyckoff.", src:"Wyckoff, Villahermosa ch. 19"},
            {term:"Phase E — Markup / Markdown", def:"Tendance établie. Les institutions ont fini d'accumuler. En distribution, la Phase C devient UTAD (fausse cassure haute) avant le markdown.", src:"Wyckoff, Villahermosa ch. 20"},
          ]},
          {type:"ibox", color:T.re, label:"Le Spring : le piège le plus rentable",
           text:"Wyckoff (Villahermosa ch. 18) : le Spring est la cassure temporaire sous le support du range, conçue pour déclencher les stops des longs et piéger les vendeurs. Les 3 critères : (1) cassure sous le support du range, (2) volume FAIBLE lors de la cassure — les gros ne vendent pas, (3) retour rapide au-dessus du support en quelques séances. C'est le setup le plus rentable de la méthode Wyckoff ET le plus dangereux pour les non-initiés."},
          {type:"ibox", color:T.am, label:"UTAD : l'équivalent en distribution",
           text:"En haut de marché, la Phase C de distribution est l'UTAD (Upthrust After Distribution) : fausse cassure AU-DESSUS de la résistance du range, attirant les acheteurs enthousiastes pendant que les institutions leur vendent leurs positions. Volume souvent élevé (les institutions absorbent la demande). Prix redescend rapidement sous la résistance = signal baissier majeur selon Villahermosa (ch. 18)."},
        ],
        qcm:[
          {q:"NVDA est en range 180-216€ depuis 3 mois. Il casse sous 180€ à 174€ sur volume FAIBLE (0.7× la moyenne 20j), puis remonte à 183€ en 3 séances sur volume modéré. Wyckoff ?",
           choices:[
            {l:"Rupture de support confirmée — baisse vers 160-165€ probable. Vendre.", c:false,
             f:"Non. Le volume FAIBLE lors de la cassure est le signal clé. Wyckoff : une vraie rupture baissière = fort volume vendeur. Volume 0.7× = les institutions ne vendent PAS. La baisse est due à l'absence d'acheteurs, pas à des ventes agressives — distinction fondamentale."},
            {l:"Spring de Phase C — cassure sur volume faible + retour rapide = signal haussier majeur. Entrée optimale avec stop sous 174€.", c:true,
             f:"Parfait. Les 3 critères du Spring sont réunis : (1) cassure sous 180€ (support du range), (2) volume 0.7× (faible = No Supply), (3) retour rapide à 183€. C'est le setup d'entrée de Phase C selon Villahermosa (ch. 18). Stop sous le Spring (174€). Objectif minimum : résistance du range (216€)."},
            {l:"Test de Phase B — le range n'est pas terminé, attendre le breakout de 216€", c:false,
             f:"Possible si le Spring n'est pas confirmé par le volume. Mais les critères (cassure + faible volume + retour rapide) pointent vers Phase C. Un test de Phase B reste généralement DANS le range — la cassure sous 180€ est significative."},
           ]
          },
          {q:"Après un markup de 6 mois (NVDA 160→250€), un range 230-255€ se forme pendant 2 mois. NVDA casse à 258€ sur FORT volume mais revient à 240€ en 4 séances. Villahermosa appelle ça ?",
           choices:[
            {l:"Breakout Phase D — volume fort confirme la cassure, pullback normal avant la poursuite", c:false,
             f:"La différence clé : un vrai breakout Phase D RESTE au-dessus de la résistance. Le retour rapide à 240€ (bien sous 255€) INVALIDE le breakout. Villahermosa (ch. 18) : si le prix ne maintient pas au-dessus de la résistance, ce n'est pas un breakout — c'est un UTAD."},
            {l:"UTAD (Upthrust After Distribution) — fausse cassure haussière pour piéger les acheteurs pendant que les institutions distribuent. Signal baissier majeur.", c:true,
             f:"Exact. UTAD = Upthrust After Distribution. Trois critères : (1) cassure au-dessus de la résistance (255€ → 258€), (2) fort volume (institutions VENDENT à la foule enthousiaste), (3) retour rapide sous la résistance. Villahermosa (ch. 18) : l'UTAD est le signal de distribution le plus puissant de la méthode Wyckoff."},
            {l:"Fausse alerte — les marchés testent souvent les résistances plusieurs fois", c:false,
             f:"Un 'test de résistance' reste généralement dans le range ou au niveau de la résistance, pas 3€ au-dessus. La cassure sur fort volume avec retour rapide est structurellement différent d'un simple test."},
           ]
          },
        ]
      },
      {
        id:"a2", anchor:"a-patterns", icon:"🔺",
        sub:"§ A2 — Patterns harmoniques",
        title:"Fibonacci, patterns harmoniques et temps de Gann",
        sources:["Pesavento & Jouflas, Trade What You See ch. 2-5","Gann, Reddy ch. 2-3"],
        formula:"Retracements : 23.6% · 38.2% · 50% · 61.8% · 78.6%\nExtensions  : 100% · 127.2% · 161.8% · 261.8%\nGartley D   : retracement 78.6% de XA",
        quote:"« Toute existence est basée sur une proportion exacte et une relation parfaite. Il n'y a pas de hasard dans la nature, car des principes mathématiques du plus haut ordre sont à la base de toutes choses. » — W.D. Gann",
        content:[
          {type:"p", text:"Larry Pesavento et Leslie Jouflas (Trade What You See, 2007) formalisent les patterns harmoniques — des structures géométriques de prix utilisant les ratios de Fibonacci pour identifier des zones de retournement probable (PRZ = Potential Reversal Zone). Ces ratios (0.618, 0.786, 1.272, 1.618) sont dérivés de la suite de Fibonacci et du nombre d'or φ = 1.618."},
          {type:"p", text:"W.D. Gann (1878-1955), documenté par Hima Reddy (2012), avait une approche différente mais complémentaire. Pour Gann, le TEMPS est aussi important que le PRIX. Ses angles (1×1 = 45° = 1 unité de temps pour 1 unité de prix) représentent l'équilibre parfait. 'Il n'y a pas de hasard dans la nature' — les patterns se répètent parce que la psychologie humaine ne change pas."},
          {type:"concepts", items:[
            {term:"Niveaux de Fibonacci", def:"0.618 (1/φ) et 0.786 (√0.618) sont les plus puissants. Pesavento : 'nous n'obtenons pas toujours un retournement parfait au centime — c'est une ZONE, pas un point.'", src:"Pesavento & Jouflas ch. 2"},
            {term:"Pattern Gartley (1935)", def:"XA → AB = 61.8% de XA → BC entre 38.2-88.6% de AB → CD = 78.6% de XA (zone PRZ). Le plus ancien pattern harmonique formalisé. Pesavento : 'le plus fiable statistiquement.'", src:"Pesavento & Jouflas ch. 5"},
            {term:"PRZ (Potential Reversal Zone)", def:"Convergence de plusieurs ratios Fibonacci dans une zone étroite. Plus de confluences = plus fort le signal. Pesavento : toujours utiliser les ratios EN COMBINAISON, jamais isolément.", src:"Pesavento & Jouflas ch. 4"},
            {term:"Angles de Gann 1×1", def:"45° = 1 unité de prix / 1 unité de temps = équilibre. Un prix au-dessus de l'angle 1×1 = tendance forte. Gann : 'time is more important than price' — le temps d'un mouvement prédit souvent sa durée future.", src:"Gann, Reddy ch. 2-3"},
          ]},
          {type:"ibox", color:T.am, label:"Pourquoi Fibonacci fonctionne",
           text:"Pesavento : pas de magie mathématique — de la psychologie collective. Des millions de traders regardent les mêmes niveaux, créant des self-fulfilling prophecies. Un retracement à 61.8% est un 'niveau d'attention maximale' : les acheteurs disciplinés placent leurs ordres là, créant mécaniquement le rebond. Les niveaux existent parce qu'assez de gens croient en eux — et cette croyance se renforce à chaque retournement confirmé."},
          {type:"ibox", color:T.bl, label:"Gann : le temps, dimension oubliée",
           text:"Gann (Reddy ch. 2) : 'the more time between the top and bottom, the more important it is.' Un mouvement qui dure longtemps laisse une empreinte temporelle proportionnelle. Ses 'carrés de 9' et cycles naturels (90, 180, 270, 360 jours) semblent ésotériques mais reflètent une observation empirique sur la répétition des patterns dans le temps. Hima Reddy (2012) modernise et simplifie l'accès à ces outils."},
        ],
        qcm:[
          {q:"NVDA passe de 160€ (X) à 220€ (A = top). Il corrige à 183€ (B = 61.8% de XA). Remonte à 202€ (C = 38.2% de AB). Redescend. Point D = 78.6% de XA. Où est la zone PRZ et quel pattern ?",
           choices:[
            {l:"PRZ à 172.8€ (78.6% de retracement de XA depuis 220€). Pattern Gartley. Zone d'achat avec stop sous 160€.", c:true,
             f:"Exact. Calcul : XA = 220 − 160 = 60€. 78.6% de 60€ = 47.16€. PRZ = 220 − 47.16 = 172.84€. C'est le Gartley de Pesavento (ch. 5) avec B=61.8% ✓ et D=78.6% ✓. Stop sous X (160€). Objectif minimum : retour vers le point A (220€). Pesavento : 'le Gartley est le pattern le plus fiable statistiquement.'"},
            {l:"PRZ à 183€ (point B = 61.8% de XA). Pattern Bat.", c:false,
             f:"Le point B à 61.8% correspond au Gartley, pas au Bat. Le Bat a B = 38.2-50% de XA. Et le PRZ n'est pas au point B — c'est le point D (la fin du pattern) qui définit la zone d'entrée."},
            {l:"Les patterns Fibonacci sont subjectifs et peu fiables — il faut d'autres confirmations", c:false,
             f:"Pesavento serait en désaccord partiel : les patterns sont subjectifs dans leur identification (skill requis) mais objectifs dans leurs ratios. Le Gartley avec B=61.8% et D=78.6% est précis. Il recommande effectivement des confirmations (volume, bougies de retournement sur la PRZ) mais pas de rejeter les patterns."},
           ]
          },
        ]
      },
    ]
  }
};

// ══════════════════════════════════════════════════════
// CONTEXTS
// ══════════════════════════════════════════════════════
const ScoreCtx = createContext(null);
function ScoreProv({ children }) {
  const [scores, setScores] = useState({});
  const register = useCallback((id, ok) =>
    setScores(p => p[id] !== undefined ? p : { ...p, [id]: ok }), []);
  return (
    <ScoreCtx.Provider value={{ scores, register }}>
      {children}
    </ScoreCtx.Provider>
  );
}

// ══════════════════════════════════════════════════════
// QCM COMPONENT
// ══════════════════════════════════════════════════════
function QCM({ qid, q, choices }) {
  const { scores, register } = useContext(ScoreCtx);
  const [sel, setSel] = useState(null);
  const [fb, setFb] = useState(null);
  const done = scores[qid] !== undefined;
  const ok = scores[qid] === true;
  const BC = done ? (ok ? T.gr : T.re) : T.b2;
  const BG = done ? (ok ? T.gbg : T.rbg) : "#12121a";
  return (
    <div style={{ border:`1px solid ${BC}`, background:BG, marginBottom:"1rem",
      position:"relative", overflow:"hidden", transition:"border-color .3s,background .3s" }}>
      <div style={{ position:"absolute", left:0, top:0, bottom:0, width:3,
        background: done ? (ok ? T.gb : T.rb) : T.gd }}/>
      <div style={{ display:"flex", alignItems:"center", gap:8, padding:".55rem 1rem",
        borderBottom:`1px solid ${T.bo}` }}>
        <span style={{ fontFamily:"monospace", fontSize:9, letterSpacing:".14em",
          textTransform:"uppercase", padding:".1rem .4rem",
          border:`1px solid ${T.bb}`, color:T.bb, background:T.blbg }}>QCM</span>
        <span style={{ fontSize:9, color:T.di, fontFamily:"monospace", flex:1 }}>{qid}</span>
        {done && <span style={{ color:ok?T.gb:T.rb, fontSize:13 }}>{ok?"✓ Correct":"✗ Incorrect"}</span>}
      </div>
      <div style={{ padding:".75rem 1rem .5rem", fontSize:12, color:T.tx, lineHeight:1.65 }}>{q}</div>
      <div style={{ display:"flex", flexDirection:"column", gap:4, padding:"0 1rem .75rem" }}>
        {choices.map((ch, i) => {
          let bc=T.bo, bg=T.bg, co=T.di;
          if (done) {
            if (ch.c) { bc=T.gr; bg=T.gbg; co=T.gb; }
            else if (sel===i) { bc=T.re; bg=T.rbg; co=T.rb; }
          }
          return (
            <div key={i} onClick={() => { if(done)return; setSel(i); register(qid,ch.c); setFb(ch.f); }}
              style={{ display:"flex", alignItems:"flex-start", gap:10, padding:".4rem .7rem",
                border:`1px solid ${bc}`, background:bg, color:co,
                cursor:done?"default":"pointer", fontSize:11, lineHeight:1.55, userSelect:"none" }}>
              <span style={{ fontFamily:"monospace", fontSize:9, border:`1px solid ${bc}`,
                padding:".04rem .28rem", flexShrink:0, marginTop:2, minWidth:18, textAlign:"center" }}>
                {["A","B","C"][i]}
              </span>
              {ch.l}
            </div>
          );
        })}
      </div>
      {fb && done && (
        <div style={{ padding:".55rem 1rem", borderTop:`1px solid ${ok?T.gr:T.re}`,
          background:ok?T.gbg:T.rbg, color:ok?T.gb:T.rb, fontSize:11, lineHeight:1.6 }}>
          <span style={{ fontFamily:"monospace", fontSize:9, display:"block",
            marginBottom:3, opacity:.7, textTransform:"uppercase" }}>
            {ok?"✓ Correct — Explication":"✗ Incorrect — Explication"}
          </span>
          {fb}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
// CHAPTER RENDERER
// ══════════════════════════════════════════════════════
// LiveCaseStudy — "Cas pratique dynamique" (doc archi générique §4/§5) :
// contrairement aux QCM ci-dessus (théorie + scénarios d'école statiques),
// ce bloc lit les vraies valeurs du titre couramment analysé (prop `d`,
// StockDashboard / timing_marche_summary) et génère une question + une
// correction 100% dynamiques. Aucune valeur numérique ni nom de société
// codés en dur ici.
function LiveCaseStudy({ pillar, d, symbol }) {
  const [reveal, setReveal] = useState(false);
  const f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };

  const PILLAR_META = {
    structure: {
      question: `Sur la base des données réelles de ${symbol}, la structure de tendance est-elle haussière ?`,
      build: () => {
        const price = f(d?.timing_last_price ?? d?.price, null);
        const s20   = f(d?.timing_sma20 ?? d?.sma20, null);
        const s50   = f(d?.timing_sma50 ?? d?.sma50, null);
        const s200  = f(d?.timing_sma200 ?? d?.sma200, null);
        const r2    = f(d?.r_squared, null);
        const facts = [
          price != null && s200 != null ? `Prix ${price.toFixed(2)} ${price > s200 ? ">" : "<"} SMA200 ${s200.toFixed(2)}` : null,
          s20 != null && s50 != null && s200 != null ? `Alignement SMA20/50/200 : ${s20.toFixed(2)} / ${s50.toFixed(2)} / ${s200.toFixed(2)}` : null,
          r2 != null ? `R² de la régression : ${r2.toFixed(2)}` : null,
        ].filter(Boolean);
        const bullish = price != null && s200 != null && price > s200 && s20 > s50 && s50 > s200;
        return { facts, ok: bullish ? "good" : "neutral",
          verdict: bullish
            ? "Structure haussière confirmée : prix au-dessus des 3 SMAs, alignement croissant."
            : "Structure non pleinement haussière : alignement des SMAs ou position du prix à surveiller." };
      },
    },
    momentum: {
      question: `Le momentum de ${symbol} accélère-t-il ou s'essouffle-t-il, sur les chiffres réels ?`,
      build: () => {
        const t12 = f(d?.tsmom_12m, null), t3 = f(d?.tsmom_3m, null), fip = f(d?.fip_score, null);
        const facts = [
          t12 != null ? `TSMOM 12M : ${t12 >= 0 ? "+" : ""}${t12.toFixed(1)}%` : null,
          t3  != null ? `TSMOM 3M : ${t3 >= 0 ? "+" : ""}${t3.toFixed(1)}%` : null,
          fip != null ? `FIP Score : ${fip.toFixed(3)}` : null,
        ].filter(Boolean);
        const accel = t12 != null && t3 != null && t12 > 0 && t3 > 0;
        const essouf = t12 != null && t3 != null && t12 > 0 && t3 < 0;
        return { facts, ok: accel ? "good" : essouf ? "neutral" : "bad",
          verdict: accel ? "Momentum en accélération : 12M et 3M positifs et convergents."
                 : essouf ? "Essoufflement : 12M positif mais 3M négatif — divergence court terme."
                 : "Momentum dégradé sur les deux horizons." };
      },
    },
    volume: {
      question: `Les volumes de ${symbol} confirment-ils la tendance en cours ?`,
      build: () => {
        const vr = f(d?.volume_ratio_20d, null);
        const wyk = d?.wyckoff_context ?? null;
        const facts = [
          vr  != null ? `Ratio volume 20J : ${vr.toFixed(2)}×` : null,
          wyk != null ? `Phase Wyckoff : ${wyk}` : null,
        ].filter(Boolean);
        const isAccum = wyk && (wyk.includes("Accumulation") || wyk.includes("Markup"));
        const isDistrib = wyk && (wyk.includes("Distribution") || wyk.includes("Markdown"));
        return { facts, ok: isAccum ? "good" : isDistrib ? "bad" : "neutral",
          verdict: isAccum ? "Volumes cohérents avec une phase d'accumulation/markup."
                 : isDistrib ? "Volumes cohérents avec une phase de distribution/markdown — prudence."
                 : "Signal volume neutre ou ambigu à ce stade." };
      },
    },
    timing: {
      question: `Le RSI et le Z-Score de ${symbol} indiquent-ils un bon point d'entrée aujourd'hui ?`,
      build: () => {
        const rsi = f(d?.rsi_14, null), z = f(d?.z_score, null);
        const facts = [
          rsi != null ? `RSI 14 : ${rsi.toFixed(1)}` : null,
          z   != null ? `Z-Score : ${z >= 0 ? "+" : ""}${z.toFixed(2)}σ` : null,
        ].filter(Boolean);
        const good = rsi != null && rsi >= 40 && rsi <= 60 && z != null && Math.abs(z) <= 1;
        const bad  = rsi != null && rsi > 70 && z != null && z > 2;
        return { facts, ok: good ? "good" : bad ? "bad" : "neutral",
          verdict: good ? "Confluence favorable pour une entrée." :
                   bad  ? "Double sur-extension (RSI + Z-Score) — timing défavorable." :
                          "Timing intermédiaire — ni confluence idéale, ni sur-extension nette." };
      },
    },
  };

  const meta = PILLAR_META[pillar];
  if (!meta) return null;
  const { facts, ok, verdict } = meta.build();
  const col = getStatusColor(ok);

  return (
    <div style={{ marginTop:"1.5rem", padding:".9rem 1rem", border:`1px solid ${col}44`,
      background:`${col}0d` }}>
      <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase",
        letterSpacing:".18em", color:col, marginBottom:8 }}>
        Cas pratique en direct — {symbol}
      </div>
      <p style={{ fontSize:12.5, color:T.tx, lineHeight:1.65, marginBottom:8, fontStyle:"italic" }}>
        {meta.question}
      </p>
      {facts.length > 0 && (
        <ul style={{ margin:"0 0 10px", paddingLeft:"1.1rem", fontSize:11.5, color:T.di, lineHeight:1.7 }}>
          {facts.map((fact, i) => <li key={i}>{fact}</li>)}
        </ul>
      )}
      {!reveal ? (
        <button onClick={() => setReveal(true)}
          style={{ padding:".4rem .9rem", border:`1px solid ${col}66`, background:"transparent",
            color:col, fontSize:11, fontWeight:700, cursor:"pointer" }}>
          Voir la lecture Invelps →
        </button>
      ) : (
        <div style={{ fontSize:12, color:col, fontWeight:600, lineHeight:1.6,
          borderTop:`1px solid ${col}33`, paddingTop:8 }}>
          {verdict}
        </div>
      )}
    </div>
  );
}

function Chapter({ module: m, levelColor, D: Dprop, liveData }) {
  const col = levelColor;
  // Use passed theme tokens, fall back to global T for backward compat
  const D = Dprop || T;
  // Symbole réellement analysé (StockDashboard) — fallback NVDA si absent
  const symbol = liveData?.symbol || liveData?.company_name || "NVDA";

  function renderContent(block, bi) {
    if (block.type === "p") return (
      <p key={bi} style={{ fontSize:13, color:T.di, lineHeight:1.75, marginBottom:".75rem" }}>{dyn(block.text, symbol)}</p>
    );
    if (block.type === "concepts") return (
      <div key={bi} style={{ margin:".8rem 0 1.2rem" }}>
        <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase",
          letterSpacing:".18em", color:T.mu, marginBottom:6 }}>Concepts clés</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr", gap:1,
          background:T.bo, border:`1px solid ${T.bo}` }}>
          {block.items.map((c,i) => (
            <div key={i} style={{ background:T.bg, padding:".75rem .9rem" }}>
              <div style={{ display:"flex", alignItems:"baseline", justifyContent:"space-between", gap:8, marginBottom:3 }}>
                <span style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:"1rem", color:T.go }}>{c.term}</span>
                {c.src && <span style={{ fontFamily:"monospace", fontSize:8, color:T.mu, flexShrink:0 }}>📖 {c.src}</span>}
              </div>
              <span style={{ fontSize:11, color:T.di, lineHeight:1.6 }}>{dyn(c.def, symbol)}</span>
            </div>
          ))}
        </div>
      </div>
    );
    if (block.type === "ibox") return (
      <div key={bi} style={{ padding:".85rem 1rem", margin:".8rem 0",
        borderLeft:`3px solid ${block.color}`,
        background:block.color+"18", fontSize:11.5, lineHeight:1.65, color:T.di }}>
        <span style={{ fontFamily:"monospace", fontSize:9, letterSpacing:".18em",
          textTransform:"uppercase", opacity:.7, display:"block", marginBottom:5 }}>{block.label}</span>
        {dyn(block.text, symbol)}
      </div>
    );
    return null;
  }

  return (
    <div style={{ background:T.bg, color:T.tx, fontFamily:"Georgia,serif" }}>
      <div style={{ maxWidth:720, margin:"0 auto" }}>

        {/* Masthead */}
        <div style={{ padding:"clamp(1.5rem,4vw,2.5rem) 0 1.8rem",
          borderBottom:`1px solid ${T.bo}` }}>
          <div style={{ fontFamily:"monospace", fontSize:9, letterSpacing:".22em",
            textTransform:"uppercase", color:T.go, marginBottom:".8rem",
            display:"flex", alignItems:"center", gap:8 }}>
            <span style={{ width:14, height:1, background:T.go, display:"inline-block" }}/>
            {m.sub}
          </div>
          <h1 style={{ fontFamily:"'Cormorant Garamond',serif",
            fontSize:"clamp(1.4rem,4vw,2.2rem)", fontWeight:300,
            lineHeight:1.2, letterSpacing:"-.02em", margin:"0 0 .8rem" }}>
            {m.icon} {m.title}
          </h1>
          <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
            {m.sources.map(s => (
              <span key={s} style={{ fontFamily:"monospace", fontSize:9, padding:".15rem .55rem",
                border:`1px solid ${T.b2}`, color:T.di }}>📖 {s}</span>
            ))}
          </div>
        </div>

        {/* Citation */}
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontStyle:"italic",
          fontSize:"1.05rem", color:T.gp, margin:"1.4rem 0 1rem",
          paddingLeft:".9rem", borderLeft:`2px solid ${T.gd}`, lineHeight:1.6 }}>
          {m.quote}
        </div>

        {/* Content blocks */}
        {m.content.map(renderContent)}

        {/* Formula */}
        {m.formula && (
          <div style={{ margin:".8rem 0 1.2rem", padding:".75rem 1rem",
            background:T.s2, border:`1px solid ${T.b2}`,
            display:"flex", alignItems:"flex-start", gap:12 }}>
            <span style={{ fontFamily:"monospace", fontSize:9, color:T.mu,
              textTransform:"uppercase", letterSpacing:".1em", flexShrink:0, paddingTop:3 }}>Formule</span>
            <span style={{ fontFamily:"monospace", fontSize:12, color:T.gp,
              lineHeight:1.55, whiteSpace:"pre-line" }}>{m.formula}</span>
          </div>
        )}

        {/* QCMs */}
        {m.qcm && m.qcm.length > 0 && (
          <div style={{ marginTop:"2rem" }}>
            <div style={{ fontFamily:"monospace", fontSize:9, textTransform:"uppercase",
              letterSpacing:".18em", color:T.mu, marginBottom:"1rem",
              paddingTop:"1.5rem", borderTop:`1px solid ${T.bo}` }}>
              Exercices — appliqués sur {symbol}
            </div>
            {m.qcm.map((q,i) => (
              <QCM key={i} qid={`${m.id}-q${i+1}`} q={dyn(q.q, symbol)}
                choices={q.choices.map(ch => ({ ...ch, l:dyn(ch.l, symbol), f:dyn(ch.f, symbol) }))}/>
            ))}
          </div>
        )}

        {/* Cas pratique dynamique — construit sur les données RÉELLES du titre
            en cours d'analyse (doc archi générique §4/§5 : "Cas pratique
            dynamique" fait partie du contenu DYNAMIQUE de la vue COURS,
            à la différence des QCM ci-dessus qui restent des cas d'école
            statiques). Uniquement affiché pour les 4 piliers diagnostiqués. */}
        {liveData && ["structure","momentum","volume","timing"].includes(m.id) && (
          <LiveCaseStudy pillar={m.id} d={liveData} symbol={symbol}/>
        )}

        {/* Module score */}
        <ModuleScore moduleId={m.id} total={m.qcm?.length||0} D={D}/>
      </div>
    </div>
  );
}

function ModuleScore({ moduleId, total, D: Dprop }) {
  const D = Dprop || T;
  const { scores } = useContext(ScoreCtx);
  if (!total) return null;
  const keys = Array.from({length:total}, (_,i) => `${moduleId}-q${i+1}`);
  const done = keys.filter(k => scores[k] !== undefined);
  if (done.length < total) return null;
  const ok  = done.filter(k => scores[k]).length;
  const pct = Math.round(ok/total*100);
  return (
    <div style={{ marginTop:"2rem", padding:"1.2rem 1.5rem",
      border:`1px solid ${T.bo}`, background:T.su }}>
      <div style={{ display:"flex", alignItems:"baseline", gap:14 }}>
        <span style={{ fontFamily:"'Cormorant Garamond',serif",
          fontSize:"2.4rem", color:T.go, lineHeight:1 }}>{ok}/{total}</span>
        <div>
          <div style={{ fontFamily:"monospace", fontSize:9, color:T.mu,
            textTransform:"uppercase", letterSpacing:".12em" }}>Score</div>
          <div style={{ height:3, background:T.b2, width:140, marginTop:5 }}>
            <div style={{ height:"100%", background:T.go,
              width:`${pct}%`, transition:"width 1s ease" }}/>
          </div>
        </div>
      </div>
      <div style={{ fontSize:11, color:T.di, marginTop:8 }}>
        {pct===100?"Maîtrise complète ✓":pct>=67?"Bonne compréhension. Relisez les points manqués.":"Relisez ce module avant de continuer."}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
// EDU MODAL — adaptive theme + mobile responsive
// ══════════════════════════════════════════════════════
function EduModal({ anchor, onClose, isDark, d }) {
  const symbol = d?.symbol || d?.company_name || "NVDA";
  const [levelId, setLevelId] = useState(() => {
    for (const [lk, lv] of Object.entries(LEVELS))
      for (const m of lv.modules)
        if (m.anchor === anchor) return lk;
    return "novice";
  });
  const [moduleId, setModuleId] = useState(() => {
    for (const lv of Object.values(LEVELS))
      for (const m of lv.modules)
        if (m.anchor === anchor) return m.id;
    return LEVELS.novice.modules[0].id;
  });
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  // Adaptive tokens — match the main page theme
  const D = isDark ? {
    bg:"#0b1220", card:"rgba(13,20,38,0.97)", border:"rgba(111,138,91,.18)",
    ts:"#e8e4da", tx:"#cdc9be", mu:"#7a8172", sep:"rgba(111,138,91,.12)",
    sur:"rgba(111,138,91,.05)", sub:"rgba(11,18,32,0.97)",
    // edu-specific
    bo:"#121f32", b2:"#1a2840", go:C.amber, gd:"#9a7c3a", gp:"#e8d5a0",
    gr:"#3a7a50", gb:C.green, gbg:"rgba(111,138,91,.08)",
    re:"#8a3a3a", rb:C.red, rbg:"rgba(198,82,63,.08)",
    bl:"#3a5a8a", bb:"#6a9aca", blbg:"rgba(122,143,196,.08)",
    di:"#7a8172", s2:"rgba(11,18,32,0.6)",
  } : {
    bg:"#f6f5f1", card:"rgba(255,255,255,0.99)", border:"rgba(30,40,34,.09)",
    ts:"#1f2a24", tx:"#2d3b34", mu:"#6f7b74", sep:"rgba(30,40,34,.07)",
    sur:"rgba(0,0,0,.018)", sub:"rgba(248,246,241,0.99)",
    // edu-specific
    bo:"#d4cfc4", b2:"#c4bfb4", go:C.amber, gd:"#8a6a2a", gp:"#4a3510",
    gr:"#2a6040", gb:C.green, gbg:"rgba(111,138,91,.08)",
    re:"#7a2a2a", rb:C.red, rbg:"rgba(198,82,63,.08)",
    bl:"#2a4a8a", bb:"#3a6aaa", blbg:"rgba(122,143,196,.08)",
    di:"#6f7b74", s2:"rgba(248,246,241,0.5)",
  };

  const level = LEVELS[levelId];
  const activeM = level.modules.find(m => m.id === moduleId) || level.modules[0];

  function switchLevel(lk) {
    setLevelId(lk);
    setModuleId(LEVELS[lk].modules[0].id);
    setSidebarOpen(false);
  }
  function selectModule(id) {
    setModuleId(id);
    setSidebarOpen(false);
  }

  const scrollbarStyle = `
    .edu-sb::-webkit-scrollbar{width:4px}
    .edu-sb::-webkit-scrollbar-thumb{background:${D.gd};border-radius:2px}
    @keyframes eduSlide{from{opacity:0;transform:translateX(-8px)}to{opacity:1;transform:translateX(0)}}
  `;

  return (
    <ScoreProv>
      <div style={{ position:"fixed", inset:0, zIndex:3000,
        background:"rgba(0,0,0,.6)", display:"flex", flexDirection:"column" }}
        onClick={onClose}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=DM+Mono:wght@300;400&display=swap');
          ${scrollbarStyle}
          .edu-modal-body{display:flex;flex:1;overflow:hidden;}
          .edu-sidebar{width:200px;flex-shrink:0;overflow-y:auto;padding:12px 0;border-right:1px solid ${D.bo};}
          @media(max-width:600px){
            .edu-sidebar{
              position:absolute;top:48px;left:0;bottom:0;z-index:10;
              width:240px;
              transform:translateX(-100%);
              transition:transform .22s ease;
              box-shadow:4px 0 20px rgba(0,0,0,.15);
            }
            .edu-sidebar.open{transform:translateX(0);}
          }
          .edu-module-tabs{display:none;}
          @media(max-width:600px){.edu-module-tabs{display:flex;}}
        `}</style>

        <div style={{
          display:"flex", flexDirection:"column",
          background:D.bg, flex:1, maxHeight:"100vh",
          overflow:"hidden", position:"relative",
        }} onClick={e=>e.stopPropagation()}>

          {/* ── HEADER */}
          <div style={{
            display:"flex", alignItems:"center", gap:8, padding:"9px 14px",
            background:D.sub, borderBottom:`1px solid ${D.bo}`,
            flexShrink:0, flexWrap:"wrap",
          }}>
            {/* Mobile hamburger */}
            <button
              onClick={() => setSidebarOpen(v=>!v)}
              style={{
                display:"none", alignItems:"center", justifyContent:"center",
                width:28, height:28, borderRadius:4, flexShrink:0,
                background:"transparent", border:`1px solid ${D.b2}`,
                color:D.mu, cursor:"pointer", fontSize:14,
              }}
              className="edu-ham">
              ☰
            </button>

            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke={EDU_COL} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
              <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
            </svg>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:9,
              textTransform:"uppercase", letterSpacing:".16em", color:EDU_COL }}>
              Cours
            </span>

            {/* Level tabs */}
            <div style={{ display:"flex", gap:3 }}>
              {Object.values(LEVELS).map(lv => (
                <button key={lv.id} onClick={() => switchLevel(lv.id)}
                  style={{
                    display:"flex", alignItems:"center", gap:4,
                    padding:"4px 9px", borderRadius:4,
                    border:`1px solid ${levelId===lv.id ? lv.color+"80" : D.b2}`,
                    background: levelId===lv.id ? lv.color+"18" : "transparent",
                    color: levelId===lv.id ? lv.color : D.di,
                    cursor:"pointer", fontFamily:"ui-monospace,monospace",
                    fontSize:9, textTransform:"uppercase", letterSpacing:".06em",
                  }}>
                  <span>{lv.icon}</span>
                  <span style={{ display:"inline" }}>{lv.label}</span>
                </button>
              ))}
            </div>

            <span style={{ marginLeft:"auto", fontFamily:"ui-monospace,monospace",
              fontSize:8, color:D.mu }}>{symbol}</span>

            <button onClick={onClose}
              style={{
                width:26, height:26, borderRadius:4, display:"flex",
                alignItems:"center", justifyContent:"center",
                background:"transparent", border:`1px solid ${D.b2}`,
                color:D.mu, cursor:"pointer", fontSize:15, flexShrink:0,
              }}>×</button>
          </div>

          {/* Mobile module tabs strip */}
          <div className="edu-module-tabs" style={{
            flexShrink:0, background:D.sub, borderBottom:`1px solid ${D.bo}`,
            overflowX:"auto", padding:"0 4px",
            display:"none", // overridden by media query
          }}>
            {level.modules.map(m => (
              <button key={m.id} onClick={() => selectModule(m.id)}
                style={{
                  display:"inline-flex", alignItems:"center", gap:5,
                  padding:"8px 12px", whiteSpace:"nowrap", flexShrink:0,
                  border:"none", background:"transparent", cursor:"pointer",
                  borderBottom:`2px solid ${activeM.id===m.id ? level.color : "transparent"}`,
                  color: activeM.id===m.id ? level.color : D.mu,
                  fontFamily:"ui-monospace,monospace", fontSize:9,
                  textTransform:"uppercase", letterSpacing:".06em",
                }}>
                {m.icon} {m.title}
              </button>
            ))}
          </div>

          {/* ── BODY */}
          <div className="edu-modal-body">

            {/* Sidebar (desktop always visible, mobile slide-in) */}
            <div
              className={`edu-sidebar edu-sb${sidebarOpen ? " open" : ""}`}
              style={{ background:D.sub }}>
              <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                textTransform:"uppercase", letterSpacing:".15em",
                color:D.mu, padding:"0 14px 6px" }}>
                {level.icon} {level.label}
              </div>
              <div style={{ fontSize:10, color:D.di, padding:"0 14px 12px",
                borderBottom:`1px solid ${D.bo}`, lineHeight:1.5 }}>
                {dyn(level.desc, symbol)}
              </div>
              <div style={{ paddingTop:8 }}>
                {level.modules.map(m => (
                  <button key={m.id} onClick={() => selectModule(m.id)}
                    style={{
                      display:"flex", alignItems:"flex-start", gap:8,
                      width:"100%", padding:"9px 14px", textAlign:"left",
                      cursor:"pointer", border:"none",
                      borderLeft:`2px solid ${activeM.id===m.id ? level.color : "transparent"}`,
                      background: activeM.id===m.id ? level.color+"14" : "transparent",
                      transition:"background .12s",
                    }}>
                    <span style={{ fontSize:13, flexShrink:0, marginTop:1 }}>{m.icon}</span>
                    <div>
                      <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                        color:D.mu, textTransform:"uppercase",
                        letterSpacing:".06em", marginBottom:2 }}>{m.sub}</div>
                      <div style={{ fontSize:11, lineHeight:1.3,
                        color: activeM.id===m.id ? level.color : D.ts }}>
                        {m.title}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sidebar backdrop on mobile */}
            {sidebarOpen && (
              <div onClick={() => setSidebarOpen(false)}
                style={{
                  position:"absolute", inset:0, zIndex:9,
                  background:"rgba(0,0,0,.4)",
                  display:"none", // overridden by media query
                }}
                className="edu-backdrop"/>
            )}

            {/* Content */}
            <div className="edu-sb"
              style={{ flex:1, minWidth:0, overflowY:"auto",
                padding:"0 clamp(.8rem,3vw,1.6rem) 4rem",
                background:D.bg }}>
              <Chapter key={activeM.id} module={activeM} levelColor={level.color} D={D} liveData={d}/>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile-specific styles injected */}
      <style>{`
        @media(max-width:600px){
          .edu-ham{display:flex !important;}
          .edu-module-tabs{display:flex !important; overflow-x:auto;}
          .edu-sidebar.open{transform:translateX(0) !important;}
          .edu-backdrop{display:block !important;}
        }
      `}</style>
    </ScoreProv>
  );
}

// ══════════════════════════════════════════════════════
// GRADUATION CAP BUTTON
// ══════════════════════════════════════════════════════
function EduCap({ onOpen, anchor }) {
  return (
    <Tip title="Cours d'analyse graphique"
      body="3 niveaux (Novice · Intermédiaire · Avancé) — Sources : Murphy, Edwards & Magee, Wyckoff, Gray & Vogel, Covel, Pesavento, Gann. Théorie + QCMs sur données NVDA réelles."
      src="8 ouvrages de référence · 7 modules · 10 QCMs">
      <button onClick={() => onOpen(anchor||"n-tendance")}
        style={{ display:"inline-flex", alignItems:"center", gap:6,
          padding:"5px 12px", borderRadius:8, cursor:"pointer",
          border:`1px solid ${C.edu}66`, background:`${C.edu}15`,
          color:C.edu, fontSize:10, fontWeight:700,
          fontFamily:"ui-monospace,monospace",
          transition:"all .15s" }}
        onMouseEnter={e=>{e.currentTarget.style.background=`${C.edu}28`;}}
        onMouseLeave={e=>{e.currentTarget.style.background=`${C.edu}15`;}}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
          <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
        </svg>
        Cours
      </button>
    </Tip>
  );
}


function applyTheme(isDark) {
  const r = document.documentElement;
  if (isDark) {
    // ── MODE NUIT — charte Invelps dark ──
    r.style.setProperty("--bg",       "#0b1220");
    r.style.setProperty("--card-bg",  "rgba(13,20,38,0.97)");
    r.style.setProperty("--card-bdr", "rgba(111,138,91,.18)");
    r.style.setProperty("--ts",       "#e8e4da");
    r.style.setProperty("--tx",       "#cdc9be");
    r.style.setProperty("--mu",       "#7a8172");
    r.style.setProperty("--sep",      "rgba(111,138,91,.12)");
    r.style.setProperty("--sur",      "rgba(111,138,91,.05)");
    r.style.setProperty("--gl",       "rgba(111,138,91,.07)");
    // extra dark tokens
    r.style.setProperty("--surface1", "#0f1a2e");
    r.style.setProperty("--surface2", "#131f30");
    r.style.setProperty("--rim",      "rgba(111,138,91,.22)");
  } else {
    // ── MODE JOUR — charte Invelps light ──
    r.style.setProperty("--bg",       MARKET_LIGHT.appBg);
    r.style.setProperty("--card-bg",  "rgba(255,255,255,0.98)");
    r.style.setProperty("--card-bdr", "rgba(30,40,34,.10)");
    r.style.setProperty("--ts",       "#1f2a24");
    r.style.setProperty("--tx",       "#2d3b34");
    r.style.setProperty("--mu",       "#6f7b74");
    r.style.setProperty("--sep",      "rgba(30,40,34,.07)");
    r.style.setProperty("--sur",      "rgba(0,0,0,.016)");
    r.style.setProperty("--gl",       "rgba(30,40,34,.055)");
    r.style.setProperty("--surface1", "#f0efe9");
    r.style.setProperty("--surface2", "#eae9e3");
    r.style.setProperty("--rim",      "rgba(30,40,34,.12)");
  }
}

// ══════════════════════════════════════════════════════════════════
// 2. DATA — Timing_marche_chart.csv (NVDA ref_year=2024)
//    Sorted ascending. SMA20/50/200 come directly from SQL.
// ══════════════════════════════════════════════════════════════════
const RAW_DAILY = [
  {d:"2025-11-06",o:196.42,h:197.62,l:186.38,c:188.08,v:223029800,s20:190.1335,s50:183.273,s200:149.3631,rsi14:47.8541},
  {d:"2025-11-07",o:184.9,h:188.32,l:178.91,c:188.15,v:264942300,s20:190.383,s50:183.4326,s200:149.5677,rsi14:47.9244},
  {d:"2025-11-10",o:195.11,h:199.94,l:193.79,c:199.05,v:198897134,s20:190.9195,s50:183.93,s200:149.8499,rsi14:57.5291},
  {d:"2025-11-11",o:195.16,h:195.42,l:191.3,c:193.16,v:176483327,s20:191.576,s50:184.3776,s200:150.2236,rsi14:51.953},
  {d:"2025-11-12",o:195.72,h:195.89,l:191.13,c:193.8,v:154935300,s20:192.2745,s50:184.8412,s200:150.5477,rsi14:52.4918},
  {d:"2025-11-13",o:191.05,h:191.44,l:183.85,c:186.86,v:207423100,s20:192.527,s50:185.1452,s200:150.8635,rsi14:46.4133},
  {d:"2025-11-14",o:182.86,h:191.01,l:180.58,c:190.17,v:186591900,s20:192.8745,s50:185.6082,s200:151.191,rsi14:49.4216},
  {d:"2025-11-17",o:185.97,h:189.0,l:184.32,c:186.6,v:173628900,s20:193.0725,s50:185.974,s200:151.5237,rsi14:46.3962},
  {d:"2025-11-18",o:183.38,h:184.8,l:179.65,c:181.36,v:213598900,s20:193.0825,s50:186.186,s200:151.8472,rsi14:42.3029},
  {d:"2025-11-19",o:184.79,h:187.86,l:182.83,c:186.52,v:247246436,s20:193.3945,s50:186.3698,s200:152.1866,rsi14:47.2393},
  {d:"2025-11-20",o:195.95,h:196.0,l:179.85,c:180.64,v:343504849,s20:193.3185,s50:186.4392,s200:152.4656,rsi14:42.7507},
  {d:"2025-11-21",o:181.24,h:184.56,l:172.93,c:178.88,v:346926200,s20:192.9495,s50:186.4604,s200:152.7166,rsi14:41.4802},
  {d:"2025-11-24",o:179.49,h:183.5,l:176.48,c:182.55,v:256618335,s20:192.5025,s50:186.5564,s200:152.9802,rsi14:45.1413},
  {d:"2025-11-25",o:174.91,h:178.16,l:169.55,c:177.82,v:320600300,s20:191.342,s50:186.6152,s200:153.2014,rsi14:41.5347},
  {d:"2025-11-26",o:181.63,h:182.91,l:178.24,c:180.26,v:183852010,s20:190.003,s50:186.8146,s200:153.4387,rsi14:44.0194},
  {d:"2025-11-28",o:179.01,h:179.29,l:176.5,c:177.0,v:121332800,s20:188.7085,s50:186.8298,s200:153.668,rsi14:41.4828},
  {d:"2025-12-01",o:174.76,h:180.3,l:173.68,c:179.92,v:188131000,s20:187.58,s50:186.8948,s200:153.8912,rsi14:44.5642},
  {d:"2025-12-02",o:181.76,h:185.66,l:180.0,c:181.46,v:182632230,s20:186.309,s50:186.8518,s200:154.1042,rsi14:46.174},
  {d:"2025-12-03",o:181.08,h:182.45,l:179.11,c:179.59,v:165138023,s20:185.354,s50:186.875,s200:154.3051,rsi14:44.4847},
  {d:"2025-12-04",o:181.62,h:184.52,l:179.96,c:183.38,v:167364900,s20:184.7625,s50:187.0032,s200:154.5259,rsi14:48.59},
  {d:"2025-12-05",o:183.89,h:184.66,l:180.91,c:182.41,v:143971100,s20:184.479,s50:187.0976,s200:154.7374,rsi14:47.6194},
  {d:"2025-12-08",o:182.64,h:188.0,l:182.4,c:185.55,v:204378106,s20:184.349,s50:187.2448,s200:154.993,rsi14:51.0295},
  {d:"2025-12-09",o:185.56,h:185.72,l:183.32,c:184.97,v:144719705,s20:183.645,s50:187.3072,s200:155.2664,rsi14:50.3771},
  {d:"2025-12-10",o:184.97,h:185.48,l:182.04,c:183.78,v:162785400,s20:183.176,s50:187.2512,s200:155.5522,rsi14:48.9931},
  {d:"2025-12-11",o:180.28,h:181.32,l:176.62,c:180.93,v:182136641,s20:182.5325,s50:187.125,s200:155.8005,rsi14:45.7514},
  {d:"2025-12-12",o:181.11,h:182.82,l:174.62,c:175.02,v:204274918,s20:181.9405,s50:186.8476,s200:156.0748,rsi14:39.8613},
  {d:"2025-12-15",o:177.94,h:178.42,l:175.03,c:176.29,v:164775636,s20:181.2465,s50:186.621,s200:156.3316,rsi14:41.6012},
  {d:"2025-12-16",o:176.26,h:178.49,l:174.9,c:177.72,v:148588100,s20:180.8025,s50:186.4646,s200:156.6499,rsi14:43.5805},
  {d:"2025-12-17",o:176.1,h:176.13,l:170.31,c:170.94,v:222775500,s20:180.2815,s50:186.1826,s200:156.9247,rsi14:37.1512},
  {d:"2025-12-18",o:174.53,h:176.15,l:171.82,c:174.14,v:176096001,s20:179.6625,s50:185.8832,s200:157.2089,rsi14:41.5352},
  {d:"2025-12-19",o:176.67,h:181.45,l:176.34,c:180.99,v:324925927,s20:179.68,s50:185.6516,s200:157.561,rsi14:49.6342},
  {d:"2025-12-22",o:183.92,h:184.16,l:182.35,c:183.69,v:129064400,s20:179.9205,s50:185.6622,s200:157.916,rsi14:52.4314},
  {d:"2025-12-23",o:182.97,h:189.33,l:182.9,c:189.21,v:174873621,s20:180.2535,s50:185.68,s200:158.3271,rsi14:57.6142},
  {d:"2025-12-24",o:187.94,h:188.91,l:186.59,c:188.61,v:65528545,s20:180.793,s50:185.8516,s200:158.7264,rsi14:56.8886},
  {d:"2025-12-26",o:189.92,h:192.69,l:188.0,c:190.53,v:139740300,s20:181.3065,s50:186.0656,s200:159.1003,rsi14:58.6817},
  {d:"2025-12-29",o:187.71,h:188.76,l:185.91,c:188.22,v:120006103,s20:181.8675,s50:186.1938,s200:159.4635,rsi14:55.6811},
  {d:"2025-12-30",o:188.24,h:188.99,l:186.93,c:187.54,v:97687300,s20:182.2485,s50:186.2802,s200:159.7929,rsi14:54.7928},
  {d:"2025-12-31",o:189.57,h:190.56,l:186.49,c:186.5,v:120100500,s20:182.5005,s50:186.3574,s200:160.1277,rsi14:53.3901},
  {d:"2026-01-02",o:189.84,h:192.93,l:188.26,c:188.85,v:148240500,s20:182.9635,s50:186.5112,s200:160.4949,rsi14:56.1235},
  {d:"2026-01-05",o:191.76,h:193.63,l:186.15,c:188.12,v:183529733,s20:183.2005,s50:186.668,s200:160.8478,rsi14:55.0436},
  {d:"2026-01-06",o:190.52,h:192.17,l:186.82,c:187.24,v:176862604,s20:183.442,s50:186.7696,s200:161.1914,rsi14:53.7022},
  {d:"2026-01-07",o:188.57,h:191.37,l:186.56,c:189.11,v:153543200,s20:183.62,s50:186.8266,s200:161.5485,rsi14:56.1479},
  {d:"2026-01-08",o:189.11,h:189.55,l:183.71,c:185.04,v:172457048,s20:183.6235,s50:186.6976,s200:161.8666,rsi14:49.9618},
  {d:"2026-01-09",o:185.08,h:186.34,l:183.67,c:184.86,v:131327534,s20:183.6775,s50:186.3742,s200:162.1875,rsi14:49.701},
  {d:"2026-01-12",o:183.22,h:187.12,l:183.02,c:184.94,v:137968500,s20:183.878,s50:185.9322,s200:162.5434,rsi14:49.8263},
  {d:"2026-01-13",o:185.0,h:188.11,l:183.4,c:185.81,v:160128939,s20:184.4175,s50:185.5906,s200:162.9152,rsi14:51.2493},
  {d:"2026-01-14",o:184.32,h:184.46,l:180.8,c:183.14,v:159586135,s20:184.76,s50:185.2036,s200:163.2826,rsi14:46.8573},
  {d:"2026-01-15",o:186.5,h:189.7,l:186.33,c:187.05,v:206188642,s20:185.2265,s50:184.807,s200:163.676,rsi14:53.1845},
  {d:"2026-01-16",o:189.08,h:190.44,l:186.08,c:186.23,v:187967200,s20:185.991,s50:184.5578,s200:164.0564,rsi14:51.7918},
  {d:"2026-01-20",o:181.9,h:182.38,l:177.61,c:178.07,v:223345300,s20:186.1875,s50:184.215,s200:164.3946,rsi14:40.4425},
  {d:"2026-01-21",o:179.05,h:185.38,l:178.4,c:183.32,v:200381000,s20:186.304,s50:184.1198,s200:164.8022,rsi14:48.2932},
  {d:"2026-01-22",o:184.75,h:186.17,l:183.93,c:184.84,v:139636626,s20:186.3615,s50:184.0536,s200:165.2549,rsi14:50.3345},
  {d:"2026-01-23",o:187.5,h:189.6,l:186.82,c:187.67,v:142748100,s20:186.2845,s50:183.826,s200:165.705,rsi14:53.9774},
  {d:"2026-01-26",o:187.16,h:189.12,l:185.99,c:186.47,v:124799649,s20:186.1775,s50:183.6922,s200:166.1558,rsi14:52.228},
  {d:"2026-01-27",o:187.24,h:190.0,l:185.7,c:188.52,v:143711600,s20:186.077,s50:183.5866,s200:166.5268,rsi14:54.9161},
  {d:"2026-01-28",o:191.27,h:192.35,l:189.84,c:191.52,v:148552700,s20:186.242,s50:183.6798,s200:166.9466,rsi14:58.5885},
  {d:"2026-01-29",o:191.34,h:193.48,l:186.06,c:192.51,v:171764400,s20:186.4905,s50:183.7266,s200:167.3545,rsi14:59.7535},
  {d:"2026-01-30",o:191.21,h:194.49,l:189.47,c:191.13,v:179489500,s20:186.722,s50:183.8172,s200:167.7566,rsi14:57.3322},
  {d:"2026-02-02",o:187.2,h:190.3,l:184.88,c:185.61,v:165794100,s20:186.56,s50:183.9022,s200:168.1236,rsi14:48.8118},
  {d:"2026-02-03",o:186.24,h:186.27,l:176.23,c:180.34,v:204019600,s20:186.171,s50:183.7786,s200:168.5028,rsi14:42.342},
  {d:"2026-02-04",o:179.46,h:179.58,l:171.91,c:174.19,v:207014116,s20:185.5185,s50:183.6496,s200:168.8664,rsi14:36.296},
  {d:"2026-02-05",o:174.93,h:176.82,l:171.03,c:171.88,v:206312900,s20:184.657,s50:183.5096,s200:169.2412,rsi14:34.314},
  {d:"2026-02-06",o:176.69,h:187.0,l:174.6,c:185.41,v:231346241,s20:184.6755,s50:183.5668,s200:169.6738,rsi14:51.1423},
  {d:"2026-02-09",o:184.26,h:193.66,l:183.95,c:190.04,v:196387400,s20:184.9345,s50:183.8112,s200:170.1104,rsi14:55.3572},
  {d:"2026-02-10",o:191.38,h:192.48,l:188.12,c:188.54,v:136764825,s20:185.1145,s50:183.9768,s200:170.521,rsi14:53.7397},
  {d:"2026-02-11",o:192.45,h:193.26,l:188.77,c:190.05,v:144192700,s20:185.3265,s50:184.2378,s200:170.9162,rsi14:55.16},
  {d:"2026-02-12",o:193.03,h:193.61,l:186.51,c:186.94,v:189932500,s20:185.5165,s50:184.3782,s200:171.3073,rsi14:51.643},
  {d:"2026-02-13",o:187.48,h:187.5,l:181.59,c:182.81,v:161888021,s20:185.3045,s50:184.4052,s200:171.6762,rsi14:47.3274},
  {d:"2026-02-17",o:181.75,h:187.15,l:179.18,c:184.97,v:162276900,s20:185.2415,s50:184.5128,s200:172.0565,rsi14:49.6951},
  {d:"2026-02-18",o:188.75,h:190.37,l:186.76,c:187.98,v:164749125,s20:185.737,s50:184.6048,s200:172.4383,rsi14:52.8742},
  {d:"2026-02-19",o:187.06,h:188.43,l:185.66,c:187.9,v:126554526,s20:185.966,s50:184.7146,s200:172.8053,rsi14:52.7787},
  {d:"2026-02-20",o:186.57,h:190.33,l:185.94,c:189.82,v:178422337,s20:186.215,s50:184.8,s200:173.1853,rsi14:54.8841},
  {d:"2026-02-23",o:191.4,h:193.95,l:189.58,c:191.55,v:171584839,s20:186.409,s50:184.9316,s200:173.5753,rsi14:56.7551},
  {d:"2026-02-24",o:191.49,h:193.77,l:187.4,c:192.85,v:175123602,s20:186.728,s50:185.113,s200:173.9543,rsi14:58.1592},
  {d:"2026-02-25",o:194.45,h:197.63,l:193.79,c:195.56,v:250637102,s20:187.08,s50:185.4056,s200:174.3452,rsi14:61.0019},
  {d:"2026-02-26",o:194.27,h:194.29,l:184.32,c:184.89,v:360807907,s20:186.7485,s50:185.603,s200:174.6865,rsi14:47.3588},
  {d:"2026-02-27",o:181.25,h:182.59,l:176.38,c:177.19,v:311636500,s20:185.9825,s50:185.621,s200:174.9574,rsi14:40.3461},
  {d:"2026-03-02",o:175.01,h:183.46,l:174.64,c:182.48,v:209095331,s20:185.55,s50:185.7162,s200:175.2201,rsi14:46.2362},
  {d:"2026-03-03",o:178.49,h:180.9,l:176.92,c:180.05,v:178099430,s20:185.272,s50:185.8984,s200:175.4437,rsi14:44.083},
  {d:"2026-03-04",o:180.44,h:184.7,l:180.06,c:183.04,v:177731200,s20:185.407,s50:186.0764,s200:175.6848,rsi14:47.3331},
  {d:"2026-03-05",o:181.17,h:184.06,l:177.88,c:183.34,v:198779729,s20:185.8645,s50:186.1234,s200:175.9245,rsi14:47.6618},
  {d:"2026-03-06",o:179.84,h:182.76,l:176.82,c:177.82,v:189021949,s20:186.1615,s50:186.006,s200:176.1357,rsi14:42.4161},
  {d:"2026-03-09",o:176.83,h:182.91,l:175.56,c:182.65,v:177213600,s20:186.0235,s50:185.8748,s200:176.377,rsi14:47.827},
  {d:"2026-03-10",o:182.4,h:186.44,l:182.01,c:184.77,v:179118528,s20:185.76,s50:185.798,s200:176.6419,rsi14:50.0458},
  {d:"2026-03-11",o:185.91,h:187.62,l:184.45,c:186.03,v:145280400,s20:185.6345,s50:185.708,s200:176.9079,rsi14:51.3695},
  {d:"2026-03-12",o:184.05,h:184.94,l:181.75,c:183.14,v:155762700,s20:185.289,s50:185.6064,s200:177.1671,rsi14:48.2137},
  {d:"2026-03-13",o:184.92,h:186.09,l:179.94,c:180.25,v:160988425,s20:184.9545,s50:185.4606,s200:177.3909,rsi14:45.2219},
  {d:"2026-03-16",o:182.97,h:188.88,l:181.41,c:183.22,v:217307400,s20:184.975,s50:185.395,s200:177.6329,rsi14:48.7421},
  {d:"2026-03-17",o:185.06,h:185.4,l:181.68,c:181.93,v:182497800,s20:184.823,s50:185.2566,s200:177.8467,rsi14:47.3197},
  {d:"2026-03-18",o:182.48,h:183.38,l:180.33,c:180.4,v:156683136,s20:184.444,s50:185.1022,s200:178.073,rsi14:45.6193},
  {d:"2026-03-19",o:178.01,h:179.98,l:175.79,c:178.56,v:170968527,s20:183.977,s50:184.9286,s200:178.2789,rsi14:43.5906},
  {d:"2026-03-20",o:178.0,h:178.26,l:171.72,c:172.7,v:241323529,s20:183.121,s50:184.6004,s200:178.4363,rsi14:37.822},
  {d:"2026-03-23",o:177.26,h:178.37,l:174.76,c:175.64,v:182836300,s20:182.3255,s50:184.4124,s200:178.6049,rsi14:41.9711},
  {d:"2026-03-24",o:174.83,h:176.22,l:173.98,c:175.2,v:147667844,s20:181.443,s50:184.2192,s200:178.7809,rsi14:41.5245},
  {d:"2026-03-25",o:177.1,h:181.22,l:176.85,c:178.68,v:162602100,s20:180.599,s50:184.094,s200:178.9658,rsi14:46.3838},
  {d:"2026-03-26",o:176.07,h:176.51,l:171.14,c:171.24,v:186152232,s20:179.9165,s50:183.8026,s200:179.1088,rsi14:38.9346},
  {d:"2026-03-27",o:170.0,h:170.97,l:167.01,c:167.52,v:196212700,s20:179.433,s50:183.4902,s200:179.2266,rsi14:35.8356},
  {d:"2026-03-30",o:168.78,h:169.45,l:164.27,c:165.17,v:185627000,s20:178.5675,s50:183.0526,s200:179.3383,rsi14:33.9948},
  {d:"2026-03-31",o:166.97,h:174.62,l:166.96,c:174.4,v:226181301,s20:178.285,s50:182.816,s200:179.4853,rsi14:45.7762},
  {d:"2026-04-01",o:176.0,h:177.37,l:174.75,c:175.75,v:168132005,s20:177.9205,s50:182.7696,s200:179.6542,rsi14:47.259},
  {d:"2026-04-02",o:172.18,h:177.49,l:171.37,c:177.39,v:143143200,s20:177.623,s50:182.651,s200:179.8177,rsi14:49.0807},
  {d:"2026-04-06",o:177.16,h:177.79,l:175.76,c:177.64,v:107564300,s20:177.614,s50:182.507,s200:179.9853,rsi14:49.3678},
  {d:"2026-04-07",o:175.73,h:178.23,l:173.66,c:178.1,v:132534902,s20:177.3865,s50:182.3156,s200:180.1484,rsi14:49.9272},
  {d:"2026-04-08",o:184.5,h:185.26,l:180.3,c:182.08,v:147732700,s20:177.252,s50:182.2278,s200:180.3396,rsi14:54.6011},
  {d:"2026-04-09",o:181.84,h:184.08,l:180.62,c:183.91,v:116428523,s20:177.146,s50:182.1356,s200:180.5383,rsi14:56.6068},
  {d:"2026-04-10",o:184.31,h:190.0,l:184.3,c:188.63,v:160459525,s20:177.4205,s50:182.0778,s200:180.7419,rsi14:61.3496},
  {d:"2026-04-13",o:186.03,h:189.66,l:185.74,c:189.31,v:133648200,s20:177.8735,s50:182.0138,s200:180.9169,rsi14:61.9941},
  {d:"2026-04-14",o:190.84,h:196.51,l:190.77,c:196.51,v:161307010,s20:178.538,s50:182.1214,s200:181.1243,rsi14:68.066},
  {d:"2026-04-15",o:196.54,h:200.4,l:195.74,c:198.87,v:185338400,s20:179.385,s50:182.3866,s200:181.3299,rsi14:69.7708},
  {d:"2026-04-16",o:197.43,h:199.85,l:195.81,c:198.35,v:134012900,s20:180.2825,s50:182.7468,s200:181.5317,rsi14:68.898},
  {d:"2026-04-17",o:199.9,h:201.7,l:199.27,c:201.68,v:160324417,s20:181.4385,s50:183.2966,s200:181.7737,rsi14:71.368},
  {d:"2026-04-20",o:199.98,h:202.17,l:197.84,c:202.06,v:119381400,s20:182.9065,s50:183.9002,s200:181.9977,rsi14:71.6448},
  {d:"2026-04-21",o:202.13,h:202.75,l:199.0,c:199.88,v:107945302,s20:184.1185,s50:184.1896,s200:182.2004,rsi14:67.6077},
  {d:"2026-04-22",o:200.99,h:202.5,l:199.0,c:202.5,v:107501042,s20:185.4835,s50:184.4388,s200:182.4217,rsi14:69.8095},
  {d:"2026-04-23",o:202.46,h:203.83,l:197.22,c:199.64,v:113561830,s20:186.5315,s50:184.6608,s200:182.6199,rsi14:64.6439},
  {d:"2026-04-24",o:199.96,h:210.95,l:199.81,c:208.27,v:214134441,s20:188.383,s50:185.0252,s200:182.8468,rsi14:71.4975},
  {d:"2026-04-27",o:209.65,h:216.83,l:207.38,c:216.61,v:187172421,s20:190.8375,s50:185.6186,s200:183.1094,rsi14:76.2823},
  {d:"2026-04-28",o:209.49,h:214.73,l:208.2,c:213.17,v:180275400,s20:193.2375,s50:186.2258,s200:183.3507,rsi14:70.9887},
  {d:"2026-04-29",o:212.7,h:212.72,l:207.58,c:209.25,v:123711821,s20:194.98,s50:186.7114,s200:183.5765,rsi14:65.4177},
  {d:"2026-04-30",o:209.93,h:210.3,l:198.7,c:199.57,v:225239200,s20:196.171,s50:186.9432,s200:183.7209,rsi14:54.1225},
  {d:"2026-05-01",o:201.28,h:203.0,l:197.12,c:198.45,v:128647000,s20:197.224,s50:187.1542,s200:183.8563,rsi14:52.9826},
  {d:"2026-05-04",o:199.5,h:201.73,l:194.74,c:198.48,v:125368100,s20:198.266,s50:187.3274,s200:183.9837,rsi14:53.0112},
  {d:"2026-05-05",o:199.3,h:200.24,l:196.03,c:196.5,v:113406620,s20:199.186,s50:187.4264,s200:184.1042,rsi14:50.8181},
  {d:"2026-05-06",o:199.89,h:204.738,l:198.61,c:204.7,v:44070942,s20:200.4735,s50:187.726,s200:184.2864,rsi14:60.8093},
];

// ══════════════════════════════════════════════════════════════════
// SMA values already in RAW_DAILY from CSV.
// We still compute OLS regression + sigma bands + rolling Z-score.
// ══════════════════════════════════════════════════════════════════
function calcRegression(data) {
  const n  = data.length;
  const cs = data.map(d => d.c);
  const mx = (n-1)/2;
  const my = cs.reduce((a,b)=>a+b,0)/n;
  const ssxy = cs.reduce((s,y,i)=>s+(i-mx)*(y-my),0);
  const ssxx = cs.reduce((s,_,i)=>s+(i-mx)**2,0);
  const sl = ssxy/ssxx, ic = my - sl*mx;
  const res = cs.map((y,i)=>y-(ic+sl*i));
  const sg  = Math.sqrt(res.reduce((s,r)=>s+r*r,0)/n);
  return data.map((_,i)=>({
    reg: +(ic+sl*i).toFixed(2),
    b2p: +(ic+sl*i+2*sg).toFixed(2),
    b1p: +(ic+sl*i+sg).toFixed(2),
    b1n: +(ic+sl*i-sg).toFixed(2),
    b2n: +(ic+sl*i-2*sg).toFixed(2),
  }));
}
function calcZScore(data) {
  const cs = data.map(d => d.c);
  return data.map((_,i)=>{
    const w = Math.min(60, i+1);
    const sl = cs.slice(i-w+1, i+1);
    const zm = sl.reduce((a,b)=>a+b,0)/sl.length;
    const zs = Math.sqrt(sl.reduce((s,y)=>s+(y-zm)**2,0)/sl.length)||1;
    return +((cs[i]-zm)/zs).toFixed(3);
  });
}

const RS_LB_MAP = {
  STRONG_LEADER: "SL",
  LEADER: "L",
  NEUTRAL: "N",
  LAGGARD: "Lg",
  STRONG_LAGGARD: "SLg",
};

function buildDailyFromPriceChart(priceChart) {
  if (!Array.isArray(priceChart) || !priceChart.length) return null;
  const raw = priceChart.map((row) => ({
    d: String(row.date).slice(0, 10),
    o: row.open ?? row.close,
    h: row.high ?? row.close,
    l: row.low ?? row.close,
    c: row.close,
    v: row.volume,
    s20: row.sma20,
    s50: row.sma50,
    s200: row.sma200,
    rsi14: row.rsi14,
  }));
  const reg = calcRegression(raw);
  const z = calcZScore(raw);
  return raw.map((d, i) => ({ ...d, ...reg[i], z: z[i] }));
}

function buildRsDataFromChart(rsChart) {
  if (!Array.isArray(rsChart) || !rsChart.length) return null;
  return rsChart.map((row) => ({
    d: String(row.date_month || row.date || "").slice(0, 7),
    r: Number(row.rs_ratio) || 0,
    lb: RS_LB_MAP[row.rs_trend_label] || "N",
    r1: row.rs_return_1m != null ? Number(row.rs_return_1m) : undefined,
    r3: row.rs_return_3m != null ? Number(row.rs_return_3m) : undefined,
    r6: row.rs_return_6m != null ? Number(row.rs_return_6m) : undefined,
    r12: row.rs_return_12m != null ? Number(row.rs_return_12m) : undefined,
    nv: row.stock_return_1m != null ? Number(row.stock_return_1m) : undefined,
    nx: row.index_return_1m != null ? Number(row.index_return_1m) : undefined,
  }));
}

const _REG  = calcRegression(RAW_DAILY);
const _Z    = calcZScore(RAW_DAILY);

const DAILY = RAW_DAILY.map((d,i) => ({
  ...d,
  ...(_REG[i]),
  z: _Z[i],
}));
// ══════════════════════════════════════════════════════════════════
const RS_DATA = [
  {d:"2005-01",r:0.0001257,lb:"N"},
  {d:"2005-02",r:0.0001599,lb:"N",r1:27.202,nv:26.48,nx:-0.57},
  {d:"2005-03",r:0.0001366,lb:"N",r1:-14.566,nv:-16.18,nx:-1.89},
  {d:"2005-04",r:0.0001286,lb:"N",r1:-5.831,r3:2.337,nv:-9.75,nx:-4.16},
  {d:"2005-05",r:0.0001464,lb:"N",r1:13.813,r3:-8.435,nv:23.57,nx:8.58},
  {d:"2005-06",r:0.0001509,lb:"N",r1:3.060,r3:10.455,nv:-0.22,nx:-3.18},
  {d:"2005-07",r:0.0001405,lb:"N",r1:-6.884,r3:9.221,r6:11.773,nv:0.08,nx:7.47},
  {d:"2005-08",r:0.0001616,lb:"N",r1:15.059,r3:10.416,r6:1.103,nv:13.38,nx:-1.46},
  {d:"2005-09",r:0.0001784,lb:"N",r1:10.342,r3:18.219,r6:30.579,nv:11.73,nx:1.26},
  {d:"2005-10",r:0.0001770,lb:"N",r1:-0.739,r3:26.020,r6:37.641,nv:-2.13,nx:-1.40},
  {d:"2005-11",r:0.0001887,lb:"N",r1:6.605,r3:16.761,r6:28.923,nv:12.91,nx:5.91},
  {d:"2005-12",r:0.0001931,lb:"N",r1:2.307,r3:8.259,r6:27.982,nv:0.63,nx:-1.64},
  {d:"2006-01",r:0.0002228,lb:"SL",r1:15.367,r3:25.825,r6:58.565,r12:77.233,nv:19.96,nx:3.98},
  {d:"2006-02",r:0.0002370,lb:"SL",r1:6.391,r3:25.572,r6:46.620,r12:48.236,nv:3.89,nx:-2.35},
  {d:"2006-03",r:0.0002811,lb:"SL",r1:18.614,r3:45.587,r6:57.611,r12:105.806,nv:20.96,nx:1.98},
  {d:"2006-04",r:0.0002864,lb:"SL",r1:1.864,r3:28.546,r6:61.743,r12:122.624,nv:1.69,nx:-0.17},
  {d:"2006-05",r:0.0002425,lb:"SL",r1:-15.324,r3:2.308,r6:28.471,r12:65.629,nv:-21.36,nx:-7.12},
  {d:"2006-06",r:0.0002274,lb:"L",r1:-6.225,r3:-19.116,r6:17.757,r12:50.708,nv:-6.48,nx:-0.28},
  {d:"2006-07",r:0.0002445,lb:"L",r1:7.515,r3:-14.628,r6:9.742,r12:74.012,nv:3.02,nx:-4.18},
  {d:"2006-08",r:0.0003083,lb:"SL",r1:26.105,r3:27.142,r6:30.077,r12:90.719,nv:31.98,nx:4.66},
  {d:"2006-09",r:0.0002981,lb:"L",r1:-3.288,r3:31.124,r6:6.059,r12:67.160,nv:1.27,nx:4.71},
  {d:"2006-10",r:0.0003354,lb:"SL",r1:12.510,r3:37.217,r6:17.144,r12:89.472,nv:17.84,nx:4.74},
  {d:"2006-11",r:0.0003442,lb:"SL",r1:2.602,r3:11.643,r6:41.945,r12:82.358,nv:6.08,nx:3.39},
  {d:"2006-12",r:0.0003671,lb:"SL",r1:6.668,r3:23.136,r6:61.461,r12:90.132,nv:4.62,nx:-1.92},
  {d:"2007-01",r:0.0002860,lb:"L",r1:-22.085,r3:-14.727,r6:17.009,r12:28.408,nv:-20.52,nx:2.01},
  {d:"2007-02",r:0.0003042,lb:"N",r1:6.337,r3:-11.623,r6:-1.334,r12:28.342,nv:4.52,nx:-1.71},
  {d:"2007-03",r:0.0002706,lb:"Lg",r1:-11.024,r3:-26.281,r6:-9.226,r12:-3.726,nv:-10.48,nx:0.61},
  {d:"2007-04",r:0.0002935,lb:"N",r1:8.444,r3:2.604,r6:-12.506,r12:2.494,nv:14.28,nx:5.38},
  {d:"2007-05",r:0.0002994,lb:"N",r1:2.017,r3:-1.564,r6:-13.005,r12:23.484,nv:5.32,nx:3.24},
  {d:"2007-06",r:0.0003618,lb:"N",r1:20.851,r3:33.699,r6:-1.439,r12:59.138,nv:21.22,nx:0.31},
  {d:"2007-07",r:0.0003947,lb:"SL",r1:9.093,r3:34.499,r6:38.002,r12:61.474,nv:8.98,nx:-0.11},
  {d:"2007-08",r:0.0004288,lb:"SL",r1:8.615,r3:43.198,r6:40.958,r12:39.078,nv:11.80,nx:2.93},
  {d:"2007-09",r:0.0004333,lb:"SL",r1:1.052,r3:19.738,r6:60.089,r12:45.320,nv:6.25,nx:5.15},
  {d:"2007-10",r:0.0003950,lb:"L",r1:-8.821,r3:0.076,r6:34.602,r12:17.768,nv:-2.37,nx:7.07},
  {d:"2007-11",r:0.0003963,lb:"L",r1:0.328,r3:-7.559,r6:32.374,r12:15.158,nv:-6.39,nx:-6.69},
  {d:"2007-12",r:0.0004348,lb:"L",r1:9.700,r3:0.352,r6:20.160,r12:18.431,nv:9.48,nx:-0.20},
  {d:"2008-01",r:0.0003450,lb:"N",r1:-20.656,r3:-12.674,r6:-12.607,r12:20.603,nv:-29.92,nx:-11.68},
  {d:"2008-02",r:0.0003273,lb:"N",r1:-5.121,r3:-17.417,r6:-23.659,r12:7.609,nv:-10.07,nx:-5.22},
  {d:"2008-03",r:0.0002776,lb:"N",r1:-15.174,r3:-36.142,r6:-35.917,r12:2.590,nv:-13.39,nx:2.10},
  {d:"2008-04",r:0.0002679,lb:"Lg",r1:-3.511,r3:-22.343,r6:-32.185,r12:-8.720,nv:3.84,nx:7.62},
  {d:"2008-05",r:0.0003038,lb:"N",r1:13.402,r3:-7.182,r6:-23.348,r12:1.467,nv:20.19,nx:5.99},
  {d:"2008-06",r:0.0002548,lb:"SLg",r1:-16.146,r3:-8.247,r6:-41.408,r12:-29.596,nv:-24.21,nx:-9.62},
  {d:"2008-07",r:0.0001575,lb:"SLg",r1:-38.173,r3:-41.207,r6:-54.344,r12:-60.100,nv:-37.77,nx:0.66},
  {d:"2008-08",r:0.0001688,lb:"SLg",r1:7.143,r3:-44.453,r6:-48.442,r12:-60.640,nv:8.50,nx:1.26},
  {d:"2008-09",r:0.0001690,lb:"SLg",r1:0.128,r3:-33.672,r6:-39.142,r12:-61.001,nv:-15.27,nx:-15.38},
  {d:"2008-10",r:0.0001641,lb:"SLg",r1:-2.899,r3:4.170,r6:-38.756,r12:-58.468,nv:-18.21,nx:-15.77},
  {d:"2008-11",r:0.0001575,lb:"SLg",r1:-4.008,r3:-6.672,r6:-48.159,r12:-60.263,nv:-14.73,nx:-11.17},
  {d:"2008-12",r:0.0001923,lb:"SLg",r1:22.099,r3:13.807,r6:-24.515,r12:-55.772,nv:24.77,nx:2.18},
  {d:"2009-01",r:0.0001754,lb:"N",r1:-8.795,r3:6.896,r6:11.353,r12:-49.160,nv:-11.16,nx:-2.59},
  {d:"2009-02",r:0.0001853,lb:"N",r1:5.663,r3:17.667,r6:9.816,r12:-43.382,nv:0.00,nx:-5.36},
  {d:"2009-03",r:0.0001993,lb:"N",r1:7.528,r3:3.625,r6:17.933,r12:-28.229,nv:19.08,nx:10.74},
  {d:"2009-04",r:0.0002058,lb:"N",r1:3.293,r3:17.360,r6:25.453,r12:-23.167,nv:16.43,nx:12.72},
  {d:"2009-05",r:0.0001816,lb:"N",r1:-11.756,r3:-1.988,r6:15.327,r12:-40.213,nv:-9.15,nx:2.96},
  {d:"2009-06",r:0.0001958,lb:"N",r1:7.800,r3:-1.740,r6:1.822,r12:-23.139,nv:10.93,nx:2.90},
  {d:"2009-07",r:0.0002016,lb:"L",r1:2.965,r3:-2.053,r6:14.951,r12:28.001,nv:11.75,nx:8.54},
  {d:"2009-08",r:0.0002234,lb:"SL",r1:10.789,r3:22.971,r6:20.526,r12:32.357,nv:12.30,nx:1.36},
  {d:"2009-09",r:0.0002186,lb:"L",r1:-2.136,r3:11.637,r6:9.694,r12:29.365,nv:3.51,nx:5.77},
  {d:"2009-10",r:0.0001882,lb:"N",r1:-13.903,r3:-6.652,r6:-8.568,r12:14.704,nv:-16.50,nx:-3.02},
  {d:"2009-11",r:0.0001847,lb:"L",r1:-1.842,r3:-17.294,r6:1.705,r12:17.293,nv:4.06,nx:6.02},
  {d:"2009-12",r:0.0002510,lb:"SL",r1:35.891,r3:14.843,r6:28.207,r12:30.543,nv:43.03,nx:5.26},
  {d:"2010-01",r:0.0002210,lb:"L",r1:-11.968,r3:17.424,r6:9.613,r12:26.001,nv:-17.61,nx:-6.41},
  {d:"2010-02",r:0.0002227,lb:"N",r1:0.769,r3:20.547,r6:-0.300,r12:20.165,nv:5.26,nx:4.46},
  {d:"2010-03",r:0.0002251,lb:"L",r1:1.066,r3:-10.345,r6:2.962,r12:12.943,nv:8.83,nx:7.68},
  {d:"2010-04",r:0.0002081,lb:"L",r1:-7.555,r3:-5.851,r6:10.554,r12:1.081,nv:-5.56,nx:2.16},
  {d:"2010-06",r:0.0001596,lb:"Lg",r1:-23.310,r3:-28.348,r6:-13.625,r12:-12.153,nv:-33.33,nx:-13.07},
  {d:"2010-07",r:0.0001233,lb:"SLg",r1:-22.753,r3:-45.235,r6:-50.900,r12:-37.051,nv:-17.21,nx:7.18},
  {d:"2010-08",r:0.0001364,lb:"SLg",r1:10.628,r3:-34.463,r6:-38.297,r12:-32.366,nv:4.90,nx:-5.18},
  {d:"2010-09",r:0.0001498,lb:"SLg",r1:9.850,r3:-6.126,r6:-32.737,r12:-32.939,nv:24.18,nx:13.05},
  {d:"2010-10",r:0.0001415,lb:"SLg",r1:-5.564,r3:14.763,r6:-37.150,r12:-35.288,nv:0.41,nx:6.33},
  {d:"2010-11",r:0.0001624,lb:"Lg",r1:14.774,r3:19.064,r6:-21.969,r12:-13.734,nv:14.39,nx:-0.34},
  {d:"2010-12",r:0.0001736,lb:"N",r1:6.923,r3:15.892,r6:8.792,r12:-6.031,nv:12.00,nx:4.75},
  {d:"2011-01",r:0.0002621,lb:"L",r1:50.965,r3:85.264,r6:112.615,r12:4.393,nv:55.32,nx:2.89},
  {d:"2011-02",r:0.0002410,lb:"L",r1:-8.051,r3:48.421,r6:76.715,r12:9.038,nv:-5.27,nx:3.03},
  {d:"2011-03",r:0.0002049,lb:"N",r1:-14.968,r3:18.034,r6:36.792,r12:-7.990,nv:-15.40,nx:-0.51},
  {d:"2011-04",r:0.0002080,lb:"N",r1:1.505,r3:-20.637,r6:47.031,r12:-7.590,nv:4.33,nx:2.78},
  {d:"2011-05",r:0.0002112,lb:"L",r1:1.532,r3:-12.366,r6:30.068,r12:1.493,nv:0.20,nx:-1.31},
  {d:"2011-06",r:0.0001713,lb:"N",r1:-18.860,r3:-16.377,r6:-1.296,r12:7.382,nv:-20.48,nx:-2.00},
  {d:"2011-07",r:0.0001463,lb:"N",r1:-14.597,r3:-29.642,r6:-44.162,r12:18.720,nv:-13.21,nx:1.62},
  {d:"2011-08",r:0.0001526,lb:"N",r1:4.292,r3:-27.730,r6:-36.667,r12:11.920,nv:-1.08,nx:-5.15},
  {d:"2011-09",r:0.0001568,lb:"N",r1:2.769,r3:-8.466,r6:-23.456,r12:4.706,nv:-1.90,nx:-4.54},
  {d:"2011-10",r:0.0001568,lb:"N",r1:-0.039,r3:7.137,r6:-24.620,r12:10.831,nv:10.28,nx:10.33},
  {d:"2011-11",r:0.0001702,lb:"N",r1:8.593,r3:11.557,r6:-19.378,r12:4.863,nv:5.61,nx:-2.75},
  {d:"2011-12",r:0.0001736,lb:"L",r1:1.987,r3:10.708,r6:1.336,r12:0.023,nv:1.22,nx:-0.76},
  {d:"2012-01",r:0.0001499,lb:"N",r1:-13.654,r3:-4.371,r6:2.455,r12:-42.791,nv:-6.45,nx:8.35},
  {d:"2012-02",r:0.0001474,lb:"Lg",r1:-1.655,r3:-13.396,r6:-3.388,r12:-38.812,nv:4.53,nx:6.29},
  {d:"2012-03",r:0.0001397,lb:"SLg",r1:-5.257,r3:-19.548,r6:-10.933,r12:-31.825,nv:-0.48,nx:5.04},
  {d:"2012-04",r:0.0001193,lb:"SLg",r1:-14.579,r3:-20.409,r6:-23.888,r12:-42.627,nv:-15.56,nx:-1.15},
  {d:"2012-05",r:0.0001259,lb:"SLg",r1:5.551,r3:-14.577,r6:-26.021,r12:-40.356,nv:-2.15,nx:-7.30},
  {d:"2012-06",r:0.0001321,lb:"SLg",r1:4.874,r3:-5.443,r6:-23.927,r12:-22.910,nv:8.65,nx:3.60},
  {d:"2012-07",r:0.0001281,lb:"Lg",r1:-3.020,r3:7.352,r6:-14.558,r12:-12.460,nv:-2.03,nx:1.02},
  {d:"2012-08",r:0.0001291,lb:"Lg",r1:0.812,r3:2.533,r6:-12.414,r12:-15.381,nv:5.76,nx:4.91},
  {d:"2012-09",r:0.0001191,lb:"SLg",r1:-7.740,r3:-9.800,r6:-14.709,r12:-24.034,nv:-6.84,nx:0.97},
  {d:"2012-10",r:0.0001131,lb:"Lg",r1:-5.103,r3:-11.737,r6:-5.248,r12:-27.882,nv:-10.23,nx:-5.40},
  {d:"2012-11",r:0.0001145,lb:"Lg",r1:1.233,r3:-11.369,r6:-9.124,r12:-32.770,nv:2.38,nx:1.13},
  {d:"2012-12",r:0.0001188,lb:"SLg",r1:3.838,r3:-0.245,r6:-10.021,r12:-31.550,nv:3.18,nx:-0.63},
  {d:"2013-01",r:0.0001129,lb:"SLg",r1:-4.972,r3:-0.108,r6:-11.832,r12:-24.667,nv:-2.45,nx:2.65},
  {d:"2013-02",r:0.0001156,lb:"SLg",r1:2.329,r3:0.974,r6:-10.506,r12:-21.615,nv:2.59,nx:0.26},
  {d:"2013-04",r:0.0001192,lb:"N",r1:3.160,r3:0.315,r6:0.069,r12:-14.651,nv:8.77,nx:5.44},
  {d:"2013-05",r:0.0001227,lb:"L",r1:2.885,r3:8.608,r6:8.491,r12:2.798,nv:6.25,nx:3.27},
  {d:"2013-06",r:0.0001206,lb:"N",r1:-1.653,r3:4.382,r6:5.398,r12:-4.218,nv:-4.03,nx:-2.42},
  {d:"2013-07",r:0.0001168,lb:"Lg",r1:-3.194,r3:-2.047,r6:-1.739,r12:-11.586,nv:2.81,nx:6.21},
  {d:"2013-08",r:0.0001201,lb:"N",r1:2.865,r3:-2.066,r6:6.364,r12:-6.221,nv:2.32,nx:-0.53},
  {d:"2013-09",r:0.0001209,lb:"N",r1:0.622,r3:0.199,r6:4.590,r12:-6.398,nv:5.35,nx:4.70},
  {d:"2013-10",r:0.0001127,lb:"Lg",r1:-6.744,r3:-3.476,r6:-5.452,r12:-5.387,nv:-2.12,nx:4.96},
  {d:"2013-11",r:0.0001118,lb:"Lg",r1:-0.804,r3:-6.918,r6:-8.841,r12:-1.101,nv:2.43,nx:3.26},
  {d:"2013-12",r:0.0001115,lb:"Lg",r1:-0.286,r3:-7.758,r6:-7.574,r12:-2.585,nv:2.69,nx:2.99},
  {d:"2014-01",r:0.0001116,lb:"Lg",r1:0.063,r3:-1.025,r6:-4.465,r12:-6.127,nv:-1.87,nx:-1.93},
  {d:"2014-02",r:0.0001266,lb:"L",r1:13.431,r3:13.177,r6:5.348,r12:12.052,nv:19.02,nx:4.93},
  {d:"2014-03",r:0.0001245,lb:"L",r1:-1.604,r3:11.681,r6:3.017,r12:7.746,nv:-4.28,nx:-2.72},
  {d:"2014-04",r:0.0001304,lb:"L",r1:4.755,r3:16.918,r6:15.720,r12:9.411,nv:4.36,nx:-0.38},
  {d:"2014-05",r:0.0001271,lb:"L",r1:-2.553,r3:0.443,r6:13.679,r12:3.629,nv:1.66,nx:4.32},
  {d:"2014-06",r:0.0001204,lb:"N",r1:-5.277,r3:-3.306,r6:7.989,r12:-0.190,nv:-2.42,nx:3.01},
  {d:"2014-07",r:0.0001161,lb:"N",r1:-3.559,r3:-10.980,r6:4.081,r12:-0.567,nv:-2.48,nx:1.12},
  {d:"2014-08",r:0.0001191,lb:"Lg",r1:2.569,r3:-6.301,r6:-5.885,r12:-0.852,nv:7.58,nx:4.88},
  {d:"2014-09",r:0.0001143,lb:"Lg",r1:-4.028,r3:-5.065,r6:-8.204,r12:-5.434,nv:-4.81,nx:-0.81},
  {d:"2014-10",r:0.0001175,lb:"N",r1:2.775,r3:1.169,r6:-9.939,r12:4.218,nv:5.53,nx:2.69},
  {d:"2014-11",r:0.0001209,lb:"N",r1:2.876,r3:1.471,r6:-4.922,r12:8.084,nv:7.32,nx:4.32},
  {d:"2014-12",r:0.0001248,lb:"L",r1:3.226,r3:9.141,r6:3.613,r12:11.891,nv:0.81,nx:-2.34},
  {d:"2015-01",r:0.0001192,lb:"L",r1:-4.452,r3:1.467,r6:2.653,r12:6.842,nv:-6.43,nx:-2.07},
  {d:"2015-02",r:0.0001250,lb:"N",r1:4.848,r3:3.413,r6:4.934,r12:-1.242,nv:12.23,nx:7.04},
  {d:"2015-03",r:0.0001239,lb:"N",r1:-0.901,r3:-0.722,r6:8.353,r12:-0.536,nv:-3.29,nx:-2.41},
  {d:"2015-04",r:0.0001263,lb:"N",r1:1.994,r3:5.976,r6:7.530,r12:-3.157,nv:3.89,nx:1.86},
  {d:"2015-05",r:0.0001227,lb:"N",r1:-2.854,r3:-1.810,r6:1.541,r12:-3.457,nv:-0.79,nx:2.13},
  {d:"2015-06",r:0.0001144,lb:"Lg",r1:-6.777,r3:-7.632,r6:-8.299,r12:-4.986,nv:-9.08,nx:-2.47},
  {d:"2015-07",r:0.0001090,lb:"Lg",r1:-4.713,r3:-13.706,r6:-8.550,r12:-6.123,nv:-0.50,nx:4.42},
  {d:"2015-08",r:0.0001315,lb:"L",r1:20.608,r3:7.134,r6:5.196,r12:10.386,nv:12.29,nx:-6.90},
  {d:"2015-09",r:0.0001474,lb:"SL",r1:12.106,r3:28.835,r6:19.003,r12:28.943,nv:9.65,nx:-2.19},
  {d:"2015-10",r:0.0001526,lb:"SL",r1:3.511,r3:39.955,r6:20.772,r12:29.867,nv:15.09,nx:11.19},
  {d:"2015-11",r:0.0001700,lb:"SL",r1:11.432,r3:29.308,r6:38.533,r12:40.669,nv:11.81,nx:0.34},
  {d:"2015-12",r:0.0001837,lb:"SL",r1:8.050,r3:24.630,r6:60.567,r12:47.242,nv:6.40,nx:-1.53},
  {d:"2016-01",r:0.0001711,lb:"SL",r1:-6.845,r3:12.162,r6:56.976,r12:43.555,nv:-13.21,nx:-6.84},
  {d:"2016-02",r:0.0001866,lb:"SL",r1:9.056,r3:9.770,r6:41.941,r12:49.316,nv:7.07,nx:-1.82},
  {d:"2016-03",r:0.0001994,lb:"SL",r1:6.845,r3:8.546,r6:35.280,r12:60.987,nv:14.03,nx:6.73},
  {d:"2016-04",r:0.0002085,lb:"SL",r1:4.550,r3:21.823,r6:36.638,r12:65.021,nv:1.23,nx:-3.17},
  {d:"2016-05",r:0.0002586,lb:"SL",r1:24.064,r3:38.587,r6:52.127,r12:110.746,nv:29.28,nx:4.21},
  {d:"2016-06",r:0.0002671,lb:"SL",r1:3.279,r3:33.961,r6:45.410,r12:133.481,nv:0.85,nx:-2.35},
  {d:"2016-07",r:0.0003023,lb:"SL",r1:13.180,r3:45.019,r6:76.666,r12:177.323,nv:21.19,nx:7.07},
  {d:"2016-08",r:0.0003249,lb:"SL",r1:7.464,r3:25.616,r6:74.087,r12:147.101,nv:8.39,nx:0.86},
  {d:"2016-09",r:0.0003507,lb:"SL",r1:7.955,r3:31.303,r6:75.895,r12:137.952,nv:10.32,nx:2.19},
  {d:"2016-10",r:0.0003707,lb:"SL",r1:5.707,r3:22.634,r6:77.842,r12:143.001,nv:4.09,nx:-1.53},
  {d:"2016-11",r:0.0004885,lb:"SL",r1:31.761,r3:50.360,r6:88.876,r12:187.331,nv:32.02,nx:0.20},
  {d:"2016-12",r:0.0006024,lb:"SL",r1:23.327,r3:71.771,r6:125.539,r12:227.956,nv:24.68,nx:1.10},
  {d:"2017-01",r:0.0005374,lb:"SL",r1:-10.787,r3:44.968,r6:77.780,r12:214.078,nv:-6.14,nx:5.20},
  {d:"2017-02",r:0.0004897,lb:"SL",r1:-8.893,r3:0.239,r6:50.720,r12:162.384,nv:-5.09,nx:4.17},
  {d:"2017-03",r:0.0005040,lb:"L",r1:2.935,r3:-16.335,r6:43.712,r12:152.782,nv:4.98,nx:1.99},
  {d:"2017-04",r:0.0004674,lb:"L",r1:-7.257,r3:-13.025,r6:26.086,r12:124.235,nv:-4.74,nx:2.71},
  {d:"2017-05",r:0.0006253,lb:"SL",r1:33.779,r3:27.712,r6:28.018,r12:141.795,nv:38.70,nx:3.68},
  {d:"2017-06",r:0.0006712,lb:"SL",r1:7.327,r3:33.160,r6:11.409,r12:151.271,nv:4.70,nx:-2.45},
  {d:"2017-07",r:0.0006904,lb:"SL",r1:2.872,r3:47.704,r6:28.466,r12:128.387,nv:7.12,nx:4.13},
  {d:"2017-08",r:0.0007080,lb:"SL",r1:2.545,r3:13.219,r6:44.595,r12:117.933,nv:4.43,nx:1.84},
  {d:"2017-09",r:0.0007476,lb:"SL",r1:5.588,r3:11.386,r6:48.322,r12:113.156,nv:5.42,nx:-0.16},
  {d:"2017-10",r:0.0008274,lb:"SL",r1:10.676,r3:19.836,r6:77.002,r12:123.176,nv:15.66,nx:4.50},
  {d:"2017-11",r:0.0008279,lb:"SL",r1:0.061,r3:16.932,r6:32.390,r12:69.482,nv:1.93,nx:1.87},
  {d:"2017-12",r:0.0007739,lb:"SL",r1:-6.525,r3:3.517,r6:15.303,r12:28.458,nv:-6.07,nx:0.48},
  {d:"2018-01",r:0.0008878,lb:"SL",r1:14.718,r3:7.298,r6:28.581,r12:65.183,nv:24.65,nx:8.65},
  {d:"2018-02",r:0.0008987,lb:"SL",r1:1.230,r3:8.552,r6:26.932,r12:83.536,nv:-0.16,nx:-1.38},
  {d:"2018-03",r:0.0008798,lb:"SL",r1:-2.103,r3:13.687,r6:17.685,r12:74.552,nv:-6.01,nx:-3.99},
  {d:"2018-04",r:0.0008508,lb:"L",r1:-3.295,r3:-4.165,r6:2.829,r12:82.010,nv:-2.94,nx:0.37},
  {d:"2018-05",r:0.0009070,lb:"L",r1:6.610,r3:0.929,r6:9.560,r12:45.046,nv:12.46,nx:5.48},
  {d:"2018-06",r:0.0008550,lb:"L",r1:-5.735,r3:-2.816,r6:10.486,r12:27.393,nv:-4.75,nx:1.05},
  {d:"2018-07",r:0.0008462,lb:"N",r1:-1.026,r3:-0.535,r6:-4.678,r12:22.566,nv:1.66,nx:2.72},
  {d:"2018-08",r:0.0009171,lb:"L",r1:8.374,r3:1.109,r6:2.049,r12:29.532,nv:14.71,nx:5.84},
  {d:"2018-09",r:0.0009216,lb:"L",r1:0.496,r3:7.793,r6:4.758,r12:23.284,nv:0.14,nx:-0.35},
  {d:"2018-10",r:0.0007564,lb:"Lg",r1:-17.928,r3:-10.615,r6:-11.094,r12:-8.579,nv:-25.04,nx:-8.66},
  {d:"2018-11",r:0.0005886,lb:"SLg",r1:-22.189,r3:-35.822,r6:-35.110,r12:-28.907,nv:-22.39,nx:-0.26},
  {d:"2018-12",r:0.0006714,lb:"Lg",r1:14.074,r3:-27.151,r6:-21.474,r12:-13.240,nv:3.91,nx:-8.91},
  {d:"2019-01",r:0.0005198,lb:"SLg",r1:-22.585,r3:-31.284,r6:-38.578,r12:-41.452,nv:-15.53,nx:9.11},
  {d:"2019-02",r:0.0005537,lb:"SLg",r1:6.530,r3:-5.923,r6:-39.624,r12:-38.387,nv:9.47,nx:2.76},
  {d:"2019-03",r:0.0006085,lb:"SLg",r1:9.895,r3:-9.369,r6:-33.977,r12:-30.835,nv:14.25,nx:3.96},
  {d:"2019-04",r:0.0005822,lb:"SLg",r1:-4.330,r3:12.001,r6:-23.038,r12:-31.576,nv:0.89,nx:5.46},
  {d:"2019-05",r:0.0004924,lb:"SLg",r1:-15.413,r3:-11.068,r6:-16.335,r12:-45.710,nv:-22.52,nx:-8.40},
  {d:"2019-06",r:0.0005358,lb:"SLg",r1:8.804,r3:-11.951,r6:-20.201,r12:-37.337,nv:17.09,nx:7.62},
  {d:"2019-07",r:0.0005593,lb:"N",r1:4.394,r3:-3.922,r6:7.609,r12:-33.905,nv:6.81,nx:2.32},
  {d:"2019-08",r:0.0005448,lb:"Lg",r1:-2.598,r3:10.634,r6:-1.611,r12:-40.596,nv:-4.56,nx:-2.01},
  {d:"2019-09",r:0.0005613,lb:"Lg",r1:3.036,r3:4.769,r6:-7.752,r12:-39.095,nv:3.82,nx:0.76},
  {d:"2019-10",r:0.0006284,lb:"N",r1:11.951,r3:12.353,r6:7.947,r12:-16.922,nv:16.78,nx:4.31},
  {d:"2019-11",r:0.0006450,lb:"L",r1:2.632,r3:18.385,r6:30.975,r12:9.579,nv:6.69,nx:3.96},
  {d:"2019-12",r:0.0006848,lb:"L",r1:6.171,r3:21.988,r6:27.805,r12:1.987,nv:10.33,nx:3.92},
  {d:"2020-01",r:0.0006840,lb:"SL",r1:-0.113,r3:8.842,r6:22.287,r12:31.591,nv:2.84,nx:2.96},
  {d:"2020-02",r:0.0007977,lb:"SL",r1:16.626,r3:23.683,r6:46.423,r12:44.063,nv:9.76,nx:-5.89},
  {d:"2020-03",r:0.0008498,lb:"SL",r1:6.533,r3:24.105,r6:51.392,r12:39.656,nv:-1.63,nx:-7.66},
  {d:"2020-04",r:0.0008288,lb:"SL",r1:-2.468,r3:21.180,r6:31.894,r12:42.375,nv:12.35,nx:15.19},
  {d:"2020-05",r:0.0009293,lb:"SL",r1:12.121,r3:16.498,r6:44.088,r12:88.719,nv:19.03,nx:6.17},
  {d:"2020-06",r:0.0009353,lb:"SL",r1:0.648,r3:10.063,r6:36.594,r12:74.574,nv:6.98,nx:6.29},
  {d:"2020-07",r:0.0009729,lb:"SL",r1:4.014,r3:17.377,r6:42.237,r12:73.937,nv:11.68,nx:7.37},
  {d:"2020-08",r:0.0011040,lb:"SL",r1:13.477,r3:18.797,r6:38.396,r12:102.643,nv:26.01,nx:11.05},
  {d:"2020-09",r:0.0011850,lb:"SL",r1:7.335,r3:26.690,r6:39.439,r12:111.099,nv:1.20,nx:-5.72},
  {d:"2020-10",r:0.0011336,lb:"SL",r1:-4.332,r3:16.525,r6:36.773,r12:80.396,nv:-7.39,nx:-3.20},
  {d:"2020-11",r:0.0010922,lb:"L",r1:-3.651,r3:-1.063,r6:17.533,r12:69.352,nv:6.94,nx:11.00},
  {d:"2020-12",r:0.0010133,lb:"L",r1:-7.226,r3:-14.485,r6:8.339,r12:47.984,nv:-2.54,nx:5.05},
  {d:"2021-01",r:0.0010050,lb:"L",r1:-0.821,r3:-11.347,r6:3.303,r12:46.934,nv:-0.54,nx:0.29},
  {d:"2021-02",r:0.0010620,lb:"N",r1:5.673,r3:-2.768,r6:-3.802,r12:33.135,nv:5.54,nx:-0.12},
  {d:"2021-03",r:0.0010198,lb:"N",r1:-3.980,r3:0.634,r6:-13.943,r12:19.997,nv:-2.63,nx:1.41},
  {d:"2021-04",r:0.0010829,lb:"N",r1:6.194,r3:7.753,r6:-4.474,r12:30.654,nv:12.43,nx:5.88},
  {d:"2021-05",r:0.0011866,lb:"L",r1:9.572,r3:11.728,r6:8.636,r12:27.684,nv:8.19,nx:-1.26},
  {d:"2021-06",r:0.0013741,lb:"SL",r1:15.806,r3:34.750,r6:35.605,r12:46.913,nv:23.15,nx:6.34},
  {d:"2021-07",r:0.0013035,lb:"SL",r1:-5.140,r3:20.368,r6:29.700,r12:33.983,nv:-2.50,nx:2.78},
  {d:"2021-08",r:0.0014369,lb:"SL",r1:10.233,r3:21.094,r6:35.297,r12:30.153,nv:14.82,nx:4.16},
  {d:"2021-09",r:0.0014105,lb:"L",r1:-1.834,r3:2.649,r6:38.320,r12:19.035,nv:-7.46,nx:-5.73},
  {d:"2021-10",r:0.0016132,lb:"SL",r1:14.369,r3:23.761,r6:48.969,r12:42.304,nv:23.41,nx:7.90},
  {d:"2021-11",r:0.0020253,lb:"SL",r1:25.545,r3:40.952,r6:70.685,r12:85.425,nv:27.81,nx:1.80},
  {d:"2021-12",r:0.0018021,lb:"SL",r1:-11.022,r3:27.760,r6:31.144,r12:77.838,nv:-10.01,nx:1.14},
  {d:"2022-01",r:0.0016403,lb:"SL",r1:-8.976,r3:1.681,r6:25.841,r12:63.216,nv:-16.73,nx:-8.52},
  {d:"2022-02",r:0.0017130,lb:"L",r1:4.434,r3:-15.418,r6:19.221,r12:61.302,nv:-0.41,nx:-4.64},
  {d:"2022-03",r:0.0018391,lb:"SL",r1:7.361,r3:2.057,r6:30.387,r12:80.352,nv:11.89,nx:4.22},
  {d:"2022-04",r:0.0014430,lb:"N",r1:-21.537,r3:-12.027,r6:-10.548,r12:33.255,nv:-32.03,nx:-13.37},
  {d:"2022-05",r:0.0014768,lb:"N",r1:2.340,r3:-13.790,r6:-27.082,r12:24.461,nv:0.65,nx:-1.65},
  {d:"2022-06",r:0.0013178,lb:"Lg",r1:-10.765,r3:-28.345,r6:-26.871,r12:-4.096,nv:-18.80,nx:-9.00},
  {d:"2022-07",r:0.0014025,lb:"N",r1:6.427,r3:-2.807,r6:-14.496,r12:7.599,nv:19.79,nx:12.55},
  {d:"2022-08",r:0.0012296,lb:"Lg",r1:-12.328,r3:-16.738,r6:-28.220,r12:-14.423,nv:-16.91,nx:-5.22},
  {d:"2022-09",r:0.0011065,lb:"SLg",r1:-10.011,r3:-16.034,r6:-39.834,r12:-21.552,nv:-19.55,nx:-10.60},
  {d:"2022-10",r:0.0011836,lb:"SLg",r1:6.968,r3:-15.608,r6:-17.977,r12:-26.628,nv:11.20,nx:3.96},
  {d:"2022-11",r:0.0014065,lb:"Lg",r1:18.827,r3:14.383,r6:-4.763,r12:-30.554,nv:25.33,nx:5.48},
  {d:"2022-12",r:0.0013355,lb:"N",r1:-5.047,r3:20.692,r6:1.340,r12:-25.891,nv:-13.65,nx:-9.06},
  {d:"2023-01",r:0.0016146,lb:"N",r1:20.900,r3:36.412,r6:15.121,r12:-1.567,nv:33.74,nx:10.62},
  {d:"2023-02",r:0.0019282,lb:"L",r1:19.423,r3:37.097,r6:56.815,r12:12.562,nv:18.83,nx:-0.49},
  {d:"2023-03",r:0.0021075,lb:"L",r1:9.298,r3:57.808,r6:90.462,r12:14.593,nv:19.64,nx:9.46},
  {d:"2023-04",r:0.0020950,lb:"SL",r1:-0.595,r3:29.750,r6:76.995,r12:45.178,nv:-0.11,nx:0.49},
  {d:"2023-05",r:0.0026540,lb:"SL",r1:26.683,r3:37.638,r6:88.697,r12:79.710,nv:36.32,nx:7.61},
  {d:"2023-06",r:0.0027867,lb:"SL",r1:5.001,r3:32.227,r6:108.665,r12:111.461,nv:11.82,nx:6.49},
  {d:"2023-07",r:0.0029657,lb:"SL",r1:6.422,r3:41.561,r6:83.676,r12:111.450,nv:10.47,nx:3.81},
  {d:"2023-08",r:0.0031843,lb:"SL",r1:7.372,r3:19.982,r6:65.141,r12:158.965,nv:5.63,nx:-1.62},
  {d:"2023-09",r:0.0029561,lb:"SL",r1:-7.166,r3:6.079,r6:40.265,r12:167.152,nv:-11.87,nx:-5.07},
  {d:"2023-10",r:0.0028300,lb:"L",r1:-4.266,r3:-4.574,r6:35.086,r12:139.096,nv:-6.25,nx:-2.08},
  {d:"2023-11",r:0.0029327,lb:"L",r1:3.627,r3:-7.902,r6:10.501,r12:108.513,nv:14.69,nx:10.67},
  {d:"2023-12",r:0.0029431,lb:"L",r1:0.355,r3:-0.441,r6:5.611,r12:120.373,nv:5.88,nx:5.51},
  {d:"2024-01",r:0.0035904,lb:"SL",r1:21.996,r3:26.869,r6:21.066,r12:122.370,nv:24.25,nx:1.85},
  {d:"2024-02",r:0.0043843,lb:"SL",r1:22.111,r3:49.499,r6:37.686,r12:127.375,nv:28.57,nx:5.29},
  {d:"2024-03",r:0.0049500,lb:"SL",r1:12.901,r3:68.190,r6:67.448,r12:134.871,nv:14.22,nx:1.17},
  {d:"2024-04",r:0.0049539,lb:"SL",r1:0.080,r3:37.976,r6:75.049,r12:136.468,nv:-4.38,nx:-4.46},
  {d:"2024-05",r:0.0059142,lb:"SL",r1:19.385,r3:34.895,r6:101.666,r12:122.844,nv:26.89,nx:6.28},
  {d:"2024-06",r:0.0062765,lb:"SL",r1:6.126,r3:26.799,r6:113.264,r12:125.231,nv:12.69,nx:6.18},
  {d:"2024-07",r:0.0060437,lb:"SL",r1:-3.710,r3:21.997,r6:68.327,r12:103.788,nv:-5.28,nx:-1.63},
  {d:"2024-08",r:0.0060982,lb:"SL",r1:0.902,r3:3.111,r6:39.091,r12:91.508,nv:2.01,nx:1.10},
  {d:"2024-09",r:0.0060536,lb:"L",r1:-0.731,r3:-3.551,r6:22.297,r12:104.783,nv:1.73,nx:2.48},
  {d:"2024-10",r:0.0066746,lb:"SL",r1:10.257,r3:10.439,r6:34.733,r12:135.849,nv:9.32,nx:-0.85},
  {d:"2024-11",r:0.0066052,lb:"SL",r1:-1.039,r3:8.315,r6:11.684,r12:125.229,nv:4.14,nx:5.23},
  {d:"2024-12",r:0.0063911,lb:"L",r1:-3.243,r3:5.574,r6:1.825,r12:117.156,nv:-2.86,nx:0.39},
  {d:"2025-01",r:0.0055904,lb:"N",r1:-12.528,r3:-16.244,r6:-7.500,r12:55.702,nv:-10.59,nx:2.22},
  {d:"2025-02",r:0.0059815,lb:"N",r1:6.997,r3:-9.443,r6:-1.914,r12:36.429,nv:4.04,nx:-2.76},
  {d:"2025-03",r:0.0056218,lb:"N",r1:-6.013,r3:-12.036,r6:-7.133,r12:13.573,nv:-13.24,nx:-7.69},
  {d:"2025-04",r:0.0055654,lb:"N",r1:-1.004,r3:-0.447,r6:-16.618,r12:12.343,nv:0.50,nx:1.52},
  {d:"2025-05",r:0.0063319,lb:"N",r1:13.774,r3:5.859,r6:-4.137,r12:7.063,nv:24.06,nx:9.04},
  {d:"2025-06",r:0.0069664,lb:"L",r1:10.019,r3:23.916,r6:9.002,r12:10.991,nv:16.92,nx:6.27},
  {d:"2025-07",r:0.0076608,lb:"SL",r1:9.969,r3:37.652,r6:37.036,r12:26.758,nv:12.58,nx:2.38},
  {d:"2025-08",r:0.0074387,lb:"SL",r1:-2.900,r3:17.479,r6:24.362,r12:21.982,nv:-2.07,nx:0.85},
  {d:"2025-09",r:0.0075600,lb:"SL",r1:1.630,r3:8.521,r6:34.475,r12:24.883,nv:7.12,nx:5.40},
  {d:"2025-10",r:0.0078308,lb:"L",r1:3.582,r3:2.219,r6:40.706,r12:17.323,nv:8.53,nx:4.77},
  {d:"2025-11",r:0.0069589,lb:"L",r1:-11.134,r3:-6.449,r6:9.902,r12:5.355,nv:-12.59,nx:-1.64},
  {d:"2025-12",r:0.0073581,lb:"L",r1:5.736,r3:-2.670,r6:5.624,r12:15.131,nv:5.37,nx:-0.35}
];


const LB_COLORS = {SL:"#6f8a5b", L:"#8da57a", N:"#8a8680", Lg:"#caa85e", SLg:"#c6523f"};
const LB_FULL   = {SL:"Strong Leader", L:"Leader", N:"Neutral", Lg:"Laggard", SLg:"Strong Laggard"};


// 3. TOOLTIP — position:fixed, viewport coords (v2_2 pattern)
//    Never clipped by overflow:hidden. Fond blanc. z-index 9999.
// ══════════════════════════════════════════════════════════════════
// TipIcon: accepts text as plain string OR as TIPS object {title?, body, src?}
function TipIcon({ text }) {
  if (!text) return null;
  const tipProps = typeof text === "string" ? { body: text } : text;
  return (
    <Tip {...tipProps}>
      <span style={{
        display:"inline-flex", alignItems:"center", justifyContent:"center",
        width:14, height:14, borderRadius:"50%",
        border:"1px solid rgba(181,154,91,.55)",
        fontSize:8, color:"rgba(181,154,91,.85)",
        marginLeft:4, flexShrink:0, lineHeight:1,
        verticalAlign:"middle", cursor:"help",
      }}>?</span>
    </Tip>
  );
}



// ══════════════════════════════════════════════════════════════════
// 4. GAUGE — from ValorisationCard.jsx
//    Demi-cercle W=320/H=170, 5 segments de couleur
// ══════════════════════════════════════════════════════════════════
function Gauge({ value, labelTop, labelBottom }) {
  const W=320, H=170, cx=W/2, cy=H-8, r=130;
  // INVERSÉ : rouge (Faible) à gauche → vert (Élevée) à droite
  const COLORS = ["#c6523f","#d28a52","#caa85e","#a9b38a","#6f8a5b"];
  const clamped = value != null ? Math.max(0, Math.min(1, value)) : 0.5;
  const angle = (-180 + 180*clamped) * Math.PI/180;
  const nx = cx + (r-34)*Math.cos(angle);
  const ny = cy + (r-34)*Math.sin(angle);
  const arc = (a0,a1,rr) => {
    const s=a0*Math.PI/180, e=a1*Math.PI/180;
    const x1=cx+rr*Math.cos(s), y1=cy+rr*Math.sin(s);
    const x2=cx+rr*Math.cos(e), y2=cy+rr*Math.sin(e);
    return `M${x1} ${y1} A${rr} ${rr} 0 ${Math.abs(a1-a0)>180?1:0} 1 ${x2} ${y2}`;
  };
  const step = 180/5;
  // Active arc color : rouge si faible, vert si élevé
  const activeCol = clamped >= 0.7 ? C.green : clamped >= 0.4 ? C.amber : C.red;
  const needleCol = "var(--ts)";
  return (
    <div style={{ width:"100%", maxWidth:"min(280px,100%)", margin:"0 auto", overflow:"hidden" }}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", height:"auto", display:"block" }}>
        {/* Track */}
        <path d={arc(-180,0,r)} strokeWidth={18} stroke="rgba(148,163,184,.12)" fill="none" strokeLinecap="round"/>
        {/* Colored segments (rouge gauche → vert droite) */}
        {COLORS.map((col,i)=>(
          <path key={i} d={arc(-180+step*i,-180+step*(i+1),r)}
            strokeWidth={18} stroke={col} fill="none"
            strokeLinecap={i===0||i===4?"round":"butt"} opacity={.55}/>
        ))}
        {/* Separators */}
        {[0.25,0.5,0.75].map(t=>{
          const a=(-180+180*t)*Math.PI/180;
          const x1=cx+(r-24)*Math.cos(a), y1=cy+(r-24)*Math.sin(a);
          const x2=cx+(r-10)*Math.cos(a), y2=cy+(r-10)*Math.sin(a);
          return <line key={t} x1={x1} y1={y1} x2={x2} y2={y2} stroke="var(--card-bg)" strokeWidth="2"/>;
        })}
        {/* Axis labels */}
        {[{t:0,label:"Faible"},{t:0.5,label:"Neutre"},{t:1,label:"Élevée"}].map(({t,label})=>{
          const a=(-180+180*t)*Math.PI/180, dist=r+20;
          const x=cx+dist*Math.cos(a), y=cy+dist*Math.sin(a);
          return (
            <text key={t} x={x} y={y+4}
              textAnchor="middle" fontSize="8.5" fontWeight="600"
              fill="var(--mu)" fontFamily="ui-monospace,monospace">{label}</text>
          );
        })}
        {/* Needle */}
        <line x1={cx} y1={cy} x2={nx} y2={ny}
          strokeWidth={8} stroke={needleCol} strokeLinecap="round" opacity={.85}/>
        <circle cx={cx} cy={cy} r={9} fill={needleCol} opacity={.85}/>
        <circle cx={cx} cy={cy} r={3.5} fill="rgba(255,255,255,0.5)"/>
        {/* Labels */}
        <text x={cx} y={32} textAnchor="middle" fontSize="18" fontWeight="800"
          fill="var(--ts)" fontFamily="ui-monospace,monospace">{labelTop}</text>
        <text x={cx} y={H-26} textAnchor="middle" fontSize="11" fontWeight="600"
          fill="var(--mu)" fontFamily="ui-monospace,monospace">{labelBottom}</text>
      </svg>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 5. MODAL "COMPRENDRE"
//    Fond blanc, backdrop semi-opaque, aucune référence Murphy
// ══════════════════════════════════════════════════════════════════
// ANALYSE modal content — valeurs issues de timing_marche_summary + StockDashboard (NVDA 2026-04-30)
// Chaque section est une fonction(d) → string pour permettre l'injection des données réelles
function buildAnalyse(d) {
  const tss       = Math.round(parseFloat(d?.trend_score         ?? 65.2));
  const tqs       = Math.round(parseFloat(d?.trend_quality_score ?? 46));
  const ltScore   = parseFloat(d?.lt_trend_score  ?? 94.4).toFixed(0);
  const stScore   = parseFloat(d?.st_trend_score  ?? 55.3).toFixed(0);
  const cagr      = parseFloat(d?.cagr            ?? 37.3).toFixed(1);
  const r2        = parseFloat(d?.r_squared       ?? 0.844).toFixed(2);
  const sma20     = parseFloat(d?.sma20           ?? 196.17).toFixed(2);
  const sma50     = parseFloat(d?.sma50           ?? 186.94).toFixed(2);
  const sma200    = parseFloat(d?.sma200          ?? 183.72).toFixed(2);
  const suppLvl   = parseFloat(d?.support_level   ?? 175.56).toFixed(2);
  const suppTests = d?.support_touches            ?? 46;
  const resLvl    = parseFloat(d?.resist_level    ?? 211.34).toFixed(2);
  const resTests  = d?.resist_touches             ?? 2;
  const t12       = parseFloat(d?.tsmom_12m       ?? 60.12).toFixed(1);
  const t6        = parseFloat(d?.tsmom_6m        ?? -6.53).toFixed(1);
  const t3        = parseFloat(d?.tsmom_3m        ?? -6.49).toFixed(1);
  const momScore  = Math.round(parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86));
  const ldScore   = Math.round(parseFloat(d?.dd_momentum_score ?? 53.86));
  const fip       = parseFloat(d?.fip_score       ?? -0.034).toFixed(3);
  const joursPos  = parseFloat(d?.positive_days_pct ?? 51.7).toFixed(1);
  const r20d      = parseFloat(d?.volume_ratio_20d ?? 1.4767).toFixed(2);
  const r60d      = parseFloat(d?.volume_ratio_60d ?? 1.2805).toFixed(2);
  const suppVR    = parseFloat(d?.support_vol_ratio ?? 1.043).toFixed(3);
  const wyckoff   = d?.wyckoff_context            ?? "Distribution";
  const wyEv      = d?.wyckoff_event              ?? "UTAD";
  const m1        = parseFloat(d?.last_return_m1  ?? 14.43).toFixed(1);
  const vScore    = Math.round(parseFloat(d?.volume_score ?? 68));
  const rsi14     = parseFloat(d?.rsi_14          ?? 60.8).toFixed(1);
  const zScore    = parseFloat(d?.z_score         ?? 1.317).toFixed(2);
  const oppScore  = parseFloat(d?.opportunity_score ?? 9.21).toFixed(0);
  const timingScore = Math.round(parseFloat(d?.timing_quality ?? 40));
  const coherence = parseInt(d?.coherence_score   ?? -1);
  const tscore    = parseFloat(d?.tscore          ?? 32.3).toFixed(0);
  const structColor = colorForPct(tss);
  const momColor    = colorForPct(momScore);
  const volColor    = colorForPct(vScore);
  const timColor    = colorForPct(timingScore);

  return {
    structure: {
      title:"Structure de Tendance", icon:"📈", color:structColor,
      sections:[
        {h:"Qu'est-ce que la structure de tendance ?", p:"La structure de tendance évalue la direction du prix sur trois horizons : long terme (5 ans), annuel et primaire. Une tendance haussière est caractérisée par une succession de hauts et bas croissants — c'est la donnée la plus importante de l'analyse."},
        {h:"Trend Quality & Strength Scores", p:`Le Trend Quality Score (${tqs}/100) mesure la régularité de la progression via le R² de la régression (${r2}). Le Trend Strength Score (${tss}/100) évalue l'intensité directionnelle. Le score LT (${ltScore}/100) confirme une structure long terme solide. Score CT (${stScore}/100) : momentum 1 an modéré.`},
        {h:"Moyennes mobiles et alignement", p:`SMA 20 : ${sma20} · SMA 50 : ${sma50} · SMA 200 : ${sma200}. L'alignement SMA20 > SMA50 > SMA200 est la configuration haussière idéale selon Murphy (TAOTFM ch. 9). Un CAGR de +${cagr}% confirme la qualité de la croissance à long terme.`},
        {h:"Supports et résistances", p:`Support principal : ${suppLvl} (${suppTests} tests réussis) — zone d'accumulation historique majeure. Résistance principale : ${resLvl} (${resTests} tests). La significativité d'un niveau est proportionnelle au nombre de tests et au volume associé (Edwards & Magee, TAST ch. 13).`},
        {h:"Interprétation Invelps", p:`Score ${tss}/100 : ${tss >= 75 ? "structure haussière de haute qualité" : tss >= 55 ? "tendance globalement positive" : "structure sous pression"}. R² ${r2} — ${parseFloat(r2) >= 0.75 ? "très bonne cohérence de tendance" : "cohérence modérée"}. CAGR +${cagr}% sur l'historique complet.`},
      ]
    },
    momentum: {
      title:"Force & Momentum", icon:"⚡", color:momColor,
      sections:[
        {h:"Qu'est-ce que le momentum ?", p:"Le momentum mesure la vitesse et la persistance du mouvement de prix. Un titre en fort momentum tend statistiquement à continuer sa direction sur 3 à 12 mois — c'est l'inertie de marché documentée sur l'ensemble des marchés financiers développés (Moskowitz, Ooi & Pedersen, JFE 2012)."},
        {h:"Time-Series Momentum (TSMOM)", p:`TSMOM 12M (${parseFloat(t12)>=0?"+":""}${t12}%) = momentum long terme ${parseFloat(t12)>=20?"très fort":"modéré"}. TSMOM 6M (${parseFloat(t6)>=0?"+":""}${t6}%) et TSMOM 3M (${parseFloat(t3)>=0?"+":""}${t3}%) : ${parseFloat(t12)>0 && parseFloat(t3)<0?"divergence court terme — tendance LT intacte, essoufflement récent":"horizons alignés"}. Gray & Vogel : surveiller le FIP pour distinguer correction saine de retournement.`},
        {h:"Leadership Score et FIP", p:`Le Leadership Score (${ldScore}/100) indique ${ldScore>=70?"que le titre surperforme son univers":"un leadership modéré dans l'univers"}. Le FIP score (${fip}) reflète l'équilibre jours positifs/négatifs (${joursPos}% de séances positives). Ces métriques mesurent la qualité interne du momentum.`},
        {h:"La divergence de momentum", p:`${parseFloat(t12)>0 && parseFloat(t3)<0?"Divergence confirmée : TSMOM 12M positif mais CT négatif. Ce n'est pas un signal de retournement, mais de prudence — Murphy (TAOTFM ch. 10) : l'une des configurations les plus précieuses de l'AT. Une reprise du TSMOM 3M au-dessus de zéro confirmerait la poursuite.":"Horizons momentum convergents — signal de cohérence directionnelle."}`},
        {h:"Interprétation Invelps", p:`Score ${momScore}/100 : momentum ${momScore>=75?"solide":"modéré à faible"}. Direction : ${d?.momentum_direction ?? "Retournement baissier"}. ${momScore<55?"Signal d'essoufflement — attendre la confirmation d'une reprise CT avant toute entrée.":"Le signal long terme reste dominant."}`},
      ]
    },
    volume: {
      title:"Participation & Volume", icon:"📊", color:volColor,
      sections:[
        {h:"Pourquoi les volumes sont essentiels", p:"Le volume confirme ou infirme la tendance de prix. Une hausse sur volumes croissants est solide — sans conviction institutionnelle large, le mouvement est fragile. Murphy : « le volume confirme la tendance » (TAOTFM ch. 7)."},
        {h:"Les ratios de volume Invelps", p:`Ratio 20J : ${r20d}x ${parseFloat(r20d)>=1.2?"= participation institutionnelle solide sur le mois":"= participation dans la norme"}. Ratio 60J : ${r60d}x ${parseFloat(r60d)>=1.0?"= intérêt soutenu sur 2 mois":"= légère baisse structurelle"}. Support Vol. Ratio : ${suppVR} (${parseFloat(suppVR)>=1.1?"acheteurs plus actifs sur les zones support":"neutre"}). M1 : ${parseFloat(m1)>=0?"+":""}${m1}%.`},
        {h:`Phase Wyckoff : ${wyckoff}${wyEv && wyEv!=="None"?" — "+wyEv:""}`, p:`Le cycle de Wyckoff identifie 4 phases : Accumulation → Markup → Distribution → Markdown. ${wyckoff==="Distribution"?`La ${wyEv==="UTAD"?"phase UTAD (Upthrust After Distribution) signale une fausse cassure haussière — les institutions vendent à la foule enthousiaste. Signal baissier majeur selon Villahermosa.":"Distribution désigne la phase où les grands opérateurs allègent progressivement. La pression vendeuse s'accumule en coulisses."}`:wyckoff==="Accumulation"?"L'Accumulation désigne la phase où les grands opérateurs construisent leurs positions avant la hausse.":"La phase actuelle mérite surveillance."}`},
        {h:"Confirmation ou Divergence Prix/Volume", p:`M1 : ${parseFloat(m1)>=0?"+":""}${m1}% avec ratio volume 20J : ${r20d}x. ${parseFloat(m1)>0 && parseFloat(r20d)>=1.2?"Confirmation Prix/Volume : hausse accompagnée de volumes supérieurs — configuration idéale selon Murphy et Wyckoff.":parseFloat(m1)>0 && parseFloat(r20d)<1.0?"Divergence : hausse sans volume — fragilité à surveiller.":"Contexte volumétrique neutre."}`},
        {h:"Interprétation Invelps", p:`Score ${vScore}/100 : participation ${vScore>=75?"solide":vScore>=55?"acceptable":"sous la moyenne"}. Phase ${wyckoff}${wyEv&&wyEv!=="None"?" ("+wyEv+")":""} — ${wyckoff==="Distribution"?"signal de prudence sur les entrées court terme, pas de signal de vente automatique.":"contexte à surveiller."}`},
      ]
    },
    timing: {
      title:"Timing & Opportunité", icon:"⏱", color:timColor,
      sections:[
        {h:"RSI et zones d'entrée", p:`RSI 14 : ${rsi14} — ${parseFloat(rsi14)>=70?"zone de surachat (>70) : prudence accrue sur les entrées immédiates":parseFloat(rsi14)>=55?"zone de force : momentum positif confirmé":parseFloat(rsi14)<=30?"zone de survente (<30) : potentielle opportunité de rebond":"zone neutre"}. La zone idéale d'achat en tendance haussière se situe entre 40 et 55 — sur correction secondaire.`},
        {h:"Z-Score et canal statistique", p:`Z-Score ${parseFloat(zScore)>=0?"+":""}${zScore} : le prix est ${Math.abs(parseFloat(zScore)).toFixed(2)} écart${Math.abs(parseFloat(zScore))>1?"s":""}-type ${parseFloat(zScore)>=0?"au-dessus":"en dessous"} de sa moyenne historique. Les zones >+2σ sont statistiquement rares (~5% des observations). Statut : ${d?.crossover_signal ?? "+1σ breakout"} — ${d?.valuation_status ?? "Surévalué"}.`},
        {h:"Scores d'opportunité et de timing", p:`L'Opportunity Score (${oppScore}/100) et le Timing Score (${timingScore}/100) indiquent ${parseInt(oppScore)<30?"un point d'entrée défavorable — risque de piège":parseInt(oppScore)<60?"un contexte de tendance forte mais point d'entrée sous-optimal":"un timing favorable"}. ${parseInt(oppScore)<30?"Attendre un repli vers les SMAs améliorerait significativement le ratio risque/rendement.":""}`},
        {h:"Cohérence inter-piliers", p:`Cohérence : ${coherence >= 3 ? "forte convergence haussière" : coherence >= 1 ? "légère convergence haussière" : coherence >= -1 ? "signaux en tension — point de bascule" : "divergence baissière marquée"} (score ${coherence}/6). TScore global : ${tscore}/100 — ${parseInt(tscore)>=75?"signal fort":parseInt(tscore)>=50?"signal mixte":"signal faible"}.`},
        {h:"Interprétation Invelps", p:`Score ${timingScore}/100 : ${timingScore>=60?"timing d'entrée acceptable":timingScore>=40?"timing sous-optimal — patienter pour un meilleur point d'entrée":"timing défavorable — éviter les positions longues"}. Scénario optimal : ${parseFloat(sma20)>0?`attendre un repli vers SMA20 (${sma20}) ou SMA50 (${sma50})`:"attendre une reprise du momentum CT"} avant d'initier ou renforcer une position.`},
      ]
    },
  };
}
// Fallback statique si d non disponible (même structure)
const ANALYSE_STATIC = buildAnalyse(null);

function AnalyseModal({ pillar, onClose, d }) {
  const ANALYSE = buildAnalyse(d);
  const content = ANALYSE[pillar];
  useEffect(()=>{
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  },[]);
  if (!content) return null;
  return (
    <div onClick={onClose} style={{
      position:"fixed", inset:0, zIndex:2000,
      background:"rgba(0,0,0,.45)",
      display:"flex", alignItems:"flex-end", justifyContent:"center",
    }}>
      <div onClick={e=>e.stopPropagation()} style={{
        background:"var(--card-bg)",
        border:"1px solid var(--card-bdr)",
        borderRadius:"20px 20px 0 0",
        width:"100%", maxWidth:600, maxHeight:"84vh",
        overflowY:"auto", padding:"22px 20px 40px",
        boxShadow:"0 -8px 40px rgba(0,0,0,.25)",
      }}>
        {/* Header */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:18 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:10,
              background:`${content.color}18`, border:`1px solid ${content.color}44`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:18, flexShrink:0 }}>{content.icon}</div>
            <div>
              <div style={{ fontSize:13, fontWeight:800, color:content.color,
                fontFamily:"ui-monospace,monospace" }}>{content.title}</div>
              <div style={{ fontSize:10, color:"var(--mu)", fontWeight:600, marginTop:1 }}>
                Analyse technique Invelps
              </div>
            </div>
          </div>
          <button onClick={onClose}
            style={{ background:"none", border:"1px solid var(--sep)", borderRadius:8,
              width:30, height:30, display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:13, color:"var(--mu)", cursor:"pointer" }}>✕</button>
        </div>
        {/* Sections */}
        {content.sections.map((s,i) => (
          <div key={i} style={{ marginBottom:16, paddingBottom:16,
            borderBottom: i<content.sections.length-1 ? "1px solid var(--sep)" : "none" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:7 }}>
              <div style={{ width:21, height:21, borderRadius:999, background:content.color,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:9, fontWeight:900, color:"#fff", flexShrink:0 }}>{i+1}</div>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--ts)" }}>{s.h}</div>
            </div>
            <p style={{ fontSize:12, color:"var(--mu)", lineHeight:1.72,
              fontWeight:500, paddingLeft:29 }}>{s.p}</p>
          </div>
        ))}
        <button onClick={onClose}
          style={{ marginTop:14, width:"100%", padding:"12px", borderRadius:12,
            border:"none", background:content.color, color:"#fff",
            fontSize:13, fontWeight:800, cursor:"pointer" }}>Fermer</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 6. X-AXIS LABEL GENERATOR — adaptatif selon la période
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// NVDA DATA for ProfilDescriptionRadarCard
// ══════════════════════════════════════════════════════════════════
// ── Fallbacks développement — valeurs réelles NVDA au 2026-04-30 ─────────────
// En production ces constantes sont remplacées par d prop (voir composant principal)
const NVDA_COMPANY_DEFAULT = {
  name:"NVDA", fullName:"NVIDIA Corporation",
  exchange:"NGS", sector:"Information Technology",
  industry:"Semiconductors", region:"North America",
  marketCapClass:"Mega Cap", style:"High_Quality",
  cyclicals:"Asset Plays",
  price:199.57, mtdPct:14.43, ytdPct:7.01,
  description:"NVIDIA Corporation provides graphics, and compute and networking solutions in the United States, Taiwan, China, and internationally. The company's Graphics segment offers GeForce GPUs for gaming and PCs, the GeForce NOW game streaming service and related infrastructure, and solutions for gaming platforms; Quadro/NVIDIA RTX GPUs for enterprise workstation graphics; vGPU software for cloud-based visual and virtual computing; automotive platforms for infotainment systems; and Omniverse software for building 3D designs and virtual worlds. Its Compute & Networking segment provides Data Center platforms and systems for AI, HPC, and accelerated computing; Mellanox networking and interconnect solutions; automotive AI Cockpit, autonomous driving development agreements, and autonomous vehicle solutions; cryptocurrency mining processors; Jetson for robotics and other embedded platforms; and NVIDIA AI Enterprise and other software.",
};
// StockDashboard_Decisions.iscore
const NVDA_ISCORE_DEFAULT = 76.08;
// StockDashboard_Decisions : radar_fondamentaux / radar_valorisation / radar_marche / radar_momentum / radar_risque_rendement
const NVDA_RADAR_DEFAULT = [
  { axis:"Fondamentaux", value: 93.76 },
  { axis:"Valorisation", value: 61.53 },
  { axis:"Marché",       value: 74.75 },
  { axis:"Momentum",     value: 53.86 },
  { axis:"Risque/Rend.", value: 85.69 },
];

function buildXLabels(data, range, width = 900) {
  if (!data.length) return [];

  const FR_MONTHS = ["Jan","Fév","Mar","Avr","Mai","Juin","Juil","Août","Sep","Oct","Nov","Déc"];

  // 1) On identifie la première barre disponible de chaque mois.
  //    L’axe X reste donc toujours en lecture mensuelle, même si les données sont daily.
  const monthTicks = [];
  let lastKey = null;
  data.forEach((d, i) => {
    const dt = new Date(d.d);
    if (Number.isNaN(dt.getTime())) return;
    const key = `${dt.getFullYear()}-${dt.getMonth()}`;
    if (key !== lastKey) {
      lastKey = key;
      monthTicks.push({
        i,
        month: dt.getMonth(),
        year: dt.getFullYear(),
        isJanuary: dt.getMonth() === 0,
      });
    }
  });

  if (!monthTicks.length) return [];

  // 2) Lecture mensuelle forcée sur TOUS les horizons.
  //    On garde une graduation et un libellé par mois.
  //    Pour les horizons longs, les libellés seront inclinés dans le SVG afin
  //    d’éviter le chevauchement sans repasser en trimestriel/annuel.
  return monthTicks.map((t, idx) => {
    const showYear = t.isJanuary || idx === 0;
    return {
      ...t,
      idx,
      tick: true,
      major: t.isJanuary || idx === 0,
      label: `${FR_MONTHS[t.month]}${showYear ? ` '${String(t.year).slice(2)}` : ""}`,
    };
  });
}

// ══════════════════════════════════════════════════════════════════
// 7. CANDLE CHART v6
//    • SMAs affichées SEULEMENT si période ≤ 1Y (daily mode)
//    • Bougies corrigées (bW recalculé depuis W réel)
//    • Panneaux Volume + RSI14 sous le prix
//    • Axe X adaptatif
// ══════════════════════════════════════════════════════════════════

// Compute RSI14 on data array using closes
function computeRSI14(closes) {
  const period = 14;
  const rsi = new Array(closes.length).fill(null);
  if (closes.length < period + 1) return rsi;
  let avgGain = 0, avgLoss = 0;
  for (let i = 1; i <= period; i++) {
    const diff = closes[i] - closes[i-1];
    if (diff > 0) avgGain += diff; else avgLoss -= diff;
  }
  avgGain /= period; avgLoss /= period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < closes.length; i++) {
    const diff = closes[i] - closes[i-1];
    const gain = diff > 0 ? diff : 0;
    const loss = diff < 0 ? -diff : 0;
    avgGain = (avgGain * (period-1) + gain) / period;
    avgLoss = (avgLoss * (period-1) + loss) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return rsi;
}

function CandleChart({ data, range, showSMA20, showSMA50, showSMA200, showReg, showBands, showZScore, showVolume, showRSI, showLevels, srLevels, chartType, isDark }) {
  const SR_LEVELS = srLevels || {
    support:    { price: 175.56, label: "Support",    tests: 46, col: "#6f8a5b", volR: 1.043 },
    resistance: { price: 211.34, label: "Résistance", tests: 2,  col: "#c6523f", volR: 1.377 },
  };
  const wrapRef = useRef(null);
  const [W, setW] = useState(360);
  const [hov, setHov] = useState(null);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width || 280)));
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  // SMAs only shown for ≤1Y
  const isShortRange = ["3M","6M","1Y"].includes(range);
  const effectiveSMA20  = showSMA20  && isShortRange;
  const effectiveSMA50  = showSMA50  && isShortRange;
  const effectiveSMA200 = showSMA200 && isShortRange;

  // Panel heights
  const compact = W < 520;
  const priceH  = Math.max(compact ? 190 : 220, Math.min(380, W * (compact ? .56 : .40)));
  const volH    = showVolume ? (compact ? 42 : 52) : 0;
  const rsiH    = showRSI   ? (compact ? 112 : 140) : 0;
  const zH      = showZScore ? (compact ? 50 : 62) : 0;
  const SEP     = compact ? 7 : 10; // gap between panels
  const PAD     = compact ? { t:8, r:46, b:42, l:30 } : { t:8, r:60, b:46, l:42 };

  // Layout Y offsets
  const priceTop = 0;
  const volTop   = priceH + (showVolume ? SEP : 0);
  const rsiTop   = volTop + volH + (showRSI ? SEP : 0);
  const zTop     = rsiTop + rsiH + (showZScore ? SEP : 0);
  const totalH   = priceH
    + (showVolume ? volH + SEP : 0)
    + (showRSI    ? rsiH + SEP : 0)
    + (showZScore ? zH   + SEP : 0)
  ;

  const cW = W - PAD.l - PAD.r;
  const cH = priceH - PAD.t - PAD.b;

  const n    = data.length;
  const slot = cW / Math.max(1, n);
  // Fix: use real pixel width, ensure minimum 1.5px candle
  const bW   = Math.max(1.5, Math.min(12, slot * 0.72));
  const cx   = i => PAD.l + (i + 0.5) * slot;

  // RSI14 — lu directement depuis timing_marche_chart (colonne rsi14)
  // Fallback JS si rsi14 absent (données sans la colonne)
  const rsiFromDB  = useMemo(() => data.map(d => d.rsi14 ?? null), [data]);
  const rsiFromJS  = useMemo(() => {
    if (rsiFromDB.some(v => v !== null)) return null; // DB disponible → pas de calcul JS
    const closes = data.map(d => d.c);
    const period = 14;
    const rsi = new Array(closes.length).fill(null);
    if (closes.length < period + 1) return rsi;
    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i <= period; i++) {
      const diff = closes[i] - closes[i-1];
      if (diff > 0) avgGain += diff; else avgLoss -= diff;
    }
    avgGain /= period; avgLoss /= period;
    rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    for (let i = period + 1; i < closes.length; i++) {
      const diff = closes[i] - closes[i-1];
      const gain = diff > 0 ? diff : 0;
      const loss = diff < 0 ? -diff : 0;
      avgGain = (avgGain * (period-1) + gain) / period;
      avgLoss = (avgLoss * (period-1) + loss) / period;
      rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
    }
    return rsi;
  }, [data, rsiFromDB]);
  const rsiArr = rsiFromDB.some(v => v !== null) ? rsiFromDB : (rsiFromJS ?? []);

  // Price domain
  const priceDomainVals = data.flatMap(d => {
    const vals = [d.h, d.l];
    if (showBands)      { if (d.b2p) vals.push(d.b2p); if (d.b2n) vals.push(d.b2n); }
    if (effectiveSMA20  && d.s20)  vals.push(d.s20);
    if (effectiveSMA50  && d.s50)  vals.push(d.s50);
    if (effectiveSMA200 && d.s200) vals.push(d.s200);
    return vals;
  }).filter(v => v != null && isFinite(v));
  // Include S/R levels in domain so they don't fall outside chart
  if (showLevels) {
    priceDomainVals.push(SR_LEVELS.support.price, SR_LEVELS.resistance.price);
  }
  const minP   = Math.min(...priceDomainVals) * 0.993;
  const maxP   = Math.max(...priceDomainVals) * 1.007;
  const rangeP = maxP - minP || 1;
  const py = v => PAD.t + cH - ((v - minP) / rangeP * cH);

  // Volume domain
  const maxVol = Math.max(...data.map(d => d.v || 0));

  // RSI domain
  const rsiPH = Math.max(82, rsiH - 28); // pixel height for rsi plot
  const rsiY  = (v) => rsiTop + PAD.t + rsiPH - ((v - 0) / 100 * rsiPH);

  // Z-Score
  const zMin=-3.5, zMax=3.5;
  const zPH = zH - 20;
  const zY = z => zTop + PAD.t + zPH - ((z - zMin) / (zMax - zMin) * zPH);
  const zPath = data.map((d,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${zY(d.z ?? 0).toFixed(1)}`).join(" ");
  const lastZ = data[n-1]?.z ?? 0;
  const zCol  = lastZ>2?C.red:lastZ>1?C.amber:lastZ<-1?"#4a7fa0":C.amber;

  // RSI line / area paths
  let rsiPath = "";
  let rsiAreaPath = "";
  let rsiStarted = false;
  let rsiFirstX = null;
  let rsiLastX = null;
  data.forEach((d,i) => {
    const rv = rsiArr[i];
    if (rv == null) return;
    const x = cx(i).toFixed(1);
    const y = rsiY(rv).toFixed(1);
    if (!rsiStarted) {
      rsiPath += `M${x},${y} `;
      rsiAreaPath += `M${x},${rsiY(50).toFixed(1)} L${x},${y} `;
      rsiStarted = true;
      rsiFirstX = x;
    } else {
      rsiPath += `L${x},${y} `;
      rsiAreaPath += `L${x},${y} `;
    }
    rsiLastX = x;
  });
  if (rsiStarted) rsiAreaPath += `L${rsiLastX},${rsiY(50).toFixed(1)} L${rsiFirstX},${rsiY(50).toFixed(1)} Z`;
  const lastRSI = rsiArr[n-1];
  const rsiStateCol  = lastRSI >= 70 ? C.red : lastRSI <= 30 ? C.green : RSI_COL.line;

  // SMA path builder
  const smaPath = (key, col) => {
    let path = "", prev = false;
    data.forEach((d,i) => {
      if (d[key] != null) {
        path += prev ? `L${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} ` : `M${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} `;
        prev = true;
      } else { prev = false; }
    });
    return path ? <path d={path} fill="none" stroke={col} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity=".95"/> : null;
  };

  // Sigma dashed path
  const sigPath = (key, cfg) => {
    let path = "", prev = false;
    data.forEach((d,i) => {
      if (d[key] != null) {
        path += prev ? `L${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} ` : `M${cx(i).toFixed(1)},${py(d[key]).toFixed(1)} `;
        prev = true;
      } else { prev = false; }
    });
    return path ? <path d={path} fill="none" stroke={cfg.col} strokeWidth={cfg.w} strokeDasharray={cfg.dash} opacity=".88"/> : null;
  };

  // Line + area
  const linePath = data.map((d,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${py(d.c).toFixed(1)}`).join(" ");
  const areaPath = linePath + ` L${cx(n-1).toFixed(1)},${(PAD.t+cH).toFixed(1)} L${cx(0).toFixed(1)},${(PAD.t+cH).toFixed(1)} Z`;

  const yTicks   = Array.from({length:7}, (_,i) => minP + rangeP*i/6);
  const xLabels  = useMemo(() => buildXLabels(data, range, W), [data, range, W]);
  const mutC     = isDark ? "#7a8172" : "#6f7b74";
  const gridC    = isDark ? "rgba(111,138,91,.08)" : "rgba(30,40,34,.05)";

  const hovD     = hov != null ? data[hov] : null;
  const hovLabel = hovD ? new Date(hovD.d).toLocaleDateString("fr-FR",{day:"2-digit",month:"short"}) : "";
  const hovRSI   = hov != null ? rsiArr[hov] : null;

  return (
    <div ref={wrapRef} style={{ width:"100%", overflow:"hidden" }}>
      <svg
        viewBox={`0 0 ${W} ${totalH}`}
        style={{ width:"100%", height:"auto", display:"block", cursor:"crosshair", overflow:"hidden" }}
        onMouseMove={e => {
          const rect = e.currentTarget.getBoundingClientRect();
          const rx = (e.clientX - rect.left) / rect.width * W;
          const i = Math.max(0, Math.min(n-1, Math.round((rx - PAD.l) / slot - 0.5)));
          setHov(i);
        }}
        onMouseLeave={() => setHov(null)}
      >
        <defs>
          <linearGradient id="tm6-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RSI_COL.line} stopOpacity=".18"/>
            <stop offset="100%" stopColor={RSI_COL.line} stopOpacity="0"/>
          </linearGradient>
          <linearGradient id="tm6-volg" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.amber} stopOpacity=".35"/>
            <stop offset="100%" stopColor={C.amber} stopOpacity=".1"/>
          </linearGradient>
          <linearGradient id="tm6-rsig" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={RSI_COL.line} stopOpacity=".34"/>
            <stop offset="100%" stopColor={RSI_COL.line} stopOpacity=".04"/>
          </linearGradient>
          <clipPath id="tm6-price"><rect x={PAD.l} y={PAD.t-2} width={cW} height={cH+4}/></clipPath>
          <clipPath id="tm6-vol"><rect x={PAD.l} y={volTop} width={cW} height={volH+2}/></clipPath>
          <clipPath id="tm6-rsi"><rect x={PAD.l} y={rsiTop} width={cW} height={rsiH+2}/></clipPath>
          <clipPath id="tm6-z"><rect x={PAD.l} y={zTop} width={cW} height={zH+2}/></clipPath>
        </defs>

        {/* ── PRICE PANEL ── */}

        {/* Y price grid */}
        {yTicks.map((v,i) => (
          <line key={i} x1={PAD.l} x2={PAD.l+cW} y1={py(v).toFixed(1)} y2={py(v).toFixed(1)}
            stroke={gridC} strokeWidth=".8"/>
        ))}
        {/* Y price labels */}
        {yTicks.filter((_,i)=>i%2===0).map((v,i) => (
          <text key={i} x={PAD.l-4} y={(py(v)+3).toFixed(1)} textAnchor="end"
            fill={mutC} fontSize="8" fontFamily="ui-monospace,monospace">{Math.round(v)}</text>
        ))}

        {/* Sigma bands */}
        {showBands && (
          <g clipPath="url(#tm6-price)">
            {sigPath("b2p", SIG.b2p)}
            {sigPath("b1p", SIG.b1p)}
            {showReg && sigPath("reg", SIG.reg)}
            {sigPath("b1n", SIG.b1n)}
            {sigPath("b2n", SIG.b2n)}
          </g>
        )}
        {!showBands && showReg && (
          <g clipPath="url(#tm6-price)">{sigPath("reg", SIG.reg)}</g>
        )}

        {/* SMAs — only for short ranges */}
        <g clipPath="url(#tm6-price)">
          {effectiveSMA200 && smaPath("s200", SMA_COL.s200)}
          {effectiveSMA50  && smaPath("s50",  SMA_COL.s50)}
          {effectiveSMA20  && smaPath("s20",  SMA_COL.s20)}
        </g>

        {/* ── SUPPORT / RESISTANCE LEVELS ── */}
        {showLevels && [SR_LEVELS.support, SR_LEVELS.resistance].map(lvl => {
          const y = py(lvl.price);
          const isVisible = y >= PAD.t - 2 && y <= PAD.t + cH + 2;
          if (!isVisible) return null;
          const isSupport = lvl.col === SR_LEVELS.support.col;
          const labelBg   = isSupport ? `${C.green}22` : `${C.red}22`;
          const testStr   = `${lvl.tests} test${lvl.tests > 1 ? "s" : ""}`;
          return (
            <g key={lvl.label}>
              {/* Dashed horizontal line full width */}
              <line
                x1={PAD.l} x2={PAD.l + cW}
                y1={y.toFixed(1)} y2={y.toFixed(1)}
                stroke={lvl.col} strokeWidth="1.5"
                strokeDasharray="6 4" opacity=".8"/>
              {/* Label badge on right side */}
              <rect x={PAD.l + cW + 2} y={(y - 9).toFixed(1)} width={52} height={17} rx={3}
                fill={lvl.col} opacity=".9"/>
              <text x={PAD.l + cW + 28} y={(y + 4).toFixed(1)}
                textAnchor="middle" fontSize="7.5" fontWeight="800"
                fill="#fff" fontFamily="ui-monospace,monospace">
                {lvl.price.toFixed(2)}
              </text>
              {/* Label text left side (inside chart) */}
              <rect x={PAD.l + 4} y={(y - 14).toFixed(1)} width={70} height={13} rx={3}
                fill={labelBg} stroke={lvl.col} strokeWidth=".8" opacity=".9"/>
              <text x={PAD.l + 8} y={(y - 5).toFixed(1)}
                fontSize="7.5" fontWeight="800"
                fill={lvl.col} fontFamily="ui-monospace,monospace">
                {lvl.label} ({testStr})
              </text>
            </g>
          );
        })}

        {/* Price candles or line */}
        <g clipPath="url(#tm6-price)">
          {chartType === "line" ? (
            <>
              <path d={areaPath} fill="url(#tm6-grad)"/>
              <path d={linePath} fill="none" stroke={RSI_COL.line} strokeWidth="2"/>
            </>
          ) : (
            data.map((d,i) => {
              const bull  = d.c >= d.o;
              const col   = bull ? C.green : C.red;
              const x     = cx(i);
              const bodyT = py(Math.max(d.o, d.c));
              const bodyB = py(Math.min(d.o, d.c));
              const bodyH = Math.max(1.5, bodyB - bodyT);
              return (
                <g key={i} opacity={hov!=null&&hov!==i?0.38:1} style={{transition:"opacity .04s"}}>
                  <line x1={x.toFixed(1)} x2={x.toFixed(1)}
                    y1={py(d.h).toFixed(1)} y2={py(d.l).toFixed(1)}
                    stroke={col} strokeWidth="1" opacity=".85"/>
                  <rect
                    x={(x - bW/2).toFixed(1)} y={bodyT.toFixed(1)}
                    width={Math.max(1, bW).toFixed(1)} height={bodyH.toFixed(1)}
                    fill={bull ? col : col}
                    fillOpacity={bull ? 0.88 : 0.95}
                    stroke={col} strokeWidth="0.4"/>
                </g>
              );
            })
          )}
        </g>

        {/* Current price tag */}
        {(() => {
          const last = data[n-1];
          const y = py(last.c);
          return (
            <g>
              <line x1={PAD.l} x2={PAD.l+cW} y1={y.toFixed(1)} y2={y.toFixed(1)}
                stroke={C.red} strokeWidth="1" strokeDasharray="3 3" opacity=".45"/>
              <rect x={PAD.l+cW+2} y={(y-9).toFixed(1)} width={52} height={16} rx={3} fill={C.red}/>
              <text x={PAD.l+cW+28} y={(y+4.5).toFixed(1)} textAnchor="middle"
                fontSize="8.5" fontWeight="800" fill="#fff" fontFamily="ui-monospace,monospace">
                {last.c.toFixed(1)}
              </text>
            </g>
          );
        })()}

        {/* X axis — repères mensuels */}
        {xLabels.map(({i, major}) => (
          <line key={`x-tick-${i}`}
            x1={cx(i).toFixed(1)} x2={cx(i).toFixed(1)}
            y1={(PAD.t + cH + 2).toFixed(1)} y2={(PAD.t + cH + (major ? 10 : 6)).toFixed(1)}
            stroke={major ? C.amber : mutC} strokeWidth={major ? "1" : ".7"} opacity={major ? ".72" : ".38"}/>
        ))}
        {xLabels.filter(({label}) => label).map(({i, label, major}) => {
          const x = cx(i);
          const y = PAD.t + cH + PAD.b - 5;
          const rotate = xLabels.length > 14;
          return (
            <text key={`x-label-${i}`} x={x.toFixed(1)} y={y.toFixed(1)}
              transform={rotate ? `rotate(-45 ${x.toFixed(1)} ${y.toFixed(1)})` : undefined}
              textAnchor={rotate ? "end" : "middle"}
              fill={major ? C.amber : mutC} fontSize={rotate ? "7.2" : "8.3"} fontWeight={major ? "800" : "600"}
              fontFamily="ui-sans-serif">{label}</text>
          );
        })}

        {/* Crosshair */}
        {hov != null && (
          <g>
            <line x1={cx(hov).toFixed(1)} x2={cx(hov).toFixed(1)}
              y1={PAD.t} y2={PAD.t+cH} stroke={C.amber} strokeWidth="1" strokeDasharray="3 3" opacity=".55"/>
            {data[hov] && (
              <line x1={PAD.l} x2={PAD.l+cW}
                y1={py(data[hov].c).toFixed(1)} y2={py(data[hov].c).toFixed(1)}
                stroke={C.amber} strokeWidth="1" strokeDasharray="3 3" opacity=".35"/>
            )}
          </g>
        )}

        {/* OHLC tooltip bubble */}
        {hovD && (() => {
          const bw2 = 142, bh = showRSI ? 98 : 80;
          const bx  = Math.min(cx(hov) + 10, PAD.l + cW - bw2 - 2);
          const by  = Math.max(PAD.t + 2, py(hovD.c) - bh - 6);
          const bull = hovD.c >= hovD.o;
          const priceCol = bull ? (isDark?"#8da57a":C.green) : (isDark?"#e07a6a":C.red);
          return (
            <g>
              <rect x={bx} y={by} width={bw2} height={bh} rx={7}
                fill={isDark?"#0d1a2e":"#fff"}
                stroke={isDark?"rgba(111,138,91,.22)":"rgba(30,40,34,.11)"}
                strokeWidth="1" opacity=".97"/>
              <text x={bx+8} y={by+14} fontSize="9" fontWeight="700"
                fill={isDark?"#e8e4da":"#1f2a24"} fontFamily="ui-monospace,monospace">{hovLabel}</text>
              {[["O",hovD.o],["H",hovD.h],["L",hovD.l],["C",hovD.c]].map(([k,v],ii) => (
                <g key={k}>
                  <text x={bx+8+(ii%2)*68} y={by+28+Math.floor(ii/2)*18}
                    fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">{k}</text>
                  <text x={bx+19+(ii%2)*68} y={by+28+Math.floor(ii/2)*18}
                    fontSize="8.5" fontWeight="700" fill={priceCol} fontFamily="ui-monospace,monospace">{v.toFixed(2)}</text>
                </g>
              ))}
              {hovRSI != null && (
                <g>
                  <text x={bx+8} y={by+70} fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">RSI 14</text>
                  <text x={bx+52} y={by+70} fontSize="8.5" fontWeight="700"
                    fill={hovRSI>=70?C.red:hovRSI<=30?C.green:RSI_COL.line} fontFamily="ui-monospace,monospace">{hovRSI.toFixed(1)}</text>
                  {hovD.v && (
                    <>
                      <text x={bx+8} y={by+84} fontSize="8.5" fontWeight="600" fill={mutC} fontFamily="ui-monospace,monospace">Vol.</text>
                      <text x={bx+30} y={by+84} fontSize="8.5" fontWeight="700"
                        fill={hovD.c>=hovD.o?C.green:C.red} fontFamily="ui-monospace,monospace">
                        {(hovD.v/1e6).toFixed(1)}M
                      </text>
                    </>
                  )}
                </g>
              )}
            </g>
          );
        })()}

        {/* ── VOLUME PANEL ── */}
        {showVolume && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={volTop} y2={volTop} stroke={gridC} strokeWidth="1"/>
            <text x={PAD.l+4} y={volTop+11} fontSize="8" fontWeight="700" fill={mutC}>VOL</text>
            <g clipPath="url(#tm6-vol)">
              {data.map((d,i) => {
                if (!d.v) return null;
                const barH = Math.max(1, (d.v / maxVol) * (volH - 14));
                const x    = cx(i);
                const col  = d.c >= d.o ? C.green : C.red;
                const recent = i >= n - Math.ceil(n * 0.15);
                return (
                  <rect key={i}
                    x={(x - bW/2).toFixed(1)}
                    y={(volTop + volH - barH - 2).toFixed(1)}
                    width={Math.max(1, bW).toFixed(1)}
                    height={barH.toFixed(1)}
                    fill={col}
                    opacity={hov===i ? 1 : recent ? 0.7 : 0.45}
                    rx="0.5"/>
                );
              })}
            </g>
            {/* Vol avg line */}
            {(() => {
              const avgV = data.reduce((s,d)=>s+(d.v||0),0)/n;
              const avgY = volTop + volH - (avgV/maxVol)*(volH-14) - 2;
              return <line x1={PAD.l} x2={PAD.l+cW} y1={avgY.toFixed(1)} y2={avgY.toFixed(1)}
                stroke={C.amber} strokeWidth="1" strokeDasharray="4 3" opacity=".5"/>;
            })()}
          </>
        )}

        {/* ── RSI14 PANEL ── */}
        {showRSI && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={rsiTop} y2={rsiTop} stroke={gridC} strokeWidth="1"/>
            <text x={PAD.l+4} y={rsiTop+13} fontSize="9" fontWeight="800" fill={mutC}>RSI 14</text>
            {lastRSI != null && (
              <g>
                <rect x={PAD.l+cW-96} y={rsiTop+5} width={94} height={22} rx={6}
                  fill={RSI_COL.badgeBg}
                  stroke={RSI_COL.badgeBorder}/>
                <text x={PAD.l+cW-88} y={rsiTop+19} fontSize="9" fontWeight="900" fill={rsiStateCol} fontFamily="ui-monospace,monospace">
                  RSI {lastRSI.toFixed(1)}
                </text>
              </g>
            )}
            {/* Zones RSI : surachat / neutre / survente */}
            <rect x={PAD.l} y={rsiY(100).toFixed(1)} width={cW} height={(rsiY(70)-rsiY(100)).toFixed(1)}
              fill={C.red} opacity=".055" clipPath="url(#tm6-rsi)"/>
            <rect x={PAD.l} y={rsiY(30).toFixed(1)} width={cW} height={(rsiY(0)-rsiY(30)).toFixed(1)}
              fill={C.green} opacity=".055" clipPath="url(#tm6-rsi)"/>
            {/* 70 / 50 / 30 lines */}
            {[70,50,30].map(v=>{
              const y = rsiY(v);
              return (
                <g key={v}>
                  <line x1={PAD.l} x2={PAD.l+cW} y1={y.toFixed(1)} y2={y.toFixed(1)}
                    stroke={v===50?C.amber:v===70?`${C.red}88`:`${C.green}88`}
                    strokeWidth={v===50?"1.4":"1"} strokeDasharray={v===50?"0":"5 4"}/>
                  <text x={PAD.l-4} y={(y+3).toFixed(1)} textAnchor="end"
                    fill={v===70?C.red:v===30?C.green:mutC} fontSize="8.5" fontWeight="800" fontFamily="ui-monospace,monospace">{v}</text>
                  <text x={PAD.l+cW+5} y={(y+3).toFixed(1)} textAnchor="start"
                    fill={v===70?C.red:v===30?C.green:mutC} fontSize="8" fontWeight="700" fontFamily="ui-monospace,monospace">{v}</text>
                </g>
              );
            })}
            {/* RSI fill + courbe principale */}
            {rsiAreaPath && (
              <path d={rsiAreaPath} fill="url(#tm6-rsig)" clipPath="url(#tm6-rsi)"/>
            )}
            <path d={rsiPath} fill="none" stroke={RSI_COL.line} strokeWidth="4"
              clipPath="url(#tm6-rsi)" strokeLinecap="round" strokeLinejoin="round"/>
            <path d={rsiPath} fill="none" stroke="#ffffff" strokeWidth="1.1" opacity={isDark ? ".20" : ".38"}
              clipPath="url(#tm6-rsi)" strokeLinecap="round" strokeLinejoin="round"/>
            {/* Current RSI tag */}
            {lastRSI != null && (() => {
              const y = rsiY(lastRSI);
              return (
                <g>
                  <circle cx={(PAD.l+cW).toFixed(1)} cy={y.toFixed(1)} r="3" fill={RSI_COL.line}/>
                  <rect x={PAD.l+cW+2} y={(y-8).toFixed(1)} width={50} height={14} rx={3}
                    fill={lastRSI>=70?C.red:lastRSI<=30?C.green:RSI_COL.line}/>
                  <text x={PAD.l+cW+27} y={(y+3.5).toFixed(1)} textAnchor="middle"
                    fontSize="8" fontWeight="800" fill="#fff" fontFamily="ui-monospace,monospace">
                    {lastRSI.toFixed(1)}
                  </text>
                </g>
              );
            })()}
            {/* RSI crosshair */}
            {hov!=null && hovRSI!=null && (
              <circle cx={cx(hov).toFixed(1)} cy={rsiY(hovRSI).toFixed(1)} r="3" fill={RSI_COL.line} opacity=".95"/>
            )}
          </>
        )}

        {/* ── Z-SCORE PANEL ── */}
        {showZScore && (
          <>
            <line x1={PAD.l} x2={PAD.l+cW} y1={zTop} y2={zTop} stroke={gridC} strokeWidth="1"/>
            {[-2,-1,0,1,2].map(z=>(
              <line key={z} x1={PAD.l} x2={PAD.l+cW} y1={zY(z).toFixed(1)} y2={zY(z).toFixed(1)}
                stroke={gridC} strokeWidth=".8" strokeDasharray={z===0?"0":"3 4"}/>
            ))}
            <text x={PAD.l+4} y={(zTop+11).toFixed(1)} fontSize="8" fontWeight="700" fill={mutC}>Z-Score</text>
            <polygon
              points={`${PAD.l},${zY(0)} ${zPath} ${PAD.l+cW},${zY(0)}`}
              fill={lastZ>=0?`${C.amber}0f`:"#4a7fa00e"} clipPath="url(#tm6-z)"/>
            <polyline points={zPath} fill="none" stroke={C.amber} strokeWidth="1.6" clipPath="url(#tm6-z)"/>
            <circle cx={cx(n-1).toFixed(1)} cy={zY(lastZ).toFixed(1)} r="3.5" fill={zCol}/>
            {/* Z tag */}
            <rect x={PAD.l+cW+2} y={(zY(lastZ)-8).toFixed(1)} width={50} height={14} rx={3}
              fill={lastZ>1?C.amber:`${C.amber}1a`}
              stroke={C.amber} strokeWidth={lastZ>1?0:.7}/>
            <text x={PAD.l+cW+27} y={(zY(lastZ)+3.5).toFixed(1)} textAnchor="middle"
              fontSize="8" fontWeight="800"
              fill={lastZ>1?"#fff":C.amber} fontFamily="ui-monospace,monospace">
              {lastZ>=0?"+":""}{lastZ.toFixed(2)}
            </text>
            {[-2,0,2].map(z=>(
              <text key={z} x={PAD.l-3} y={(zY(z)+3).toFixed(1)} textAnchor="end"
                fill={mutC} fontSize="7.5" fontFamily="ui-monospace,monospace">{z}</text>
            ))}
          </>
        )}
      </svg>
    </div>
  );
}
// ══════════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════════
// RS CHART — Relative Strength historique NVDA vs NDX (2005→2025)
// Reproduit le composant RSChart de TimingMarcheCard_v2_2.jsx
// 2 modes : RS Ratio (courbe log colorée) + Rendements (barres 12M)
// ══════════════════════════════════════════════════════════════════
function RSChart({ isDark, rsData }) {
  const data = rsData?.length ? rsData : RS_DATA;
  const [rsMode, setRsMode] = useState("ratio");
  const [tooltip, setTooltip] = useState(null);
  const wrapRef = useRef(null);
  const [W, setW] = useState(360);

  useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new ResizeObserver(([e]) => setW(Math.max(280, e.contentRect.width || 280)));
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  const mutC  = isDark ? "#7a8172" : "#6f7b74";
  const gridC = isDark ? "rgba(111,138,91,.08)" : "rgba(30,40,34,.055)";
  const bgC   = isDark ? "#0d1426" : "#fff";
  const txC   = isDark ? "#e8e4da" : "#1f2a24";

  const H = 180;
  const PAD = { t:10, r:8, b:26, l:52 };
  const cW = W - PAD.l - PAD.r;
  const cH = H - PAD.t - PAD.b;
  const n  = data.length;
  const cx = i => PAD.l + (i + 0.5) * (cW / n);

  // ── RS Ratio mode (log scale)
  const ratios = data.map(p => p.r);
  const minR = Math.min(...ratios), maxR = Math.max(...ratios);
  const logMin = Math.log(minR), logMax = Math.log(maxR);
  const pyR = v => PAD.t + cH - ((Math.log(Math.max(v, 0.00001)) - logMin) / (logMax - logMin) * cH);
  const yTicksR = [0.0001, 0.0005, 0.001, 0.003, 0.005, 0.007].filter(v => v >= minR * 0.5 && v <= maxR * 2);

  // ── Rendements 12M mode
  const ret12s = data.map(p => p.r12 || 0);
  const minRet = Math.min(...ret12s), maxRet = Math.max(...ret12s), rngRet = maxRet - minRet || 1;
  const pyRet = v => PAD.t + cH - ((v - minRet) / rngRet * cH);
  const zeroY = pyRet(0);

  // X labels (years)
  const xYears = ["2006","2008","2010","2012","2014","2016","2018","2020","2022","2024"];
  const xLbls  = xYears.map(y => {
    const i = data.findIndex(p => p.d.startsWith(y));
    return i >= 0 ? { y, i } : null;
  }).filter(Boolean);

  // Tooltip hover
  const handleMouseMove = e => {
    const rect = e.currentTarget.getBoundingClientRect();
    const rx = (e.clientX - rect.left) / rect.width * W;
    const i  = Math.max(0, Math.min(n - 1, Math.floor((rx - PAD.l) / (cW / n))));
    setTooltip({ i, pt: data[i] });
  };

  const RS_MODES = [
    { id:"ratio",     label:"RS Ratio",   tip:"Ratio NVDA/NDX en échelle logarithmique depuis 2005. Une hausse = NVDA surperforme le Nasdaq. Le ratio a été multiplié par ~60 en 20 ans." },
    { id:"rendement", label:"Rendements", tip:"Surperformance relative de NVDA vs NDX sur 12 mois glissants. Barres colorées par label RS (Strong Leader → Strong Laggard)." },
  ];

  return (
    <div ref={wrapRef} style={{ width:"100%" }}>
      {/* Controls */}
      <div style={{ display:"flex", gap:6, marginBottom:10, alignItems:"center", flexWrap:"wrap" }}>
        {RS_MODES.map(m => (
          <Tip key={m.id} body={m.tip}>
            <button onClick={() => setRsMode(m.id)} style={{
              padding:"4px 10px", borderRadius:5,
              border:`1px solid ${rsMode===m.id ? C.amber+"66" : "var(--card-bdr)"}`,
              background: rsMode===m.id ? `${C.amber}18` : "transparent",
              cursor:"pointer", fontSize:10, fontWeight:800,
              color: rsMode===m.id ? C.amber : "var(--mu)",
              fontFamily:"ui-monospace,monospace", textTransform:"uppercase", letterSpacing:".06em",
            }}>{m.label}</button>
          </Tip>
        ))}
        <span style={{ marginLeft:"auto", fontSize:9, fontFamily:"ui-monospace,monospace", color:"var(--mu)" }}>
          251 pts mensuel · 2005→2025
        </span>
      </div>

      {/* Chart SVG */}
      <div style={{ position:"relative" }} onMouseLeave={() => setTooltip(null)}>
        <svg viewBox={`0 0 ${W} ${H}`}
          style={{ width:"100%", height:"auto", display:"block", cursor:"crosshair" }}
          onMouseMove={handleMouseMove}>
          <defs>
            <linearGradient id="rsg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={C.green} stopOpacity=".18"/>
              <stop offset="100%" stopColor={C.green} stopOpacity="0"/>
            </linearGradient>
          </defs>

          {/* ── MODE RATIO ── */}
          {rsMode === "ratio" && (<>
            {/* Grid */}
            {yTicksR.map((v, i) => (
              <line key={i} x1={PAD.l} x2={W - PAD.r}
                y1={pyR(v).toFixed(1)} y2={pyR(v).toFixed(1)}
                stroke={gridC} strokeWidth=".5" strokeDasharray="3 3"/>
            ))}
            {yTicksR.map((v, i) => (
              <text key={i} x={PAD.l - 3} y={(pyR(v) + 3).toFixed(1)}
                textAnchor="end" fill={mutC} fontSize="7" fontFamily="ui-monospace,monospace">
                {v.toFixed(4)}
              </text>
            ))}
            {/* Area fill */}
            <path
              d={data.map((p,i) => `${i===0?"M":"L"}${cx(i).toFixed(1)},${pyR(p.r).toFixed(1)}`).join(" ")
                + ` L${cx(n-1).toFixed(1)},${(PAD.t+cH).toFixed(1)} L${cx(0).toFixed(1)},${(PAD.t+cH).toFixed(1)} Z`}
              fill="url(#rsg)" opacity=".25"/>
            {/* Colored segments by label */}
            {data.map((pt, i) => {
              if (i === 0) return null;
              return (
                <line key={i}
                  x1={cx(i-1).toFixed(1)} y1={pyR(data[i-1].r).toFixed(1)}
                  x2={cx(i).toFixed(1)}   y2={pyR(pt.r).toFixed(1)}
                  stroke={LB_COLORS[pt.lb] || "#8a8680"}
                  strokeWidth="1.5" opacity=".88"/>
              );
            })}
            {/* Crosshair */}
            {tooltip && (
              <line x1={cx(tooltip.i).toFixed(1)} x2={cx(tooltip.i).toFixed(1)}
                y1={PAD.t} y2={PAD.t + cH}
                stroke={C.amber} strokeWidth=".8" strokeDasharray="3 2"/>
            )}
          </>)}

          {/* ── MODE RENDEMENTS ── */}
          {rsMode === "rendement" && (<>
            {[-60,-40,-20,0,20,40,60].filter(v => v >= minRet - 5 && v <= maxRet + 5).map((v, i) => (
              <line key={i} x1={PAD.l} x2={W - PAD.r}
                y1={pyRet(v).toFixed(1)} y2={pyRet(v).toFixed(1)}
                stroke={v === 0 ? mutC : gridC}
                strokeWidth={v === 0 ? .8 : .5}
                strokeDasharray={v === 0 ? "" : "3 3"}/>
            ))}
            {[-40,-20,0,20,40].filter(v => v >= minRet - 5 && v <= maxRet + 5).map((v, i) => (
              <text key={i} x={PAD.l - 3} y={(pyRet(v) + 3.5).toFixed(1)}
                textAnchor="end" fill={mutC} fontSize="7" fontFamily="ui-monospace,monospace">{v}%</text>
            ))}
            {data.map((pt, i) => {
              const v  = pt.r12 || 0;
              const bh = Math.abs(pyRet(v) - zeroY);
              const by = v >= 0 ? pyRet(v) : zeroY;
              const bw = Math.max(1, cW / n - 0.3);
              return (
                <rect key={i}
                  x={(cx(i) - bw/2).toFixed(1)} y={by.toFixed(1)}
                  width={bw.toFixed(1)} height={Math.max(1, bh).toFixed(1)}
                  fill={LB_COLORS[pt.lb] || "#8a8680"} opacity=".78"/>
              );
            })}
            {tooltip && (
              <line x1={cx(tooltip.i).toFixed(1)} x2={cx(tooltip.i).toFixed(1)}
                y1={PAD.t} y2={PAD.t + cH}
                stroke={C.amber} strokeWidth=".8" strokeDasharray="3 2"/>
            )}
          </>)}

          {/* X axis labels */}
          {xLbls.map(({ y, i }) => (
            <text key={y} x={cx(i).toFixed(1)} y={H - 4}
              textAnchor="middle" fill={mutC} fontSize="7.5" fontFamily="ui-monospace,monospace">{y}</text>
          ))}
        </svg>

        {/* Tooltip overlay */}
        {tooltip && (
          <div style={{
            position:"absolute", top:4, left:8, pointerEvents:"none",
            background:bgC, border:`1px solid rgba(30,40,34,.12)`,
            borderRadius:7, padding:"6px 10px",
            fontSize:9, fontFamily:"ui-monospace,monospace",
            color:txC, minWidth:140, zIndex:10,
            boxShadow:"0 4px 14px rgba(0,0,0,.12)",
          }}>
            <div style={{ color:C.amber, marginBottom:4, fontSize:10, fontWeight:700 }}>{tooltip.pt.d}</div>
            <div style={{ display:"grid", gridTemplateColumns:"auto auto", gap:"2px 10px" }}>
              {rsMode==="ratio" && <>
                <span style={{ color:"var(--mu)" }}>RS Ratio</span>
                <span>{tooltip.pt.r?.toFixed(6)}</span>
                <span style={{ color:"var(--mu)" }}>Label</span>
                <span style={{ color:LB_COLORS[tooltip.pt.lb], fontWeight:700 }}>
                  {LB_FULL[tooltip.pt.lb] || tooltip.pt.lb}
                </span>
                {tooltip.pt.r12 != null && <>
                  <span style={{ color:"var(--mu)" }}>Rend 12M</span>
                  <span style={{ color:tooltip.pt.r12 >= 0 ? C.green : C.red, fontWeight:700 }}>
                    {tooltip.pt.r12 >= 0 ? "+" : ""}{tooltip.pt.r12?.toFixed(1)}%
                  </span>
                </>}
              </>}
              {rsMode==="rendement" && <>
                <span style={{ color:"var(--mu)" }}>Rend 12M</span>
                <span style={{ color:(tooltip.pt.r12||0) >= 0 ? C.green : C.red, fontWeight:700 }}>
                  {(tooltip.pt.r12||0) >= 0 ? "+" : ""}{(tooltip.pt.r12||0).toFixed(1)}%
                </span>
                <span style={{ color:"var(--mu)" }}>Rend 1M</span>
                <span style={{ color:(tooltip.pt.r1||0) >= 0 ? C.green : C.red }}>
                  {(tooltip.pt.r1||0) >= 0 ? "+" : ""}{(tooltip.pt.r1||0).toFixed(1)}%
                </span>
                <span style={{ color:"var(--mu)" }}>Label</span>
                <span style={{ color:LB_COLORS[tooltip.pt.lb], fontWeight:700 }}>
                  {LB_FULL[tooltip.pt.lb] || tooltip.pt.lb}
                </span>
              </>}
            </div>
          </div>
        )}
      </div>

      {/* Legend */}
      <div style={{ display:"flex", gap:10, marginTop:6, flexWrap:"wrap" }}>
        {Object.entries(LB_FULL).map(([lb, label]) => (
          <div key={lb} style={{ display:"flex", alignItems:"center", gap:4 }}>
            {rsMode === "ratio"
              ? <div style={{ width:16, height:2, background:LB_COLORS[lb], borderRadius:1 }}/>
              : <div style={{ width:10, height:10, borderRadius:2, background:LB_COLORS[lb] }}/>
            }
            <span style={{ fontSize:8, color:"var(--mu)", fontFamily:"ui-monospace,monospace" }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function MRow({ label, value, color, tip }) {
  return (
    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center",
      padding:"5px 2px", borderBottom:"1px solid var(--sep)", gap:8, cursor:"default" }}
      onMouseEnter={e=>e.currentTarget.style.background="var(--sur)"}
      onMouseLeave={e=>e.currentTarget.style.background=""}>
      <span style={{ fontSize:11, color:"var(--mu)", fontWeight:600,
        display:"flex", alignItems:"center" }}>
        {label}{tip && <TipIcon text={tip}/>}
      </span>
      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11,
        fontWeight:700, color:color||"var(--ts)", flexShrink:0 }}>{value}</span>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 9. PILLAR CARD
// ══════════════════════════════════════════════════════════════════
function PillarCard({ number, title, score, color, pillar, tip, d, children, onGuide }) {
  const [modal, setModal] = useState(null);
  const diagQ = PILLAR_QUESTIONS[pillar] || null;
  return (
    <>
      <div style={{ background:"var(--card-bg)", borderRadius:14, border:"1px solid var(--card-bdr)",
        borderTop:`3px solid ${color}`, padding:14,
        boxShadow:"0 2px 8px rgba(16,40,28,.05)" }}>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom: diagQ ? 6 : 10 }}>
          <div style={{ width:22, height:22, borderRadius:999, background:color,
            display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:10, fontWeight:900, color:"#fff", flexShrink:0 }}>{number}</div>
          <span style={{ flex:1, fontFamily:"ui-monospace,monospace", fontSize:10,
            fontWeight:800, color, letterSpacing:".07em", lineHeight:1.2 }}>
            {title}{tip && <TipIcon text={tip}/>}
          </span>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:14,
            fontWeight:800, color, flexShrink:0 }}>{score}/100</span>
        </div>
        {/* Question diagnostique par axe */}
        {diagQ && (
          <div style={{ display:"flex", alignItems:"flex-start", gap:6, marginBottom:10,
            padding:"6px 10px", borderRadius:7,
            background:`${color}09`, border:`1px solid ${color}22` }}>
            <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7, fontWeight:800,
              color, flexShrink:0, marginTop:1, letterSpacing:".05em" }}>QUESTION</span>
            <span style={{ fontSize:9.5, color:"var(--ts)", fontStyle:"italic",
              lineHeight:1.45, flex:1 }}>{diagQ}</span>
            {onGuide && (
              <button onClick={() => onGuide(pillar)} title="Guide d'analyse"
                style={{ marginLeft:"auto", flexShrink:0, width:18, height:18,
                  display:"flex", alignItems:"center", justifyContent:"center",
                  background:"transparent", border:`1px solid ${color}30`,
                  borderRadius:4, cursor:"pointer", fontSize:9, color }}>🧭</button>
            )}
          </div>
        )}
        {children}
        <div style={{ marginTop:12 }}>
          {/* Bouton analyse */}
          <button
            onClick={() => setModal(pillar)}
            style={{ width:"100%", padding:"9px 12px", borderRadius:10,
              border:`1px solid ${color}44`, background:`${color}0d`,
              fontSize:11, fontWeight:800, color, cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"space-between",
              transition:"background .12s" }}
            onMouseEnter={e=>e.currentTarget.style.background=`${color}1e`}
            onMouseLeave={e=>e.currentTarget.style.background=`${color}0d`}>
            <span>◈ Comprendre cette analyse</span><span>›</span>
          </button>
        </div>
      </div>
      {modal && <AnalyseModal pillar={modal} onClose={()=>setModal(null)} d={d}/>}
    </>
  );
}

// ══════════════════════════════════════════════════════════════════
// GUIDE DE LECTURE MODAL (Niveau 5)
// ══════════════════════════════════════════════════════════════════
// ── GUIDE D'ANALYSE TECHNIQUE PAR PILIER ────────────────────────────────────
// Questions clés, indicateurs, seuils interprétatifs et logique analytique
// Calqué sur l'architecture CompassModal de AnalyseFondamentaleCard
const TECH_PILLARS = [
  { key:"structure", label:"Structure de Tendance", abbr:"STRUCT", icon:"◈", weight:40, color:C.green,  ordre:1 },
  { key:"momentum",  label:"Force / Momentum",      abbr:"MOM",    icon:"⚡", weight:25, color:C.amber,  ordre:2 },
  { key:"volume",    label:"Participation / Volume", abbr:"VOL",    icon:"◎", weight:20, color:C.olive,  ordre:3 },
  { key:"timing",    label:"Timing / Opportunité",  abbr:"TIMING", icon:"⊕", weight:15, color:C.orange, ordre:4 },
];

const TECH_ANALYSIS_ORDER = ["structure","momentum","volume","timing"];

const TECH_GUIDE = {
  structure: {
    ordre: 1,
    titre: "La tendance de fond est-elle haussière ?",
    intro: "Murphy (TAOTFM ch. 1) : 'la règle d'or de l'analyse technique est de ne jamais trader contre la tendance principale.' La première question est toujours directionnelle : dans quel sens se déplace le prix sur les trois horizons temporels ? La réponse conditionne tous les signaux suivants — un signal haussier sur un titre en tendance baissière reste un piège.",
    questions: [
      {
        q: "La tendance primaire est-elle haussière ?",
        indicateurs: ["SMA 200", "Pente OLS (β)", "R²"],
        seuil: "Prix > SMA200 = marché haussier · β positif + R² ≥ 0.50 = tendance fiable · R² < 0.30 = tendance peu exploitable",
        logique: "La SMA200 (~200 séances, ~10 mois) est la frontière universelle entre bull et bear market. Murphy (ch. 9) : 'un titre au-dessus de sa SMA200 est dans son marché haussier structural, en dessous dans son marché baissier.' La pente OLS (β) sans R² n'a pas de valeur — un β positif avec R² = 0.17 signifie que 83% de la variance est du bruit : la tendance existe sur le papier, pas dans la réalité.",
      },
      {
        q: "Les moyennes mobiles confirment-elles la direction à court et moyen terme ?",
        indicateurs: ["SMA 20", "SMA 50", "Golden Cross", "Death Cross", "Alignement des SMA"],
        seuil: "SMA20 > SMA50 > SMA200 = alignement haussier parfait · Golden Cross (SMA50 > SMA200) = confirmation LT · Death Cross = retournement baissier",
        logique: "L'alignement ordonné SMA20 > SMA50 > SMA200 est la configuration haussière de référence selon Murphy (ch. 9). Le Golden Cross — SMA50 qui franchit la SMA200 par le haut — est le signal de confirmation structurelle le plus suivi par les institutionnels. Edwards & Magee l'appelaient 'la confirmation la plus puissante de la tendance primaire.' À l'inverse, le Death Cross précède statistiquement des baisses prolongées.",
      },
      {
        q: "La progression est-elle régulière ou erratique ?",
        indicateurs: ["R²", "CAGR 5Y", "Canal de régression ±1σ / ±2σ"],
        seuil: "R² > 0.75 = tendance très ordonnée · 0.50–0.75 = exploitable · < 0.50 = chemin trop erratique · Prix dans ±1σ = zone neutre · Prix > +2σ = sur-extension rare",
        logique: "Le R² (coefficient de détermination) mesure quelle fraction de la variance du prix est expliquée par la tendance linéaire. Murphy (ch. 4) : 'c'est le signal de confiance de la régression.' Le canal ±1σ / ±2σ encadre respectivement ~68% et ~95% des observations historiques. Gray & Vogel (QM ch. 6) : les vrais compounders progressent de façon régulière — CAGR élevé ET R² élevé. Un titre très volatile avec R² faible amplifie le risque de mauvais timing.",
      },
    ],
    conclusion_analytique: "Séquence d'analyse : (1) SMA200 — au-dessus ou en dessous ? C'est le filtre de premier niveau. (2) Alignement SMA20/50/200 — le court terme confirme-t-il le long terme ? (3) R² — la tendance est-elle régulière assez pour être exploitable ? Un titre haussier (SMA200 +) avec alignement parfait et R² > 0.65 représente la structure la plus solide. Ne jamais se fier à β sans vérifier R² — Murphy (ch. 4) insiste : 'un CAGR élevé sur un chemin chaotique ne vaut rien.'",
  },

  momentum: {
    ordre: 2,
    titre: "Le mouvement accélère-t-il ou s'essouffle-t-il ?",
    intro: "Moskowitz, Ooi & Pedersen (Journal of Financial Economics, 2012) ont prouvé la persistance du momentum sur 200 ans d'histoire et plus de 40 marchés : les gagnants récents continuent statistiquement de surperformer. Gray & Vogel (Quantitative Momentum, 2016) ont affiné cette découverte : la direction du momentum ne suffit pas — sa régularité (qualité) est tout aussi prédictive. Un momentum positif mais erratique sous-performe un momentum positif et régulier.",
    questions: [
      {
        q: "Le TSMOM est-il positif et cohérent sur les trois horizons ?",
        indicateurs: ["TSMOM 12M = (P_t / P_{t−252j}) − 1", "TSMOM 6M = (P_t / P_{t−126j}) − 1", "TSMOM 3M = (P_t / P_{t−63j}) − 1"],
        seuil: "TSMOM 12M > 0 = signal principal · 12M + 6M + 3M tous positifs = confirmation forte · 12M+ / 3M− = essoufflement · 12M− = biais baissier",
        logique: "Moskowitz (JFE 2012) : le TSMOM 12M est le signal le plus robuste et le plus documenté — il capture la persistance du momentum institutionnel. Gray & Vogel (ch. 6) : la configuration 12M positif / 3M négatif est la plus commune avant consolidation — elle signale un essoufflement à court terme dans une tendance de fond intacte. Ce n'est pas un signal de vente, c'est un signal de patience.",
      },
      {
        q: "Le momentum est-il de qualité — régulier ou construit sur des pics isolés ?",
        indicateurs: ["FIP = SIGN(TSMOM_12M) × (% jours baissiers − % jours haussiers)", "% de jours de clôture positive", "Pattern : Accélération / Essoufflement / Transition"],
        seuil: "FIP ≤ 0 = 'smooth momentum' (régulier, robuste) · FIP > 0 = 'frog in the pan' (erratique, fragile) · % jours positifs > 55% = continuité saine · Pattern Accélération (3M > 6M > 12M) = signal idéal",
        logique: "Da, Gurun & Warachka (Review of Financial Studies, 2014) ont introduit le FIP : une grenouille dans l'eau qui chauffe progressivement ne réagit pas. TSMOM 12M = +60% mais construit sur 5 séances exceptionnelles au milieu d'une mer de jours baissiers — c'est un 'frog'. Gray & Vogel (ch. 6, p.118) : ces titres sous-performent les 'smooth momentum' de 3 à 5%/an sur les 3-12 mois suivants. Le FIP qualifie la conviction derrière le chiffre.",
      },
      {
        q: "Le titre surperforme-t-il son marché de référence ?",
        indicateurs: ["RS Ratio = P_titre / P_indice (base 1)", "RS Return 12M vs NASDAQ", "Position dans le classement sectoriel"],
        seuil: "RS Ratio en hausse = leadership croissant · RS Ratio > 1 et montant = force relative confirmée · RS Return 12M > 0% = surperformance annuelle",
        logique: "Jegadeesh & Titman (Journal of Finance, 1993) : les titres qui surperforment leur indice sur 3-12 mois tendent à continuer de surperformer sur les 3-12 mois suivants. C'est le momentum cross-sectionnel (relatif), distinct du TSMOM absolu mais complémentaire. Gray & Vogel : un titre avec TSMOM positif ET RS Ratio en hausse cumule les deux signaux de momentum les plus robustes empiriquement.",
      },
    ],
    conclusion_analytique: "Hiérarchie analytique : TSMOM 12M (direction) → FIP (qualité) → RS Ratio (force relative). Lire le momentum en cascade : si le 12M est négatif, le reste de l'analyse momentum n'a pas de sens haussier. Si le 12M est positif, chercher la confirmation du FIP (régularité) et du RS (leadership). La meilleure configuration : TSMOM 12M positif + FIP ≤ 0 + RS Ratio en hausse. La pire : TSMOM 12M positif + FIP élevé + RS Ratio en baisse — le chiffre brut ment sur la santé réelle du momentum.",
  },

  volume: {
    ordre: 3,
    titre: "Les institutionnels participent-ils au mouvement ?",
    intro: "Wyckoff (Studies in Tape Reading, 1910) a posé trois lois fondamentales dont la Loi 3 : 'l'effort (volume) doit être proportionnel au résultat (prix).' Murphy (TAOTFM ch. 7) : 'le volume confirme la tendance — il ne l'initie jamais, mais son absence la fragilise.' Le volume est l'empreinte digitale des mains fortes : là où le volume s'accumule, les institutions agissent.",
    questions: [
      {
        q: "Les volumes sont-ils en hausse ou en baisse par rapport à leur moyenne ?",
        indicateurs: ["Volume Ratio 5J / moy. 60J", "Volume Ratio 20J / moy. 60J", "Volume Ratio 60J / moy. 120J"],
        seuil: "Ratio > 1.2 = participation élevée (signal fort) · 0.85–1.2 = participation normale · < 0.85 = participation faible (signal fragile) · Ratio 60J décroissant = tendance structurelle de désengagement",
        logique: "Murphy (ch. 7) : 'hausse des prix + volume supérieur à la moyenne = signal fort — les institutionnels participent. Hausse des prix + volume faible = No Demand — la progression manque de conviction.' La Loi 3 de Wyckoff : si le prix monte mais que le volume baisse sur 20 séances (ratio < 0.85), c'est une divergence Effort/Résultat — la tendance se fragilise sans crier gare.",
      },
      {
        q: "Les acheteurs absorbent-ils les vendeurs sur les supports clés ?",
        indicateurs: ["Support Volume Ratio (vol. rebonds / vol. moyen)", "Résistance Volume Ratio", "Signal d'absorption (Absorption / Neutral / Distribution)"],
        seuil: "Support Vol. Ratio > 1.05 = absorption active · Résistance Vol. Ratio < 1.0 = pression vendeuse faible = résistance fragile · Signal 'Absorption' = mains fortes actives",
        logique: "Wyckoff (ch. 18-19, Villahermosa) : 'No Supply' = rebonds sur les supports accompagnés de volumes vendeurs faibles — les institutions ne soldent pas leurs positions. Le Support Volume Ratio > 1 mesure exactement cela : les acheteurs sont proportionnellement plus actifs lors des rebonds que dans le marché global. C'est la 'cause' que Wyckoff appelle accumulation discrète — la Phase B avant le markup.",
      },
      {
        q: "Dans quelle phase du cycle de Wyckoff se trouve le titre ?",
        indicateurs: ["Phase Wyckoff (Accumulation / Markup / Distribution / Markdown)", "Wyckoff Event (Spring / UTAD / Breakout…)", "Tendance Volume 20J / 60J (Rising / Falling / Neutral)"],
        seuil: "Accumulation Phase B/C = construction de cause · Markup Phase D/E = tendance en cours · Distribution Phase B/C = alerte · UTAD (Upthrust After Distribution) = piège haussier avant markdown",
        logique: "Villahermosa (The Wyckoff Methodology in Depth, 2019, ch. 14-20) : le cycle Wyckoff est la lecture la plus granulaire de la relation prix/volume. Distribution = les institutions allègent dans l'euphorie pendant que le public achète. L'UTAD est la fausse cassure haute conçue pour piéger les derniers acheteurs — le volume y est fort mais le prix retombe rapidement. C'est le signal baissier le plus dangereux à confondre avec un breakout.",
      },
    ],
    conclusion_analytique: "Ordre d'analyse volumétrique : (1) Volume Ratio 20J — le flux global confirme-t-il la tendance ? (2) Support Vol. Ratio — les mains fortes absorbent-elles les corrections ? (3) Phase Wyckoff — à quel stade du cycle sommes-nous ? Murphy : 'on n'a jamais tort de vérifier le volume avant d'agir sur un signal de prix.' La divergence Prix↑ / Volume↓ est l'un des signaux précurseurs les plus fiables de retournement selon les trois lois de Wyckoff.",
  },

  timing: {
    ordre: 4,
    titre: "Est-ce le bon moment pour entrer ?",
    intro: "Murphy (TAOTFM ch. 1) : 'même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.' Le timing ne contredit pas la tendance — il précise le meilleur moment pour y entrer. Wilder (New Concepts in Technical Trading Systems, 1978) et Pesavento (Trade What You See) ont formalisé des outils qui mesurent objectivement l'état du momentum à court terme et l'éloignement du prix par rapport à sa valeur d'équilibre statistique.",
    questions: [
      {
        q: "Le RSI indique-t-il une zone de surachat ou de survente ?",
        indicateurs: ["RSI 14 = 100 − [100 / (1 + RS)]", "RS = moy. gains 14j / moy. pertes 14j", "Divergence RSI / Prix"],
        seuil: "RSI > 70 = surachat (correction possible) · RSI < 30 = survente (rebond possible) · En tendance forte : zone d'achat décalée à 40–55 · Divergence baissière (prix↑ + RSI↓) = essoufflement interne",
        logique: "Wilder (1978) : le RSI mesure la vitesse et l'amplitude des mouvements de prix sur 14 séances. Murphy (ch. 10) insiste sur une nuance critique : en tendance haussière très forte, le RSI peut rester entre 50 et 80 pendant des mois — appliquer mécaniquement le seuil 70 conduit à vendre trop tôt. La VRAIE puissance du RSI réside dans la divergence : prix fait un nouveau plus haut mais RSI fait un plus haut inférieur = la force interne s'érode. Murphy : 'l'une des configurations les plus précieuses de l'analyse technique.'",
      },
      {
        q: "Le prix est-il éloigné de sa droite de régression historique ?",
        indicateurs: ["Z-Score = (Prix − Droite de régression) / σ résiduel", "Position dans le canal : −2σ / −1σ / Neutre / +1σ / +2σ", "Valorisation relative (Sous-évalué / Neutre / Surévalué)"],
        seuil: "Z-Score > +2σ = sur-extension statistiquement rare (~2.5% des observations) · +1 à +2σ = attention croissante · −1 à 0σ = zone neutre à favorable · < −1σ = zone d'opportunité historique",
        logique: "Le Z-Score de régression est distinct du RSI : il mesure non pas le momentum du mouvement mais l'éloignement du prix par rapport à sa trajectoire tendancielle de long terme. Un Z-Score à +2σ signifie que le prix a quitté sa trajectoire statistique normale — le retour à la moyenne est probable, même si le délai est imprévisible. Murphy (ch. 4) : 'les bandes statistiques révèlent si un titre est temporairement trop cher par rapport à son histoire.' Combiner RSI (momentum CT) + Z-Score (éloignement LT) donne une image complète du timing.",
      },
      {
        q: "Les signaux de court, moyen et long terme convergent-ils dans la même direction ?",
        indicateurs: ["Golden Cross / Death Cross (SMA50/SMA200)", "Position prix / SMA20 / SMA50", "Signal sigma (+2σ breakout / −1σ pullback / Zone neutre)"],
        seuil: "Prix > SMA20 > SMA50 > SMA200 = convergence haussière totale · Pullback sur SMA20 en tendance = meilleur point d'entrée · Breakout +2σ = signal fort mais risque d'extension",
        logique: "Murphy (TAOTFM ch. 15) : 'quand le court, le moyen et le long terme pointent dans la même direction, le signal est extrêmement fiable.' La convergence est rare — c'est précisément pour cela qu'elle est précieuse. Un pullback sur la SMA20 dans une tendance haussière (SMA20 > SMA50 > SMA200) est le timing idéal décrit au ch. 9 : le prix revient tester son premier niveau de support dynamique avant de repartir. C'est l'entrée avec le meilleur ratio risque/récompense.",
      },
    ],
    conclusion_analytique: "Séquence de timing : RSI → Z-Score → Convergence des SMAs. Si les trois signaux sont défavorables simultanément (RSI > 70 + Z-Score > +2σ + prix très au-dessus des SMAs), le ratio risque/récompense est dégradé même pour un titre en excellente tendance. Le timing optimal selon Murphy et Pesavento : RSI entre 40 et 60 (zone de force, pas de surachat) + Z-Score entre 0 et +1σ (pas sur-étendu) + pullback sur SMA20 ou SMA50 (support dynamique testé). Cette confluence est rare — attendre vaut mieux qu'entrer au mauvais prix.",
  },
};

function GuideModal({ onClose, currentPillar }) {
  // Toujours ouvrir sur la grille des piliers
  const [selectedPillar, setSelectedPillar] = useState(null);
  const validKeys = ["structure","momentum","volume","timing"];
  const highlightedPillar = validKeys.includes(currentPillar) ? currentPillar : null;

  const guide  = selectedPillar ? TECH_GUIDE[selectedPillar]           : null;
  const pillar = selectedPillar ? TECH_PILLARS.find(p => p.key === selectedPillar) : null;

  const selectPillar = key => setSelectedPillar(key);
  const goHome       = ()  => setSelectedPillar(null);

  const idx     = selectedPillar ? TECH_ANALYSIS_ORDER.indexOf(selectedPillar) : -1;
  const prevKey = idx > 0                                ? TECH_ANALYSIS_ORDER[idx - 1] : null;
  const nextKey = idx < TECH_ANALYSIS_ORDER.length - 1  ? TECH_ANALYSIS_ORDER[idx + 1] : null;
  const prevP   = prevKey ? TECH_PILLARS.find(p => p.key === prevKey) : null;
  const nextP   = nextKey ? TECH_PILLARS.find(p => p.key === nextKey) : null;

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  // ── Grille d'accueil ─────────────────────────────────────────────────────
  const PillarGrid = () => (
    <div style={{ flex:1, overflowY:"auto" }}>
      {/* Intro */}
      <div style={{ padding:"12px 16px 10px", borderBottom:"1px solid var(--sep)",
        background:`${C.green}06` }}>
        <div style={{ fontFamily:"monospace", fontSize:7.5, color:C.green,
          textTransform:"uppercase", letterSpacing:".12em", marginBottom:6, fontWeight:700 }}>
          Comment utiliser ce guide
        </div>
        <p style={{ fontSize:"clamp(10px,1.3vw,11.5px)", color:"var(--tx)", lineHeight:1.7, margin:0 }}>
          L'analyse technique suit une logique de <strong style={{color:"var(--ts)"}}>filtre
          séquentiel</strong> : commencer par la direction (SMA200, alignement, R²), puis la
          force (TSMOM, FIP, RS Ratio), puis la conviction du volume (ratios, Wyckoff), et conclure
          par le timing d'entrée (RSI, Z-Score, convergence SMAs). Un signal haussier sur un titre
          baissier reste un piège — Murphy ch. 1.
        </p>
      </div>
      {/* Grille 2×2 */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, padding:"12px 14px" }}>
        {TECH_PILLARS.map((p, i) => {
          const isCurrent = highlightedPillar === p.key;
          return (
            <button key={p.key} onClick={() => selectPillar(p.key)} style={{
              display:"flex", flexDirection:"column", alignItems:"flex-start",
              padding:"10px 12px", gap:4,
              border:`1.5px solid ${isCurrent ? p.color+"80" : p.color+"40"}`,
              background: isCurrent ? `${p.color}09` : `${p.color}06`,
              borderRadius:10, cursor:"pointer", transition:"all .15s", position:"relative",
              outline: isCurrent ? `2px solid ${p.color}35` : "none", outlineOffset:1,
            }}>
              <div style={{ position:"absolute", top:6, right:8,
                fontFamily:"monospace", fontSize:7, fontWeight:800,
                color:"var(--mu)", opacity:.6 }}>{i + 1}</div>
              {isCurrent && (
                <div style={{ position:"absolute", top:6, left:8,
                  fontFamily:"monospace", fontSize:6, fontWeight:700,
                  color:"#fff", background:p.color, padding:"1px 5px", borderRadius:3 }}>actif</div>
              )}
              <span style={{ fontSize:15, color:p.color, marginTop: isCurrent ? 8 : 0 }}>{p.icon}</span>
              <div style={{ fontFamily:"monospace", fontSize:"clamp(7.5px,1.8vw,9.5px)",
                fontWeight:700, color:p.color, lineHeight:1.2 }}>{p.label}</div>
              <div style={{ fontFamily:"monospace", fontSize:7, color:"var(--mu)" }}>
                {p.weight}% · {{
                  structure:"SMA · R²",
                  momentum: "TSMOM · FIP · RS",
                  volume:   "Vol. Ratio · Wyckoff",
                  timing:   "RSI · Z-Score · SMAs",
                }[p.key]}
              </div>
              <div style={{ fontSize:"clamp(8px,1.1vw,9.5px)", color:"var(--tx)", lineHeight:1.4,
                textAlign:"left" }}>
                {TECH_GUIDE[p.key]?.titre}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="compass-overlay" style={{
      position:"fixed", inset:0, zIndex:9000,
      width:"100vw", height:"100vh",
      background:"rgba(0,0,0,.55)",
      backdropFilter:"blur(5px)", display:"flex", alignItems:"center", justifyContent:"center",
      padding:"clamp(8px,3vw,20px)", boxSizing:"border-box",
    }} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="compass-modal" style={{
        background:"var(--card-bg)", border:"1px solid var(--card-bdr)",
        borderRadius:"clamp(10px,1.5vw,18px)",
        width:"min(960px,96vw)", height:"min(680px,88vh)",
        display:"flex", flexDirection:"column",
        boxShadow:"0 24px 80px rgba(0,0,0,.3)",
        overflow:"hidden",
      }}>

        {/* ══ HEADER — une seule ligne, identique au screenshot ══ */}
        <div style={{
          padding:"10px 14px", borderBottom:"1px solid var(--sep)",
          display:"flex", alignItems:"center", gap:8, flexShrink:0,
          background: selectedPillar && pillar
            ? `linear-gradient(90deg,${pillar.color}10 0%,transparent 60%)`
            : `linear-gradient(90deg,${C.green}10 0%,transparent 60%)`,
        }}>

          {/* ← Piliers (visible seulement sur vue pilier) */}
          {selectedPillar ? (
            <button onClick={goHome} style={{
              display:"flex", alignItems:"center", gap:5, padding:"5px 10px",
              background:"var(--sur)", border:"1px solid var(--sep)",
              borderRadius:7, cursor:"pointer", flexShrink:0, whiteSpace:"nowrap",
            }}>
              <span style={{ fontSize:12, color:"var(--mu)" }}>←</span>
              <span style={{ fontFamily:"monospace", fontSize:8, color:"var(--mu)" }}>Piliers</span>
            </button>
          ) : (
            <div style={{ width:32, height:32, borderRadius:9,
              background:`${C.green}18`, border:`1px solid ${C.green}28`,
              display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
              🧭
            </div>
          )}

          {/* Badge N/4 + icône + label + poids + score — inline */}
          <div style={{ flex:1, minWidth:0, display:"flex", alignItems:"center",
            gap:6, flexWrap:"nowrap", overflow:"hidden" }}>
            {selectedPillar && pillar ? (
              <>
                <span style={{ fontFamily:"monospace", fontSize:7.5, fontWeight:800,
                  color:"#fff", background:pillar.color,
                  padding:"2px 7px", borderRadius:4, flexShrink:0 }}>
                  {idx + 1} / 4
                </span>
                <span style={{ fontSize:11, color:pillar.color, flexShrink:0 }}>{pillar.icon}</span>
                <span style={{ fontFamily:"monospace", fontSize:"clamp(9px,1.5vw,12px)",
                  fontWeight:700, color:pillar.color,
                  whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                  {pillar.label}
                </span>
                <span style={{ fontFamily:"monospace", fontSize:8, color:"var(--mu)",
                  whiteSpace:"nowrap", flexShrink:0 }}>
                  · {pillar.weight}% du TScore
                </span>
              </>
            ) : (
              <div>
                <div style={{ fontFamily:"monospace", fontSize:"clamp(9px,1.5vw,12px)",
                  fontWeight:700, color:"var(--ts)" }}>Guide d'analyse technique</div>
                <div style={{ fontFamily:"monospace", fontSize:8, color:"var(--mu)", marginTop:1 }}>
                  4 piliers · Filtre séquentiel · TScore Invelps
                </div>
              </div>
            )}
          </div>

          {/* ← → navigation (visible seulement sur vue pilier) */}
          {selectedPillar && (
            <div style={{ display:"flex", gap:4, flexShrink:0 }}>
              <button onClick={() => prevP && selectPillar(prevKey)} disabled={!prevP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: prevP ? "var(--sur)" : "transparent",
                border:`1px solid ${prevP ? "var(--sep)" : "transparent"}`,
                borderRadius:6, cursor: prevP ? "pointer" : "default",
                fontSize:13, color: prevP ? prevP.color : "var(--mu)", opacity: prevP ? 1 : .3,
              }}>←</button>
              <button onClick={() => nextP && selectPillar(nextKey)} disabled={!nextP} style={{
                width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
                background: nextP ? `${nextP.color}15` : "transparent",
                border:`1px solid ${nextP ? nextP.color+"40" : "transparent"}`,
                borderRadius:6, cursor: nextP ? "pointer" : "default",
                fontSize:13, color: nextP ? nextP.color : "var(--mu)", opacity: nextP ? 1 : .3,
              }}>→</button>
            </div>
          )}

          {/* × fermer */}
          <button onClick={onClose} style={{
            width:28, height:28, display:"flex", alignItems:"center", justifyContent:"center",
            background:"var(--sur)", border:"1px solid var(--sep)",
            borderRadius:7, cursor:"pointer", fontSize:16, color:"var(--mu)", flexShrink:0,
          }}>×</button>
        </div>

        {/* ══ CORPS ══ */}
        <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>

          {/* ── Vue accueil ── */}
          {!selectedPillar && (
            <div style={{ flex:1, overflow:"hidden", display:"flex", flexDirection:"column" }}>
              <PillarGrid/>
            </div>
          )}

          {/* ── Vue pilier : scroll direct, sans sidebar ── */}
          {selectedPillar && guide && pillar && (
            <div style={{ flex:1, overflowY:"auto", padding:"clamp(14px,2vw,20px) clamp(14px,2.5vw,24px)" }}>

              {/* Titre principal + intro — style screenshot */}
              <div style={{ marginBottom:16, paddingBottom:14, borderBottom:"1px solid var(--sep)" }}>
                <h2 style={{ fontFamily:"ui-sans-serif,system-ui,sans-serif",
                  fontSize:"clamp(16px,3vw,20px)", fontWeight:700,
                  color:"var(--ts)", lineHeight:1.25, margin:"0 0 10px" }}>
                  {guide.titre}
                </h2>
                <p style={{ fontSize:"clamp(10.5px,1.4vw,12px)", color:"var(--tx)",
                  lineHeight:1.75, margin:0 }}>
                  {guide.intro}
                </p>
              </div>

              {/* Label QUESTIONS À SE POSER */}
              <div style={{ fontFamily:"monospace", fontSize:7.5, fontWeight:700, color:C.amber,
                textTransform:"uppercase", letterSpacing:".14em", marginBottom:10 }}>
                Questions à se poser
              </div>

              {/* Cards Q */}
              <div style={{ display:"flex", flexDirection:"column", gap:10, marginBottom:16 }}>
                {guide.questions.map((item, qi) => (
                  <div key={qi} style={{
                    border:"1px solid var(--sep)",
                    borderLeft:`3px solid ${pillar.color}`,
                    borderRadius:"0 10px 10px 0",
                    background:"var(--sur)",
                    padding:"10px 14px",
                  }}>
                    {/* Badge Q + question */}
                    <div style={{ display:"flex", alignItems:"flex-start", gap:8, marginBottom:8 }}>
                      <span style={{
                        fontFamily:"monospace", fontSize:7, fontWeight:700,
                        color:"#fff", background:pillar.color,
                        padding:"2px 6px", borderRadius:4,
                        flexShrink:0, marginTop:1,
                      }}>Q{qi + 1}</span>
                      <span style={{ fontSize:"clamp(11px,1.5vw,13px)", fontWeight:700,
                        color:"var(--ts)", lineHeight:1.3 }}>{item.q}</span>
                    </div>

                    {/* INDICATEURS : chips */}
                    <div style={{ display:"flex", alignItems:"center", gap:6,
                      flexWrap:"wrap", marginBottom:8 }}>
                      <span style={{ fontFamily:"monospace", fontSize:7,
                        color:"var(--mu)", textTransform:"uppercase",
                        letterSpacing:".05em" }}>Indicateurs :</span>
                      {item.indicateurs.map((ind, ri) => (
                        <span key={ri} style={{
                          fontFamily:"monospace", fontSize:8.5, fontWeight:700,
                          color:pillar.color,
                          background:`${pillar.color}12`,
                          border:`1px solid ${pillar.color}35`,
                          padding:"2px 8px", borderRadius:4,
                        }}>{ind}</span>
                      ))}
                    </div>

                    {/* Bloc seuils — fond crème/teinté amber */}
                    <div style={{
                      background:`${C.amber}09`, border:`1px solid ${C.amber}22`,
                      borderRadius:6, padding:"6px 10px", marginBottom:8,
                    }}>
                      <span style={{ fontFamily:"monospace", fontSize:7, color:C.amber,
                        textTransform:"uppercase", letterSpacing:".08em",
                        marginRight:6, fontWeight:700 }}>Seuils ·</span>
                      <span style={{ fontFamily:"monospace", fontSize:9, color:"var(--tx)",
                        lineHeight:1.6 }}>{item.seuil}</span>
                    </div>

                    {/* Logique — italique gris */}
                    <p style={{ fontSize:"clamp(9.5px,1.2vw,11px)", color:"var(--mu)",
                      lineHeight:1.7, margin:0, fontStyle:"italic" }}>
                      {item.logique}
                    </p>
                  </div>
                ))}
              </div>

              {/* Conclusion analytique */}
              <div style={{
                background:`${C.green}09`, border:`1px solid ${C.green}28`,
                borderRadius:10, padding:"11px 14px", marginBottom:14,
              }}>
                <div style={{ fontFamily:"monospace", fontSize:7.5, fontWeight:700,
                  color:C.green2, textTransform:"uppercase",
                  letterSpacing:".12em", marginBottom:6 }}>
                  ◈ Conclusion analytique
                </div>
                <p style={{ fontSize:"clamp(10px,1.3vw,11.5px)", color:"var(--tx)",
                  lineHeight:1.72, margin:0 }}>
                  {guide.conclusion_analytique}
                </p>
              </div>

              {/* Navigation bas de page */}
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"center", paddingTop:10, borderTop:"1px solid var(--sep)" }}>
                {prevP ? (
                  <button onClick={() => selectPillar(prevKey)} style={{
                    display:"flex", alignItems:"center", gap:7, padding:"7px 13px",
                    background:"var(--sur)", border:"1px solid var(--sep)",
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <span style={{ fontSize:12, color:"var(--mu)" }}>←</span>
                    <div style={{ textAlign:"left" }}>
                      <div style={{ fontFamily:"monospace", fontSize:7, color:"var(--mu)" }}>
                        {TECH_ANALYSIS_ORDER.indexOf(prevKey) + 1} / 4
                      </div>
                      <div style={{ fontFamily:"monospace", fontSize:9,
                        color:prevP.color, fontWeight:700 }}>{prevP.label}</div>
                    </div>
                  </button>
                ) : <div/>}
                {nextP && (
                  <button onClick={() => selectPillar(nextKey)} style={{
                    display:"flex", alignItems:"center", gap:7, padding:"7px 13px",
                    background:`${nextP.color}10`, border:`1px solid ${nextP.color}30`,
                    borderRadius:8, cursor:"pointer",
                  }}>
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontFamily:"monospace", fontSize:7, color:"var(--mu)" }}>
                        {TECH_ANALYSIS_ORDER.indexOf(nextKey) + 1} / 4
                      </div>
                      <div style={{ fontFamily:"monospace", fontSize:9,
                        color:nextP.color, fontWeight:700 }}>{nextP.label}</div>
                    </div>
                    <span style={{ fontSize:12, color:nextP.color }}>→</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Tog({ label, active, color, tip, onChange }) {
  const btn = (
    <button onClick={onChange} style={{
      display:"flex", alignItems:"center", gap:5, borderRadius:7,
      border:`1px solid ${active?color+"55":"var(--card-bdr)"}`,
      background: active ? color+"14" : "transparent",
      padding:"5px 10px", fontSize:10, fontWeight:800,
      color: active ? color : "var(--mu)", transition:"all .12s", cursor:"pointer",
    }}>
      <span style={{ width:7, height:7, borderRadius:999, flexShrink:0,
        background: active ? color : "var(--sep)" }}/>
      {label}
    </button>
  );
  return tip ? <Tip {...(typeof tip==="string"?{body:tip}:tip)}>{btn}</Tip> : btn;
}


// ══════════════════════════════════════════════════════════════════
// 11c. COURS MODAL — Analyse Technique
// ══════════════════════════════════════════════════════════════════
function CourseModal({ onClose, isDark }) {
  const [activeChap, setActiveChap] = useState("intro");
  const [menuOpen, setMenuOpen] = useState(false);

  const CHAPTERS = [
    { id:"intro",     num:"0",  label:"Introduction" },
    { id:"postulats", num:"1",  label:"3 Postulats fondateurs" },
    { id:"structure", num:"2",  label:"Structure de Tendance" },
    { id:"momentum",  num:"3",  label:"Momentum" },
    { id:"volume",    num:"4",  label:"Volume & Wyckoff" },
    { id:"timing",    num:"5",  label:"Timing d'entrée" },
    { id:"tscore",    num:"6",  label:"TScore Invelps" },
    { id:"synth",     num:"7",  label:"Synthèse pratique" },
  ];

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const h = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", h);
    return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", h); };
  }, [onClose]);

  useEffect(() => {
    const container = document.getElementById("tm-course-scroll");
    if (!container) return;
    const timer = setTimeout(() => {
      const handleScroll = () => {
        const sections = container.querySelectorAll("[data-csec]");
        let current = "intro";
        sections.forEach(el => {
          const top = el.getBoundingClientRect().top - container.getBoundingClientRect().top;
          if (top <= 80) current = el.dataset.csec;
        });
        setActiveChap(prev => {
          if (prev === current) return prev;
          setTimeout(() => {
            document.getElementById(`tm-sc-${current}`)?.scrollIntoView({ behavior:"smooth", block:"nearest" });
          }, 0);
          return current;
        });
      };
      container.addEventListener("scroll", handleScroll, { passive:true });
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const scrollTo = id => {
    const container = document.getElementById("tm-course-scroll");
    const el = container?.querySelector(`#tm-cs-${id}`);
    if (el && container) container.scrollTo({ top: el.offsetTop - 20, behavior:"smooth" });
    setActiveChap(id);
    setMenuOpen(false);
  };

  const navBg  = isDark ? "#0c0b09" : "#f5f2ec";
  const navBdr = isDark ? "#1e1e26" : "#d4cfc4";
  const bg     = isDark ? "#0d1117" : "#f8f7f4";
  const card   = isDark ? "rgba(15,23,42,.96)" : "#ffffff";
  const bdr    = isDark ? "rgba(148,163,184,.12)" : "rgba(30,40,34,.08)";
  const ts     = isDark ? "#e2e8f0" : "#1f2a24";
  const tx     = isDark ? "#cbd5e1" : "#2d3b34";
  const mu     = isDark ? "#64748b" : "#6f7b74";
  const go     = C.green;
  const am     = C.amber;
  const re     = C.red;
  const su     = isDark ? "rgba(255,255,255,.025)" : "rgba(0,0,0,.018)";
  const bo     = isDark ? "rgba(148,163,184,.12)" : "rgba(30,40,34,.07)";

  // ── Micro-composants ───────────────────────────────────────────
  const Sec = ({ id, num, tag, title }) => (
    <div id={`tm-cs-${id}`} data-csec={id} style={{ paddingTop:"2rem", scrollMarginTop:"60px", maxWidth:780 }}>
      <div style={{ fontFamily:"monospace", fontSize:".43rem", letterSpacing:".2em",
        textTransform:"uppercase", color:go, marginBottom:".25rem" }}>{num} — {tag}</div>
      <h2 style={{ fontFamily:"Georgia,serif", fontSize:"clamp(1.05rem,2.4vw,1.35rem)",
        color:ts, fontWeight:400, margin:"0 0 .65rem",
        borderBottom:`2px solid ${go}28`, paddingBottom:".3rem" }}>{title}</h2>
    </div>
  );
  const P = ({ children }) => (
    <p style={{ fontSize:".81rem", color:tx, lineHeight:1.8, margin:"0 0 .55rem", maxWidth:720 }}>{children}</p>
  );
  const B = ({ children }) => <strong style={{ color:ts }}>{children}</strong>;
  const Ref = ({ src }) => (
    <span style={{ fontFamily:"monospace", fontSize:".43rem", color:go, marginLeft:4, opacity:.8 }}>[{src}]</span>
  );
  const Bx = ({ label, color, children }) => (
    <div style={{ margin:".5rem 0", padding:".65rem .88rem",
      background:`${color||go}0c`, border:`1px solid ${color||go}26`, borderRadius:7, maxWidth:780 }}>
      {label && <div style={{ fontFamily:"monospace", fontSize:".42rem", color:color||go,
        textTransform:"uppercase", letterSpacing:".1em", marginBottom:".28rem", fontWeight:700 }}>{label}</div>}
      <div style={{ fontSize:".78rem", color:tx, lineHeight:1.72 }}>{children}</div>
    </div>
  );
  const Ind = ({ name, formula, seuil, note }) => (
    <div style={{ margin:".5rem 0", border:`1px solid ${bo}`, borderRadius:8, overflow:"hidden" }}>
      <div style={{ padding:"6px 12px", background:su, display:"flex", alignItems:"center", gap:8,
        borderBottom:`1px solid ${bo}` }}>
        <span style={{ fontFamily:"monospace", fontSize:".62rem", fontWeight:700, color:ts }}>{name}</span>
        {formula && <span style={{ fontFamily:"monospace", fontSize:".55rem", color:go, marginLeft:"auto" }}>{formula}</span>}
      </div>
      <div style={{ padding:"8px 12px" }}>
        {seuil && <div style={{ fontFamily:"monospace", fontSize:".55rem", color:am, marginBottom:".3rem" }}>
          Seuils : {seuil}</div>}
        {note && <div style={{ fontSize:".75rem", color:tx, lineHeight:1.65, fontStyle:"italic" }}>{note}</div>}
      </div>
    </div>
  );
  const Q = ({ q, options, correct, expl }) => (
    <div style={{ margin:".65rem 0", border:`1px solid ${go}22`, borderRadius:9, overflow:"hidden" }}>
      <div style={{ padding:"8px 12px", background:`${go}09`,
        fontSize:".77rem", fontWeight:700, color:ts, borderBottom:`1px solid ${go}22` }}>{q}</div>
      <div style={{ padding:"6px 10px", display:"flex", flexDirection:"column", gap:4 }}>
        {options.map((o, i) => (
          <div key={i} style={{ display:"flex", gap:7, alignItems:"flex-start" }}>
            <span style={{ fontFamily:"monospace", fontSize:".55rem", fontWeight:800, flexShrink:0, marginTop:2,
              color: i === correct ? go : mu }}>{String.fromCharCode(65+i)}.</span>
            <span style={{ fontSize:".75rem", color: i === correct ? ts : mu,
              fontWeight: i === correct ? 700 : 400 }}>{o}</span>
            {i === correct && <span style={{ color:go, fontWeight:800, fontSize:".7rem", flexShrink:0 }}>✓</span>}
          </div>
        ))}
        {expl && (
          <div style={{ marginTop:6, paddingTop:6, borderTop:`1px dashed ${bo}`,
            fontSize:".72rem", color:mu, lineHeight:1.65, fontStyle:"italic" }}>{expl}</div>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ position:"fixed", inset:0, zIndex:10000,
      width:"100vw", height:"100vh",
      background:"rgba(0,0,0,.88)",
      backdropFilter:"blur(6px)", display:"flex", flexDirection:"column",
      boxSizing:"border-box" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>

      {/* ── Header compact ── */}
      <div style={{ flexShrink:0, background:navBg, borderBottom:`1px solid ${navBdr}`,
        display:"flex", alignItems:"center", padding:"10px 16px", gap:10 }}>
        <div style={{ width:32, height:32, borderRadius:8,
          background:`${go}18`, border:`1px solid ${go}28`,
          display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={go}
            strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/>
            <path d="M6 12v5c3.33 2 8.67 2 12 0v-5"/>
          </svg>
        </div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
            <span style={{ fontFamily:"monospace", fontSize:9, fontWeight:800,
              color:go, textTransform:"uppercase", letterSpacing:".12em" }}>COURS 4.1</span>
            <span style={{ fontFamily:"monospace", fontSize:9, color:mu }}>
              — Analyse Technique · Timing de Marché
            </span>
          </div>
          <div style={{ fontFamily:"monospace", fontSize:7.5, color:mu, marginTop:2 }}>
            8 chapitres · Murphy · Moskowitz · Wyckoff · Wilder
          </div>
        </div>
        <button onClick={onClose} style={{
          width:34, height:34, flexShrink:0, display:"flex", alignItems:"center",
          justifyContent:"center",
          background:isDark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)",
          border:`1px solid ${navBdr}`, borderRadius:8, cursor:"pointer",
          fontSize:16, color:mu,
        }}>×</button>
      </div>

      {/* ── Contenu scroll pleine largeur ── */}
      <div id="tm-course-scroll" style={{ flex:1, overflowY:"auto", background:bg,
        padding:"clamp(16px,4vw,40px)" }}>
        <div style={{ maxWidth:800, margin:"0 auto" }}>

            {/* 0 — Introduction */}
            <Sec id="intro" num="0" tag="Introduction" title="L'analyse technique : lire le marché plutôt que l'entreprise"/>
            <P>L'analyse technique (AT) est l'étude des <B>cours boursiers et des volumes</B> dans le but de prévoir les mouvements futurs des prix. Elle repose sur un postulat radical : <B>tout ce qui est connaissable est déjà dans le prix</B>.</P>
            <Bx label="Pourquoi étudier l'analyse technique ?" color={go}>
              Pour un investisseur fondamental, l'AT répond à une question que les DCF ne peuvent pas résoudre : <B>quand entrer ?</B> Damodaran reconnaît qu'un titre peut être fondamentalement sous-évalué et continuer de baisser pendant 24 mois. L'AT fournit le timing que le fondamental ne donne pas. Murphy (ch. 1) : «&nbsp;le fondamental dit <em>quoi acheter</em>, le technique dit <em>quand acheter</em>.&nbsp;»
            </Bx>

            {/* 1 — Postulats */}
            <Sec id="postulats" num="1" tag="Fondements" title="Les 3 postulats fondateurs"/>
            <P><B>Postulat 1 — Le marché intègre tout.</B> Chaque prix de clôture reflète l'ensemble des informations disponibles. Murphy (ch. 1) : «&nbsp;le marché sait tout avant vous.&nbsp;» <Ref src="Murphy, TAOTFM ch. 1"/></P>
            <P><B>Postulat 2 — Les prix évoluent en tendances.</B> Un titre en hausse a statistiquement plus de chances de continuer à monter. C'est la base empirique de l'effet momentum documenté par Jegadeesh & Titman (1993). <Ref src="Jegadeesh & Titman, JoF 1993"/></P>
            <P><B>Postulat 3 — L'histoire se répète.</B> Les patterns reflètent la psychologie humaine — peur et cupidité — qui restent constants. <Ref src="Edwards & Magee, TAST 1948"/></P>
            <Q q="Murphy dit que 'le marché intègre tout'. Pourquoi une action peut-elle baisser sans aucune annonce officielle ?"
              options={[
                "Par hasard — les marchés sont aléatoires à court terme.",
                "Des acteurs informés vendent avant que l'information soit publique, laissant des traces dans le prix et le volume.",
                "Le cours suit toujours les SMAs avec un délai de 10 séances.",
                "L'analyse fondamentale a détecté un problème dans les comptes.",
              ]}
              correct={1}
              expl="Wyckoff : les institutions laissent inévitablement des empreintes dans le prix et le volume bien avant les annonces officielles."/>

            {/* 2 — Structure */}
            <Sec id="structure" num="2" tag="Pilier 1 · 40%" title="Structure de Tendance : direction, régularité, fiabilité"/>
            <P>La première question est <B>directionnelle</B> : le titre est-il au-dessus ou en dessous de sa SMA200 ? C'est la frontière universelle entre marché haussier et baissier. Murphy (ch. 9). <Ref src="Murphy, TAOTFM ch. 9"/></P>
            <Ind name="SMA 200 jours" formula="Moy. 200 derniers cours"
              seuil="Prix > SMA200 = bull · Prix < SMA200 = bear · Golden Cross (SMA50>SMA200) = confirmation"
              note="Référence universelle des institutionnels. Un Golden Cross (SMA50 > SMA200) est le signal de confirmation structurelle le plus suivi."/>
            <Ind name="Régression OLS + R²" formula="P_t = α + β·t"
              seuil="R² > 0.65 = fiable · 0.50–0.65 = exploitable · < 0.50 = trop erratique"
              note="β sans R² n'a aucune valeur. Un CAGR élevé avec R² = 0.17 signifie que 83% de la variance est du bruit — ce titre monte sur le papier mais descend souvent dans la réalité."/>
            <Ind name="Canal statistique ±1σ / ±2σ" formula="Droite ± écart-type des résidus"
              seuil="Prix dans ±1σ = normal · +2σ = sur-extension rare (~2.5% des observations)"
              note="95% des observations historiques se trouvent dans le canal ±2σ. Un prix à +2σ est statistiquement rare — signal de prudence, pas d'achat."/>
            <Bx label="Séquence d'analyse structure" color={C.green}>
              <B>(1)</B> SMA200 : au-dessus ou en dessous ? <B>(2)</B> Alignement SMA20 &gt; SMA50 &gt; SMA200 : le court terme confirme-t-il le long terme ? <B>(3)</B> R² : la tendance est-elle exploitable ? Un titre haussier avec alignement parfait et R² &gt; 0.65 représente la configuration la plus solide.
            </Bx>

            {/* 3 — Momentum */}
            <Sec id="momentum" num="3" tag="Pilier 2 · 25%" title="Force & Momentum : direction, qualité, force relative"/>
            <P>Moskowitz, Ooi & Pedersen (2012) ont prouvé la persistance du momentum sur <B>200 ans</B> d'histoire et plus de 40 marchés. Gray & Vogel : la <B>régularité</B> du momentum est tout aussi prédictive que sa direction. <Ref src="Moskowitz, Ooi & Pedersen, JFE 2012"/></P>
            <Ind name="TSMOM 12M" formula="(P_t / P_{t-252j}) − 1"
              seuil="TSMOM 12M > 0 = signal principal · 12M+6M+3M tous positifs = confirmation forte"
              note="Le signal le plus robuste et le plus documenté. La configuration 12M+/3M− signale un essoufflement à court terme dans une tendance de fond intacte."/>
            <Ind name="FIP — Frog In The Pan" formula="SIGN(TSMOM) × (% jours baissiers − % haussiers)"
              seuil="FIP ≤ 0 = smooth momentum (régulier) · FIP > 0 = erratique (fragile)"
              note="Da, Gurun & Warachka (2014) : un TSMOM 12M de +60% construit sur 5 séances exceptionnelles sous-performe de 3–5%/an vs smooth momentum. Le FIP qualifie la conviction derrière le chiffre."/>
            <Ind name="RS Ratio — Force Relative" formula="P_titre / P_indice (base 1)"
              seuil="RS en hausse = leadership croissant · RS > 1 et montant = force confirmée"
              note="Jegadeesh & Titman (1993) : les titres qui surperforment leur indice sur 3–12 mois tendent à continuer. TSMOM+ ET RS Ratio en hausse = double signal de momentum."/>

            {/* 4 — Volume */}
            <Sec id="volume" num="4" tag="Pilier 3 · 20%" title="Volume & Wyckoff : l'empreinte des mains fortes"/>
            <P>Wyckoff (1910) : Loi de l'Effort et du Résultat — <B>le volume (effort) doit être proportionnel au mouvement de prix (résultat)</B>. Murphy (ch. 7) : «&nbsp;le volume confirme la tendance — son absence la fragilise.&nbsp;» <Ref src="Murphy, TAOTFM ch. 7"/></P>
            <Ind name="Volume Ratio 20J / moy. 60J" formula="Vol_20j / Vol_60j"
              seuil="Ratio > 1.2 = participation élevée · 0.85–1.2 = normale · < 0.85 = faible"
              note="Hausse de prix + volume > moyenne = signal fort (institutionnels présents). Hausse de prix + volume faible = No Demand — la progression manque de conviction."/>
            <Bx label="Cycle de Wyckoff — 4 phases" color={C.olive}>
              <B>Accumulation</B> : institutions achètent discrètement, prix en range. <B>Markup</B> : tendance haussière établie. <B>Distribution</B> : institutions allègent dans l'euphorie — signal de danger. <B>Markdown</B> : baisse prolongée. L'<B>UTAD</B> (Upthrust After Distribution) est la fausse cassure haute conçue pour piéger les derniers acheteurs.
            </Bx>

            {/* 5 — Timing */}
            <Sec id="timing" num="5" tag="Pilier 4 · 15%" title="Timing d'entrée : RSI, Z-Score, convergence SMAs"/>
            <P>Murphy (ch. 1) : «&nbsp;même dans la meilleure tendance, acheter au mauvais moment détruit le ratio risque/récompense.&nbsp;» Le timing ne contredit pas la tendance — il précise le <B>meilleur moment pour y entrer</B>.</P>
            <Ind name="RSI 14" formula="100 − [100 / (1 + RS)] · RS = moy.gains14j / moy.pertes14j"
              seuil="RSI > 70 = surachat · < 30 = survente · En tendance forte : zone 40–55 = zone d'achat optimale"
              note="La vraie puissance du RSI : la divergence baissière (prix↑ + RSI↓) signale un essoufflement interne. Ne pas appliquer mécaniquement 70 en tendance forte — le RSI peut rester > 60 pendant des mois."/>
            <Ind name="Z-Score (régression)" formula="(Prix − Droite OLS) / σ résiduel"
              seuil="Z > +2σ = sur-extension rare · 0 à +1σ = zone favorable · < −1σ = opportunité"
              note="Mesure l'éloignement du prix par rapport à sa trajectoire tendancielle. Un Z > +2σ : le retour vers la droite est probable, même si le délai est imprévisible. Combiner RSI (CT) + Z-Score (LT)."/>
            <Bx label="Timing optimal — confluence des 3 signaux" color={C.orange}>
              <B>Configuration idéale</B> : RSI entre 40 et 60 (zone de force, non surachat) + Z-Score entre 0 et +1σ (pas sur-étendu) + pullback sur SMA20 ou SMA50 (support dynamique testé). Cette confluence est rare — c'est précisément pour cela qu'elle est précieuse.
            </Bx>

            {/* 6 — TScore */}
            <Sec id="tscore" num="6" tag="Synthèse Invelps" title="TScore : la synthèse en un seul chiffre"/>
            <P>Le TScore Invelps est un <B>score composite 0–100</B> qui agrège les 4 piliers avec leurs pondérations : Structure (40%) + Momentum (25%) + Volume (20%) + Timing (15%).</P>
            <Bx label="Interprétation du TScore" color={go}>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {[
                  { range:"≥ 75", label:"Configuration solide · Signal d'entrée ou de renforcement.", col:go },
                  { range:"55–74", label:"Configuration acceptable · Surveiller avant d'agir.", col:am },
                  { range:"40–54", label:"Configuration mixte · Patienter ou alléger.", col:am },
                  { range:"< 40", label:"Configuration défavorable · Éviter les positions longues.", col:re },
                ].map(r => (
                  <div key={r.range} style={{ display:"flex", gap:10, alignItems:"baseline" }}>
                    <span style={{ fontFamily:"monospace", fontSize:".6rem", fontWeight:800, color:r.col, minWidth:40 }}>{r.range}</span>
                    <span style={{ fontSize:".75rem", color:tx }}>→ {r.label}</span>
                  </div>
                ))}
              </div>
            </Bx>

            {/* 7 — Synthèse pratique */}
            <Sec id="synth" num="7" tag="Méthode" title="Synthèse pratique : le filtre séquentiel"/>
            <Bx label="Ordre d'analyse recommandé" color={am}>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {[
                  { n:"1", t:"Structure (filtre éliminatoire)", d:"SMA200 au-dessus ? R² > 0.50 ? Si non sur les deux : ne pas entrer, indépendamment du reste." },
                  { n:"2", t:"Momentum (confirmation)", d:"TSMOM 12M positif ? FIP ≤ 0 ? RS Ratio en hausse ? La convergence des trois est requise pour un signal fort." },
                  { n:"3", t:"Volume (validation)", d:"Volumes confirment-ils ? Wyckoff : quelle phase ? Distribution = sortie, Accumulation = attendre le markup." },
                  { n:"4", t:"Timing (optimisation)", d:"RSI entre 40–60 + Z-Score 0–+1σ + pullback SMA20. Attendre la confluence avant d'entrer." },
                ].map(s => (
                  <div key={s.n} style={{ display:"flex", gap:10, alignItems:"flex-start" }}>
                    <span style={{ fontFamily:"monospace", fontSize:".55rem", fontWeight:800,
                      color:"#fff", background:am, padding:"1px 6px", borderRadius:3, flexShrink:0, marginTop:2 }}>Étape {s.n}</span>
                    <div>
                      <div style={{ fontSize:".75rem", fontWeight:700, color:ts, marginBottom:2 }}>{s.t}</div>
                      <div style={{ fontSize:".72rem", color:tx, lineHeight:1.65 }}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Bx>
            <Q q="Un titre présente : SMA20 > SMA50 > SMA200, TSMOM 12M +45%, FIP = +0.4, RSI 72. Quel est le signal composite ?"
              options={[
                "BUY immédiat — structure et momentum excellents.",
                "Attendre — FIP > 0 (momentum erratique) et RSI en surachat. Patienter un pullback.",
                "SELL — RSI à 72 est toujours baissier.",
                "Analyser le volume d'abord — aucun signal sans données de volume.",
              ]}
              correct={1}
              expl="FIP = +0.4 : momentum construit sur pics isolés (erratique). RSI 72 : surachat (correction possible). Structure excellente mais timing et qualité du momentum insuffisants — patienter un repli vers SMA20/50."/>

            <div style={{ height:"3rem" }}/>
          </div>
        </div>

      {/* ── Sidebar overlay chapitres ── */}
      {menuOpen && (
        <div style={{
          position:"absolute", top:0, left:0, bottom:0,
          width:"min(280px,80vw)", zIndex:10200,
          background:navBg, borderRight:`1px solid ${navBdr}`,
          display:"flex", flexDirection:"column",
          boxShadow:"4px 0 24px rgba(0,0,0,.4)",
        }} onClick={e => e.stopPropagation()}>
          <div style={{ padding:"12px 16px 10px", borderBottom:`1px solid ${navBdr}`,
            display:"flex", alignItems:"center", justifyContent:"space-between", flexShrink:0 }}>
            <span style={{ fontFamily:"monospace", fontSize:"7px", color:mu,
              textTransform:"uppercase", letterSpacing:".15em", fontWeight:700 }}>Chapitres</span>
            <button onClick={() => setMenuOpen(false)} style={{
              width:24, height:24, display:"flex", alignItems:"center", justifyContent:"center",
              background:"transparent", border:"none", cursor:"pointer", fontSize:14, color:mu,
            }}>×</button>
          </div>
          <div style={{ flex:1, overflowY:"auto", padding:"6px 0" }}>
            {CHAPTERS.map(c => (
              <button id={`tm-sc-${c.id}`} key={c.id} onClick={() => scrollTo(c.id)} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10,
                padding:"10px 16px", border:"none", cursor:"pointer",
                background: activeChap === c.id ? `${go}12` : "transparent",
                borderLeft: activeChap === c.id ? `3px solid ${go}` : "3px solid transparent",
                transition:"all .12s",
              }}>
                <span style={{ fontFamily:"monospace", fontSize:"7px",
                  color: activeChap === c.id ? go : mu,
                  fontWeight:700, minWidth:14, textAlign:"right", flexShrink:0 }}>{c.num}</span>
                <span style={{ fontFamily:"monospace", fontSize:"10px", textAlign:"left",
                  fontWeight: activeChap === c.id ? 700 : 500,
                  color: activeChap === c.id ? go : tx }}>{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Backdrop ferme la sidebar ── */}
      {menuOpen && (
        <div style={{ position:"absolute", inset:0, zIndex:10100,
          background:"rgba(0,0,0,.35)" }}
          onClick={() => setMenuOpen(false)}/>
      )}

      {/* ── FAB ☰ bas-droite ── */}
      <button onClick={() => setMenuOpen(v => !v)} style={{
        position:"absolute", bottom:24, right:20, zIndex:10300,
        width:52, height:52, borderRadius:16,
        background: isDark ? "#1a2234" : "#f0ede6",
        border:`1px solid ${navBdr}`,
        boxShadow:"0 4px 20px rgba(0,0,0,.4)",
        display:"flex", alignItems:"center", justifyContent:"center",
        cursor:"pointer", fontSize:22, color:go,
      }}>☰</button>

    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 11bis. VERDICT BANNER — étape finale de l'architecture générique
// (doc §2 : Header → Questions → Sections → Verdict/Synthèse finale ;
//  §8 : VerdictBanner fait partie des composants réutilisables communs
//  à tous les modules Invelps — Timing, Market Structure, Fundamentals…)
// Contrairement au bloc "SYNTHÈSE D'ANALYSE" plus haut dans la carte
// (réponse synthétique AVANT les preuves, §9 règle 2), le VerdictBanner
// clôt la page APRÈS les 4 sections d'analyse détaillées : il synthétise
// l'ensemble (§9 règle 6), 100% dynamique — aucune donnée codée en dur.
// ══════════════════════════════════════════════════════════════════
function VerdictBanner({ d, company, scoreGlobal, marketBias, regimeCol, actionLabel, actionCol }) {
  const f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
  const wyckoff   = d?.wyckoff_context ?? "Neutre";
  const wyEvent   = d?.wyckoff_event   ?? "None";
  const rsi14     = f(d?.rsi_14, null);
  const tsmom12   = f(d?.tsmom_12m, null);
  const zScore    = f(d?.z_score, null);
  const symbol    = company?.name || d?.symbol || "Le titre";

  const ACTION_VERBS = {
    ENTRER:     "renforcer ou initier une position",
    SURVEILLER: "surveiller avant tout renforcement",
    ATTENDRE:   "attendre un point d'entrée plus favorable",
    ÉVITER:     "éviter toute prise de position à ce stade",
  };
  const verb = ACTION_VERBS[actionLabel] || "surveiller l'évolution du titre";

  const clauses = [];
  if (tsmom12 != null) {
    clauses.push(tsmom12 >= 0
      ? `un momentum annuel positif (TSMOM 12M ${tsmom12 >= 0 ? "+" : ""}${tsmom12.toFixed(1)}%)`
      : `un momentum annuel négatif (TSMOM 12M ${tsmom12.toFixed(1)}%)`);
  }
  clauses.push(`une phase Wyckoff de ${wyckoff.toLowerCase()}${wyEvent && wyEvent !== "None" ? ` (${wyEvent})` : ""}`);
  if (rsi14 != null) {
    clauses.push(rsi14 > 70 ? `un RSI en zone de surachat (${rsi14.toFixed(1)})`
               : rsi14 < 30 ? `un RSI en zone de survente (${rsi14.toFixed(1)})`
               : `un RSI neutre (${rsi14.toFixed(1)})`);
  }
  if (zScore != null && Math.abs(zScore) > 1.5) {
    clauses.push(`une sur-extension statistique (Z-Score ${zScore >= 0 ? "+" : ""}${zScore.toFixed(2)}σ)`);
  }

  return (
    <div className="tm5-card" style={{ borderTop:`3px solid ${actionCol}` }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
        gap:8, marginBottom:12, flexWrap:"wrap" }}>
        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
          textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
          VERDICT — SYNTHÈSE FINALE
        </span>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800, color:regimeCol }}>
            {marketBias.toUpperCase()}
          </span>
          <span style={{
            display:"inline-flex", alignItems:"center", gap:4, borderRadius:99, padding:"3px 10px",
            fontSize:11, fontWeight:800, background:`${actionCol}18`, border:`1px solid ${actionCol}44`,
            color:actionCol, fontFamily:"ui-monospace,monospace",
          }}>
            {actionLabel}
          </span>
        </div>
      </div>

      <p style={{ fontSize:12.5, lineHeight:1.75, color:"var(--ts)", margin:0 }}>
        {symbol} affiche un score technique global de <strong style={{ color:getScoreColor(scoreGlobal) }}>{scoreGlobal}/100</strong>{" "}
        porté par {clauses.length > 1 ? clauses.slice(0, -1).join(", ") + " et " + clauses[clauses.length - 1] : (clauses[0] || "un diagnostic technique neutre")}.{" "}
        Sur la base des 4 piliers (structure, momentum, volume, timing), la lecture d'ensemble invite à{" "}
        <strong style={{ color:actionCol }}>{verb}</strong>.
      </p>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// 12. MAIN COMPONENT
// ══════════════════════════════════════════════════════════════════
export default function TimingMarcheCard({ d, th, nav }) {
  const { darkMode: isDark } = useInvelpsTheme();

  useEffect(() => {
    applyTheme(isDark);
  }, [isDark]);

  // Inject minimal CSS
  useEffect(() => {
    if (!document.getElementById("tm5-base-css")) {
      const s = document.createElement("style");
      s.id = "tm5-base-css";
      s.textContent = `
        .tm5 *{box-sizing:border-box;margin:0;padding:0;}
        .tm5{
          font-family:ui-sans-serif,system-ui,-apple-system,'Segoe UI',sans-serif;
          width:100%;
          max-width:none;
          min-width:0;
        }
        .tm5 button{font-family:inherit;}
        .tm5 svg{max-width:100%;}
        .tm5 img{max-width:100%;height:auto;}
        /* Prevent horizontal scroll at document level */
        html,body{overflow-x:hidden;width:100%;margin:0;padding:0;}
        body:has(.tm5) #root{max-width:none;width:100%;margin:0;padding:0;}
        body:has(.tm5) main{max-width:none;}
        .tm5-sticky-head{
          position:sticky;
          top:var(--inv-nav-h,60px);
          z-index:25;
          background:var(--card-bg);
          border-bottom:1px solid var(--sep);
        }
        .tm5-page{
          width:100%;
          max-width:none;
          margin:0;
          padding:10px 10px 48px;
          display:flex;
          flex-direction:column;
          gap:10px;
        }
        @media(min-width:640px){.tm5-page{padding:16px 20px 64px;gap:16px;}}
        @media(min-width:1024px){.tm5-page{padding:20px 28px 72px;gap:20px;}}
        .tm5-card{
          background:var(--card-bg);
          border:1px solid var(--card-bdr);
          border-radius:14px;
          padding:14px;
          min-width:0;
          width:100%;
          overflow:hidden;
        }
        @media(min-width:640px){.tm5-card{padding:16px;}}

        /* ── Diagnostic: 4 blocs CÔTE À CÔTE ── */
        .tm9-diag-grid{
          display:grid;
          grid-template-columns:1fr;
          gap:0;
          width:100%;
        }
        .tm9-diag-grid > *{min-width:0;}
        @media(min-width:640px){
          .tm9-diag-grid{grid-template-columns:1fr 1fr;}
        }
        @media(min-width:1080px){
          .tm9-diag-grid{grid-template-columns:repeat(4,1fr);}
        }
        @media(max-width:639px){
          .tm9-diag-grid{grid-template-columns:1fr;}
          .tm9-diag-grid > *{
            border-right:none !important;
            border-bottom:1px solid var(--sep);
            padding-left:0 !important;
            padding-right:0 !important;
            padding-top:12px;
            padding-bottom:12px;
          }
          .tm9-diag-grid > *:first-child{padding-top:0;}
          .tm9-diag-grid > *:last-child{border-bottom:none;padding-bottom:0;}
        }

        /* ── ProfilDescriptionRadarCard ──
           Mobile (<640px) : 1 colonne, sans bordures latérales
           Tablet (640–820px) : 2 colonnes
           Desktop (>820px) : 3 colonnes
        */
        .pdrc-grid{
          display:grid;
          grid-template-columns:1fr;
          gap:16px;
          width:100%;
        }
        .pdrc-grid > *{min-width:0;}
        /* Sur mobile : pas de padding/bordure latérale */
        .pdrc-col-sep{border-right:none !important; padding-right:0 !important;}
        .pdrc-col-mid{padding-left:0 !important; padding-right:0 !important; border-right:none !important;}
        .pdrc-col-last{padding-left:0 !important;}
        @media(min-width:640px){
          .pdrc-grid{grid-template-columns:1fr 1fr;gap:0;}
          .pdrc-col-sep{border-right:1px solid var(--sep) !important; padding-right:20px !important;}
          .pdrc-col-mid{padding:0 20px !important; border-right:1px solid var(--sep) !important;}
          .pdrc-col-last{padding-left:20px !important;}
        }
        @media(min-width:1024px){
          .pdrc-grid{grid-template-columns:minmax(220px,.9fr) minmax(0,1.35fr) minmax(220px,.9fr);gap:0;}
        }
        .tm5-diag{display:flex;flex-direction:column;gap:12px;}
        @media(min-width:480px){.tm5-diag{flex-direction:row;align-items:flex-start;gap:16px;}}
        .tm5-diag-right{flex:1;min-width:0;display:flex;flex-direction:column;gap:12px;}
        @media(min-width:768px){.tm5-diag-right{flex-direction:row;gap:16px;}}
        .tm5-sbars-m{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-top:12px;padding-top:12px;border-top:1px solid var(--sep);}
        @media(min-width:560px){.tm5-sbars-m{display:none;}}
        .tm5-sbars-d{display:none;}
        @media(min-width:768px){.tm5-sbars-d{display:block;flex:1;min-width:0;}}
        .tm5-pillars{display:grid;grid-template-columns:1fr;gap:10px;}
        @media(min-width:480px){.tm5-pillars{grid-template-columns:1fr 1fr;}}
        @media(min-width:1024px){.tm5-pillars{grid-template-columns:repeat(4,1fr);}}
        .tm5-bottom{display:grid;grid-template-columns:1fr;gap:12px;}
        @media(min-width:768px){.tm5-bottom{grid-template-columns:1fr 1fr;}}
        .tm5-toggles{display:flex;flex-wrap:wrap;gap:5px;align-items:center;}
        .tm5-chart-type{margin-left:auto;display:flex;gap:4px;flex-shrink:0;}
        .tm5-legend{display:flex;flex-wrap:wrap;gap:5px 12px;padding:10px 0 2px;}
        @media(max-width:560px){
          .tm5-page{padding-left:8px;padding-right:8px;}
          .tm5-card{padding:12px;border-radius:12px;}
          .tm5-toggles{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:4px;}
          .tm5-toggles > *{width:100%;min-width:0;justify-content:center;padding:4px 8px!important;}
          .tm5-chart-type{grid-column:1/-1;margin-left:0;width:100%;}
          .tm5-chart-type button{flex:1;}
          .tm5-legend{display:grid;grid-template-columns:1fr;gap:6px;}
        }
        .tm5-murphy{display:flex;overflow-x:auto;gap:0;-webkit-overflow-scrolling:touch;}
        .tm5-murphy::-webkit-scrollbar{display:none;}
        /* ── CompassModal responsive ── */
        .compass-overlay{padding:clamp(6px,3vw,20px)!important;}
        .compass-modal{width:min(960px,98vw)!important;max-height:92dvh!important;}
        .compass-seqbar{display:flex!important;}
        @media(max-width:720px){
          .compass-overlay{align-items:stretch!important;padding:0!important;}
          .compass-modal{width:100vw!important;max-height:100dvh!important;height:100dvh!important;border-radius:0!important;}
          .compass-seqbar{display:none!important;}
        }
        /* CourseModal responsive */
        .tm-course-sidebar{display:flex!important;}
        .tm-course-fab{display:none!important;}
        @media(max-width:640px){
          .tm-course-sidebar{display:none!important;}
          .tm-course-fab{display:flex!important;}
        }
      `;
      document.head.appendChild(s);
    }
  }, []);

  const [showGuide, setShowGuide] = useState(null); // null ou clé de pilier technique
  const [showCours, setShowCours] = useState(false);
  const [period,    setPeriod   ] = useState("1Y");
  const [showSMA20, setSMA20    ] = useState(true);
  const [showSMA50, setSMA50    ] = useState(false);
  const [showSMA200,setSMA200   ] = useState(false);
  const [showReg,   setReg      ] = useState(true);
  const [showBands, setBands    ] = useState(true);
  const [showZScore,setZScore   ] = useState(true);
  const [showVolume,setVolume   ] = useState(true);
  const [showRSI,   setRSI      ] = useState(true);
  const [showLevels,setLevels   ] = useState(true);
  const [chartType, setChartType] = useState("line");

  // ── Education modal
  const [eduModal, setEduModal] = useState(null);
  const openEdu  = useCallback(anchor => setEduModal({ anchor }), []);
  const closeEdu = useCallback(() => setEduModal(null), []);

  // SMAs only relevant for ≤1Y
  const isShortRange = ["3M","6M","1Y"].includes(period);

  const apiDaily = useMemo(() => buildDailyFromPriceChart(d?.priceChart), [d?.priceChart]);
  const apiRsData = useMemo(() => buildRsDataFromChart(d?.rsChart), [d?.rsChart]);
  const baseDaily = apiDaily?.length ? apiDaily : DAILY;

  // Filter data by period
  const chartData = useMemo(() => {
    // 1. Filter by period
    const allData = baseDaily;
    const lastDate = new Date(allData[allData.length-1].d);
    const cutoff = new Date(lastDate);
    if (period==="3M")  cutoff.setMonth(cutoff.getMonth()-3);
    else if (period==="6M") cutoff.setMonth(cutoff.getMonth()-6);
    else if (period==="1Y") cutoff.setFullYear(cutoff.getFullYear()-1);

    const filtered = period==="3Y"||period==="5Y"||period==="MAX"
      ? (() => {
          if (period==="3Y") { cutoff.setFullYear(cutoff.getFullYear()-3); }
          else if (period==="5Y") { cutoff.setFullYear(cutoff.getFullYear()-5); }
          return cutoff.getFullYear() < 2000 ? allData : allData.filter(d => new Date(d.d) >= cutoff);
        })()
      : allData.filter(d => new Date(d.d) >= cutoff);

    // 2. Recompute Z-Score on the window (rolling 60j within the window)
    const closes = filtered.map(d => d.c);
    const zScores = closes.map((_, i) => {
      const w   = Math.min(60, i + 1);
      const sl  = closes.slice(i - w + 1, i + 1);
      const zm  = sl.reduce((a,b) => a+b, 0) / sl.length;
      const zs  = Math.sqrt(sl.reduce((s,y) => s + (y-zm)**2, 0) / sl.length) || 1;
      return +((closes[i] - zm) / zs).toFixed(3);
    });

    // 3. Rebase RS to start of the window (RS=100 at first candle of period)
    const base0    = filtered[0];
    const nvdaBase = base0.c;
    const ndxBase  = base0._ndx || 1;

    return filtered.map((d, i) => ({
      ...d,
      z:  zScores[i],
      rs: d._ndx != null
        ? +((d.c / nvdaBase) / (d._ndx / ndxBase) * 100).toFixed(2)
        : null,
    }));
  }, [period, baseDaily]);

  const last     = chartData[chartData.length-1];
  const sma20v   = chartData.filter(d=>d.s20!=null).slice(-1)[0]?.s20;
  const sma50v   = chartData.filter(d=>d.s50!=null).slice(-1)[0]?.s50;
  const sma200v  = chartData.filter(d=>d.s200!=null).slice(-1)[0]?.s200;

  // ── Données société — StockDashboard ────────────────────────────────────
  const company = {
    name:           d?.symbol              ?? NVDA_COMPANY_DEFAULT.name,
    fullName:       d?.company_name        ?? NVDA_COMPANY_DEFAULT.fullName,
    exchange:       d?.exchange_code       ?? NVDA_COMPANY_DEFAULT.exchange,
    sector:         d?.sector              ?? NVDA_COMPANY_DEFAULT.sector,
    industry:       d?.industry            ?? NVDA_COMPANY_DEFAULT.industry,
    region:         d?.region              ?? NVDA_COMPANY_DEFAULT.region,
    marketCapClass: d?.market_cap_class    ?? NVDA_COMPANY_DEFAULT.marketCapClass,
    style:          d?.stock_style         ?? NVDA_COMPANY_DEFAULT.style,
    cyclicals:      d?.peter_lynch_category ?? NVDA_COMPANY_DEFAULT.cyclicals,
    price:          d?.last_price          ?? NVDA_COMPANY_DEFAULT.price,
    mtdPct:         d?.last_return_m1      ?? NVDA_COMPANY_DEFAULT.mtdPct,
    ytdPct:         d?.ytd_pct             ?? NVDA_COMPANY_DEFAULT.ytdPct,
    description:    d?.description         ?? NVDA_COMPANY_DEFAULT.description,
  };

  // ── IScore + Radar — StockDashboard_Decisions ────────────────────────────
  const iscore = parseFloat(d?.iscore ?? NVDA_ISCORE_DEFAULT);
  const radarData = d?.radar_fondamentaux != null ? [
    { axis:"Fondamentaux", value: parseFloat(d.radar_fondamentaux)      },
    { axis:"Valorisation", value: parseFloat(d.radar_valorisation)      },
    { axis:"Marché",       value: parseFloat(d.radar_marche)            },
    { axis:"Momentum",     value: parseFloat(d.radar_momentum)          },
    { axis:"Risque/Rend.", value: parseFloat(d.radar_risque_rendement)  },
  ] : NVDA_RADAR_DEFAULT;

  // ── SR Levels — timing_marche_summary ───────────────────────────────────
  const SR_LEVELS = {
    support: {
      price: parseFloat(d?.support_level    ?? SR_LEVELS_DEFAULT.support.price),
      label: "Support",
      tests: d?.support_touches             ?? SR_LEVELS_DEFAULT.support.tests,
      volR:  parseFloat(d?.support_vol_ratio ?? 1.043),
      col:   "#6f8a5b",
    },
    resistance: {
      price: parseFloat(d?.resist_level     ?? SR_LEVELS_DEFAULT.resistance.price),
      label: "Résistance",
      tests: d?.resist_touches              ?? SR_LEVELS_DEFAULT.resistance.tests,
      volR:  parseFloat(d?.resist_vol_ratio  ?? 1.377),
      col:   "#c6523f",
    },
  };

  // ── Scores 4 piliers — timing_marche_summary ────────────────────────────
  const scoreStructure = Math.round(parseFloat(d?.trend_score    ?? 65.2));
  const scoreMomentum  = Math.round(parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86));
  const scoreVolume    = Math.round(parseFloat(d?.volume_score   ?? 68));
  const scoreTiming    = Math.round(parseFloat(d?.timing_quality ?? 40));

  // TScore technique = moyenne pondérée des 4 piliers (structure 40% · momentum 25% · volume 20% · timing 15%)
  const scoreGlobal = d?.tscore_global != null
    ? Math.round(parseFloat(d.tscore_global))
    : Math.round(scoreStructure * 0.40 + scoreMomentum * 0.25 + scoreVolume * 0.20 + scoreTiming * 0.15);

  const momentumDir    = d?.momentum_direction ?? "Retournement baissier";

  // ── Diagnostic régime/signal/action ─────────────────────────────────────
  const marketBias   = d?.market_bias_label ?? "Pression baissière";
  const tscoreSignal = d?.tscore_signal     ?? (scoreGlobal >= 65 ? "Signal fort · configuration technique solide" : scoreGlobal >= 50 ? "Signal modéré · surveiller" : "Signal faible · patienter");

  // actionLabel dérivé du TScore technique si interp_action absent
  const actionInterp = d?.interp_action ?? "";
  const actionLabel  = actionInterp.includes("ENTRER")     ? "ENTRER"
                     : actionInterp.includes("SURVEILLER") ? "SURVEILLER"
                     : actionInterp.includes("ATTENDRE")   ? "ATTENDRE"
                     : actionInterp.includes("ÉVITER")     ? "ÉVITER"
                     : scoreGlobal >= 70 ? "ENTRER"
                     : scoreGlobal >= 55 ? "SURVEILLER"
                     : scoreGlobal >= 42 ? "ATTENDRE"
                     : "ÉVITER";
  const actionCol    = getStatusColor(
                         actionLabel === "ENTRER" ? "good" : actionLabel === "ÉVITER" ? "bad" : "neutral"
                       );
  const regimeCol    = getStatusColor(
                         marketBias.toLowerCase().includes("haussier") ? "good"
                         : marketBias.toLowerCase().includes("baissier") ? "bad" : "neutral"
                       );

  // ── last_updated ─────────────────────────────────────────────────────────
  const lastUpdated = d?.updated_at ?? "2026-06-03T14:07:20";

  const sC = v => v>=75?C.green:v>=55?C.amber:C.red;

  // Legend items below chart
  const legendItems = [
    { dot:RSI_COL.line,    label:`Prix : ${last?.c.toFixed(2)||"—"}` },
    ...(showSMA20&&isShortRange  ? [{ dot:SMA_COL.s20,  label:`SMA 20 : ${sma20v?.toFixed(2)||"—"}`,  tipKey:"legend_sma20"  }] : []),
    ...(showSMA50&&isShortRange  ? [{ dot:SMA_COL.s50,  label:`SMA 50 : ${sma50v?.toFixed(2)||"—"}`,  tipKey:"legend_sma50"  }] : []),
    ...(showSMA200&&isShortRange ? [{ dot:SMA_COL.s200, label:`SMA 200 : ${sma200v?.toFixed(2)||"—"}`, tipKey:"legend_sma200" }] : []),
    ...(showReg    ? [{ dot:SIG.reg.col,  label:`Régression : ${last?.reg?.toFixed(2)||"—"}`, dash:SIG.reg.dash, tipKey:"legend_reg" }] : []),
    ...(showBands  ? [
      { dot:SIG.b2p.col, label:`+2σ : ${last?.b2p?.toFixed(2)||"—"}`, dash:SIG.b2p.dash, tipKey:"legend_b2" },
      { dot:SIG.b1p.col, label:`+1σ : ${last?.b1p?.toFixed(2)||"—"}`, dash:SIG.b1p.dash, tipKey:"legend_b1" },
      { dot:SIG.b1n.col, label:`-1σ : ${last?.b1n?.toFixed(2)||"—"}`, dash:SIG.b1n.dash, tipKey:"legend_b1" },
      { dot:SIG.b2n.col, label:`-2σ : ${last?.b2n?.toFixed(2)||"—"}`, dash:SIG.b2n.dash, tipKey:"legend_b2" },
    ] : []),
    ...(showZScore ? [{ dot:C.amber, label:`Z-Score : ${last?(last.z>=0?"+":"")+last.z.toFixed(2):"—"}`, tipKey:"toggle_zscore" }] : []),
    ...(showVolume ? [{ dot:C.green, label:"Volume", tipKey:"toggle_volume" }] : []),
    ...(showRSI    ? [{ dot:RSI_COL.line, label:"RSI 14", tipKey:"toggle_rsi" }] : []),
    ...(showLevels ? [
      { dot:SR_LEVELS.support.col,    label:`Support ${SR_LEVELS.support.price}`,    dash:"5 3", tipKey:"legend_support" },
      { dot:SR_LEVELS.resistance.col, label:`Résistance ${SR_LEVELS.resistance.price}`, dash:"5 3", tipKey:"legend_resist"  },
    ] : []),
  ];

  return (
    <React.Fragment>
    <div className="tm5" style={{
      background: isDark ? "var(--bg)" : MARKET_LIGHT.appBg, minHeight:"100vh", color:"var(--ts)", width:"100%",
    }}>
    <div className="tm5-page">

      {/* ══ PROFIL / DESCRIPTION / RADAR ══ */}
      <ProfilDescriptionRadarCard
        company={company}
        iscore={iscore}
        radarData={radarData}
        onOpenEdu={openEdu}
        onOpenGuide={(pillarKey) => setShowGuide(pillarKey || "structure")}
        onOpenCours={() => setShowCours(true)}
      />

      {/* ══ DIAGNOSTIC TECHNIQUE GLOBAL ══ */}
      {(() => {
        // ── Extraction des métriques AT — champs exacts de timing_marche_summary ──
        const _f   = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
        const lastPr   = _f(d?.last_price,        199.57);   // last_price
        const sma200v  = _f(d?.sma200,             183.72);  // sma200
        const sma50v   = _f(d?.sma50,              186.94);  // sma50
        const sma20v   = _f(d?.sma20,              196.17);  // sma20
        const r2v      = _f(d?.r_squared,           0.84378); // r_squared
        // tsmom stocké en % bruts dans la DB : 60.12 = +60.12%, -6.49 = -6.49%
        const tsmom12v = _f(d?.tsmom_12m,          60.12);   // tsmom_12m
        const tsmom6v  = _f(d?.tsmom_6m,           -6.53);   // tsmom_6m
        const tsmom3v  = _f(d?.tsmom_3m,           -6.49);   // tsmom_3m
        const fipv     = _f(d?.fip_score,          -0.0342); // fip_score
        const volR20v  = _f(d?.volume_ratio_20d,   1.4767);  // volume_ratio_20d
        const wyckoffv = d?.wyckoff_context  ?? "Distribution"; // wyckoff_context
        const wyEventv = d?.wyckoff_event    ?? "UTAD";          // wyckoff_event
        const rsi14v   = _f(d?.rsi_14,             60.81);   // rsi_14
        const zScorev  = _f(d?.z_score,            1.317);   // z_score
        const momDirv  = d?.momentum_direction ?? "Retournement baissier"; // momentum_direction

        // ── Réponses depuis diag_q* (Timing_marche_interpretations v2) ──────────
        // diag_q1_ok / q2_ok / q3_ok / q4_ok : 1=Oui · 0=Partiellement · -1=Non
        // Fallback calcul dynamique si champs absents (déploiement SP en attente)
        const _okToStatus = v => v === 1 ? true : v === -1 ? false : "mid";

        const diagResults = {
          structure: (() => {
            if (d?.diag_q1_tendance) return {
              ok:  _okToStatus(parseInt(d.diag_q1_ok ?? 0)),
              txt: d.diag_q1_tendance,
            };
            // Fallback
            if (!sma200v || !lastPr) return null;
            const above   = lastPr > sma200v;
            const aligned = sma20v && sma50v ? sma20v > sma50v && sma50v > sma200v : null;
            const r2txt   = r2v != null ? `, R² = ${(r2v*100).toFixed(0)}%` : "";
            if (!above)
              return { ok:false, txt:`Non — cours ${lastPr.toFixed(2)} < SMA200 ${sma200v.toFixed(2)} : tendance primaire baissière${r2txt}. Murphy ch. 9.` };
            if (above && aligned === true)
              return { ok:true,  txt:`Oui — cours ${lastPr.toFixed(2)} > SMA200 ${sma200v.toFixed(2)}, alignement SMA20 (${sma20v?.toFixed(1)}) > SMA50 (${sma50v?.toFixed(1)}) > SMA200${r2txt}.` };
            if (above && aligned === false && sma20v && sma50v)
              return { ok:"mid", txt:`Partiellement — cours ${lastPr.toFixed(2)} > SMA200 ${sma200v.toFixed(2)}${r2txt}, mais ${sma20v > sma50v ? "SMA50 > SMA200 non vérifiée" : "SMA20 > SMA50 non vérifiée"}.` };
            return { ok:"mid", txt:`Partiellement — cours au-dessus de la SMA200 (${sma200v.toFixed(2)}) mais alignement SMA incomplet.` };
          })(),
          momentum: (() => {
            if (d?.diag_q2_momentum) return {
              ok:  _okToStatus(parseInt(d.diag_q2_ok ?? 0)),
              txt: d.diag_q2_momentum,
            };
            // Fallback
            if (tsmom12v < 0)
              return { ok:false, txt:`Non — TSMOM 12M = ${tsmom12v.toFixed(1)}% : biais baissier annuel. Moskowitz (2012) : ne pas entrer à contre-courant du momentum annuel.` };
            if (tsmom12v > 0 && tsmom3v < 0)
              return { ok:"mid", txt:`Il s'essouffle — TSMOM 12M = +${tsmom12v.toFixed(1)}% (tendance annuelle positive) mais TSMOM 3M = ${tsmom3v.toFixed(1)}% (baisse récente). Murphy ch. 10 : signal d'essoufflement.` };
            if (tsmom12v > 0 && fipv != null && fipv <= 0)
              return { ok:true,  txt:`Il accélère — TSMOM 12M = +${tsmom12v.toFixed(1)}%${fipv != null ? `, FIP = ${fipv.toFixed(3)} (≤ 0)` : ""} : momentum positif et régulier. Gray & Vogel : smooth momentum, configuration idéale.` };
            return { ok: tsmom12v > 0 ? "mid" : false, txt:`TSMOM 12M = ${tsmom12v >= 0 ? "+" : ""}${tsmom12v.toFixed(1)}%, 3M = ${tsmom3v >= 0 ? "+" : ""}${tsmom3v.toFixed(1)}%${fipv != null ? `, FIP = ${fipv.toFixed(3)}` : ""}.` };
          })(),
          volume: (() => {
            if (d?.diag_q3_volume) return {
              ok:  _okToStatus(parseInt(d.diag_q3_ok ?? 0)),
              txt: d.diag_q3_volume,
            };
            // Fallback
            const isDistrib = wyckoffv && (wyckoffv.includes("Distribution") || wyckoffv.includes("Markdown"));
            if (isDistrib)
              return { ok:false, txt:`Non — phase Wyckoff ${wyckoffv}${wyEventv ? ` (${wyEventv})` : ""} : les institutionnels allègent. Volume Ratio 20J = ${volR20v.toFixed(2)}×. Villahermosa ch. 20 : signal baissier majeur.` };
            if (volR20v >= 1.2)
              return { ok:true,  txt:`Oui — Volume Ratio 20J = ${volR20v.toFixed(2)}× la moyenne (+${((volR20v-1)*100).toFixed(0)}%)${wyckoffv ? `, phase Wyckoff ${wyckoffv}` : ""}. Murphy ch. 7 : le volume confirme la tendance.` };
            if (volR20v < 0.85)
              return { ok:"mid", txt:`Partiellement — Volume Ratio 20J = ${volR20v.toFixed(2)}× (sous la normale). Wyckoff : No Demand — mouvement sans conviction institutionnelle.` };
            return { ok:"mid", txt:`Partiellement — Volume Ratio 20J = ${volR20v.toFixed(2)}×${wyckoffv ? `, phase Wyckoff ${wyckoffv}` : ""}. Volume dans la norme.` };
          })(),
          timing: (() => {
            if (d?.diag_q4_timing) return {
              ok:  _okToStatus(parseInt(d.diag_q4_ok ?? 0)),
              txt: d.diag_q4_timing,
            };
            // Fallback
            const rsiOB   = rsi14v > 70, rsiOS = rsi14v < 30;
            const rsiForce= rsi14v >= 40 && rsi14v <= 60;
            const zExt    = zScorev > 2,  zNeut = zScorev >= -1 && zScorev <= 1;
            if (rsiOB && zExt)
              return { ok:false, txt:`Non — double sur-extension : RSI 14 = ${rsi14v.toFixed(1)} (surachat) ET Z-Score +${zScorev.toFixed(2)}σ. Ratio risque/rendement dégradé. Attendre un repli${sma20v ? ` vers SMA20 (${sma20v.toFixed(2)})` : ""}.` };
            if (rsiOS)
              return { ok:true,  txt:`Oui — RSI 14 = ${rsi14v.toFixed(1)} (survente < 30) : zone de rebond fiable (Wilder 1978). Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ.` };
            if (rsiForce && zNeut)
              return { ok:true,  txt:`Oui — RSI 14 = ${rsi14v.toFixed(1)} (zone de force 40–60) ET Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ (près de la droite OLS) : confluence idéale. Murphy ch. 15.` };
            if (rsiOB)
              return { ok:"mid", txt:`Pas encore — RSI 14 = ${rsi14v.toFixed(1)} (surachat > 70). Wilder : patienter un repli vers RSI 40–55. SMA20 = ${sma20v?.toFixed(2) ?? "—"} est la cible naturelle.` };
            if (zExt)
              return { ok:"mid", txt:`Pas encore — Z-Score +${zScorev.toFixed(2)}σ : cours trop éloigné de la droite OLS. RSI 14 = ${rsi14v.toFixed(1)}. Attendre Z-Score 0/+1σ.` };
            return { ok:"mid", txt:`Acceptable — RSI 14 = ${rsi14v.toFixed(1)}, Z-Score = ${zScorev >= 0 ? "+" : ""}${zScorev.toFixed(2)}σ. Conditions correctes sans être optimales.` };
          })(),
        };
        const pillarsDisp = [
          { key:"structure", label:"Structure de Tendance", icon:"◈", score:scoreStructure, color:C.green,  weight:"40%", indic:"SMA200 · Alignement · R²" },
          { key:"momentum",  label:"Force / Momentum",      icon:"⚡", score:scoreMomentum,  color:C.amber,  weight:"25%", indic:"TSMOM 12M · FIP · RS Ratio" },
          { key:"volume",    label:"Participation / Volume", icon:"◎", score:scoreVolume,    color:C.olive,  weight:"20%", indic:"Vol. Ratio 20J · Wyckoff" },
          { key:"timing",    label:"Timing / Opportunité",  icon:"⊕", score:scoreTiming,    color:C.orange, weight:"15%", indic:"RSI 14 · Z-Score · SMAs" },
        ];

        return (
          <div className="tm5-card">
            {/* ── Header ── */}
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
              gap:8, marginBottom:14, paddingBottom:11, borderBottom:"1px solid var(--sep)",
              flexWrap:"wrap" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                  textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
                  DIAGNOSTIC TECHNIQUE GLOBAL
                </span>
                <TipIcon text={TIPS.score_global}/>
              </div>
              {/* Badges clés AT en ligne */}
              <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                {rsi14v != null && (() => {
                  const rStatus = rsi14v > 70 ? "bad" : rsi14v < 30 ? "good" : rsi14v >= 40 && rsi14v <= 60 ? "good" : "neutral";
                  const rc = getStatusColor(rStatus);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:6, background:`${rc}0d`, border:`1px solid ${rc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:rc }}>
                        RSI {rsi14v.toFixed(1)} · {rsi14v > 70 ? "Surachat" : rsi14v < 30 ? "Survente" : rsi14v >= 40 && rsi14v <= 60 ? "Force" : "Neutre"}
                      </span>
                    </div>
                  );
                })()}
                {zScorev != null && (() => {
                  const zStatus = Math.abs(zScorev) > 2 ? "bad" : Math.abs(zScorev) > 1 ? "neutral" : "good";
                  const zc = getStatusColor(zStatus);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:6, background:`${zc}0d`, border:`1px solid ${zc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:zc }}>
                        Z-Score {zScorev >= 0 ? "+" : ""}{zScorev.toFixed(2)}σ
                      </span>
                    </div>
                  );
                })()}
                {wyckoffv && (() => {
                  const wc = getPhaseColor(wyckoffv);
                  return (
                    <div style={{ padding:"3px 9px", borderRadius:6, background:`${wc}0d`, border:`1px solid ${wc}28` }}>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800, color:wc }}>
                        {wyckoffv}{wyEventv && wyEventv !== "None" ? ` · ${wyEventv}` : ""}
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* ── Top row : Gauge + barres piliers + Régime/Signal/Action ── */}
            <div style={{ width:"100%" }} className="tm9-diag-grid">
              {/* Gauge */}
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center",
                justifyContent:"center", paddingRight:16, borderRight:"1px solid var(--sep)" }}>
                <Gauge value={scoreGlobal/100} labelTop={`${scoreGlobal} / 100`} labelBottom="TScore"/>
              </div>

              {/* Barres 4 piliers */}
              <div style={{ padding:"0 16px", borderRight:"1px solid var(--sep)",
                display:"flex", flexDirection:"column", justifyContent:"center" }}>
                <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                  letterSpacing:".1em", color:"var(--mu)", marginBottom:12 }}>
                  RÉPARTITION DES SCORES
                </div>
                {pillarsDisp.map(s => (
                  <div key={s.key} style={{ marginBottom:9 }}>
                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                      <div style={{ display:"flex", flexDirection:"column", gap:1 }}>
                        <span style={{ fontSize:10, color:"var(--mu)", fontWeight:600 }}>{s.label}</span>
                        {s.key === "momentum" && momentumDir && (
                          <Tip {...TIPS.mom_direction}>
                            <span style={{ fontSize:8, fontWeight:700, color:C.orange,
                              fontFamily:"ui-monospace,monospace", letterSpacing:".04em", cursor:"default" }}>
                              {momentumDir}
                            </span>
                          </Tip>
                        )}
                      </div>
                      <div style={{ display:"flex", gap:5, flexShrink:0 }}>
                        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                          fontWeight:800, color:colorForPct(s.score) }}>{s.score}/100</span>
                        <span style={{ fontSize:9, color:"var(--mu)" }}>{s.weight}</span>
                      </div>
                    </div>
                    <div style={{ height:6, borderRadius:999, background:"var(--sep)", overflow:"hidden" }}>
                      <div style={{ height:"100%", width:`${s.score}%`,
                        background:`linear-gradient(90deg,${colorForPct(s.score)}cc,${colorForPct(s.score)})`,
                        borderRadius:999, transition:"width .5s ease" }}/>
                    </div>
                  </div>
                ))}
              </div>

              {/* Régime / Signal / Action */}
              <div style={{ padding:"0 16px", borderRight:"1px solid var(--sep)",
                display:"flex", flexDirection:"column", justifyContent:"center", gap:8 }}>
                {[
                  { sub:"RÉGIME TECHNIQUE", val:marketBias.toUpperCase(),                        col:regimeCol, tipKey:"regime" },
                  { sub:"SIGNAL PRINCIPAL", val:tscoreSignal.split("·")[0].trim().toUpperCase(), col:C.amber,   tipKey:"signal" },
                  { sub:"ACTION SUGGÉRÉE",  val:actionLabel,                                     col:actionCol, tipKey:"action" },
                ].map(({sub,val,col,tipKey}) => (
                  <div key={sub} style={{ padding:"9px 12px", borderRadius:9,
                    background:`${col}0e`, border:`1px solid ${col}28` }}>
                    <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                      letterSpacing:".09em", color:"var(--mu)", marginBottom:3,
                      display:"flex", alignItems:"center", gap:4 }}>
                      {sub}<TipIcon text={TIPS[tipKey]}/>
                    </div>
                    <div style={{ fontFamily:"ui-monospace,monospace", fontSize:11,
                      fontWeight:800, color:col, lineHeight:1.3 }}>{val}</div>
                  </div>
                ))}
              </div>


              {/* 4ème colonne : Arguments positifs / Points de vigilance */}
              {(() => {
                const _lp   = parseFloat(d?.timing_last_price ?? d?.price ?? 199.57);
                const _s200 = parseFloat(d?.timing_sma200 ?? d?.sma200 ?? 183.72);
                const _s50  = parseFloat(d?.timing_sma50  ?? d?.sma50  ?? 186.94);
                const _s20  = parseFloat(d?.timing_sma20  ?? d?.sma20  ?? 196.17);
                const _r2   = parseFloat(d?.r_squared ?? 0.84);
                const _t12  = parseFloat(d?.tsmom_12m ?? 60.12);
                const _t3   = parseFloat(d?.tsmom_3m  ?? -6.49);
                const _fip  = parseFloat(d?.fip_score ?? -0.034);
                const _vr   = parseFloat(d?.volume_ratio_20d ?? 1.4767);
                const _wyk  = d?.wyckoff_context ?? "Distribution";
                const _wev  = d?.wyckoff_event   ?? "UTAD";
                const _rsi  = parseFloat(d?.rsi_14  ?? 60.81);
                const _z    = parseFloat(d?.z_score ?? 1.317);

                const above   = _lp > _s200;
                const aligned = _s20 > _s50 && _s50 > _s200;
                const isDistrib = _wyk.includes("Distribution") || _wyk.includes("Markdown");
                const isAccum   = _wyk.includes("Accumulation") || _wyk.includes("Markup");

                const pos = [];
                if (above && aligned)   pos.push("Tendance primaire robuste");
                else if (above)         pos.push("Cours au-dessus de la SMA200");
                if (_r2 >= 0.65)        pos.push(`Tendance régulière (R² ${(_r2*100).toFixed(0)}%)`);
                if (_t12 > 20)          pos.push(`Momentum annuel solide (TSMOM 12M +${_t12.toFixed(1)}%)`);
                if (_fip <= 0 && _t12 > 0) pos.push("Momentum régulier (FIP ≤ 0)");
                if (_vr >= 1.2 && !isDistrib) pos.push(`Volumes élevés (+${((_vr-1)*100).toFixed(0)}% vs moyenne)`);
                if (isAccum)            pos.push(`Phase Wyckoff : ${_wyk}`);
                if (_rsi >= 40 && _rsi <= 60) pos.push(`RSI en zone de force (${_rsi.toFixed(1)})`);
                if (_rsi < 30)          pos.push(`RSI en survente — opportunité (${_rsi.toFixed(1)})`);
                if (scoreMomentum >= 65) pos.push("Leadership sectoriel confirmé");

                const vig = [];
                if (!above)             vig.push("Cours sous la SMA200 — tendance baissière");
                if (!aligned && above)  vig.push("Alignement SMA incomplet");
                if (_t12 > 0 && _t3 < 0) vig.push(`Momentum court terme en repli (3M ${_t3.toFixed(1)}%)`);
                if (_t12 < 0)           vig.push(`TSMOM 12M négatif (${_t12.toFixed(1)}%)`);
                if (isDistrib)          vig.push(`Phase Wyckoff ${_wyk}${_wev && _wev !== "None" ? ` — ${_wev}` : ""}`);
                if (_fip > 0.3)         vig.push("Momentum erratique (FIP > 0)");
                if (_rsi > 70)          vig.push(`RSI en surachat (${_rsi.toFixed(1)})`);
                if (_z > 1.5)           vig.push(`Prix au-dessus de sa droite OLS (Z +${_z.toFixed(2)}σ)`);
                if (_vr < 0.85)         vig.push("Volumes faibles — No Demand");
                if (scoreTiming < 40)   vig.push("Timing sous-optimal");

                return (
                  <div style={{ paddingLeft:16, display:"flex", flexDirection:"column", gap:12 }}>
                    {pos.length > 0 && (
                      <div>
                        <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                          letterSpacing:".1em", color:C.green, marginBottom:7 }}>
                          ARGUMENTS POSITIFS
                        </div>
                        {pos.slice(0,4).map((p,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start",
                            gap:6, marginBottom:5 }}>
                            <span style={{ color:C.green, fontSize:11, flexShrink:0 }}>✓</span>
                            <span style={{ fontSize:9.5, color:"var(--ts)", lineHeight:1.4 }}>{p}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {vig.length > 0 && (
                      <div>
                        <div style={{ fontSize:9, fontWeight:800, textTransform:"uppercase",
                          letterSpacing:".1em", color:C.orange, marginBottom:7 }}>
                          POINTS DE VIGILANCE
                        </div>
                        {vig.slice(0,4).map((v,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"flex-start",
                            gap:6, marginBottom:5 }}>
                            <span style={{ color:C.orange, fontSize:11, flexShrink:0 }}>⚠</span>
                            <span style={{ fontSize:9.5, color:"var(--ts)", lineHeight:1.4 }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>{/* fin tm9-diag-grid */}
            {/* ── Ce que les indicateurs révèlent + Lecture croisée (pleine largeur) ── */}
            <div style={{ marginTop:16, paddingTop:14, borderTop:"1px solid var(--sep)" }}>
              <div style={{ marginBottom:10 }}>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                  color:"var(--ts)", marginBottom:3 }}>
                  Ce que les indicateurs révèlent
                </div>
                <div style={{ fontFamily:"ui-monospace,monospace", fontSize:8, color:"var(--mu)" }}>
                  4 lectures — tendance · force · volume · timing
                </div>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                {pillarsDisp.filter(p => diagResults[p.key] != null).map(p => {
                  const res  = diagResults[p.key];
                  const col  = getStatusColor(res.ok === true ? "good" : res.ok === false ? "bad" : "neutral");
                  const icon = res.ok === true ? "✓" : res.ok === false ? "✗" : "~";
                  return (
                    <div key={p.key} style={{ display:"flex", gap:8, alignItems:"flex-start",
                      padding:"9px 12px", borderRadius:9,
                      background:`${col}07`, border:`1px solid ${col}22` }}>
                      <span style={{ fontSize:14, color:p.color, flexShrink:0, marginTop:1 }}>{p.icon}</span>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap", marginBottom:3 }}>
                          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8,
                            fontWeight:700, color:p.color }}>{p.label}</span>
                          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:7,
                            color:"var(--mu)", opacity:.7 }}>· {p.indic}</span>
                        </div>
                        <p style={{ margin:"0 0 4px", fontSize:9.5, fontStyle:"italic",
                          color:"var(--mu)", lineHeight:1.4 }}>
                          {PILLAR_QUESTIONS[p.key]}
                        </p>
                        <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                          fontWeight:700, color:col, lineHeight:1.55 }}>
                          {res.txt}
                        </p>
                      </div>
                      <button onClick={() => setShowGuide(p.key)} title={`Guide ${p.label}`}
                        style={{ width:22, height:22, flexShrink:0, display:"flex",
                          alignItems:"center", justifyContent:"center",
                          background:"transparent", border:`1px solid ${p.color}28`,
                          borderRadius:4, cursor:"pointer", fontSize:9, color:p.color }}>🧭</button>
                    </div>
                  );
                })}
              </div>

              {/* ── Lecture croisée ── */}
              {(() => {
                // Priorité : champs DB (lecture_croisee_*)
                if (d?.lecture_croisee_titre && d?.lecture_croisee_txt) {
                  const okVal = parseInt(d.lecture_croisee_ok ?? 0);
                  const col   = okVal === 1 ? C.green : okVal === -1 ? C.red : C.orange;
                  const icon  = okVal === 1 ? "✓" : okVal === -1 ? "✗" : "⚠";
                  return (
                    <div style={{ marginTop:10, padding:"10px 14px", borderRadius:10,
                      background:`${col}08`, border:`1px solid ${col}28` }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                        <span style={{ fontSize:13, color:col }}>{icon}</span>
                        <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800,
                          color:col, textTransform:"uppercase", letterSpacing:".1em" }}>
                          {d.lecture_croisee_titre}
                        </span>
                      </div>
                      <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                        color:"var(--tx)", lineHeight:1.72 }}>{d.lecture_croisee_txt}</p>
                    </div>
                  );
                }

                // Fallback : calcul dynamique
                const t12pos    = tsmom12v > 0;
                const t3neg     = tsmom3v  < 0;
                const isDistrib = wyckoffv && (wyckoffv.includes("Distribution") || wyckoffv.includes("Markdown"));
                const isAccum   = wyckoffv && (wyckoffv.includes("Accumulation") || wyckoffv.includes("Markup"));
                const volFort   = volR20v >= 1.2;
                const volFaible = volR20v < 0.85;
                const rsiOB     = rsi14v > 70;
                const rsiOS     = rsi14v < 30;
                const above     = lastPr && sma200v && lastPr > sma200v;

                let lecture = null;

                if (t12pos && t3neg && isDistrib)
                  lecture = { col:C.orange, icon:"⚠", titre:"Lecture croisée — signal de transition",
                    txt:`Le momentum ralentit (+${tsmom12v.toFixed(1)}% sur 12M mais −${Math.abs(tsmom3v).toFixed(1)}% sur 3M) pendant que les institutionnels allègent (phase Wyckoff ${wyckoffv}${wyEventv && wyEventv !== "None" ? ` — ${wyEventv}` : ""}). Ces deux signaux racontent la même histoire : les mains fortes sortent progressivement en profitant du flux des acheteurs tardifs. Le prix peut encore monter brièvement, mais la pression vendeuse augmente en coulisse.` };

                else if (t12pos && !t3neg && volFort && isAccum && above)
                  lecture = { col:C.green, icon:"✓", titre:"Lecture croisée — configuration solide",
                    txt:`Tendance haussière, momentum positif sur les deux horizons, et institutionnels en accumulation avec des volumes élevés. Les trois piliers se confirment mutuellement : direction, force et participation sont alignées.` };

                else if (above && t12pos && t3neg && !isDistrib && !volFaible)
                  lecture = { col:C.amber, icon:"~", titre:"Lecture croisée — consolidation dans une tendance haussière",
                    txt:`La tendance de fond reste haussière et les volumes ne montrent pas d'alerte institutionnelle. L'essoufflement du momentum à court terme (−${Math.abs(tsmom3v).toFixed(1)}% sur 3M dans une progression annuelle de +${tsmom12v.toFixed(1)}%) ressemble à une consolidation normale plutôt qu'à un retournement. Ces phases de respiration peuvent offrir un meilleur point d'entrée que d'acheter en pleine accélération.` };

                else if (t12pos && volFaible && !isDistrib)
                  lecture = { col:C.amber, icon:"~", titre:"Lecture croisée — hausse sans conviction volumétrique",
                    txt:`Le momentum est positif mais les volumes sont sous la normale (${volR20v.toFixed(2)}× la moyenne). Une hausse sans volume est fragile — elle peut se retourner rapidement faute de participation institutionnelle. Wyckoff nomme cela "No Demand".` };

                else if (rsiOS && above)
                  lecture = { col:C.green, icon:"✓", titre:"Lecture croisée — opportunité dans la tendance",
                    txt:`Le titre est en survente (RSI ${rsi14v.toFixed(1)}) à l'intérieur d'une tendance haussière confirmée. C'est le type d'entrée idéal : un repli temporaire dans une tendance de fond intacte. Le ratio risque/rendement est favorable.` };

                else if (rsiOB && t3neg)
                  lecture = { col:C.red, icon:"✗", titre:"Lecture croisée — double signal de prudence",
                    txt:`Le RSI (${rsi14v.toFixed(1)}) signale un surachat ET le momentum à 3 mois est négatif (${tsmom3v.toFixed(1)}%). Ces deux signaux indiquent que le titre a monté trop vite et commence à perdre sa dynamique. Ce n'est pas le moment d'entrer.` };

                if (!lecture) return null;
                return (
                  <div style={{ marginTop:10, padding:"10px 14px", borderRadius:10,
                    background:`${lecture.col}08`, border:`1px solid ${lecture.col}28` }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                      <span style={{ fontSize:13, color:lecture.col }}>{lecture.icon}</span>
                      <span style={{ fontFamily:"ui-monospace,monospace", fontSize:8, fontWeight:800,
                        color:lecture.col, textTransform:"uppercase", letterSpacing:".1em" }}>
                        {lecture.titre}
                      </span>
                    </div>
                    <p style={{ margin:0, fontSize:"clamp(10px,1.3vw,11.5px)",
                      color:"var(--tx)", lineHeight:1.72 }}>{lecture.txt}</p>
                  </div>
                );
              })()}
            </div>

            {/* Mobile fallback bars */}
            <div className="tm5-sbars-m">
              {pillarsDisp.map(s => (
                <div key={s.key} style={{ borderRadius:10, padding:"9px 10px",
                  background:"var(--sur)", border:"1px solid var(--sep)" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <span style={{ fontSize:10, color:"var(--mu)", fontWeight:600 }}>{s.label}</span>
                    <span style={{ fontSize:9, color:"var(--mu)" }}>{s.weight}</span>
                  </div>
                  <div style={{ fontFamily:"ui-monospace,monospace", fontSize:18,
                    fontWeight:800, color:colorForPct(s.score), lineHeight:1, marginBottom:4 }}>{s.score}</div>
                  <div style={{ height:4, borderRadius:999, background:"var(--sep)", overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${s.score}%`,
                      background:colorForPct(s.score), borderRadius:999 }}/>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ══ BANDEAU NARRATIF (Niveau 2) ══ */}
      {(() => {
        // ── Génération dynamique cohérente avec la lecture croisée ──────────
        const _f = (v, def) => { const n = parseFloat(v ?? def); return isNaN(n) ? null : n; };
        const nLastPr   = _f(d?.last_price, d?.price ?? 199.57);
        const nSma200   = _f(d?.sma200,     183.72);
        const nSma20    = _f(d?.sma20,      196.17);
        const nSma50    = _f(d?.sma50,      186.94);
        const nT12      = _f(d?.tsmom_12m,  60.12) / 100;
        const nT6       = _f(d?.tsmom_6m,   -6.53) / 100;
        const nT3       = _f(d?.tsmom_3m,   -6.49) / 100;
        const nRsi      = _f(d?.rsi_14,     60.81);
        const nZ        = _f(d?.z_score,    1.317);
        const nVolR     = _f(d?.volume_ratio_20d, 1.4767);
        const nWyckoff  = d?.wyckoff_context ?? null;
        const nWyEv     = d?.wyckoff_event   ?? null;
        const company   = d?.company_name ?? d?.symbol ?? "Le titre";

        const isDistrib = nWyckoff && (nWyckoff.includes("Distribution") || nWyckoff.includes("Markdown"));
        const isAccum   = nWyckoff && (nWyckoff.includes("Accumulation") || nWyckoff.includes("Markup"));
        const above200  = nLastPr && nSma200 && nLastPr > nSma200;
        const aligned   = nSma20 && nSma50 && nSma200 ? nSma20 > nSma50 && nSma50 > nSma200 : null;
        const momEssouf = nT12 > 0 && nT3 < 0;
        const momAcc    = nT12 > 0 && nT3 >= 0;

        // Phrase 1 : Tendance
        const p1 = above200
          ? `La tendance de fond est haussière${aligned ? ", confirmée par l'alignement SMA20 > SMA50 > SMA200" : " mais l'alignement des moyennes mobiles n'est pas encore complet"}.`
          : `La tendance de fond est baissière — le cours (${nLastPr?.toFixed(2)}) est sous la SMA200 (${nSma200?.toFixed(2)}).`;

        // Phrase 2 : Momentum — 3 horizons, formulé en clair
        const p2 = nT12 < 0
          ? `Le momentum est négatif sur les trois horizons : 12 mois ${(nT12*100).toFixed(1)}%, 6 mois ${(nT6*100).toFixed(1)}%, 3 mois ${(nT3*100).toFixed(1)}%. Le mouvement de fond est baissier.`
          : nT12 > 0 && nT6 < 0 && nT3 < 0
          ? `Le momentum annuel reste positif (+${(nT12*100).toFixed(1)}% sur 12 mois), mais il s'essouffle sur les horizons intermédiaire et court : ${(nT6*100).toFixed(1)}% sur 6 mois et ${(nT3*100).toFixed(1)}% sur 3 mois. Le retournement est profond.`
          : nT12 > 0 && nT6 >= 0 && nT3 < 0
          ? `Le momentum annuel reste positif (+${(nT12*100).toFixed(1)}% sur 12 mois, +${(nT6*100).toFixed(1)}% sur 6 mois), mais il s'essouffle récemment : ${(nT3*100).toFixed(1)}% sur 3 mois. Essoufflement de court terme dans une tendance de fond intacte.`
          : nT12 > 0 && nT6 < 0 && nT3 >= 0
          ? `Signal mixte : +${(nT12*100).toFixed(1)}% sur 12 mois, ${(nT6*100).toFixed(1)}% sur 6 mois, +${(nT3*100).toFixed(1)}% sur 3 mois. Rebond récent après une phase de correction intermédiaire.`
          : nT12 > 0
          ? `Le momentum est positif sur les trois horizons : +${(nT12*100).toFixed(1)}% sur 12 mois, ${nT6 >= 0 ? "+" : ""}${(nT6*100).toFixed(1)}% sur 6 mois, ${nT3 >= 0 ? "+" : ""}${(nT3*100).toFixed(1)}% sur 3 mois.`
          : `Momentum : ${(nT12*100).toFixed(1)}% sur 12 mois, ${(nT6*100).toFixed(1)}% sur 6 mois.`;

        // Phrase 3 : Volume / Wyckoff
        const p3 = isDistrib
          ? `Les institutionnels sont en phase de distribution${nWyEv && nWyEv !== "None" ? ` (${nWyEv})` : ""} : ils allègent leurs positions pendant que le public achète.`
          : isAccum
          ? `Les volumes confirment : phase Wyckoff ${nWyckoff}${nWyEv && nWyEv !== "None" ? ` (${nWyEv})` : ""}, les institutionnels s'accumulent.`
          : nVolR >= 1.2
          ? `Les volumes sont élevés (${nVolR.toFixed(2)}× la moyenne) — la participation institutionnelle est présente.`
          : nVolR < 0.85
          ? `Les volumes sont faibles (${nVolR.toFixed(2)}× la moyenne) — la hausse manque de conviction institutionnelle.`
          : `Les volumes sont dans la norme${nWyckoff ? ` (phase Wyckoff : ${nWyckoff})` : ""}.`;

        // Phrase 4 : Timing
        const p4 = nRsi > 70 && nZ > 2
          ? `RSI 14 à ${nRsi.toFixed(1)} (surachat) et Z-Score à +${nZ.toFixed(2)}σ : double sur-extension — ce n'est pas le bon moment pour entrer.`
          : nRsi < 30
          ? `RSI 14 à ${nRsi.toFixed(1)} (survente) : zone de rebond statistiquement favorable.`
          : nRsi >= 40 && nRsi <= 60 && Math.abs(nZ) <= 1
          ? `RSI 14 à ${nRsi.toFixed(1)} et Z-Score à ${nZ >= 0 ? "+" : ""}${nZ.toFixed(2)}σ : confluence idéale pour entrer.`
          : `RSI 14 à ${nRsi.toFixed(1)}, Z-Score à ${nZ >= 0 ? "+" : ""}${nZ.toFixed(2)}σ${nRsi > 60 ? " — légèrement en surachat, attendre un repli" : nRsi < 40 ? " — légèrement en survente" : ""}.`;

        const diag  = d?.narrative_diagnostic  ?? [p1, p2, p3, p4].join(" ");
        const concl = d?.narrative_conclusion  ?? null;
        const decSumm = d?.decision_summary    ?? "";
        const bdrCol  = actionCol;
        return (
          <div style={{
            borderRadius:12, border:`1px solid ${bdrCol}33`,
            background:`${bdrCol}08`, padding:"12px 14px",
          }}>
            {/* Badge signal */}
            <div style={{display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8, gap:8}}>
              <div style={{display:"flex", alignItems:"center", gap:6}}>
                <span style={{fontFamily:"ui-monospace,monospace", fontSize:9, fontWeight:800,
                  textTransform:"uppercase", letterSpacing:".1em", color:"var(--mu)"}}>
                  SYNTHÈSE D'ANALYSE
                </span>
                <TipIcon text="Synthèse narrative générée à partir des 4 piliers. Lire de haut en bas : diagnostic → conclusion → action."/>
              </div>
              <span style={{
                display:"inline-flex", alignItems:"center", gap:4,
                borderRadius:99, padding:"3px 10px", fontSize:10, fontWeight:800,
                background:`${bdrCol}18`, border:`1px solid ${bdrCol}44`, color:bdrCol,
                fontFamily:"ui-monospace,monospace",
              }}>
                {actionLabel}
              </span>
            </div>
            {/* Diagnostic */}
            <div style={{fontSize:11, lineHeight:1.75, color:"var(--ts)", marginBottom:6}}>
              {diag}
            </div>
            {/* Conclusion SQL si disponible */}
            {concl && <>
              <div style={{height:1, background:"var(--sep)", margin:"8px 0"}}/>
              <div style={{fontSize:11, lineHeight:1.65, color:bdrCol, fontWeight:600}}>
                {concl}
              </div>
            </>}
            {decSumm && (
              <div style={{marginTop:6, fontSize:10, color:"var(--mu)", fontWeight:600,
                lineHeight:1.5, borderTop:"1px solid var(--sep)", paddingTop:6}}>
                {decSumm}
              </div>
            )}
          </div>
        );
      })()}

      {/* ══ 2. CHART ══ */}
      <div className="tm5-card">
        {/* Header + controls */}
        <div style={{ marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap", marginBottom:9 }}>
            <Tip {...TIPS.regression}>
              <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
                textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>PRIX &amp; TENDANCE</span>
            </Tip>
          </div>
          {/* Periods */}
          <div style={{ display:"flex", gap:4, marginBottom:9, flexWrap:"wrap" }}>
            {[
              { p:"3M",  tip:TIPS.period_3m  },
              { p:"6M",  tip:TIPS.period_6m  },
              { p:"1Y",  tip:TIPS.period_1y  },
              { p:"3Y",  tip:TIPS.period_3y  },
              { p:"5Y",  tip:TIPS.period_5y  },
              { p:"MAX", tip:TIPS.period_max },
            ].map(({p,tip})=>(
              <Tip key={p} {...tip}>
                <button onClick={()=>setPeriod(p)} style={{
                  borderRadius:6,
                  border:`1px solid ${period===p?C.amber+"66":"var(--card-bdr)"}`,
                  background: period===p ? "rgba(202,168,94,.14)" : "transparent",
                  padding:"4px 11px", fontSize:11, fontWeight:800, cursor:"pointer",
                  color: period===p ? C.amber : "var(--mu)",
                  transition:"all .12s",
                }}>{p}</button>
              </Tip>
            ))}
          </div>
          {/* Toggles */}
          <div className="tm5-toggles" style={{ marginBottom:6 }}>
            <Tog label="SMA 20"  active={showSMA20&&isShortRange}  color={SMA_COL.s20}
              tip={isShortRange ? TIPS.toggle_sma20 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA20(v=>!v)}/>
            <Tog label="SMA 50"  active={showSMA50&&isShortRange}  color={SMA_COL.s50}
              tip={isShortRange ? TIPS.toggle_sma50 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA50(v=>!v)}/>
            <Tog label="SMA 200" active={showSMA200&&isShortRange} color={SMA_COL.s200}
              tip={isShortRange ? TIPS.toggle_sma200 : "SMAs affichées uniquement pour 3M, 6M, 1Y"}
              onChange={()=>isShortRange&&setSMA200(v=>!v)}/>
            <Tog label="Régression" active={showReg}    color={SIG.reg.col}  tip={TIPS.toggle_reg}     onChange={()=>setReg(v=>!v)}/>
            <Tog label="Canal σ"    active={showBands}  color={SIG.b2p.col}  tip={TIPS.toggle_canal}   onChange={()=>setBands(v=>!v)}/>
            <Tog label="Z-Score"    active={showZScore} color={C.amber}      tip={TIPS.toggle_zscore}  onChange={()=>setZScore(v=>!v)}/>
            <Tog label="Volume"     active={showVolume} color={C.green}      tip={TIPS.toggle_volume}  onChange={()=>setVolume(v=>!v)}/>
            <Tog label="RSI 14"     active={showRSI}    color={C.orange}     tip={TIPS.toggle_rsi}     onChange={()=>setRSI(v=>!v)}/>
            <Tog label="S/R"        active={showLevels} color="#8b6f9a"      tip={TIPS.toggle_sr}      onChange={()=>setLevels(v=>!v)}/>
            <div className="tm5-chart-type">
              {[["candle","🕯 Bougies","toggle_candle"],["line","〜 Ligne","toggle_line"]].map(([t,l,tk])=>(
                <Tip key={t} {...TIPS[tk]}>
                  <button onClick={()=>setChartType(t)} style={{
                    padding:"5px 11px", borderRadius:7, fontSize:10, fontWeight:800, cursor:"pointer",
                    border:`1px solid ${chartType===t?C.amber+"66":"var(--card-bdr)"}`,
                    background: chartType===t?"rgba(202,168,94,.14)":"transparent",
                    color: chartType===t?C.amber:"var(--mu)",
                  }}>{l}</button>
                </Tip>
              ))}
            </div>
          </div>
        </div>

        {/* Chart */}
        <CandleChart
          data={chartData} range={period}
          showSMA20={showSMA20} showSMA50={showSMA50} showSMA200={showSMA200}
          showReg={showReg} showBands={showBands} showZScore={showZScore}
          showVolume={showVolume} showRSI={showRSI} showLevels={showLevels}
          srLevels={SR_LEVELS}
          chartType={chartType} isDark={isDark}
        />

        {/* Legend below */}
        <div style={{ borderTop:"1px solid var(--sep)", marginTop:8 }}>
          <div className="tm5-legend">
            {legendItems.map((it,i)=>{
              const tipKey = it.tipKey;
              const tip = tipKey ? TIPS[tipKey] : null;
              const inner = (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:5, cursor: tip ? "help" : "default" }}>
                  {it.dash ? (
                    <svg width={16} height={10} style={{flexShrink:0}}>
                      <line x1={0} y1={5} x2={16} y2={5} stroke={it.dot}
                        strokeWidth={2} strokeDasharray={it.dash}/>
                    </svg>
                  ) : (
                    <div style={{ width:8, height:8, borderRadius:999, background:it.dot, flexShrink:0 }}/>
                  )}
                  <span style={{ fontFamily:"ui-monospace,monospace", fontSize:10,
                    fontWeight:600, color:"var(--mu)" }}>{it.label}</span>
                </div>
              );
              return tip ? <Tip key={i} {...tip}>{inner}</Tip> : inner;
            })}
          </div>
          {!isShortRange && (showSMA20||showSMA50||showSMA200) && (
            <div style={{ fontSize:10, color:C.amber, fontWeight:600, marginTop:4, opacity:.8 }}>
              ⓘ SMAs affichées uniquement pour 3M, 6M et 1Y (données journalières)
            </div>
          )}
        </div>
      </div>

      {/* ══ 3. RELATIVE STRENGTH HISTORIQUE ══ */}
      <div className="tm5-card">
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:14,
          paddingBottom:11, borderBottom:"1px solid var(--sep)" }}>
          <span style={{ fontFamily:"ui-monospace,monospace", fontSize:11, fontWeight:800,
            textTransform:"uppercase", letterSpacing:".12em", color:"var(--mu)" }}>
            RELATIVE STRENGTH vs NASDAQ (2005→2025)
          </span>
          <TipIcon text={TIPS.rs}/>
        </div>
        <RSChart isDark={isDark} rsData={apiRsData} />
      </div>

      {/* ══ 4. QUATRE PILIERS ══ */}
      <div className="tm5-pillars">

        {/* Structure */}
        {(() => {
          const trendDir  = d?.trend_direction       ?? "Haussière";
          const ltScore   = parseFloat(d?.lt_trend_score  ?? 94.4);
          const stScore   = parseFloat(d?.st_trend_score  ?? 55.3);
          const tqs       = Math.round(parseFloat(d?.trend_quality_score ?? 46));
          const tss       = Math.round(parseFloat(d?.trend_score         ?? 65.2));
          const compScore = Math.round(parseFloat(d?.dd_value_creation_score ?? 90.26));
          const r2val     = parseFloat(d?.r_squared   ?? 0.844);
          const cagrVal   = parseFloat(d?.cagr        ?? 37.3);
          const sma20v    = parseFloat(d?.sma20       ?? 196.171);
          const sma50v    = parseFloat(d?.sma50       ?? 186.943);
          const sma200v   = parseFloat(d?.sma200      ?? 183.721);
          const smaAlign  = d?.sma_alignment          ?? "Alignement haussier parfait";
          const arrow     = trendDir === "Haussière" ? "↑" : trendDir === "Baissière" ? "↓" : "→";
          const tCol      = trendDir === "Haussière" ? C.green : trendDir === "Baissière" ? C.red : C.amber;
          const ltLbl     = ltScore >= 70 ? `${arrow} Haussière forte` : ltScore >= 50 ? `${arrow} Haussière` : "→ Neutre";
          const stLbl     = stScore >= 70 ? `${arrow} Haussière forte` : stScore >= 50 ? `${arrow} Haussière` : stScore >= 30 ? "→ Correction" : "↓ Faiblesse CT";
          const smaShort  = smaAlign.includes("parfait") ? "Fort ↗" : smaAlign.includes("haussier") ? "Haussier ↗" : smaAlign.includes("baissier") ? "Baissier ↘" : "Mixte →";
          const tipLt     = d?.interp_lt_trend_score      ?? TIPS.tendance_5ans;
          const tipSt     = d?.interp_st_trend_score      ?? TIPS.tendance_1an;
          const tipTqs    = d?.interp_trend_quality_score ?? TIPS.trend_quality;
          const tipTss    = d?.interp_trend_score         ?? TIPS.trend_strength;
          const tipR2     = d?.interp_r_squared           ?? TIPS.r2;
          const tipCagr   = d?.interp_cagr                ?? TIPS.cagr_5y;
          const tipAlign  = d?.interp_sma_alignment       ?? TIPS.alignement;
          const tw = t => typeof t === "string" ? { body: t } : t;
          return (
        <PillarCard number={1} title="STRUCTURE DE TENDANCE" score={tss}
          color={colorForPct(tss)} pillar="structure" d={d} onGuide={() => setShowGuide("structure")}
          tip="Analyse de la direction du prix sur 3 horizons + qualité/force + niveaux clés.">
          {/* ── Ligne de synthèse pilier 1 ── */}
          {(() => {
            const synth = d?.interp_trend_global ?? `Structure ${trendDir.toLowerCase()} — CAGR +${cagrVal.toFixed(1)}%, R² ${r2val.toFixed(2)}. ${tss >= 75 ? "Tendance de haute qualité." : tss >= 55 ? "Tendance globalement positive." : "Structure sous pression."}`;
            const col   = colorForPct(tss);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          {[
            { label:"Tendance primaire", value:`${arrow} ${trendDir}`,      color:tCol,                       tip:TIPS.tendance_prim  },
            { label:"Tendance 5 ans",   value:ltLbl,                        color:tCol,                       tip:tw(tipLt)           },
            { label:"Tendance 1 an",    value:stLbl,                        color:stScore>=50?tCol:C.amber,   tip:tw(tipSt)           },
          ].map(r=><MRow key={r.label} {...r}/>)}
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          <MRow label="Trend Quality Score"  value={`${tqs}`}                   tip={tw(tipTqs)}  />
          <MRow label="Trend Strength Score" value={`${tss}`}                   tip={tw(tipTss)}  />
          <MRow label="Compounder Score"     value={`${compScore}`}             tip={TIPS.compounder} />
          <MRow label="R² (log, all)"        value={r2val.toFixed(3)}           tip={tw(tipR2)}   />
          <MRow label="CAGR"                 value={`+${cagrVal.toFixed(1)}%`}  tip={tw(tipCagr)} />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          <MRow label="SMA 20"  value={sma20v.toFixed(2)}  color={SMA_COL.s20}  tip={TIPS.sma20}  />
          <MRow label="SMA 50"  value={sma50v.toFixed(2)}  color={SMA_COL.s50}  tip={TIPS.sma50}  />
          <MRow label="SMA 200" value={sma200v.toFixed(2)} color={SMA_COL.s200} tip={TIPS.sma200} />
          <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",margin:"7px 0",
            display:"flex",alignItems:"center",gap:5}}>
            <Tip {...tw(tipAlign)}>Alignement SMA</Tip>
            <span style={{color:C.green,fontWeight:800}}>{smaShort}</span>
          </div>
        </PillarCard>
          );
        })()}

        {/* Momentum */}
        <PillarCard number={2} title="FORCE / MOMENTUM" score={scoreMomentum}
          color={colorForPct(scoreMomentum)} pillar="momentum" d={d} onGuide={() => setShowGuide("momentum")}
          tip="Analyse du momentum absolu et relatif : TSMOM, leadership, force interne.">

          {/* ── Ligne de synthèse pilier 2 ── */}
          {(() => {
            const synth = d?.interp_momentum_global ?? `Momentum ${d?.momentum_direction ?? "en retournement baissier"}. TSMOM 12M ${parseFloat(d?.tsmom_12m??60.12)>=0?"+":""}${parseFloat(d?.tsmom_12m??60.12).toFixed(1)}% / 3M ${parseFloat(d?.tsmom_3m??-6.49)>=0?"+":""}${parseFloat(d?.tsmom_3m??-6.49).toFixed(1)}%.`;
            const col   = colorForPct(scoreMomentum);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}

          {/* ── Badge momentum_direction ── */}
          {(() => {
            const dir = d?.momentum_direction ?? "Retournement baissier";
            const pat = d?.momentum_pattern   ?? "Transition / Mixte";
            const str = d?.momentum_strength  ?? "Faible";
            const dirMeta = {
              "Haussier":              { col:C.green,  icon:"↑", bg:`${C.green}12`  },
              "Baissier":              { col:C.red,    icon:"↓", bg:`${C.red}12`    },
              "Retournement haussier": { col:C.green,  icon:"⟳", bg:`${C.green}12`  },
              "Retournement baissier": { col:C.orange, icon:"⚠", bg:`${C.orange}12` },
              "Neutre / Range":        { col:C.amber,  icon:"→", bg:`${C.amber}10`  },
              "Mixte / Indéterminé":   { col:"var(--mu)", icon:"~", bg:"var(--sur)" },
            };
            const strMeta = {
              "Fort":  { col:C.green,  dot:"●" },
              "Moyen": { col:C.amber,  dot:"●" },
              "Faible":{ col:C.red,    dot:"●" },
            };
            const m = dirMeta[dir] || dirMeta["Mixte / Indéterminé"];
            const s = strMeta[str] || strMeta["Moyen"];
            return (
              <div style={{marginBottom:11}}>
                {/* Ligne principale : direction + force */}
                <Tip {...TIPS.mom_direction}>
                  <div style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"8px 11px", borderRadius:9,
                    background:m.bg, border:`1px solid ${m.col}30`,
                    cursor:"default",
                  }}>
                    <div style={{display:"flex", alignItems:"center", gap:7}}>
                      <span style={{fontSize:16, lineHeight:1, color:m.col}}>{m.icon}</span>
                      <div>
                        <div style={{fontSize:9, fontWeight:700, textTransform:"uppercase",
                          letterSpacing:".09em", color:"var(--mu)", marginBottom:1}}>
                          DIRECTION MOMENTUM
                        </div>
                        <div style={{fontSize:12, fontWeight:800, color:m.col}}>{dir}</div>
                      </div>
                    </div>
                    {/* Intensité à droite */}
                    <Tip {...TIPS.mom_strength}>
                      <div style={{
                        display:"flex", flexDirection:"column", alignItems:"flex-end", gap:1,
                        cursor:"default",
                      }}>
                        <div style={{fontSize:8, fontWeight:700, textTransform:"uppercase",
                          letterSpacing:".08em", color:"var(--mu)"}}>INTENSITÉ</div>
                        <div style={{display:"flex", alignItems:"center", gap:4}}>
                          <span style={{fontSize:10, color:s.col}}>{s.dot}</span>
                          <span style={{fontSize:11, fontWeight:800, color:s.col,
                            fontFamily:"ui-monospace,monospace"}}>{str}</span>
                        </div>
                      </div>
                    </Tip>
                  </div>
                </Tip>
                {/* Ligne pattern */}
                <Tip {...TIPS.mom_pattern}>
                  <div style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"5px 11px", marginTop:4, borderRadius:7,
                    background:"var(--sur)", border:"1px solid var(--sep)",
                    cursor:"default",
                  }}>
                    <span style={{fontSize:9, fontWeight:700, textTransform:"uppercase",
                      letterSpacing:".08em", color:"var(--mu)"}}>PATTERN</span>
                    <span style={{fontSize:10, fontWeight:700,
                      color:"var(--tx)", fontFamily:"ui-monospace,monospace"}}>{pat}</span>
                  </div>
                </Tip>
              </div>
            );
          })()}

          {/* ── Barres TSMOM ── */}
          {(() => {
            const t12 = parseFloat(d?.tsmom_12m ?? 60.12);
            const t6  = parseFloat(d?.tsmom_6m  ?? -6.53);
            const t3  = parseFloat(d?.tsmom_3m  ?? -6.49);
            const bar = v => Math.max(2, Math.min(98, 50 + v));
            const tw  = t => typeof t === "string" ? { body: t } : t;
            return [
              { label:"TSMOM 12M", value:`${t12>=0?"+":""}${t12.toFixed(1)}%`, color:t12>=0?C.green:C.red, bar:bar(t12), tip:tw(d?.interp_tsmom_12m ?? TIPS.tsmom12) },
              { label:"TSMOM 6M",  value:`${t6>=0?"+":""}${t6.toFixed(1)}%`,   color:t6>=0?C.green:C.red,  bar:bar(t6),  tip:tw(d?.interp_tsmom_6m  ?? TIPS.tsmom6)  },
              { label:"TSMOM 3M",  value:`${t3>=0?"+":""}${t3.toFixed(1)}%`,   color:t3>=0?C.green:C.red,  bar:bar(t3),  tip:tw(d?.interp_tsmom_3m  ?? TIPS.tsmom3)  },
            ].map(r=>(
            <div key={r.label} style={{marginBottom:8}}>
              <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                <Tip {...(typeof r.tip==="string"?{body:r.tip}:r.tip)}>
                  <span style={{fontSize:11,color:"var(--mu)",fontWeight:600}}>{r.label}</span>
                </Tip>
                <span style={{fontFamily:"ui-monospace,monospace",fontSize:11,
                  fontWeight:800,color:r.color}}>{r.value}</span>
              </div>
              <div style={{height:5,borderRadius:999,background:"var(--sep)",overflow:"hidden"}}>
                <div style={{height:"100%",width:`${r.bar}%`,background:r.color,borderRadius:999}}/>
              </div>
            </div>
          ));
          })()}

          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          {(() => {
            const tw = t => typeof t === "string" ? { body: t } : t;
            const momScore  = parseFloat(d?.dd_momentum_score ?? d?.momentum_score ?? 53.86);
            const ldScore   = Math.round(parseFloat(d?.dd_momentum_score ?? 53.86));
            const fip       = parseFloat(d?.fip_score ?? -0.034);
            const joursPos  = parseFloat(d?.positive_days_pct ?? 51.71);
            const m1        = parseFloat(d?.last_return_m1 ?? 14.43);
            const ytd       = parseFloat(d?.ytd_pct ?? 7.01);
            return (<>
              <MRow label="Momentum Score"     value={`${Math.round(momScore)}`}          tip={tw(d?.interp_momentum_score    ?? TIPS.mom_score)}  />
              <MRow label="Leadership Score"   value={`${ldScore}`}                        tip={TIPS.leadership}                                    />
              <MRow label="FIP (Qualité)"      value={fip.toFixed(3)}                      tip={tw(d?.interp_fip_score         ?? TIPS.fip)}         />
              <MRow label="Jours positifs (%)" value={`${joursPos.toFixed(1)}%`}           tip={tw(d?.interp_positive_days_pct ?? TIPS.jours_pos)}   />
              <MRow label="M1 Return"          value={`${m1>=0?"+":""}${m1.toFixed(1)}%`}  tip={tw(d?.interp_last_return_m1    ?? TIPS.m1_return)}   />
              <MRow label="YTD Return"         value={`${ytd>=0?"+":""}${ytd.toFixed(1)}%`} tip={TIPS.ytd}                                           />
            </>);
          })()}
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* ── Bloc divergence ── */}
          {(() => {
            const t12pos     = parseFloat(d?.tsmom_12m ?? 60.12) > 0;
            const t3pos      = parseFloat(d?.tsmom_3m  ?? -6.49) > 0;
            const isDiverg   = t12pos && !t3pos;
            const divText    = d?.interp_momentum_divergence ?? null;
            const dir        = d?.momentum_direction ?? "Retournement baissier";
            return isDiverg ? (
              <div style={{borderRadius:9,border:`1px solid ${C.orange}44`,background:`${C.orange}0d`,padding:"9px 10px"}}>
                <div style={{fontSize:10,fontWeight:800,color:C.orange,marginBottom:3,display:"flex",alignItems:"center",gap:5}}>
                  Divergence : OUI ⚠ <TipIcon text={TIPS.divergence}/>
                </div>
                <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
                  {divText ?? `TSMOM 12M positif mais CT négatif — ${dir.toLowerCase()}.`}
                </div>
              </div>
            ) : (
              <div style={{borderRadius:9,border:"1px solid var(--sep)",background:"var(--sur)",padding:"9px 10px"}}>
                <div style={{fontSize:10,fontWeight:700,color:"var(--mu)",display:"flex",alignItems:"center",gap:5}}>
                  Cohérence momentum ✓
                </div>
                <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
                  Les horizons 3M / 6M / 12M convergent dans la même direction.
                </div>
              </div>
            );
          })()}
        </PillarCard>

        {/* Volume */}
        {(() => {
          const tw        = t => typeof t === "string" ? { body: t } : t;
          const vScore    = Math.round(parseFloat(d?.volume_score         ?? 68));
          const oppScore  = parseFloat(d?.opportunity_score               ?? 9.21);
          const liqScore  = Math.round(parseFloat(d?.liquidity_score      ?? 0.9995) * 100);
          const r20d      = parseFloat(d?.volume_ratio_20d                ?? 1.4767);
          const r60d      = parseFloat(d?.volume_ratio_60d                ?? 1.2805);
          const suppVR    = parseFloat(d?.support_vol_ratio               ?? 1.043);
          const resVR     = parseFloat(d?.resist_vol_ratio                ?? 1.377);
          const absorption= d?.absorption_distribution                    ?? "Neutral";
          const wyckoff   = d?.wyckoff_context                            ?? "Distribution";
          const wyckoffEv = d?.wyckoff_event                              ?? "UTAD";
          const priceM1   = parseFloat(d?.last_return_m1                  ?? 14.43);
          // volume_trend_20_60 : si absent, déduire depuis les ratios (20d > 60d = Rising)
          const _rawTrend  = d?.volume_trend_20_60 ?? null;
          const trend20_60 = _rawTrend ?? (r20d >= r60d * 1.05 ? "Rising" : r20d <= r60d * 0.95 ? "Falling" : "Neutral");
          // Divergence : prix monte MAIS volumes en baisse relative ET ratio 20J sous la normale
          const volDiverg = priceM1 > 2 && r20d < 0.85;
          const rCol      = v => v >= 1.2 ? C.green : v >= 0.85 ? C.amber : C.red;
          const absLabel  = { Absorption:"Absorption", Distribution:"Distribution", Neutral:"Neutre" }[absorption] || absorption;
          const absCol    = absorption === "Absorption" ? C.green : absorption === "Distribution" ? C.red : "var(--mu)";
          const wyCol     = wyckoff === "Distribution" ? C.orange : wyckoff === "Accumulation" ? C.green : "var(--mu)";
          const wyLabel   = wyckoffEv && wyckoffEv !== "None" ? `${wyckoff} — ${wyckoffEv}` : wyckoff;

          return (
        <PillarCard number={3} title="PARTICIPATION / VOLUME" score={vScore}
          color={colorForPct(vScore)} pillar="volume" d={d} onGuide={() => setShowGuide("volume")}
          tip="Analyse de la participation des volumes à la tendance. Phase Wyckoff.">
          {/* ── Ligne de synthèse pilier 3 ── */}
          {(() => {
            const synth = d?.interp_volume_global ?? `Phase ${wyckoff}${wyckoffEv&&wyckoffEv!=="None"?" — "+wyckoffEv:""}. Ratio 20J : ${r20d.toFixed(2)}x. ${wyckoff==="Distribution"?"Signal de prudence sur les entrées.":""}`;
            const col   = colorForPct(vScore);
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          <MRow label="Volume Score"    value={vScore}             tip={tw(d?.interp_volume_global    ?? TIPS.vol_score)} />
          <MRow label="Liquidité Score" value={`${liqScore}`}      tip={tw(d?.interp_liquidity        ?? TIPS.liq_score)} />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>
          <MRow label="Ratio Volume 20J" value={`${r20d.toFixed(2)}x`} color={rCol(r20d)} tip={tw(d?.interp_volume_ratio_20j ?? TIPS.vol_20j)} />
          <MRow label="Ratio Volume 60J" value={`${r60d.toFixed(2)}x`} color={rCol(r60d)} tip={tw(d?.interp_volume_ratio_60j ?? TIPS.vol_60j)} />
          <MRow label="Support Vol. Ratio"
                value={suppVR != null ? suppVR.toFixed(2) : "—"}
                color={suppVR >= 1 ? C.green : C.red}
                tip={TIPS.supp_vol}  />
          <MRow label="Résistance Vol. Ratio"
                value={resVR != null ? resVR.toFixed(2) : "—"}
                tip={TIPS.res_vol}   />
          <MRow label="Absorption Signal" value={absLabel} color={absCol} tip={tw(d?.interp_absorption  ?? TIPS.absorption)} />
          <MRow label="Phase Wyckoff"     value={wyLabel}  color={wyCol}  tip={tw(d?.interp_wyckoff_phase ?? TIPS.wyckoff)}   />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* Prix / Volume divergence */}
          {/* Prix / Volume — contexte Wyckoff */}
          {(() => {
            const isDistrib = wyckoff === "Distribution" || wyckoff === "Markdown";
            const trendCol  = isDistrib && trend20_60 === "Rising"
              ? C.orange
              : trend20_60 === "Rising"  ? C.green
              : trend20_60 === "Falling" ? C.red
              : C.amber;
            const trendIcon  = trend20_60 === "Rising" ? "↑" : trend20_60 === "Falling" ? "↓" : "→";
            const showAlert  = volDiverg || (isDistrib && trend20_60 === "Rising");
            const alertLabel = volDiverg ? "⚠ Divergence" : "⚠ Contexte";
            return (
              <div style={{borderRadius:9,
                border:`1px solid ${showAlert ? C.orange+"44" : "var(--sep)"}`,
                background: showAlert ? `${C.orange}0d` : "var(--sur)",
                padding:"9px 10px"}}>
                <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".09em",
                  color:"var(--mu)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
                  PRIX / VOLUME <TipIcon text={TIPS.prix_vol}/>
                </div>
                <div style={{display:"flex",gap:14,alignItems:"center"}}>
                  <div>
                    <div style={{fontSize:9,color:"var(--mu)",fontWeight:600}}>Perf. M1</div>
                    <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:800,
                      color: priceM1 >= 0 ? C.green : C.red}}>
                      {priceM1 >= 0 ? "↑" : "↓"} {priceM1 >= 0 ? "+" : ""}{priceM1.toFixed(1)}%
                    </div>
                  </div>
                  <div style={{width:1,height:28,background:"var(--sep)"}}/>
                  <div>
                    <div style={{fontSize:9,color:"var(--mu)",fontWeight:600}}>Tendance Vol. 20/60J</div>
                    <div style={{fontFamily:"ui-monospace,monospace",fontSize:13,fontWeight:800,
                      color: trendCol}}>
                      {trendIcon} {trend20_60}
                    </div>
                  </div>
                  {showAlert && (
                    <div style={{marginLeft:"auto",fontSize:9,fontWeight:800,color:C.orange}}>
                      {alertLabel}
                    </div>
                  )}
                </div>
                {isDistrib && trend20_60 === "Rising" && (
                  <p style={{margin:"8px 0 0",fontSize:9.5,color:`${C.orange}dd`,
                    lineHeight:1.6,fontStyle:"italic"}}>
                    Volume élevé en phase de Distribution : les institutionnels allègent sur la demande forte de la foule. Le volume confirme la distribution, pas la hausse.
                  </p>
                )}
              </div>
            );
          })()}
        </PillarCard>
          );
        })()}

        {/* Timing */}
        {(() => {
          const tw          = t => typeof t === "string" ? { body: t } : t;
          const rsi14       = parseFloat(d?.rsi_14            ?? 60.81);
          const zScore      = parseFloat(d?.z_score           ?? 1.317);
          const oppScore    = parseFloat(d?.opportunity_score ?? 9.21);
          const timingScore = parseFloat(d?.timing_quality    ?? 40);
          const tscore      = parseFloat(d?.tscore            ?? 32.3);
          const coherence   = parseInt(d?.coherence_score     ?? -1);
          const coherenceLbl= d?.coherence_label              ?? "Marché en tension — point de bascule";
          const crossSignal = d?.crossover_signal             ?? "+1σ breakout";
          const valStatus   = d?.valuation_status             ?? "Surévalué";
          const tscorelbl   = tscore >= 75 ? "ENTRER"
                            : tscore >= 60 ? "SURVEILLER"
                            : tscore >= 45 ? "ATTENDRE"
                            : "ÉVITER";
          const tscoreCol   = tscore >= 75 ? C.green
                            : tscore >= 60 ? C.green
                            : tscore >= 45 ? C.amber
                            : C.red;
          const oppPct      = `${Math.round(oppScore)}%`;
          const coherenceCol= coherence >= 3 ? C.green
                            : coherence >= 1 ? C.green
                            : coherence >= -1 ? C.amber
                            : C.red;
          // Jauge position (0–100 → 0%–100% left)
          const gaugePos    = `${Math.max(2, Math.min(96, oppScore))}%`;

          return (
        <PillarCard number={4} title="TIMING / OPPORTUNITÉ" score={Math.round(timingScore)}
          color={colorForPct(Math.round(timingScore))} pillar="timing" d={d} onGuide={() => setShowGuide("timing")}
          tip="Analyse du point d'entrée optimal : RSI, Z-Score, cohérence inter-timeframes.">
          {/* ── Ligne de synthèse pilier 4 ── */}
          {(() => {
            const synth = d?.interp_action
              ? d.interp_action
              : `RSI ${rsi14.toFixed(1)} — ${rsi14>=70?"surachat":rsi14>=55?"zone de force":rsi14<=30?"survente":"neutre"}. Z-Score ${zScore>=0?"+":""}${zScore.toFixed(2)}. Opp. Score ${oppScore.toFixed(0)}/100.`;
            const col   = colorForPct(Math.round(timingScore));
            return (
              <div style={{borderRadius:8, padding:"7px 10px", marginBottom:10,
                background:`${col}0d`, border:`1px solid ${col}33`,
                fontSize:10, fontWeight:600, color:"var(--ts)", lineHeight:1.55}}>
                {synth}
              </div>
            );
          })()}
          <MRow label="RSI (14)"
                value={rsi14.toFixed(1)}
                color={rsi14 >= 70 ? C.red : rsi14 >= 55 ? C.green : rsi14 <= 30 ? C.red : C.amber}
                tip={tw(d?.interp_rsi_14 ?? TIPS.rsi)} />
          <MRow label="Z-Score (régression)"
                value={`${zScore >= 0 ? "+" : ""}${zScore.toFixed(2)}`}
                color={zScore >= 2 ? C.red : zScore >= 1 ? C.amber : zScore <= -1 ? C.green : "var(--tx)"}
                tip={tw(d?.interp_z_score_timing ?? TIPS.zscore)} />
          <MRow label="Opportunity Score"
                value={`${oppScore.toFixed(0)}/100`}
                color={oppScore >= 65 ? C.green : oppScore >= 50 ? C.amber : C.red}
                tip={tw(d?.interp_opportunity_score ?? TIPS.opp_score)} />
          <MRow label="Timing Score"
                value={`${Math.round(timingScore)}/100`}
                tip={tw(d?.interp_timing_score ?? TIPS.timing_score)} />
          <MRow label="TScore"
                value={`${tscore.toFixed(0)}/100`}
                color={tscoreCol}
                tip={TIPS.tscore} />
          <MRow label="Cohérence inter-piliers"
                value={`${coherence >= 0 ? "+" : ""}${coherence}/6`}
                color={coherenceCol}
                tip={tw(d?.interp_coherence ?? TIPS.coherence)} />
          <MRow label="Signal sigma"
                value={crossSignal || "Zone neutre"}
                color={crossSignal && crossSignal.startsWith("+2") ? C.red
                     : crossSignal && crossSignal.startsWith("+1") ? C.amber
                     : crossSignal && crossSignal.startsWith("-") ? C.green
                     : "var(--mu)"}
                tip={TIPS.cross_sma} />
          <MRow label="Valorisation (régression)"
                value={valStatus}
                color={valStatus === "Surévalué" ? C.orange : valStatus === "Sous-évalué" ? C.green : C.amber}
                tip={TIPS.val_status} />
          <div style={{height:1,background:"var(--sep)",margin:"7px 0"}}/>

          {/* Jauge d'opportunité */}
          <div style={{borderRadius:9,
            border:`1px solid ${tscoreCol}33`,
            background:`${tscoreCol}08`,padding:"10px"}}>
            <div style={{fontSize:9,fontWeight:800,textTransform:"uppercase",letterSpacing:".09em",
              color:"var(--mu)",marginBottom:6,display:"flex",alignItems:"center",gap:4}}>
              JAUGE D'OPPORTUNITÉ <TipIcon text={TIPS.jauge}/>
            </div>
            <div style={{position:"relative",height:11,borderRadius:999,
              background:`linear-gradient(to right,${C.red},${C.amber},${C.green})`,
              marginBottom:5,overflow:"visible"}}>
              <div style={{position:"absolute",top:-3,left:gaugePos,transform:"translateX(-50%)",
                width:7,height:17,borderRadius:2,background:"#fff",
                boxShadow:"0 0 0 1.5px rgba(0,0,0,.18)",zIndex:2}}/>
            </div>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:8,
              color:"var(--mu)",fontWeight:700,marginBottom:8}}>
              {["Faible","Neutre","Bon","Excellent"].map(l=><span key={l}>{l}</span>)}
            </div>
            <div style={{fontSize:10,fontWeight:800,color:tscoreCol,marginBottom:2}}>
              Action suggérée : {tscorelbl}
            </div>
            <div style={{fontSize:10,color:"var(--mu)",fontWeight:600,lineHeight:1.5}}>
              {coherenceLbl}
            </div>
          </div>
        </PillarCard>
          );
        })()}
      </div>

      {/* ── Verdict / Synthèse finale (doc archi générique §2 et §9.6) ── */}
      <VerdictBanner d={d} company={company} scoreGlobal={scoreGlobal}
        marketBias={marketBias} regimeCol={regimeCol}
        actionLabel={actionLabel} actionCol={actionCol}/>

      {/* Footer */}
      <div style={{ display:"flex", justifyContent:"space-between", flexWrap:"wrap",
        gap:6, fontSize:10, color:"var(--mu)", fontWeight:600, opacity:.6, paddingTop:4 }}>
        <span>Les performances passées ne préjugent pas des performances futures.</span>
        <span>Données fournies à titre indicatif</span>
        <span>Dernière mise à jour : {(() => { const dt = new Date(lastUpdated); return dt.toLocaleDateString("fr-FR",{day:"2-digit",month:"2-digit",year:"numeric"}) + " " + dt.toLocaleTimeString("fr-FR",{hour:"2-digit",minute:"2-digit"}); })()}</span>
      </div>

    </div>
    </div>

    {/* ══ EDUCATION MODAL ══ */}
    {eduModal && <EduModal anchor={eduModal.anchor} onClose={closeEdu} isDark={isDark} d={d}/>}
    {/* ══ COURS MODAL ══ */}
    {showCours && <CourseModal onClose={() => setShowCours(false)} isDark={isDark}/>}
    {/* ══ GUIDE MODAL ══ */}
    {showGuide && <GuideModal currentPillar={showGuide} onClose={() => setShowGuide(null)}/>}
  </React.Fragment>
  );
}
